/**
 * Menu Manage Module — Complete implementation.
 *
 * Requirements implemented:
 * 1. View-first navigation (Eye icon), brand selector, custom tag system
 * 2. Professional list/table item builder with OptionGroups (min/max)
 * 3. Simplified Ministry Vault with download & edit
 * 4. Aggregator-style Digital Preview with cart + modifier drawer
 * 5. Navigation Guard (dirty check)
 * 6. Archive & Recovery + Ministry Sync alert + Dashboard deep-linking
 */

import React, { startTransition, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Routes, Route, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { usePersistentState } from '../../hooks/usePersistentState';
import { getRepository, isRemoteMode } from '../../config/repository';
import { saveMenuDraft, publishMenuLive, cloneMenuProfile, discardMenuDraft, unpublishMenu } from '../../data/ApiModuleRepository';
import { uploadFile, getMediaUrl } from '../../core/fileUpload';
import type { User, Brand, Department } from '../../domain/entities';
import { UserRole, isPlatformRole } from '../../domain/entities';
import { ModuleTabs, TabGate } from '../shared/ModuleTabs';
import { hasTabVariant, canAccessTab } from '../../core/security';
import { PLATFORM_MODULES } from '../../core/appRegistry';
import { cn } from '../../ui/utils';
import { hasDataChanged } from '../../core/utils';
import { useToast } from '../../ui/Toast';
import { ConfirmDeleteDialog, type DeleteConfirmState } from '../../ui/ConfirmDeleteDialog';
import { CustomSelect } from '../../components/shared/CustomSelect';
import { BranchPicker } from './shared/BranchPicker';
import {
  BookOpen, Plus, Search, Edit2, Trash2, X, Check, ChevronRight, ChevronDown,
  ChevronUp, Eye, EyeOff, FileText, Upload, AlertTriangle, CheckCircle2, Timer, Clock, Hourglass,
  Package, FolderOpen, Shield, GripVertical, Image as ImageIcon,
  Smartphone, Monitor, RotateCcw, Archive, Download, XCircle,
  ShoppingCart, Minus, ArrowLeft, RefreshCw, Link2, Layers, Settings as SettingsIcon,
  List, Hash, DollarSign, ToggleLeft, Copy, ClipboardPaste, Library, Save,
  Filter, MoreVertical, Activity, ExternalLink, Globe, ChevronLeft, FileSpreadsheet, Info,
  Lock, History, MapPin, Sparkles, Loader2, Unlink, Send, Star, Zap, AlertCircle,
  CircleDollarSign, Languages, Wrench, ArrowUpDown, Square, MinusSquare, TrendingUp,
  Megaphone, Rocket, Bell, PackageCheck,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MenuProfile, MenuCategory, MenuItem, MenuItemOption, OptionGroup,
  MenuProfileType, MenuProfileStatus, MinistryFile, MenuSettings,
  DEFAULT_MENU_SETTINGS, LibraryModifier, SECONDARY_LANGUAGES, CURRENCY_OPTIONS,
  MenuData, PricingStrategy,
  type VaultFileCategory,
  type AvailabilitySchedule, type DayOfWeek, ALL_DAYS, DAY_LABELS, DEFAULT_SCHEDULE, type TimeSlot,
  type MenuChangeRequest, type ChangeRequestStatus, type PendingEntity,
} from './types';
import { isAvailableNow, scheduleLabel, filterByWaterfall, nextSlotLabel, type ClosureReason } from './services/ScheduleService';
import { shouldHidePrice, formatCardPrice } from './utils/priceFormatter';
import { useScrollSpy } from './hooks/useScrollSpy';
import { usePendingChanges, type ProposedChange, computeChangedFields } from './hooks/usePendingChanges';
import { computeMenuDiff, countChanges, type CategoryDiff, type ItemDiff } from './services/MenuDiffService';
import { buildComparison, type ComparisonModel, type ComparisonField, type ChangeCategory } from './services/ComparisonService';
import { submitChangeRequest, fetchChangeRequests, fetchChangeRequestDetail, approveChangeRequest, rejectChangeRequest, fetchMenuSettings, saveMenuSettings, apiFetch } from '../../data/ApiModuleRepository';
import { ReviewTimeline } from '../aggregator/ReviewTimeline';
import { SubmissionStatus } from '../aggregator/types';
import { exportBlankTemplate, exportMenuToExcel, importMenuFromExcel } from './services/MenuExcelEngine';
import type { ImportResult } from './services/MenuExcelEngine';
import { extractBulkPackage, matchImagesToItems } from './services/BulkOnboardingService';
import { exportTemplate1, exportTemplate2, exportTemplateBoth, exportRelationalTemplate1, exportRelationalTemplate2, exportRelationalBoth } from './services/MenuExcelTemplates';
import { exportRelationalMenu } from './services/RelationalExportService';
import { ExportModal } from './components/ExportModal';
import { PantryTab } from './components/PantryTab';
import { RecipeTab } from './components/RecipeTab';
import { generateRecipeBook } from './components/KitchenCardPreview';
import { ExcelToolbar, ImportExcelDialog } from './components/ImportExcelDialog';
import type { MenuImportState } from './components/ImportExcelDialog';
import { MenuBusyDialog } from './components/MenuBusyDialog';
import { useBackgroundTask } from '../../hooks/useBackgroundTask';
import { auditMenuProfile, ISSUE_CONFIG } from './services/MenuHealthAudit';
import type { HealthIssue } from './services/MenuHealthAudit';
import { buildAvailabilityIndex, checkOptionAvailability, toggleGlobalOptionAvailability, bulkToggleByName, searchLibraryOptions, getLinkedProducts, countLinkedProducts, unlinkGroup } from './services/AvailabilityService';
import { translateText, translateBatch, translateBatchJson, isAiCoolingDown, getCooldownRemaining, checkAiEnabled } from '../../core/translateService';
import type { AvailabilityIndex, LibraryOptionMatch, LinkedProductInfo } from './services/AvailabilityService';
import { getModuleName } from './services/ModuleOrchestrationService';
import { isPromoExpired, isPromoActive, discountLabel, fundingLabel, getDisplayPrice, getFinancialBreakdown } from './services/PromoService';
import { useMenuLocalization, MENU_SETTINGS_UPDATED } from './useMenuLocalization';
import {
  DEMO_MENU_PROFILE, DEMO_MENU_PROFILE_2,
  DEMO_MINISTRY_FILE, DEMO_MINISTRY_FILE_2,
} from './data/demoSeed';
import { genId, duplicateItem, duplicateGroup, isWithinBrandScope, MENU_DATA_UPDATED_EVENT, updateItemCategories, linkBulkModifiers, createCleanOptionGroup, createCleanOption, deepCloneItemGroups, deduplicateIds, UNASSIGNED_CAT_ID, UNASSIGNED_ITEM_ID, ensureUnassignedBucket, isUnassignedCat, smartCopyItem, propagateGroupEdit, isSharedGroup, detachGroupCopy, generateGroupCode, generateCategoryCode, generateItemCode, hydrateOptionGroups, extractOptionGroupIds, countCategoryLinkedProducts } from './utils';
import { MenuClipboardProvider, useMenuClipboard } from './MenuClipboardProvider';
import { reorderCategories, reorderItems, reorderGroups, reorderOptions } from './sortUtils';
import { useFinancialVisibility } from './hooks/useFinancialVisibility';
import { LiveToggleSwitch } from '../../ui/LiveToggleSwitch';

type FinancialSettingsSlice = {
  enableRecipeAndCosting: boolean;
  allowManagersToViewCost: boolean;
  allowNonAdminRecipeAccess: boolean;
  allowNonAdminCostAccess: boolean;
};

type FinancialSettingsInput = (Partial<MenuSettings> & {
  enableRecipeAndCosting?: boolean;
  allowManagersToViewCost?: boolean;
}) | Record<string, unknown>;

type MenuSettingsEventDetail = {
  settings?: FinancialSettingsInput | null;
};

function toFinancialSettingsSlice(raw?: FinancialSettingsInput | null): FinancialSettingsSlice {
  const settings = raw as Partial<MenuSettings> & { enableRecipeAndCosting?: boolean; allowManagersToViewCost?: boolean; };
  const isEngineEnabled = !!settings?.enableRecipeAndCosting || !!settings?.allowNonAdminRecipeAccess;
  const managerAllowed = !!settings?.allowManagersToViewCost || !!settings?.allowNonAdminCostAccess;

  return {
    enableRecipeAndCosting: isEngineEnabled,
    allowManagersToViewCost: managerAllowed,
    allowNonAdminRecipeAccess: isEngineEnabled,
    allowNonAdminCostAccess: managerAllowed,
  };
}

const DEFAULT_FINANCIAL_SETTINGS: FinancialSettingsSlice = toFinancialSettingsSlice(DEFAULT_MENU_SETTINGS);

function toMenuSettingsState(raw?: FinancialSettingsInput | null): MenuSettings {
  const nextSettings = { ...DEFAULT_MENU_SETTINGS, ...(raw as Partial<MenuSettings> | undefined) };
  const financialSettings = toFinancialSettingsSlice(raw);

  return {
    ...nextSettings,
    allowNonAdminRecipeAccess: financialSettings.enableRecipeAndCosting,
    allowNonAdminCostAccess: financialSettings.allowManagersToViewCost,
  };
}

function getSettingsFromEvent(event: Event): FinancialSettingsInput | null | undefined {
  return (event as CustomEvent<MenuSettingsEventDetail>).detail?.settings;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Shimmer skeleton overlay for Arabic text field while AI is translating */
function TranslateShimmer() {
  return (
    <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none z-10">
      <div className="absolute inset-0 ai-shimmer-input" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex items-center gap-1.5 text-[10px] font-bold text-violet-500">
          <Sparkles size={11} className="animate-pulse" /> AI translating…
        </div>
      </div>
    </div>
  );
}

/** Inline translate (magic wand) button for Arabic fields with cooldown + clear */
function TranslateBtn({ sourceText, onResult, onClear, size = 'sm', currentValue }: {
  sourceText: string;
  onResult: (t: string) => void;
  onClear?: () => void;
  size?: 'sm' | 'xs';
  currentValue?: string;
}) {
  const [busy, setBusy] = React.useState(false);
  const [cooldownSec, setCooldownSec] = React.useState(0);
  const toast = useToast();

  // Cooldown ticker
  React.useEffect(() => {
    if (!isAiCoolingDown()) return;
    setCooldownSec(getCooldownRemaining());
    const timer = setInterval(() => {
      const rem = getCooldownRemaining();
      setCooldownSec(rem);
      if (rem <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [busy]);

  const handle = async (retry = false) => {
    if (!sourceText.trim()) {
      toast.error('Nothing to translate', 'Fill in the English field first.');
      return;
    }
    if (isAiCoolingDown()) {
      toast.error('AI is resting', `Ready in ${getCooldownRemaining()} seconds.`);
      return;
    }

    setBusy(true);
    try {
      const translated = await translateText(sourceText, 'ar', retry);
      onResult(translated);
    } catch (err: any) {
      toast.error('Translation Failed', err?.message || 'Could not translate text.');
    } finally {
      setBusy(false);
      setCooldownSec(getCooldownRemaining());
    }
  };

  const ic = size === 'xs' ? 10 : 12;
  const px = size === 'xs' ? 'p-1' : 'p-1.5';
  const isCooling = cooldownSec > 0;

  return (
    <span className="inline-flex items-center gap-0.5 shrink-0">
      <button type="button" onClick={() => handle(false)} disabled={busy || isCooling}
        title={isCooling ? `AI is resting… ready in ${cooldownSec}s` : 'Auto-translate from English'}
        className={cn(px, 'hover:bg-violet-100 rounded-md transition-colors disabled:opacity-50')}>
        {busy ? <Loader2 size={ic} className="text-violet-500 animate-spin" /> : <Sparkles size={ic} className={isCooling ? 'text-zinc-300' : 'text-violet-500'} />}
      </button>
      {currentValue && (
        <button type="button" onClick={() => handle(true)} disabled={busy || isCooling}
          title="Re-translate with enhanced food-menu context"
          className={cn(px, 'hover:bg-amber-50 rounded-md transition-colors disabled:opacity-50')}>
          <RotateCcw size={ic} className={isCooling ? 'text-zinc-300' : 'text-amber-500'} />
        </button>
      )}
      {onClear && currentValue && (
        <button type="button" onClick={onClear} title="Clear AI translation"
          className={cn(px, 'hover:bg-red-50 rounded-md transition-colors')}>
          <X size={ic} className="text-zinc-300 hover:text-red-400" />
        </button>
      )}
    </span>
  );
}

/** Bulk translate all Arabic fields at once (one Gemini request via batch JSON) */
function TranslateAllBtn({ fields }: { fields: { key: string; source: string; onResult: (v: string) => void }[] }) {
  const [busy, setBusy] = React.useState(false);
  const [cooldownSec, setCooldownSec] = React.useState(0);
  const toast = useToast();

  React.useEffect(() => {
    if (!isAiCoolingDown()) return;
    setCooldownSec(getCooldownRemaining());
    const timer = setInterval(() => {
      const rem = getCooldownRemaining();
      setCooldownSec(rem);
      if (rem <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [busy]);

  const handle = async () => {
    if (isAiCoolingDown()) {
      toast.error('AI is resting', `Ready in ${getCooldownRemaining()} seconds.`);
      return;
    }
    const toTranslate = fields.filter(f => f.source.trim());
    if (toTranslate.length === 0) { toast.error('Nothing to translate', 'Fill in the English fields first.'); return; }
    setBusy(true);
    try {
      // Build keyed map for batch JSON (1 Gemini request)
      const keyed: Record<string, string> = {};
      toTranslate.forEach(f => { keyed[f.key] = f.source; });
      let results: Record<string, string>;
      try {
        results = await translateBatchJson(keyed);
      } catch (batchErr) {
        // Batch failed — fallback to individual field translation
        toast.error('Batch incomplete', 'Retrying fields individually…');
        results = {};
        for (const f of toTranslate) {
          try {
            results[f.key] = await translateText(f.source);
          } catch { results[f.key] = ''; }
        }
      }
      // Check for missing keys and retry individually
      const missing = toTranslate.filter(f => !results[f.key]);
      if (missing.length > 0 && missing.length < toTranslate.length) {
        toast.error('Partial result', `Retrying ${missing.length} incomplete field${missing.length > 1 ? 's' : ''}…`);
        for (const f of missing) {
          try {
            results[f.key] = await translateText(f.source);
          } catch { /* leave empty */ }
        }
      }
      const applied = toTranslate.filter(f => results[f.key]);
      applied.forEach(f => f.onResult(results[f.key]));
      if (applied.length === toTranslate.length) {
        toast.success('Translated', `${applied.length} field${applied.length > 1 ? 's' : ''} translated!`);
      } else if (applied.length > 0) {
        toast.success('Partially translated', `${applied.length} of ${toTranslate.length} fields translated. Enter remaining manually.`);
      } else {
        toast.error('Translation failed', 'All fields failed. Check AI quota or try again.');
      }
    } catch (e: any) {
      if (e.message === 'AI_COOLDOWN') {
        setCooldownSec(getCooldownRemaining());
        toast.error('AI is resting', `Ready in ${getCooldownRemaining()} seconds.`);
      } else {
        const msg = e.message || 'Unknown error';
        toast.error('Translation failed', msg);
      }
    } finally { setBusy(false); }
  };
  const isCooling = cooldownSec > 0;
  return (
    <button type="button" onClick={handle} disabled={busy || isCooling}
      title={isCooling ? `AI is resting… ready in ${cooldownSec}s` : undefined}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 border border-violet-200 text-violet-700 rounded-lg text-[10px] font-bold hover:bg-violet-100 transition-colors disabled:opacity-50">
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} {busy ? 'Translating…' : isCooling ? `AI resting… ${cooldownSec}s` : '✨ Translate All Fields'}
    </button>
  );
}

function fmtDate(ts: number) {
  return new Date(ts).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function menuProfileToMenuData(profile: MenuProfile, avail?: AvailabilityIndex): MenuData {
  const gh = avail?.globalHiddenSet;
  const isOptVisible = (o: MenuItemOption, grp: OptionGroup) => {
    if (o.hidden) return false;
    if (gh && gh.size > 0 && grp.linkedLibraryId && o.libraryOptionId && gh.has(o.libraryOptionId)) return false;
    return true;
  };
  return {
    title: { en: profile.name || profile.brandName },
    brandName: profile.brandName,
    categories: profile.categories.filter(c => !c.hidden && !isUnassignedCat(c.id)).map(cat => ({
      name: { en: cat.nameEN, ar: cat.nameAR },
      items: cat.items.filter(i => !i.hidden).map(item => ({
        name: { en: item.nameEN, ar: item.nameAR },
        description: { en: item.descriptionEN || '', ar: item.descriptionAR || '' },
        price: item.basePrice,
        image: item.image,
        choiceGroups: (item.optionGroups && item.optionGroups.length > 0
          ? item.optionGroups.filter(g => !g.hidden).map(grp => ({
              name: { en: grp.nameEN, ar: grp.nameAR },
              min: grp.minSelection || 0,
              max: grp.maxSelection || 0,
              options: grp.options.filter(o => isOptVisible(o, grp)).map(opt => ({
                name: { en: opt.nameEN, ar: opt.nameAR },
                priceModifier: opt.extraPrice,
              })),
            }))
          : item.options && item.options.length > 0
            ? [{
                name: { en: 'Options', ar: 'خيارات' },
                min: 0,
                max: 0,
                options: item.options.filter(o => !o.hidden).map(opt => ({
                  name: { en: opt.nameEN, ar: opt.nameAR },
                  priceModifier: opt.extraPrice,
                })),
              }]
            : []
        ),
      })),
    })),
  };
}

function safeExportName(s: string | null | undefined): string {
    if (!s) return 'Unknown';
  return s.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').trim();
}

const STATUS_STYLES: Record<MenuProfileStatus, string> = {
  [MenuProfileStatus.DRAFT]: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  [MenuProfileStatus.PUBLISHED]: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  [MenuProfileStatus.ARCHIVED]: 'bg-slate-100 text-slate-500 border-slate-200',
};

const TAG_COLORS = [
  'bg-indigo-50 text-indigo-700 border-indigo-200',
  'bg-amber-50 text-amber-700 border-amber-200',
  'bg-violet-50 text-violet-700 border-violet-200',
  'bg-rose-50 text-rose-700 border-rose-200',
  'bg-teal-50 text-teal-700 border-teal-200',
  'bg-cyan-50 text-cyan-700 border-cyan-200',
];

function tagColor(tag: string) {
  let h = 0;
  for (let i = 0; i < tag.length; i++) h = ((h << 5) - h + tag.charCodeAt(i)) | 0;
  return TAG_COLORS[Math.abs(h) % TAG_COLORS.length];
}

function base64Download(base64Data: string, mimeType: string, fileName: string) {
  if (!base64Data) return;
  let raw = base64Data;
  if (raw.includes(',')) raw = raw.split(',')[1];
  const byteChars = atob(raw);
  const arr = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) arr[i] = byteChars.charCodeAt(i);
  const blob = new Blob([arr], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface MenuBusyState {
  mode: 'opening' | 'saving';
  title: string;
  message: string;
  detail?: string;
  estimateLabel?: string;
}

function hasImportChanges(report: ImportResult) {
  return (
    report.categoriesCreated +
    report.categoriesUpdated +
    report.itemsCreated +
    report.itemsUpdated +
    report.groupsCreated +
    report.optionsCreated
  ) > 0;
}

function menuComplexityScore(profile: MenuProfile) {
  const cats = profile.categories.filter(c => !isUnassignedCat(c.id));
  const itemCount = cats.reduce((sum, category) => sum + category.items.length, 0);
  const groupCount = cats.reduce((sum, category) => sum + category.items.reduce((itemSum, item) => itemSum + (item.optionGroups?.length || 0), 0), 0);
  const optionCount = cats.reduce((sum, category) => sum + category.items.reduce((itemSum, item) => itemSum + (item.optionGroups || []).reduce((groupSum, group) => groupSum + group.options.length, 0), 0), 0);
  return cats.length + itemCount + groupCount + optionCount;
}

function isHeavyMenu(profile: MenuProfile) {
  return menuComplexityScore(profile) >= 80;
}

function busyEstimateLabel(profile: MenuProfile, mode: MenuBusyState['mode']) {
  const score = menuComplexityScore(profile);
  const seconds = mode === 'opening'
    ? Math.max(1, Math.min(6, Math.round(score / 65)))
    : Math.max(1, Math.min(8, Math.round(score / 55)));
  return `Expected wait: about ${seconds}-${seconds + 1}s for this menu size.`;
}

function waitForNextPaint() {
  return new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
}

function readFileAsArrayBuffer(file: File, onProgress?: (ratio: number) => void) {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();

    reader.onprogress = (event) => {
      if (!event.lengthComputable) return;
      onProgress?.(event.loaded / event.total);
    };

    reader.onerror = () => reject(new Error('Could not read the selected file.'));
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.readAsArrayBuffer(file);
  });
}

async function runMenuImportWorkflow({
  file,
  existingCategories,
  strategy,
  applyCategories,
  setState,
}: {
  file: File;
  existingCategories: MenuCategory[];
  strategy: import('./components/ImportExcelDialog').ImportStrategy;
  applyCategories: (categories: MenuCategory[], report: ImportResult) => void | Promise<void>;
  setState: React.Dispatch<React.SetStateAction<MenuImportState | null>>;
}) {
  const isZip = file.name.toLowerCase().endsWith('.zip');
  setState({ fileName: file.name, stage: 'reading', progress: 8 });

  try {
    let buffer: ArrayBuffer;
    let imageFiles: Map<string, File> | undefined;

    if (isZip) {
      // ── ZIP Bulk Package: extract Excel + images ──
      setState({ fileName: file.name, stage: 'reading', progress: 12 });
      const pkg = await extractBulkPackage(file);
      buffer = pkg.excelBuffer;
      imageFiles = pkg.imageFiles;
      setState({ fileName: file.name, stage: 'analyzing', progress: 30 });
    } else {
      // ── Regular Excel import ──
      buffer = await readFileAsArrayBuffer(file, (ratio) => {
        setState((prev) => ({
          fileName: file.name,
          stage: 'reading',
          progress: Math.max(prev?.progress ?? 0, Math.round(8 + (ratio * 30))),
          report: prev?.report,
        }));
      });
      setState((prev) => ({ fileName: file.name, stage: 'analyzing', progress: 44, report: prev?.report }));
    }

    await waitForNextPaint();

    // For 'replace' strategy, pass empty array so importMenuFromExcel builds fresh
    const mergeBase = strategy === 'replace' ? [] : existingCategories;
    const { categories, report } = importMenuFromExcel(buffer, mergeBase);

    setState({ fileName: file.name, stage: 'mapping', progress: 60, report });
    await waitForNextPaint();

    // ── ZIP: Match images from ZIP to imported items & upload to R2 ──
    if (isZip && imageFiles && imageFiles.size > 0 && report.imageFilenames.size > 0) {
      const { matches, warnings } = matchImagesToItems(categories, imageFiles, report.imageFilenames);
      warnings.forEach(w => report.errors.push(w));

      if (matches.size > 0) {
        let uploaded = 0;
        const total = matches.size;
        setState({ fileName: file.name, stage: 'applying', progress: 65, report });

        for (const [itemId, imageFile] of matches) {
          try {
            const url = await uploadFile(imageFile, { moduleId: 'menu-manage' });
            // Find the item and set its image URL
            for (const cat of categories) {
              const item = cat.items.find(i => i.id === itemId);
              if (item) { item.image = url; break; }
            }
          } catch (e) {
            report.errors.push(`Failed to upload image for item ${itemId}: ${e instanceof Error ? e.message : 'Unknown error'}`);
          }
          uploaded++;
          const pct = 65 + Math.round((uploaded / total) * 23);
          setState({ fileName: file.name, stage: 'applying', progress: pct, report });
        }
      }
    }

    setState({ fileName: file.name, stage: 'mapping', progress: 90, report });
    await waitForNextPaint();

    if (hasImportChanges(report)) {
      setState({ fileName: file.name, stage: 'applying', progress: 95, report });
      await Promise.resolve(applyCategories(categories, report));
    }

    setState({ fileName: file.name, stage: 'done', progress: 100, report });
    return report;
  } catch (error) {
    setState({
      fileName: file.name,
      stage: 'error',
      progress: 100,
      error: error instanceof Error ? error.message : 'Import failed.',
    });
    return null;
  }
}

// ─── Demo Seed (DISABLED — production mode) ──────────────────────────────────

async function seedDemoDataIfNeeded() {
  // Production: no demo menu data. No-op.
  return;
}

// ═══════════════════════════════════════════════════════════════════════════════
// SHARED PREVIEW CONTEXT — Single Source of Truth across Products ↔ Preview tabs
// ═══════════════════════════════════════════════════════════════════════════════

interface MenuPreviewState {
  profiles: MenuProfile[];
  activeProfile: MenuProfile | null;
  activeCatId: string | null;
}

const MenuPreviewCtx = React.createContext<{
  state: MenuPreviewState;
  setProfiles: (p: MenuProfile[]) => void;
  setActiveProfile: (p: MenuProfile | null) => void;
  setActiveCatId: (id: string | null) => void;
}>({
  state: { profiles: [], activeProfile: null, activeCatId: null },
  setProfiles: () => {},
  setActiveProfile: () => {},
  setActiveCatId: () => {},
});

function MenuPreviewProvider({ children }: { children: React.ReactNode }) {
  const [profiles, setProfiles] = useState<MenuProfile[]>([]);
  const [activeProfile, setActiveProfile] = useState<MenuProfile | null>(null);
  const [activeCatId, setActiveCatId] = useState<string | null>(null);

  const value = useMemo(() => ({
    state: { profiles, activeProfile, activeCatId },
    setProfiles,
    setActiveProfile,
    setActiveCatId,
  }), [profiles, activeProfile, activeCatId]);

  return <MenuPreviewCtx.Provider value={value}>{children}</MenuPreviewCtx.Provider>;
}

// ─── PreviewImage: blur placeholder + decoding="async" ──────────────────────

function PreviewImage({ src, alt, className, style }: { src?: string; alt?: string; className?: string; style?: React.CSSProperties }) {
  const [loaded, setLoaded] = useState(false);
  const url = getMediaUrl(src);
  if (!url) return null;
  return (
    <div className={cn('relative overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200', className)} style={style}>
      {!loaded && <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-slate-200/80 to-slate-100/80 backdrop-blur-sm" />}
      <img src={url} alt={alt || ''} decoding="async" loading="lazy" onLoad={() => setLoaded(true)}
        className={cn('w-full h-full object-cover transition-opacity duration-300', loaded ? 'opacity-100' : 'opacity-0')} />
    </div>
  );
}

// ─── MobileSkeleton: pulse animation inside phone frame ─────────────────────

function MobilePreviewSkeleton() {
  return (
    <div className="flex justify-center">
      <div className="w-[390px] bg-white border-2 border-slate-300 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col" style={{ height: '780px' }}>
        <div className="bg-slate-900 h-7 flex items-center justify-center shrink-0"><div className="w-20 h-4 bg-slate-800 rounded-full" /></div>
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 px-5 py-4 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl animate-pulse" />
            <div className="space-y-2"><div className="h-4 w-24 bg-white/30 rounded animate-pulse" /><div className="h-3 w-16 bg-white/20 rounded animate-pulse" /></div>
          </div>
        </div>
        <div className="border-b border-slate-100 px-3 py-2 flex gap-2 shrink-0">
          {[80, 60, 70, 50].map((w, i) => <div key={i} className="h-7 rounded-full bg-slate-100 animate-pulse" style={{ width: w }} />)}
        </div>
        <div className="flex-1 overflow-hidden bg-gray-50 p-3 space-y-2">
          <div className="h-3 w-20 bg-slate-200 rounded animate-pulse mb-3" />
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 flex overflow-hidden">
              <div className="flex-1 p-3 space-y-2">
                <div className="h-3.5 w-28 bg-slate-200 rounded animate-pulse" />
                <div className="h-2.5 w-full bg-slate-100 rounded animate-pulse" />
                <div className="h-3 w-16 bg-indigo-100 rounded animate-pulse mt-2" />
              </div>
              <div className="w-24 h-24 bg-slate-100 animate-pulse" />
            </div>
          ))}
          <div className="h-3 w-20 bg-slate-200 rounded animate-pulse mt-4 mb-3" />
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 flex overflow-hidden">
              <div className="flex-1 p-3 space-y-2">
                <div className="h-3.5 w-32 bg-slate-200 rounded animate-pulse" />
                <div className="h-2.5 w-24 bg-slate-100 rounded animate-pulse" />
                <div className="h-3 w-16 bg-indigo-100 rounded animate-pulse mt-2" />
              </div>
              <div className="w-24 h-24 bg-slate-100 animate-pulse" />
            </div>
          ))}
        </div>
        <div className="bg-slate-100 h-5 flex items-center justify-center shrink-0"><div className="w-28 h-1 bg-slate-300 rounded-full" /></div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SKELETON LOADERS
// ═══════════════════════════════════════════════════════════════════════════════

function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-end justify-between">
        <div><div className="h-7 w-52 bg-slate-200 rounded" /><div className="h-4 w-80 bg-slate-100 rounded mt-2" /></div>
        <div className="h-10 w-32 bg-teal-100 rounded-xl" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => (
          <div key={i} className="bg-white border border-slate-200 p-5 rounded-2xl flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-slate-200" />
            <div><div className="h-2.5 w-12 bg-slate-200 rounded mb-2" /><div className="h-6 w-10 bg-slate-200 rounded" /></div>
          </div>
        ))}
      </div>
      <div className="h-14 bg-white border border-slate-200 rounded-2xl" />
      <div className="grid grid-cols-2 gap-4">
        {[1,2].map(i => <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 h-36" />)}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100"><div className="h-3 w-28 bg-slate-200 rounded" /></div>
        {[1,2,3,4,5].map(i => (
          <div key={i} className="flex items-center gap-4 px-5 py-3 border-t border-slate-50 first:border-t-0">
            <div className="w-9 h-9 bg-indigo-100 rounded-lg" />
            <div className="flex-1"><div className="h-3.5 w-40 bg-slate-200 rounded mb-1" /><div className="h-3 w-56 bg-slate-100 rounded" /></div>
            <div className="h-5 w-12 bg-slate-100 rounded" />
            <div className="h-5 w-16 bg-slate-100 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ModifierLibrarySkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="flex items-center justify-between">
        <div><div className="h-6 w-36 bg-slate-200 rounded" /><div className="h-4 w-64 bg-slate-100 rounded mt-2" /></div>
        <div className="h-10 w-36 bg-indigo-100 rounded-xl" />
      </div>
      <div className="h-10 bg-white border border-slate-200 rounded-xl" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1,2,3,4,5,6].map(i => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-4 h-4 bg-indigo-100 rounded" />
              <div className="h-3.5 w-28 bg-slate-200 rounded flex-1" />
            </div>
            <div className="h-3 w-20 bg-slate-100 rounded mb-2" />
            <div className="flex gap-2 flex-wrap mb-2">
              <div className="h-4 w-16 bg-slate-100 rounded" />
              <div className="h-4 w-20 bg-amber-50 rounded" />
              <div className="h-4 w-20 bg-sky-50 rounded" />
            </div>
            <div className="space-y-1">
              {[1,2,3].map(j => (
                <div key={j} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-100" />
                  <div className="h-3 w-24 bg-slate-100 rounded flex-1" />
                  <div className="h-3 w-8 bg-indigo-100 rounded" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MinistryVaultSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="flex items-end justify-between">
        <div><div className="h-7 w-36 bg-slate-200 rounded" /><div className="h-4 w-64 bg-slate-100 rounded mt-2" /></div>
        <div className="h-10 w-32 bg-teal-100 rounded-xl" />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="border-b border-slate-100 bg-slate-50 flex gap-4 px-5 py-3">
          {[60,80,100,80,60].map((w,i) => <div key={i} className="h-3 bg-slate-200 rounded" style={{width:w}} />)}
        </div>
        {[1,2,3,4].map(i => (
          <div key={i} className="flex items-center gap-4 px-5 py-3 border-t border-slate-50">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-8 h-8 bg-slate-100 rounded-lg" />
              <div className="h-3.5 w-32 bg-slate-200 rounded" />
            </div>
            <div className="h-3.5 w-24 bg-slate-100 rounded" />
            <div className="h-3.5 w-28 bg-slate-100 rounded" />
            <div className="h-3.5 w-20 bg-slate-100 rounded" />
            <div className="flex gap-1 ml-auto"><div className="w-7 h-7 bg-slate-100 rounded" /><div className="w-7 h-7 bg-slate-100 rounded" /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// VERSION HISTORY PANEL (Data Vault)
// ═══════════════════════════════════════════════════════════════════════════════

function VersionHistoryPanel({ profileId, onRestore }: { profileId: string; onRestore: (id: string) => void }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState<{ id: string; menuId: string; publishedBy: string; publishedByName: string; timestamp: number }[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<{ data: typeof versions }>(`/api/menu/profiles/${profileId}/history`);
      setVersions(res.data ?? []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [profileId]);

  useEffect(() => { if (open) loadHistory(); }, [open, loadHistory]);

  const handleRestore = async (historyId: string) => {
    if (!confirm('Restore this version? Current live data will be snapshotted first.')) return;
    setRestoring(historyId);
    try {
      await apiFetch(`/api/menu/profiles/${profileId}/restore`, { method: 'POST', body: JSON.stringify({ historyId }) });
      onRestore(historyId);
      loadHistory();
    } catch (e: any) {
      toast.error('Restore Failed', e?.message ?? 'Unknown error');
    } finally { setRestoring(null); }
  };

  const fmtTs = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' +
           d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors">
        <span className="flex items-center gap-2 text-sm font-bold text-slate-700">
          <History size={15} className="text-indigo-500" /> Version History
          {versions.length > 0 && <span className="text-[10px] font-mono bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded">{versions.length}</span>}
        </span>
        <ChevronDown size={14} className={cn('text-slate-400 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-2 max-h-64 overflow-y-auto">
          {loading && <p className="text-xs text-slate-400 animate-pulse">Loading history...</p>}
          {!loading && versions.length === 0 && (
            <p className="text-xs text-slate-400">No version history yet. Each publish creates an automatic snapshot.</p>
          )}
          {versions.map(v => (
            <div key={v.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
              <div>
                <p className="text-xs font-semibold text-slate-700">{fmtTs(v.timestamp)}</p>
                <p className="text-[10px] text-slate-400">by {v.publishedByName || v.publishedBy}</p>
              </div>
              <button
                onClick={() => handleRestore(v.id)}
                disabled={restoring === v.id}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-[11px] font-bold hover:bg-indigo-100 transition-colors disabled:opacity-50"
              >
                <RotateCcw size={12} /> {restoring === v.id ? 'Restoring...' : 'Restore'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MODULE ROOT
// ═══════════════════════════════════════════════════════════════════════════════

interface Props { user: User | null; }

export function MenuManageModule({ user }: Props) {
  const base = '/apps/menu-manage';
  const isModuleAdmin = hasTabVariant(user, 'menu-manage', 'requests', 'admin');
  const repo = getRepository();
  const [settingsSnapshot, setSettingsSnapshot] = useState<MenuSettings>(DEFAULT_MENU_SETTINGS);

  const applySettingsSnapshot = useCallback((raw?: Partial<MenuSettings> | Record<string, unknown> | null) => {
    setSettingsSnapshot(toMenuSettingsState(raw));
  }, []);

  const loadSettingsSnapshot = useCallback(() => {
    if (isRemoteMode()) {
      fetchMenuSettings()
        .then(raw => applySettingsSnapshot(raw))
        .catch(() => applySettingsSnapshot(DEFAULT_MENU_SETTINGS));
      return;
    }
    repo.getAll<MenuSettings>('menu-settings')
      .then(all => applySettingsSnapshot(all[0] || DEFAULT_MENU_SETTINGS))
      .catch(() => applySettingsSnapshot(DEFAULT_MENU_SETTINGS));
  }, [applySettingsSnapshot, repo]);

  // Pending request count for badge (status must match DB: SUBMITTED / UNDER_REVIEW)
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const refreshPendingCount = useCallback(() => {
    if (!isRemoteMode()) return;
    fetchChangeRequests({ status: 'SUBMITTED' }).then((raw: any[]) => setPendingRequestCount(raw.length)).catch(() => {});
  }, []);
  useEffect(() => { refreshPendingCount(); }, []);
  useEffect(() => {
    const h = () => refreshPendingCount();
    window.addEventListener(MENU_DATA_UPDATED_EVENT, h);
    return () => window.removeEventListener(MENU_DATA_UPDATED_EVENT, h);
  }, []);
  useEffect(() => { loadSettingsSnapshot(); }, [loadSettingsSnapshot]);
  useEffect(() => {
    const handleSettingsUpdated = (event: Event) => {
      const raw = getSettingsFromEvent(event);
      if (raw) {
        applySettingsSnapshot(raw);
        return;
      }
      loadSettingsSnapshot();
    };
    window.addEventListener(MENU_SETTINGS_UPDATED, handleSettingsUpdated);
    return () => window.removeEventListener(MENU_SETTINGS_UPDATED, handleSettingsUpdated);
  }, [applySettingsSnapshot, loadSettingsSnapshot]);

  const tabs = [
    { key: 'dashboard', label: 'Dashboard', to: base, exact: true },
    { key: 'profiles', label: 'Products', to: `${base}/profiles`, exact: false },
    { key: 'library', label: 'Option Library', to: `${base}/library`, exact: false },
    { key: 'pantry', label: 'Ingredient Registry', to: `${base}/pantry`, exact: false },
    { key: 'ministry', label: 'Ministry Vault', to: `${base}/ministry`, exact: false },
    { key: 'preview', label: 'Digital Preview', to: `${base}/preview`, exact: false },
    { key: 'requests', label: isModuleAdmin ? 'Update Requests' : 'My Requests', to: `${base}/requests`, exact: false, badge: pendingRequestCount },
    { key: 'settings', label: 'Settings', to: `${base}/settings`, exact: false },
  ];

  return (
    <MenuClipboardProvider>
    <MenuPreviewProvider>
    <div className="space-y-0">
      <ModuleTabs moduleId="menu-manage" tabs={tabs} user={user} />
      <div className="pt-4">
        <Routes>
          <Route index element={<TabGate moduleId="menu-manage" tabId="dashboard" user={user}><DashboardTab user={user} /></TabGate>} />
          <Route path="profiles/*" element={<TabGate moduleId="menu-manage" tabId="profiles" user={user}><ProfilesTab user={user} /></TabGate>} />
          <Route path="library" element={<TabGate moduleId="menu-manage" tabId="library" user={user}><ModifierLibraryTab user={user} /></TabGate>} />
          <Route path="pantry" element={<TabGate moduleId="menu-manage" tabId="pantry" user={user}><PantryTab user={user} /></TabGate>} />
          <Route path="ministry" element={<TabGate moduleId="menu-manage" tabId="ministry" user={user}><MinistryVaultTab user={user} /></TabGate>} />
          <Route path="preview" element={<TabGate moduleId="menu-manage" tabId="preview" user={user}><PreviewTab user={user} /></TabGate>} />
          <Route path="requests" element={<TabGate moduleId="menu-manage" tabId="requests" user={user}><UpdateRequestsTab user={user} /></TabGate>} />
          <Route path="settings" element={<TabGate moduleId="menu-manage" tabId="settings" user={user}><SettingsTab user={user} /></TabGate>} />
        </Routes>
      </div>
    </div>
    </MenuPreviewProvider>
    </MenuClipboardProvider>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 1. DASHBOARD TAB — with deep-linking
// ═══════════════════════════════════════════════════════════════════════════════

function DashboardTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'menu-manage', 'dashboard', 'admin');
  const repo = getRepository();
  const nav = useNavigate();
  const toast = useToast();
  const { setActiveProfile: setSharedPreviewProfile } = React.useContext(MenuPreviewCtx);
  const [profiles, setProfiles] = useState<MenuProfile[]>([]);
  const [ministryFiles, setMinistryFiles] = useState<MinistryFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // ── Sliding workspace ──
  const [activeView, setActiveView] = useState<'OVERVIEW' | 'DIAGNOSIS'>('OVERVIEW');
  const [diagProfile, setDiagProfile] = useState<MenuProfile | null>(null);
  const [diagCategory, setDiagCategory] = useState<string | null>(null); // expanded issue category

  const loadData = () => {
    seedDemoDataIfNeeded().then(() => {
      Promise.all([
        repo.getAll<MenuProfile>('menu-profiles'),
        repo.getAll<MinistryFile>('ministry-files'),
      ]).then(([p, m]) => {
        const ub = user?.assignedBrands;
        setProfiles(isAdmin ? p : p.filter(pr => !ub || ub.length === 0 || ub.includes(pr.brandId)));
        setMinistryFiles(m);
        setIsLoading(false);
        try { sessionStorage.setItem(DASH_CACHE_KEY, JSON.stringify({ profiles: p, ministryFiles: m, ts: Date.now() })); } catch { }
      });
    });
  };

  const DASH_CACHE_KEY = 'menu_dashboard_cache';
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(DASH_CACHE_KEY);
      if (cached) {
        const { profiles: cp, ministryFiles: cm, ts } = JSON.parse(cached);
        if (Date.now() - ts < 5 * 60 * 1000) {
          const ub = user?.assignedBrands;
          setProfiles(isAdmin ? cp : cp.filter((pr: MenuProfile) => !ub || ub.length === 0 || ub.includes(pr.brandId)));
          setMinistryFiles(cm);
          setIsLoading(false);
        }
      }
    } catch { }
    loadData();
  }, []);

  // ── Listen for MENU_DATA_UPDATED_EVENT to refresh audit data ──
  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener(MENU_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(MENU_DATA_UPDATED_EVENT, handler);
  }, []);

  // ── Pulse bridge: when background poller detects menu status changes, cascade to all listeners ──
  useEffect(() => {
    const handler = () => {
      window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
    };
    window.addEventListener('PULSE_MENU_STATUS_CHANGED', handler);
    return () => window.removeEventListener('PULSE_MENU_STATUS_CHANGED', handler);
  }, []);

  const [healthOpen, setHealthOpen] = useState(false);
  const totalItems = profiles.reduce((a, p) => a + p.categories.filter(c => !isUnassignedCat(c.id)).reduce((b, c) => b + c.items.length, 0), 0);
  const syncProfiles = profiles.filter(p => p.permitSyncRequired || p.ministryFileIds.length === 0);
  const draftN = profiles.filter(p => p.status === MenuProfileStatus.DRAFT).length;
  const pubN = profiles.filter(p => p.status === MenuProfileStatus.PUBLISHED).length;
  const archN = profiles.filter(p => p.status === MenuProfileStatus.ARCHIVED).length;

  // ── Audit computations ──
  const audits = useMemo(() => profiles.map(p => ({ profile: p, report: auditMenuProfile(p) })), [profiles]);
  const avgScore = audits.length ? Math.round(audits.reduce((s, a) => s + a.report.score, 0) / audits.length) : 100;
  const totalIssues = audits.reduce((s, a) => s + a.report.issues.length, 0);
  const withIssues = useMemo(() => audits.filter(a => a.report.issues.length > 0).sort((a, b) => a.report.score - b.report.score), [audits]);
  const healthyCount = audits.filter(a => a.report.score === 100).length;

  // ── Pending approval count for dashboard card ──
  const [dashPendingCount, setDashPendingCount] = useState(0);
  useEffect(() => {
    if (!isRemoteMode() || !isAdmin) return;
    fetchChangeRequests({ status: 'SUBMITTED' }).then((raw: any[]) => setDashPendingCount(raw.length)).catch(() => {});
  }, [isAdmin]);
  useEffect(() => {
    if (!isAdmin) return;
    const h = () => { fetchChangeRequests({ status: 'SUBMITTED' }).then((raw: any[]) => setDashPendingCount(raw.length)).catch(() => {}); };
    window.addEventListener(MENU_DATA_UPDATED_EVENT, h);
    return () => window.removeEventListener(MENU_DATA_UPDATED_EVENT, h);
  }, [isAdmin]);

  // ── Diagnosis data ──
  const diagReport = useMemo(() => diagProfile ? auditMenuProfile(diagProfile) : null, [diagProfile, profiles]);

  // Group issues by type for diagnosis view
  const diagGrouped = useMemo(() => {
    if (!diagReport) return [];
    const map: Record<string, HealthIssue[]> = {};
    diagReport.issues.forEach(i => {
      if (!map[i.type]) map[i.type] = [];
      map[i.type].push(i);
    });
    return Object.entries(map).map(([type, issues]) => ({ type: type as keyof typeof ISSUE_CONFIG, issues }));
  }, [diagReport]);

  // Teleport: navigate to Products tab with deep link
  const handleFixNow = (issue: HealthIssue) => {
    if (!diagProfile) return;
    nav('/apps/menu-manage/profiles', {
      state: {
        deepLinkProfileId: diagProfile.id,
        deepLinkCatId: issue.categoryId,
        deepLinkItemId: issue.itemId,
        highlightField: issue.fieldKey,
        teleportType: issue.type,
        teleportSource: 'dashboard',
      },
    });
    toast.success('Teleporting', `Navigating to "${issue.itemName || issue.categoryName}" for correction.`);
  };

  // ── Category icon config (line-icon style) ──
  const DIAG_ICONS: Record<string, { icon: React.ElementType; borderColor: string; bgColor: string; iconColor: string }> = {
    'pricing-error':   { icon: CircleDollarSign, borderColor: 'border-emerald-300', bgColor: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    'missing-image':   { icon: ImageIcon,        borderColor: 'border-rose-300',    bgColor: 'bg-rose-50',    iconColor: 'text-rose-600' },
    'language-gap':    { icon: Languages,         borderColor: 'border-amber-300',   bgColor: 'bg-amber-50',   iconColor: 'text-amber-600' },
    'description-gap': { icon: FileText,          borderColor: 'border-slate-300',   bgColor: 'bg-slate-50',   iconColor: 'text-slate-600' },
    'ministry-permit': { icon: Shield,            borderColor: 'border-violet-300',  bgColor: 'bg-violet-50',  iconColor: 'text-violet-600' },
    'empty-profile':   { icon: FolderOpen,        borderColor: 'border-slate-300',   bgColor: 'bg-slate-50',   iconColor: 'text-slate-500' },
    'empty-category':  { icon: FolderOpen,        borderColor: 'border-orange-300',  bgColor: 'bg-orange-50',  iconColor: 'text-orange-600' },
  };

  if (isLoading) return <DashboardSkeleton />;

  const ringPct = avgScore;
  const ringColor = avgScore >= 90 ? '#10B981' : avgScore >= 70 ? '#F59E0B' : '#EF4444';
  const ringBg = avgScore >= 90 ? '#D1FAE5' : avgScore >= 70 ? '#FEF3C7' : '#FEE2E2';

  return (
    <div className="relative overflow-hidden">
        {/* ═══════════════ OVERVIEW ═══════════════ */}
        {activeView === 'OVERVIEW' ? (
          <div key="overview">
            <div className="space-y-6">
              <div className="flex items-end justify-between">
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">Menu Management</h2>
                  <p className="text-sm text-slate-500 mt-1">Manage brand menus, ministry compliance, and digital previews.</p>
                </div>
                <button onClick={() => nav('/apps/menu-manage/profiles')} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700 transition-all">
                  <Plus size={14} /> New Profile
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Profiles', value: profiles.length, icon: BookOpen, color: 'bg-indigo-600' },
                  { label: 'Items', value: totalItems, icon: Package, color: 'bg-emerald-600' },
                  { label: 'Categories', value: profiles.reduce((a, p) => a + p.categories.filter(c => !isUnassignedCat(c.id)).length, 0), icon: FolderOpen, color: 'bg-violet-600' },
                  { label: 'Ministry Files', value: ministryFiles.length, icon: FileText, color: 'bg-amber-600' },
                ].map(s => (
                  <div key={s.label} className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex items-center gap-4">
                    <div className={cn('w-11 h-11 rounded-xl flex items-center justify-center text-white', s.color)}><s.icon size={20} /></div>
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{s.label}</p>
                      <p className="text-2xl font-bold tracking-tight">{s.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* ═══ Pending Approvals Alert Card (Admin only) ═══ */}
              {isAdmin && dashPendingCount > 0 && (
                <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-2 border-amber-300 rounded-2xl p-5 flex items-center justify-between shadow-sm animate-pulse-gentle">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center text-white shadow-md">
                      <AlertTriangle size={22} />
                    </div>
                    <div>
                      <p className="text-sm font-extrabold text-amber-900">⚠️ PENDING APPROVALS</p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        <span className="font-bold text-amber-900">{dashPendingCount}</span> change request{dashPendingCount !== 1 ? 's' : ''} awaiting your review
                      </p>
                    </div>
                  </div>
                  <button onClick={() => nav('/apps/menu-manage/requests')}
                    className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white rounded-xl text-xs font-bold hover:bg-amber-700 transition-colors shadow-sm">
                    Review Now <ChevronRight size={14} />
                  </button>
                </div>
              )}

              {/* ═══ Menu Health Center ═══ */}
              {profiles.length > 0 && canAccessTab(user, 'menu-manage', 'profiles') && (() => {
                return (
                  <div className="relative">
                    {/* Trigger bar */}
                    <button onClick={() => {
                      if (healthOpen) { setDiagProfile(null); setDiagCategory('__detailed__'); }
                      setHealthOpen(!healthOpen);
                      if (!healthOpen && totalIssues === 0) {
                        toast.success('All Healthy', `${healthyCount} profile${healthyCount !== 1 ? 's' : ''} scored 100%. No issues found.`);
                      }
                    }}
                      className="w-full flex items-center justify-between px-5 py-3.5 bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-all group">
                      <div className="flex items-center gap-3">
                        <span className="text-base font-bold text-slate-900">Menu Health</span>
                        {totalIssues > 0 ? (
                          <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider health-issue-pulse">YOU HAVE {totalIssues} ISSUE{totalIssues !== 1 ? 'S' : ''}</span>
                        ) : (
                          <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">ALL CLEAR</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2.5">
                        <div className="relative w-10 h-10 rounded-full border-2 border-teal-200 bg-teal-50 flex items-center justify-center">
                          <svg viewBox="0 0 36 36" className="absolute inset-0 w-full h-full -rotate-90">
                            <circle cx="18" cy="18" r="16" fill="none" stroke={ringBg} strokeWidth="2.5" strokeOpacity="0.3" />
                            <circle cx="18" cy="18" r="16" fill="none" stroke={ringColor} strokeWidth="2.5" strokeLinecap="round"
                              strokeDasharray={`${ringPct} ${100 - ringPct}`}
                              style={{animation:'dash 1.2s ease-out forwards'}} />
                          </svg>
                          <Shield size={16} className="text-teal-600 relative z-10" />
                          {totalIssues > 0 && (
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                              {totalIssues > 9 ? '9+' : totalIssues}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Dropdown panel */}
                    <AnimatePresence>
                      {healthOpen && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className={cn('overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg mt-2')}>
                        {!diagProfile ? (
                          <div key="health-list">

                      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                        <div>
                          <p className="text-sm font-bold text-slate-900">Menu Health</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                            {totalIssues > 0 ? 'PROFILES NEEDING ATTENTION' : 'ALL PROFILES HEALTHY'}
                          </p>
                        </div>
                        <button onClick={() => setHealthOpen(false)} className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-slate-600 transition-colors">
                          <X size={11} /> Close
                        </button>
                      </div>

                      {withIssues.length > 0 ? (
                        <div className="max-h-[400px] overflow-y-auto">
                          {withIssues.slice(0, 10).map(({ profile: pr, report: rp }, idx) => {
                            const sc = rp.score;
                            const ringStroke = sc >= 90 ? '#10B981' : sc >= 70 ? '#F59E0B' : '#EF4444';
                            const ringBgStroke = sc >= 90 ? '#D1FAE5' : sc >= 70 ? '#FEF3C7' : '#FEE2E2';
                            const grouped: Record<string, number> = {};
                            rp.issues.forEach(i => { grouped[i.type] = (grouped[i.type] ?? 0) + 1; });
                            const desc = Object.entries(grouped).map(([type, count]) => {
                              const cfg = ISSUE_CONFIG[type as keyof typeof ISSUE_CONFIG];
                              return `${count} ${cfg.label}`;
                            }).join(', ');

                            return (
                              <div key={pr.id}
                                className="flex items-center gap-3.5 px-5 py-3.5 border-b border-slate-50 hover:bg-slate-50/60 transition-colors group/item"
                                style={{animation: healthOpen ? `fadeSlideIn 0.3s ease-out ${idx * 0.05}s both` : 'none'}}>
                                <div className="relative w-10 h-10 shrink-0">
                                  <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                                    <circle cx="18" cy="18" r="15" fill="none" stroke={ringBgStroke} strokeWidth="3" />
                                    <circle cx="18" cy="18" r="15" fill="none" stroke={ringStroke} strokeWidth="3" strokeLinecap="round"
                                      strokeDasharray={`${sc} ${100 - sc}`} style={{ transition: 'stroke-dasharray 0.5s' }} />
                                  </svg>
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-[9px] font-extrabold" style={{ color: ringStroke }}>{sc}%</span>
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[13px] font-semibold text-slate-900 truncate">{pr.name}</p>
                                  <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed truncate">{desc}</p>
                                  <p className="text-[10px] text-slate-400 mt-0.5">{pr.brandName} · {rp.issues.length} issue{rp.issues.length !== 1 ? 's' : ''}</p>
                                </div>
                                <button
                                  onClick={() => { setDiagProfile(pr); setDiagCategory('__detailed__'); }}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition-all shrink-0 whitespace-nowrap">
                                  <Search size={11} /> Diagnose & Fix
                                </button>
                              </div>
                            );
                          })}
                          {withIssues.length > 10 && (
                            <div className="px-5 py-3 text-center border-t border-slate-100">
                              <p className="text-[11px] text-slate-400">{withIssues.length - 10} more profiles with issues</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="px-5 py-8 text-center">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 ring-4 ring-emerald-50 flex items-center justify-center mx-auto mb-2">
                            <CheckCircle2 size={18} className="text-emerald-500" />
                          </div>
                          <p className="text-sm font-bold text-emerald-700">All Menus Are Healthy!</p>
                          <p className="text-[11px] text-slate-400 mt-0.5">{healthyCount} profiles passing all quality checks</p>
                        </div>
                      )}
                          </div>
                        ) : diagReport ? (() => {
                          const { score, issues, summary } = diagReport;
                          const scoreRingClass = score >= 90 ? 'stroke-emerald-500' : score >= 70 ? 'stroke-amber-500' : 'stroke-red-500';
                          const circumference = 2 * Math.PI * 36;
                          const dashOffset = circumference - (score / 100) * circumference;
                          const summaryBadges = [
                            { key: 'language-gap' as const, count: summary.languageGaps },
                            { key: 'ministry-permit' as const, count: summary.ministryPermits },
                            { key: 'missing-image' as const, count: summary.missingImages },
                            { key: 'pricing-error' as const, count: summary.pricingErrors },
                            { key: 'description-gap' as const, count: summary.descriptionGaps },
                          ].filter(b => b.count > 0);
                          const byCat: Record<string, HealthIssue[]> = {};
                          issues.forEach(i => {
                            const key = i.categoryName || '\u2699\uFE0F Profile-Level';
                            if (!byCat[key]) byCat[key] = [];
                            byCat[key].push(i);
                          });
                          const _pendingIds = new Set((diagProfile.pendingEntities || []).map(pe => pe.entityId));
                          return (
                            <div key="health-diagnosis">
                              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
                                <button onClick={() => { setDiagProfile(null); setDiagCategory('__detailed__'); }}
                                  className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors">
                                  <ArrowLeft size={13} /> Back
                                </button>
                                <button onClick={() => { setHealthOpen(false); setDiagProfile(null); setDiagCategory('__detailed__'); }}
                                  className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-slate-600 transition-colors">
                                  <X size={11} /> Close
                                </button>
                              </div>
                              <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white px-5 py-4 flex items-center gap-4">
                                <div className="relative w-16 h-16 shrink-0">
                                  <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
                                    <circle cx="40" cy="40" r="36" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="6" />
                                    <circle cx="40" cy="40" r="36" fill="none" className={scoreRingClass} strokeWidth="6" strokeLinecap="round"
                                      strokeDasharray={circumference} strokeDashoffset={dashOffset} style={{ transition: 'stroke-dashoffset 0.8s ease-out' }} />
                                  </svg>
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-lg font-bold text-white">{score}%</span>
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-sm font-bold truncate">{diagProfile.name}</h3>
                                  <p className="text-slate-400 text-[10px] mt-0.5">{diagReport.passedChecks}/{diagReport.totalChecks} checks passed</p>
                                  <p className="text-slate-400 text-[10px] mt-0.5">{issues.length === 0 ? 'Perfect!' : `${issues.length} issue${issues.length > 1 ? 's' : ''}`}</p>
                                </div>

                              </div>
                              {issues.length === 0 ? (
                                <div className="px-5 py-8 text-center">
                                  <CheckCircle2 size={24} className="mx-auto text-emerald-500 mb-2" />
                                  <p className="text-sm font-semibold text-emerald-700">All checks passed!</p>
                                </div>
                              ) : (
                                <div className="max-h-80 overflow-y-auto">
                                  {Object.entries(byCat).map(([catName, catIssues]) => (
                                    <div key={catName}>
                                      <div className="px-4 py-1.5 bg-slate-50 border-b border-slate-100">
                                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{catName}</p>
                                      </div>
                                      <div className="divide-y divide-slate-50">
                                        {catIssues.map((issue, i) => {
                                          const cfg = ISSUE_CONFIG[issue.type];
                                          const isPending = _pendingIds.has(issue.itemId || '') || _pendingIds.has(issue.categoryId || '');
                                          return (
                                            <div key={i} className="flex items-center gap-2.5 px-4 py-2 hover:bg-indigo-50/30 transition-colors group">
                                              <span className="text-sm shrink-0">{cfg.emoji}</span>
                                              <div className="flex-1 min-w-0">
                                                <p className="text-[11px] font-medium text-slate-700 truncate">{issue.label}</p>
                                                {issue.itemName && <p className="text-[10px] text-slate-400 truncate">{issue.itemName}</p>}
                                              </div>
                                              {isPending ? (
                                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200 shrink-0 whitespace-nowrap">
                                                  <Clock size={9} /> Pending Approval
                                                </span>
                                              ) : (issue.itemId || issue.categoryId) && (
                                                <button onClick={() => handleFixNow(issue)}
                                                  className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition-all opacity-0 group-hover:opacity-100 shrink-0 whitespace-nowrap">
                                                  <Wrench size={9} /> Fix Now
                                                </button>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })() : null}
                      </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })()}

              {/* Status breakdown */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Status</p>
                  {[
                    { label: 'Draft', n: draftN, dot: 'bg-zinc-300' },
                    { label: 'Published', n: pubN, dot: 'bg-emerald-500' },
                    { label: 'Archived', n: archN, dot: 'bg-slate-300' },
                  ].map(s => (
                    <div key={s.label} className="flex items-center gap-3 py-1">
                      <div className={cn('w-2.5 h-2.5 rounded-full', s.dot)} />
                      <span className="text-sm text-slate-600 flex-1">{s.label}</span>
                      <span className="text-sm font-bold">{s.n}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">By Tag</p>
                  {([...new Set(profiles.map(p => p.tag || 'Untagged'))] as string[]).map(tag => (
                    <div key={tag} className="flex items-center gap-3 py-1">
                      <span className={cn('px-2 py-0.5 rounded text-[9px] font-bold border', tagColor(tag))}>{tag}</span>
                      <span className="text-sm font-bold ml-auto">{profiles.filter(p => (p.tag || 'Untagged') === tag).length}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent */}
              {profiles.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Recent Profiles</p>
                    <button onClick={() => nav('/apps/menu-manage/profiles')} className="text-xs font-bold text-teal-600 hover:underline">View All →</button>
                  </div>
                  {profiles.slice().sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 5).map((p, i) => {
                    const h = auditMenuProfile(p);
                    const hC = h.score >= 90 ? 'text-emerald-600 bg-emerald-50 border-emerald-200' : h.score >= 70 ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-red-600 bg-red-50 border-red-200';
                    return (
                    <div key={p.id} className={cn('flex items-center gap-4 px-5 py-3', i > 0 && 'border-t border-slate-50')}>
                      <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center"><BookOpen size={16} className="text-indigo-600" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{p.name}</p>
                        <span className="text-[11px] text-slate-400">{p.brandName} · {p.categories.filter(c => !isUnassignedCat(c.id)).length} cats · {p.categories.filter(c => !isUnassignedCat(c.id)).reduce((a, c) => a + c.items.length, 0)} items</span>
                      </div>
                      <span className={cn('px-2 py-0.5 rounded text-[9px] font-bold border flex items-center gap-1', hC)}><Activity size={9} />{h.score}%</span>
                      <span className={cn('px-2 py-0.5 rounded-full text-[9px] font-bold border', STATUS_STYLES[p.status])}>{p.status}</span>
                      <span className="text-[10px] text-slate-400 whitespace-nowrap">{fmtDate(p.updatedAt)}</span>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ) : activeView === 'DIAGNOSIS' && diagProfile && diagReport ? (() => {
          const { score, issues, summary } = diagReport;
          const scoreRingClass = score >= 90 ? 'stroke-emerald-500' : score >= 70 ? 'stroke-amber-500' : 'stroke-red-500';
          const circumference = 2 * Math.PI * 36;
          const dashOffset = circumference - (score / 100) * circumference;

          // Summary badges (non-zero only)
          const summaryBadges = [
            { key: 'language-gap' as const, count: summary.languageGaps },
            { key: 'ministry-permit' as const, count: summary.ministryPermits },
            { key: 'missing-image' as const, count: summary.missingImages },
            { key: 'pricing-error' as const, count: summary.pricingErrors },
            { key: 'description-gap' as const, count: summary.descriptionGaps },
          ].filter(b => b.count > 0);

          // Group issues by category name for the list view
          const byCat: Record<string, HealthIssue[]> = {};
          issues.forEach(i => {
            const key = i.categoryName || '⚙️ Profile-Level';
            if (!byCat[key]) byCat[key] = [];
            byCat[key].push(i);
          });
          const _diagPendingIds = new Set((diagProfile.pendingEntities || []).map(pe => pe.entityId));

          return (
          <div key="diagnosis">
            <div className="space-y-0">
              {/* ← Back */}
              <div className="mb-4">
                <button onClick={() => { setActiveView('OVERVIEW'); setDiagProfile(null); setDiagCategory('__detailed__'); }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all">
                  <ArrowLeft size={16} /> Back to Overview
                </button>
              </div>

              {/* Dark header card */}
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white px-6 py-5 flex items-center gap-5">
                  {/* Large score ring */}
                  <div className="relative w-20 h-20 shrink-0">
                    <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
                      <circle cx="40" cy="40" r="36" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="6" />
                      <circle cx="40" cy="40" r="36" fill="none" className={scoreRingClass} strokeWidth="6" strokeLinecap="round"
                        strokeDasharray={circumference} strokeDashoffset={dashOffset} style={{ transition: 'stroke-dashoffset 0.8s ease-out' }} />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xl font-bold text-white">{score}%</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold">Menu Health Score</h3>
                    <p className="text-slate-400 text-xs mt-0.5">{diagReport.passedChecks}/{diagReport.totalChecks} checks passed</p>
                    <p className="text-slate-400 text-[10px] mt-0.5">{issues.length === 0 ? 'Perfect! All checks passed.' : `${issues.length} issue${issues.length > 1 ? 's' : ''} found`}</p>
                  </div>

                </div>

                {/* Body */}
                {issues.length === 0 ? (
                  <div className="px-6 py-10 text-center">
                    <CheckCircle2 size={28} className="mx-auto text-emerald-500 mb-2" />
                    <p className="text-sm font-semibold text-emerald-700">All checks passed!</p>
                    <p className="text-[10px] text-slate-400 mt-1">This menu is complete and ready to go live.</p>
                  </div>
                ) : (
                  /* ─── Detailed view ─── */
                  <div className="max-h-[420px] overflow-y-auto">
                    {Object.entries(byCat).map(([catName, catIssues]) => (
                      <div key={catName}>
                        <div className="px-5 py-2 bg-slate-50 border-b border-slate-100">
                          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{catName}</p>
                        </div>
                        <div className="divide-y divide-slate-50">
                          {catIssues.map((issue, i) => {
                            const cfg = ISSUE_CONFIG[issue.type];
                            const isPending = _diagPendingIds.has(issue.itemId || '') || _diagPendingIds.has(issue.categoryId || '');
                            return (
                              <div key={i} className="flex items-center gap-3 px-5 py-2.5 hover:bg-indigo-50/30 transition-colors group">
                                <span className="text-sm shrink-0">{cfg.emoji}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-slate-700 truncate">{issue.label}</p>
                                  {issue.itemName && <p className="text-[10px] text-slate-400 truncate">{issue.itemName}</p>}
                                </div>
                                {isPending ? (
                                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200 shrink-0 whitespace-nowrap">
                                    <Clock size={9} /> Pending Approval
                                  </span>
                                ) : (issue.itemId || issue.categoryId) && (
                                  <button onClick={() => handleFixNow(issue)}
                                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition-all opacity-0 group-hover:opacity-100 shrink-0 whitespace-nowrap">
                                    <Wrench size={10} /> Fix Now
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          );
        })() : null}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 2. PROFILES TAB — Card Grid → View-first → Builder Drawer
// ═══════════════════════════════════════════════════════════════════════════════

function ProfilesTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'menu-manage', 'profiles', 'admin');
  /** Only tenant ADMIN or platform staff can delete profiles */
  const canDeleteProfile = user?.role === UserRole.ADMIN || isPlatformRole(user?.role as UserRole);
  const repo = getRepository();
  const toast = useToast();
  const bg = useBackgroundTask();
  const nav = useNavigate();
  const location = useLocation();
  const loc = useMenuLocalization();
  const [searchParams, setSearchParams] = useSearchParams();

  const [profiles, setProfiles] = useState<MenuProfile[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [q, setQ] = useState('');
  const [brandF, setBrandF] = useState('');
  const [tagF, setTagF] = useState('');
  const [creatorF, setCreatorF] = useState('');
  const [healthFilter, setHealthFilter] = useState<'complete' | 'incomplete' | 'all'>(() => {
    const f = searchParams.get('filter');
    return f === 'incomplete' || f === 'all' ? f : 'complete';
  });
  const [viewProfile, setViewProfile] = useState<MenuProfile | null>(null);
  const [startEditing, setStartEditing] = useState(false);
  const [highlightMinistry, setHighlightMinistry] = useState(false);
  const [exportModalProfile, setExportModalProfile] = useState<MenuProfile | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState | null>(null);
  const [deepLinkItemId, setDeepLinkItemId] = useState<string | null>(null);
  const [deepLinkCatId, setDeepLinkCatId] = useState<string | null>(null);
  const [highlightField, setHighlightField] = useState<string | null>(null);
  const [busyState, setBusyState] = useState<MenuBusyState | null>(null);
  const [cloneSource, setCloneSource] = useState<MenuProfile | null>(null);
  const [cloneName, setCloneName] = useState('');
  const [cloneTag, setCloneTag] = useState('');
  const [cloning, setCloning] = useState(false);
  const [cloneProgress, setCloneProgress] = useState(0);
  const cloneProgressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const busyTimerRef = useRef<number | null>(null);
  const profileRestoredRef = useRef(false);
  const [requireApproval, setRequireApproval] = useState(false);
  const [pricingStrategy, setPricingStrategy] = useState<PricingStrategy>('LOWEST');
  const isGodMode = isPlatformRole(user?.role as UserRole);
  const requiresCreationApproval = !!requireApproval && !isAdmin && !isGodMode && user?.role === UserRole.MANAGER;

  // ── Sliding diagnosis panel (in-place on card grid) ──
  const [diagSlideProfile, setDiagSlideProfile] = useState<MenuProfile | null>(null);
  // Keep slide-panel diagnosis in sync with latest profiles data (e.g. after load() refreshes pendingEntities)
  useEffect(() => {
    if (!diagSlideProfile) return;
    const fresh = profiles.find(p => p.id === diagSlideProfile.id);
    if (fresh && fresh !== diagSlideProfile) setDiagSlideProfile(fresh);
  }, [profiles]);

  // ── Teleport return: track if user arrived via Fix Now from dashboard/slide-panel diagnosis ──
  const [teleportOrigin, setTeleportOrigin] = useState<{ profileId: string; type: string; source?: string } | null>(null);

  // AI feature gating: check server if AI is enabled for this org + platform
  const [aiVisible, setAiVisible] = useState(false);
  useEffect(() => {
    checkAiEnabled().then(setAiVisible).catch(() => setAiVisible(false));
    // Load approval policy + pricing strategy
    if (isRemoteMode()) fetchMenuSettings().then(s => { if (s?.requireApproval) setRequireApproval(true); if (s?.pricingStrategy) setPricingStrategy(s.pricingStrategy as PricingStrategy); }).catch(() => {});
  }, []);

  const clearBusyTimer = useCallback(() => {
    if (busyTimerRef.current !== null) {
      window.clearTimeout(busyTimerRef.current);
      busyTimerRef.current = null;
    }
  }, []);

  const hideBusy = useCallback(() => {
    clearBusyTimer();
    setBusyState(null);
  }, [clearBusyTimer]);

  const scheduleBusy = useCallback((state: MenuBusyState, delay = 450) => {
    clearBusyTimer();
    busyTimerRef.current = window.setTimeout(() => {
      setBusyState(state);
      busyTimerRef.current = null;
    }, delay);
  }, [clearBusyTimer]);

  useEffect(() => () => clearBusyTimer(), [clearBusyTimer]);

  const CACHE_KEY = 'menu_profiles_cache';
  const loadFromCache = useCallback(() => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const { profiles: cp, brands: cb, ts } = JSON.parse(cached);
        if (Date.now() - ts < 5 * 60 * 1000) {
          setProfiles(cp); setBrands(cb); setIsLoading(false);
          return true;
        }
      }
    } catch { /* ignore */ }
    return false;
  }, []);

  const load = useCallback(async () => {
    await seedDemoDataIfNeeded();
    const [p, bAll] = await Promise.all([repo.getAll<MenuProfile>('menu-profiles'), repo.getBrands()]);
    const b = bAll.filter(br => br.enabledModules?.includes('menu-manage'));
    const ub = user?.assignedBrands;
    let filtered: MenuProfile[];
    if (isAdmin) filtered = brandF ? p.filter(pr => pr.brandId === brandF) : p;
    else filtered = p.filter(pr => !ub || ub.length === 0 || ub.includes(pr.brandId));
    setProfiles(filtered);
    setBrands(b);
    setIsLoading(false);
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ profiles: filtered, brands: b, ts: Date.now() })); } catch { /* quota */ }
  }, [brandF]);

  useEffect(() => { loadFromCache(); load(); }, [load, loadFromCache]);

  useEffect(() => {
    const handler = () => { load(); };
    window.addEventListener(MENU_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(MENU_DATA_UPDATED_EVENT, handler);
  }, [load]);

  // ── Auto-refresh when background pulse detects approval/rejection changes ──
  useEffect(() => {
    const h = () => load();
    window.addEventListener('PULSE_MENU_STATUS_CHANGED', h);
    return () => window.removeEventListener('PULSE_MENU_STATUS_CHANGED', h);
  }, [load]);

  // ── Keep viewProfile in sync with latest profiles (e.g. after approval merges or deletions) ──
  useEffect(() => {
    if (!viewProfile) return;
    const fresh = profiles.find(p => p.id === viewProfile.id);
    if (fresh && fresh !== viewProfile) setViewProfile(fresh);
  }, [profiles]);

  // ── Sync to shared preview context (Products → Preview zero-fetch) ──────
  const { setProfiles: setSharedProfiles, setActiveProfile: setSharedActiveProfile } = React.useContext(MenuPreviewCtx);
  useEffect(() => { setSharedProfiles(profiles); }, [profiles]);
  useEffect(() => { setSharedActiveProfile(viewProfile); }, [viewProfile]);

  const isPendingProfileCreation = useCallback((profile: MenuProfile | null | undefined) => {
    if (!profile) return false;
    if (profile.pendingChangeType === 'CREATE_PROFILE') return true;
    return !!profile.pendingEntities?.some((entity) => entity.entityId === profile.id && entity.changeType === 'CREATE_PROFILE');
  }, []);

  // Deep-link from dashboard / health panel
  useEffect(() => {
    const state = location.state as any;
    if (state?.deepLinkProfileId) {
      const target = profiles.find(p => p.id === state.deepLinkProfileId);
      if (target) {
        if (isPendingProfileCreation(target) && !isAdmin && !isGodMode) {
          toast.error('Pending Approval', 'This profile is awaiting admin approval before it can be opened.');
          window.history.replaceState({}, '');
          return;
        }
        if (isHeavyMenu(target)) {
          scheduleBusy({
            mode: 'opening',
            title: 'Rendering menu',
            message: `Preparing ${target.name || 'selected menu'} for viewing.`,
            detail: 'The popup only appears when opening the menu takes noticeably longer.',
            estimateLabel: busyEstimateLabel(target, 'opening'),
          }, 350);
          waitForNextPaint().then(() => waitForNextPaint()).then(() => {
            startTransition(() => setViewProfile(target));
          });
        } else {
          startTransition(() => setViewProfile(target));
        }
        if (state.highlightMinistry) setHighlightMinistry(true);
        if (state.deepLinkItemId) { setDeepLinkItemId(state.deepLinkItemId); setStartEditing(true); }
        if (state.deepLinkCatId) setDeepLinkCatId(state.deepLinkCatId);
        if (state.highlightField) setHighlightField(state.highlightField);
        if (state.teleportType) {
          setTeleportOrigin({ profileId: state.deepLinkProfileId, type: state.teleportType, source: state.teleportSource || 'dashboard' });
          if (!state.deepLinkItemId) {
            // Category or profile-level teleport — still need to open editing
            setStartEditing(true);
          }
        }
      }
      window.history.replaceState({}, '');
    }
  }, [profiles, location.state, scheduleBusy, isPendingProfileCreation, isAdmin, isGodMode, toast]);

  // ── Sync viewProfile → URL ?profileId= ────────────────────────────────────
  useEffect(() => {
    setSearchParams(prev => {
      const sp = new URLSearchParams(prev);
      if (viewProfile) sp.set('profileId', viewProfile.id);
      else sp.delete('profileId');
      return sp;
    }, { replace: true });
  }, [viewProfile?.id]);

  // ── Restore from ?profileId= on reload (runs once after profiles load) ────
  useEffect(() => {
    if (profileRestoredRef.current || profiles.length === 0) return;
    const pid = searchParams.get('profileId');
    if (!pid) { profileRestoredRef.current = true; return; }
    const target = profiles.find(p => p.id === pid);
    if (!target) { profileRestoredRef.current = true; return; }
    if (isPendingProfileCreation(target) && !isAdmin && !isGodMode) {
      profileRestoredRef.current = true;
      return;
    }
    profileRestoredRef.current = true;
    startTransition(() => setViewProfile(target));
  }, [profiles, searchParams, isPendingProfileCreation, isAdmin, isGodMode]);

  const openProfile = useCallback(async (profile: MenuProfile, edit = false) => {
    if (isPendingProfileCreation(profile) && !isAdmin && !isGodMode) {
      toast.error('Pending Approval', 'This profile is awaiting admin approval before it can be opened.');
      return;
    }

    const commitOpen = () => {
      startTransition(() => {
        setStartEditing(edit);
        setViewProfile(profile);
      });
    };

    if (!isHeavyMenu(profile)) {
      commitOpen();
      return;
    }

    scheduleBusy({
      mode: 'opening',
      title: edit ? 'Rendering editor' : 'Rendering menu',
      message: `Preparing ${profile.name || 'selected menu'}${edit ? ' for editing' : ''}.`,
      detail: 'This appears only when opening a large menu takes longer than normal.',
      estimateLabel: busyEstimateLabel(profile, 'opening'),
    }, 350);
    await waitForNextPaint();
    await waitForNextPaint();
    commitOpen();
  }, [isAdmin, isGodMode, scheduleBusy, toast, isPendingProfileCreation]);

  const handleDetailReady = useCallback(() => {
    hideBusy();
  }, [hideBusy]);

  const availBrands = useMemo(() => {
    const ub = user?.assignedBrands;
    return (isAdmin || !ub || ub.length === 0) ? brands : brands.filter(b => ub.includes(b.id));
  }, [brands, user, isAdmin]);

  const allTags = useMemo(() => ([...new Set(profiles.map(p => p.tag).filter(Boolean))] as string[]), [profiles]);
  const allCreators = useMemo(() => {
    const map = new Map<string, string>();
    for (const profile of profiles) {
      const creatorId = profile.createdById || profile.createdBy;
      const creatorName = profile.createdByName || 'Unknown';
      if (creatorId && !map.has(creatorId)) map.set(creatorId, creatorName);
    }
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [profiles]);

  const filtered = useMemo(() => {
    let list = profiles;
    if (q) {
      const lq = q.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(lq) || p.brandName.toLowerCase().includes(lq) || (p.tag || '').toLowerCase().includes(lq) || (p.createdByName || '').toLowerCase().includes(lq));
    }
    if (brandF) list = list.filter(p => p.brandId === brandF);
    if (tagF) list = list.filter(p => p.tag === tagF);
    if (creatorF) list = list.filter(p => (p.createdById || p.createdBy) === creatorF);
    // Health sub-tab filter
    if (healthFilter === 'complete') list = list.filter(p => auditMenuProfile(p).score === 100);
    else if (healthFilter === 'incomplete') list = list.filter(p => auditMenuProfile(p).score < 100);
    return list.sort((a, b) => b.updatedAt - a.updatedAt);
  }, [profiles, q, brandF, tagF, creatorF, healthFilter]);

  // Health counts for sub-tab badges (computed on search+tag-filtered set, before health filter)
  const healthCounts = useMemo(() => {
    let list = profiles;
    if (q) {
      const lq = q.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(lq) || p.brandName.toLowerCase().includes(lq) || (p.tag || '').toLowerCase().includes(lq) || (p.createdByName || '').toLowerCase().includes(lq));
    }
    if (brandF) list = list.filter(p => p.brandId === brandF);
    if (tagF) list = list.filter(p => p.tag === tagF);
    if (creatorF) list = list.filter(p => (p.createdById || p.createdBy) === creatorF);
    const complete = list.filter(p => auditMenuProfile(p).score === 100).length;
    return { complete, incomplete: list.length - complete, all: list.length };
  }, [profiles, q, brandF, tagF, creatorF]);

  const handleCreate = () => {
    if (availBrands.length === 0) { toast.error('No Brands', 'No brands available.'); return; }
    const b = availBrands[0];
    const p: MenuProfile = {
      id: genId(), name: '', brandId: b.id, brandName: b.name, branchIds: [],
      tag: 'Regular', type: MenuProfileType.REGULAR,
      status: MenuProfileStatus.DRAFT, categories: ensureUnassignedBucket([]),
      permitSyncRequired: false, ministryFileIds: [],
      createdById: user?.id ?? '',
      createdBy: user?.id ?? '', createdByName: user?.name ?? '',
      createdAt: Date.now(), updatedAt: Date.now(), orgId: user?.orgId,
    };
    setViewProfile(p);
    setStartEditing(true);
  };

  const stopCloneProgress = () => {
    if (cloneProgressRef.current) { clearInterval(cloneProgressRef.current); cloneProgressRef.current = null; }
  };
  const startCloneProgress = () => {
    setCloneProgress(0);
    stopCloneProgress();
    let tick = 0;
    cloneProgressRef.current = setInterval(() => {
      tick++;
      // Stage 1: 0→30 fast (preparing), Stage 2: 30→60 medium (cloning data), Stage 3: 60→85 slow (images)
      setCloneProgress(prev => {
        if (prev >= 85) return 85;
        if (prev < 30) return Math.min(30, prev + 3);
        if (prev < 60) return Math.min(60, prev + 1.5);
        return Math.min(85, prev + 0.5);
      });
    }, 150);
  };
  const handleClone = async () => {
    if (!cloneSource || !cloneName.trim()) return;
    const srcName = cloneName.trim();
    const srcTag = cloneTag.trim() || cloneSource.tag;
    const src = cloneSource;
    setCloning(true);
    startCloneProgress();
    try {
      const { id: newId } = await cloneMenuProfile(src.id, srcName, cloneTag.trim() || undefined);
      // Jump to 90% — loading profiles
      stopCloneProgress();
      setCloneProgress(90);
      await load();
      // Jump to 100% — done
      setCloneProgress(100);
      // Brief pause to show 100% completion
      await new Promise(r => setTimeout(r, 400));
      toast.success('Profile Cloned', `"${srcName}" created as DRAFT. Opening now…`);
      setCloneSource(null);
      setCloneName('');
      setCloneTag('');
      setCloneProgress(0);
      // Auto-navigate to the cloned profile
      const skeleton: MenuProfile = {
        id: newId, name: srcName, brandId: src.brandId, brandName: src.brandName, branchIds: src.branchIds || [],
        tag: srcTag, type: src.type,
        status: MenuProfileStatus.DRAFT, categories: src.categories,
        permitSyncRequired: src.permitSyncRequired, ministryFileIds: [...src.ministryFileIds],
        createdById: user?.id ?? '',
        createdBy: user?.id ?? '', createdByName: user?.name ?? '',
        createdAt: Date.now(), updatedAt: Date.now(), orgId: user?.orgId,
        connectedModules: src.connectedModules,
      };
      void openProfile(skeleton);
    } catch (err: any) {
      stopCloneProgress();
      setCloneProgress(0);
      toast.error('Clone Failed', err?.message || 'Could not duplicate the profile.');
    } finally {
      setCloning(false);
    }
  };

  const save = (p: MenuProfile) => {
    const u = { ...p, updatedAt: Date.now() };
    startTransition(() => {
      if (viewProfile?.id === u.id) setViewProfile(u);
      setProfiles((current) => {
        const exists = current.some((profile) => profile.id === u.id);
        return exists
          ? current.map((profile) => profile.id === u.id ? u : profile)
          : [u, ...current];
      });
    });
  };

  const saveDraft = (p: MenuProfile) => {
    // Data integrity: deduplicate any zombie/shared IDs before saving
    const { categories: cleanCats, fixed } = deduplicateIds(p.categories);
    if (fixed > 0) console.warn(`[Menu] Dedup fixed ${fixed} duplicate ID(s) before save.`);
    // Relational integrity: ensure every item has optionGroupIds populated
    const normalizedCats = cleanCats.map(c => ({
      ...c,
      items: c.items.map(it => ({
        ...it,
        optionGroupIds: (it.optionGroups || []).map(g => g.linkedLibraryId || g.id),
      })),
    }));
    const u = { ...p, categories: normalizedCats, updatedAt: Date.now() };

    // Optimistic: update local state immediately
    startTransition(() => { if (viewProfile?.id === u.id) setViewProfile(u); });
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ profiles: profiles.map(pr => pr.id === u.id ? u : pr), brands, ts: Date.now() })); } catch { }

    // Fire-and-forget background save
    bg.run({
      label: u.name || 'Draft',
      entityId: u.id,
      work: async () => {
        if (isRemoteMode()) {
          const result = await saveMenuDraft(u.id, u as unknown as Record<string, unknown>);
          if (result.pendingCreationRequest) {
            const pendingProfile: MenuProfile = {
              ...u,
              pendingChangeRequest: result.requestId ?? 'pending-create',
              pendingChangeType: result.pendingChangeType ?? 'CREATE_PROFILE',
            };
            startTransition(() => {
              setProfiles(current => {
                const exists = current.some((profile) => profile.id === u.id);
                return exists
                  ? current.map((profile) => profile.id === u.id ? pendingProfile : profile)
                  : [pendingProfile, ...current];
              });
              setViewProfile(current => current?.id === u.id ? null : current);
              setStartEditing(false);
            });
            await load();
            return;
          }
        } else {
          await repo.save('menu-profiles', user?.orgId ?? 'default', u);
        }
        // Auto-sync: push any non-linked option groups into the Modifier Library
        const existingMods = await repo.getAll<LibraryModifier>('modifier-library');
        const modifierSaveTasks: Promise<unknown>[] = [];
        for (const cat of u.categories) {
          for (const item of cat.items) {
            for (const grp of (item.optionGroups || [])) {
              if (grp.linkedLibraryId || !grp.nameEN?.trim() || grp.options.length === 0) continue;
              if (existingMods.some(m => m.nameEN === grp.nameEN && m.brandId === u.brandId)) continue;
              const libMod: LibraryModifier = {
                id: grp.id, nameEN: grp.nameEN, nameAR: grp.nameAR,
                options: grp.options.map(o => ({ ...o })),
                minSelection: grp.minSelection, maxSelection: grp.maxSelection,
                updatedAt: Date.now(), orgId: u.orgId || 'org1', brandId: u.brandId,
              };
              existingMods.push(libMod);
              modifierSaveTasks.push(repo.save('modifier-library', u.orgId || 'org1', libMod));
            }
          }
        }
        if (modifierSaveTasks.length > 0) await Promise.all(modifierSaveTasks);
        window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
      },
    });
  };

  const publishLive = (p: MenuProfile, opts?: { silent?: boolean }) => {
    if (p.categories.filter(c => !isUnassignedCat(c.id)).length === 0) { toast.error('Cannot Publish', 'Add at least one category first.'); return; }

    // ── Approval gate: if enabled and user is NOT admin → submit for approval ──
    if (requireApproval && !isAdmin) {
      bg.run({
        label: `Submit ${p.name || 'Menu'} for Approval`,
        entityId: p.id,
        work: async () => {
          // Use liveData from the hydrated profile as the "before" snapshot
          const previousData = (p as any).liveData ? JSON.stringify((p as any).liveData) : '[]';
          await submitChangeRequest(p.id, p.name || 'Untitled', JSON.stringify(p.categories), previousData);
          await load();
          window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
        },
      });
      return;
    }

    // Optimistic: update local state immediately
    const u = { ...p, status: MenuProfileStatus.PUBLISHED, updatedAt: Date.now() };
    if (!opts?.silent) { startTransition(() => { setViewProfile(u); }); }

    // Fire-and-forget background publish
    bg.run({
      label: u.name || 'Menu',
      entityId: u.id,
      work: async () => {
        if (isRemoteMode()) {
          await saveMenuDraft(p.id, p as unknown as Record<string, unknown>);
          await publishMenuLive(p.id);
        } else {
          await repo.save('menu-profiles', user?.orgId ?? 'default', u);
        }
        await load();
        window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
      },
    });
  };

  const del = async (id: string) => {
    await repo.delete('menu-profiles', user?.orgId ?? 'default', id);
    toast.success('Deleted', 'Profile removed.');
    if (viewProfile?.id === id) setViewProfile(null);
    await load();
    window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
  };

  const archive = async (p: MenuProfile) => {
    const u = { ...p, status: MenuProfileStatus.ARCHIVED, updatedAt: Date.now() };
    await repo.save('menu-profiles', user?.orgId ?? 'default', u);
    toast.success('Archived', `${p.name} archived.`);
    await load();
    if (viewProfile?.id === u.id) setViewProfile(u);
    window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
  };

  const restore = async (p: MenuProfile) => {
    const u = { ...p, status: MenuProfileStatus.DRAFT, updatedAt: Date.now() };
    await repo.save('menu-profiles', user?.orgId ?? 'default', u);
    toast.success('Restored', `${p.name} restored to Draft.`);
    await load();
    if (viewProfile?.id === u.id) setViewProfile(u);
    window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
  };

  // ── Profile Detail View ──
  if (viewProfile) {
    return (
      <>
        <ProfileDetailView
          profile={viewProfile} allProfiles={profiles} brands={availBrands} user={user}
          highlightMinistry={highlightMinistry} allTags={allTags} startInEditMode={startEditing}
          deepLinkItemId={deepLinkItemId} deepLinkCatId={deepLinkCatId} highlightField={highlightField}
          requireApproval={requireApproval}
          teleportOrigin={teleportOrigin}
          onBack={() => {
            const origin = teleportOrigin;
            setViewProfile(null); setHighlightMinistry(false); setStartEditing(false); setDeepLinkItemId(null); setDeepLinkCatId(null); setHighlightField(null);
            setTeleportOrigin(null);
            if (origin) {
              if (origin.source === 'slide-panel') {
                // Return to the slide-panel diagnosis on the Profiles tab
                const p = profiles.find(pr => pr.id === origin.profileId);
                if (p) setDiagSlideProfile(p);
                setSearchParams(prev => { const sp = new URLSearchParams(prev); ['profileId','edit','subtab','search','cat'].forEach(k => sp.delete(k)); return sp; }, { replace: true });
                load();
              } else {
                nav('/apps/menu-manage', { replace: true });
              }
            } else {
              setSearchParams(prev => { const sp = new URLSearchParams(prev); ['profileId','edit','subtab','search','cat'].forEach(k => sp.delete(k)); return sp; }, { replace: true });
              load();
            }
          }}
          onPublish={publishLive} onArchive={archive} onRestore={restore} onDelete={del} onSave={save} onSaveDraft={saveDraft}
          onReady={handleDetailReady}
        />
        <MenuBusyDialog
          isOpen={!!busyState}
          title={busyState?.title ?? ''}
          message={busyState?.message ?? ''}
          detail={busyState?.detail}
          estimateLabel={busyState?.estimateLabel}
        />
      </>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Products</h2>
          <p className="text-sm text-slate-500 mt-1">Create and manage dual-language menu profiles.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => exportBlankTemplate()} className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-100"><Download size={13} /> Template</button>
          <button onClick={handleCreate} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700"><Plus size={14} /> New Profile</button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="w-full pl-9 pr-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-zinc-400 transition-all" />
        </div>
        <CustomSelect value={brandF} onChange={v => setBrandF(v)} placeholder={isAdmin ? 'All Brands' : 'My Brands'}
          options={[{ value: '', label: isAdmin ? 'All Brands' : 'My Brands' }, ...availBrands.map(b => ({ value: b.id, label: b.name }))]} />
        <CustomSelect value={creatorF} onChange={v => setCreatorF(v)} placeholder="Created By"
          options={[{ value: '', label: 'Created By' }, ...allCreators]} />
        <CustomSelect value={tagF} onChange={v => setTagF(v)} placeholder="All Tags"
          options={[{ value: '', label: 'All Tags' }, ...allTags.map(t => ({ value: t, label: t }))]} />
      </div>

      {/* Health Sub-Tabs */}
      <div className="flex items-center border-b border-slate-200">
        {([
          { key: 'complete' as const,   label: 'Complete',   count: healthCounts.complete,   color: 'bg-emerald-100 text-emerald-700' },
          { key: 'incomplete' as const, label: 'Incomplete', count: healthCounts.incomplete, color: 'bg-amber-100 text-amber-700' },
          { key: 'all' as const,        label: 'All',        count: healthCounts.all,        color: 'bg-slate-100 text-slate-600' },
        ]).map(tab => (
          <button
            key={tab.key}
            onClick={() => {
              setHealthFilter(tab.key);
              setSearchParams(prev => { const sp = new URLSearchParams(prev); sp.set('filter', tab.key); return sp; }, { replace: true });
            }}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all duration-200',
              healthFilter === tab.key
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300',
            )}
          >
            {tab.label}
            <span className={cn('px-1.5 py-0.5 rounded-full text-[10px] font-bold', healthFilter === tab.key ? tab.color : 'bg-slate-50 text-slate-400')}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* ═══ In-Place Diagnosis Slide ═══ */}
      <AnimatePresence mode="wait">
        {diagSlideProfile ? (() => {
          const dReport = auditMenuProfile(diagSlideProfile);
          const dScore = dReport.score;
          const dScoreClass = dScore >= 90 ? 'stroke-emerald-500' : dScore >= 70 ? 'stroke-amber-500' : 'stroke-red-500';
          const dCirc = 2 * Math.PI * 36;
          const dOffset = dCirc - (dScore / 100) * dCirc;
          const dByCat: Record<string, typeof dReport.issues> = {};
          dReport.issues.forEach(i => { const k = i.categoryName || '⚙️ Profile-Level'; if (!dByCat[k]) dByCat[k] = []; dByCat[k].push(i); });
          const _slidePendingIds = new Set((diagSlideProfile.pendingEntities || []).map(pe => pe.entityId));
          return (
            <motion.div key="diag-slide" initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 60 }} transition={{ duration: 0.25, ease: 'easeOut' }}
              className="bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
              {/* Dark header */}
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white px-5 py-4 flex items-center gap-4">
                <button onClick={() => setDiagSlideProfile(null)}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors shrink-0">
                  <ArrowLeft size={16} />
                </button>
                <div className="relative w-14 h-14 shrink-0">
                  <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
                    <circle cx="40" cy="40" r="36" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="5" />
                    <circle cx="40" cy="40" r="36" fill="none" className={dScoreClass} strokeWidth="5" strokeLinecap="round"
                      strokeDasharray={dCirc} strokeDashoffset={dOffset} style={{ transition: 'stroke-dashoffset 0.8s ease-out' }} />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-base font-bold text-white">{dScore}%</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold truncate">{diagSlideProfile.name || 'Untitled Menu'}</h3>
                  <p className="text-slate-400 text-[10px] mt-0.5">{diagSlideProfile.brandName} · {dReport.passedChecks}/{dReport.totalChecks} checks passed</p>
                  <p className="text-slate-400 text-[10px]">{dReport.issues.length === 0 ? 'All clear — no issues' : `${dReport.issues.length} issue${dReport.issues.length > 1 ? 's' : ''} found`}</p>
                </div>
                <button onClick={() => setDiagSlideProfile(null)}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors shrink-0 text-slate-400 hover:text-white">
                  <X size={14} />
                </button>
              </div>
              {/* Issue list */}
              {dReport.issues.length === 0 ? (
                <div className="px-6 py-10 text-center">
                  <CheckCircle2 size={28} className="mx-auto text-emerald-500 mb-2" />
                  <p className="text-sm font-semibold text-emerald-700">All checks passed!</p>
                  <p className="text-[10px] text-slate-400 mt-1">This menu is complete and ready to go live.</p>
                </div>
              ) : (
                <div className="max-h-[420px] overflow-y-auto">
                  {Object.entries(dByCat).map(([catName, catIssues]) => (
                    <div key={catName}>
                      <div className="px-5 py-2 bg-slate-50 border-b border-slate-100">
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{catName}</p>
                      </div>
                      <div className="divide-y divide-slate-50">
                        {catIssues.map((issue, i) => {
                          const cfg = ISSUE_CONFIG[issue.type];
                          const isPending = _slidePendingIds.has(issue.itemId || '') || _slidePendingIds.has(issue.categoryId || '');
                          return (
                            <div key={i} className="flex items-center gap-3 px-5 py-2.5 hover:bg-indigo-50/30 transition-colors group">
                              <span className="text-sm shrink-0">{cfg.emoji}</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-slate-700 truncate">{issue.label}</p>
                                {issue.itemName && <p className="text-[10px] text-slate-400 truncate">{issue.itemName}</p>}
                              </div>
                              {isPending ? (
                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200 shrink-0 whitespace-nowrap">
                                  <Clock size={9} /> Pending Approval
                                </span>
                              ) : (issue.itemId || issue.categoryId) && (
                                <button onClick={() => {
                                  nav('/apps/menu-manage/profiles', {
                                    state: {
                                      deepLinkProfileId: diagSlideProfile.id,
                                      deepLinkCatId: issue.categoryId,
                                      deepLinkItemId: issue.itemId,
                                      highlightField: issue.fieldKey,
                                      teleportType: issue.type,
                                      teleportSource: 'slide-panel',
                                    },
                                  });
                                  setDiagSlideProfile(null);
                                }}
                                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition-all opacity-0 group-hover:opacity-100 shrink-0 whitespace-nowrap">
                                  <Wrench size={10} /> Fix Now
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          );
        })() : (
        <motion.div key="card-grid" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.2 }}>

      {/* Card Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl shadow-sm animate-pulse flex flex-col">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-slate-200" />
                  <div className="space-y-1">
                    <div className="h-3 w-20 bg-slate-200 rounded" />
                    <div className="h-2 w-14 bg-slate-100 rounded" />
                  </div>
                </div>
                <div className="h-4 w-14 bg-slate-200 rounded-full" />
              </div>
              <div className="px-4 py-3 space-y-2.5">
                <div className="h-4 w-32 bg-slate-200 rounded" />
                <div className="h-2.5 w-28 bg-slate-100 rounded" />
                <div className="h-8 w-full bg-slate-50 rounded-lg" />
              </div>
              <div className="border-t border-slate-100 px-4 py-2 flex gap-1">
                {Array.from({ length: 3 }).map((_, j) => <div key={j} className="w-7 h-7 bg-slate-100 rounded-lg" />)}
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          {profiles.length === 0 ? (
            <>
              <BookOpen size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm font-semibold text-slate-400">No profiles yet.</p>
            </>
          ) : healthFilter === 'complete' ? (
            <>
              <BookOpen size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm font-semibold text-slate-500">No menus are 100% finished yet.</p>
              <p className="text-xs text-slate-400 mt-1">Check the <button onClick={() => { setHealthFilter('incomplete'); setSearchParams(prev => { const sp = new URLSearchParams(prev); sp.set('filter', 'incomplete'); return sp; }, { replace: true }); }} className="text-amber-600 font-bold hover:underline">Incomplete</button> tab to see what needs fixing.</p>
            </>
          ) : healthFilter === 'incomplete' ? (
            <>
              <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-3" />
              <p className="text-sm font-semibold text-emerald-700">Amazing! All your menus are at 100% health.</p>
            </>
          ) : (
            <>
              <BookOpen size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm font-semibold text-slate-400">No matches.</p>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(p => {
            const catCount = p.categories.filter(c => !isUnassignedCat(c.id)).length;
            const ic = p.categories.filter(c => !isUnassignedCat(c.id)).reduce((a, c) => a + c.items.length, 0);
            const health = auditMenuProfile(p);
            const hColor = health.score >= 90 ? 'text-emerald-600 bg-emerald-50 border-emerald-200' : health.score >= 70 ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-red-600 bg-red-50 border-red-200';
            const pendingCreation = isPendingProfileCreation(p);
            const canOpenProfileCard = !pendingCreation || isAdmin || isGodMode;
            return (
              <div key={p.id} className={cn('bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-all flex flex-col group relative overflow-hidden', pendingCreation && 'border-amber-300 bg-amber-50/40 opacity-60')}>
                  {pendingCreation && (
                    <div className="absolute inset-x-3 top-3 z-10 flex justify-center pointer-events-none">
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-3 py-1 text-[9px] font-black tracking-[0.12em] text-amber-800 shadow-sm">
                        <span aria-hidden="true">🟡</span>
                        PENDING APPROVAL
                      </span>
                    </div>
                  )}
                  {/* Identity Header — Brand + Location + Status */}
                  <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
                    <div className="flex items-center gap-2.5 min-w-0">
                      {(() => {
                        const brandLogo = brands.find(b => b.id === p.brandId)?.logoUrl;
                        return brandLogo ? (
                          <img src={getMediaUrl(brandLogo)} alt={p.brandName} className="w-8 h-8 rounded-full object-contain border border-slate-200 shrink-0 bg-white" onError={e => (e.currentTarget.style.display = 'none')} />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 font-bold text-sm shrink-0">
                            {p.brandName?.charAt(0) ?? '?'}
                          </div>
                        );
                      })()}
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-slate-800 truncate">{p.brandName || 'Untitled Brand'}</p>
                        {(() => {
                          const brandObj = brands.find(b => b.id === p.brandId);
                          const allBrandBranches = brandObj?.branches ?? [];
                          const assignedIds = p.branchIds ?? [];
                          const assignedBranches = assignedIds.map(bId => allBrandBranches.find(b => b.id === bId)).filter(Boolean) as { id: string; name: string }[];
                          const isAll = allBrandBranches.length > 0 && assignedIds.length === allBrandBranches.length;

                          // Build chip label
                          let chipLabel: string;
                          if (assignedBranches.length === 0) {
                            chipLabel = 'No Branches Assigned';
                          } else if (isAll) {
                            chipLabel = 'All Branches';
                          } else if (assignedBranches.length <= 3) {
                            chipLabel = assignedBranches.map(b => b.name).join(', ');
                          } else {
                            chipLabel = `${assignedBranches.slice(0, 2).map(b => b.name).join(', ')} +${assignedBranches.length - 2} more`;
                          }

                          const isEmpty = assignedBranches.length === 0;

                          return (
                            <div className="group/branch relative inline-block mt-0.5">
                              <div className={cn(
                                'inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-xs font-medium cursor-default max-w-[180px]',
                                isEmpty
                                  ? 'bg-slate-50 border-slate-200 text-slate-400'
                                  : 'bg-slate-100 border-slate-200 text-slate-600'
                              )}>
                                <MapPin size={10} className={isEmpty ? 'text-slate-300 shrink-0' : 'text-indigo-600 shrink-0'} />
                                <span className="truncate">{chipLabel}</span>
                              </div>
                              {assignedBranches.length > 0 && (
                                <div className="absolute left-0 invisible group-hover/branch:visible opacity-0 group-hover/branch:opacity-100 bg-slate-800 text-white text-[10px] p-2.5 rounded-lg shadow-xl mt-1 z-[60] w-48 transition-all">
                                  <p className="font-bold border-b border-slate-600 pb-1 mb-1.5 text-slate-300">Assigned Branches</p>
                                  <div className="max-h-32 overflow-y-auto space-y-1 pr-1">
                                    {assignedBranches.map(br => (
                                      <div key={br.id} className="truncate flex items-center gap-1">
                                        <MapPin size={8} className="text-indigo-400 shrink-0" /> {br.name}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {!pendingCreation && p.pendingChangeRequest ? (
                        <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold border border-amber-300 bg-amber-50 text-amber-700 flex items-center gap-0.5"><Hourglass size={8} className="animate-pulse" /> Pending</span>
                      ) : null}
                      {bg.isSaving(p.id) && <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] font-bold border border-indigo-300 bg-indigo-50 text-indigo-600 animate-pulse"><Activity size={8} /> Saving</span>}
                      {p.status === MenuProfileStatus.ARCHIVED ? (
                        <span className={cn('px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border', STATUS_STYLES[p.status])}>{p.status}</span>
                      ) : isAdmin && !p.pendingChangeRequest ? (
                        <div onClick={e => e.stopPropagation()}>
                          <LiveToggleSwitch
                            isLive={p.status === MenuProfileStatus.PUBLISHED}
                            size="sm"
                            showLabel={false}
                            onToggle={(goLive) => {
                              if (goLive) { publishLive(p, { silent: true }); }
                              else {
                                const u = { ...p, status: MenuProfileStatus.DRAFT, updatedAt: Date.now() };
                                save(u);
                                if (isRemoteMode()) { unpublishMenu(p.id).catch(() => {}); }
                                window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <span className={cn('px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border', STATUS_STYLES[p.status])}>{p.status}</span>
                      )}
                    </div>
                  </div>

                  {/* Content — Name, Tags, Metadata, Health */}
                  <div className="px-4 py-3 flex-1 flex flex-col justify-between gap-2.5">
                    <div>
                      <h3 className="text-sm font-bold text-slate-800 truncate">{p.name || 'Untitled Menu'}</h3>
                      <div className="flex flex-wrap items-center gap-1 mt-1.5">
                        {(p.connectedModules?.length ?? 0) > 0 && (
                          <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 border border-blue-100 rounded text-[8px] font-bold flex items-center gap-0.5">
                            <Link2 size={8} /> {p.connectedModules!.length === 1 ? (p.connectedModules!.includes('aggregator') ? 'Aggregator' : 'Rider Ops') : `${p.connectedModules!.length} Modules`}
                          </span>
                        )}
                        {p.tag && <span className={cn('px-1.5 py-0.5 rounded text-[8px] font-bold border', tagColor(p.tag))}>{p.tag}</span>}
                      </div>
                    </div>

                    <div className="space-y-1">
                      <p className="text-[10px] text-slate-400 font-medium">{catCount} Categories · {ic} Items · {fmtDate(p.updatedAt)}</p>
                      <p className="text-[10px] text-slate-400">Created by {p.createdByName || 'Unknown'} on {fmtDate(p.createdAt)}</p>
                    </div>
                    
                    {/* Health Badge — Click to Diagnose (In-Place Slide) */}
                    <div 
                      className="flex items-center justify-between px-2.5 py-2 bg-slate-50 rounded-lg border border-slate-100 cursor-pointer transition-colors hover:bg-slate-100 group/health" 
                      onClick={(e) => { e.stopPropagation(); setDiagSlideProfile(p); }}
                    >
                      <span className={cn('px-2 py-0.5 rounded-md text-[10px] font-bold border flex items-center gap-1 bg-white', hColor)}>
                        <Activity size={10} /> {health.score}%
                      </span>
                      {health.issues.length > 0 ? (
                        <span className="flex items-center gap-1 text-[10px] font-bold text-red-500">
                          <XCircle size={10} /> {health.issues.length} Alert{health.issues.length > 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-400 font-medium">All clear</span>
                      )}
                      <span className="text-[9px] font-bold text-indigo-500 opacity-0 group-hover/health:opacity-100 transition-opacity">Diagnose →</span>
                    </div>
                    {user?.role === 'PLATFORM_OWNER' && <p className="text-[9px] text-slate-300 font-mono truncate">ORG: {p.orgId}</p>}
                  </div>

                  {/* Action Footer — Role-aware */}
                  <div className="border-t border-slate-100 px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => { if (canOpenProfileCard) { void openProfile(p); } }} disabled={!canOpenProfileCard} className={cn('p-1.5 rounded-lg transition-colors', canOpenProfileCard ? 'hover:bg-teal-50' : 'cursor-not-allowed opacity-50')} title={canOpenProfileCard ? 'View' : 'Awaiting admin approval before this profile can be opened'}><Eye size={14} className={canOpenProfileCard ? 'text-teal-600' : 'text-slate-400'} /></button>
                      {isAdmin && !p.pendingChangeRequest && (
                        <>
                          <button onClick={() => setExportModalProfile(p)} className="p-1.5 hover:bg-emerald-50 rounded-lg transition-colors" title="Export"><Download size={14} className="text-emerald-600" /></button>
                          <button onClick={() => { setCloneSource(p); setCloneName(`${p.name || 'Menu'} — Copy`); setCloneTag(p.tag || ''); }} className="p-1.5 hover:bg-violet-50 rounded-lg transition-colors" title="Duplicate"><Copy size={14} className="text-violet-600" /></button>
                          {p.status === MenuProfileStatus.ARCHIVED ? (
                            <button onClick={() => restore(p)} className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors" title="Restore"><RotateCcw size={14} className="text-blue-600" /></button>
                          ) : (
                            <button onClick={() => archive(p)} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" title="Archive"><Archive size={14} className="text-slate-400" /></button>
                          )}
                        </>
                      )}
                    </div>
                    {isAdmin && canDeleteProfile && (
                      <button
                        onClick={() => setDeleteConfirm({
                          message: `Delete menu profile "${p.name || 'Untitled'}"? This action cannot be undone.`,
                          onConfirm: () => del(p.id),
                        })}
                        className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      ><Trash2 size={14} className="text-red-400" /></button>
                    )}
                  </div>
              </div>
            );
          })}
        </div>
      )}
      </motion.div>
      )}
      </AnimatePresence>

      {exportModalProfile && (
        <ExportModal
          isOpen={true}
          data={menuProfileToMenuData(exportModalProfile)}
          profile={exportModalProfile}
          currency={loc.currencySymbol}
          onClose={() => setExportModalProfile(null)}
          onExportTemplate1={() => { exportRelationalTemplate1(exportModalProfile, `Menu_T1_${safeExportName(exportModalProfile.brandName)}_${safeExportName(exportModalProfile.name)}`, loc.currencySymbol); toast.success('Generating Export', 'Template 1 — Detailed download will start shortly.'); setExportModalProfile(null); }}
          onExportTemplate2={() => { exportRelationalTemplate2(exportModalProfile, `Menu_T2_${safeExportName(exportModalProfile.brandName)}_${safeExportName(exportModalProfile.name)}`, loc.currencySymbol); toast.success('Generating Export', 'Template 2 — Aggregator download will start shortly.'); setExportModalProfile(null); }}
          onExportBoth={() => { exportRelationalBoth(exportModalProfile, `Menu_${safeExportName(exportModalProfile.brandName)}_${safeExportName(exportModalProfile.name)}`, loc.currencySymbol); toast.success('Generating Export', 'Both Templates download will start shortly.'); setExportModalProfile(null); }}
        />
      )}

      <MenuBusyDialog
        isOpen={!!busyState}
        title={busyState?.title ?? ''}
        message={busyState?.message ?? ''}
        detail={busyState?.detail}
        estimateLabel={busyState?.estimateLabel}
      />

      <ConfirmDeleteDialog state={deleteConfirm} onClose={() => setDeleteConfirm(null)} />

      {/* Clone Menu Profile Modal */}
      {cloneSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => !cloning && setCloneSource(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 pt-5 pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className={cn('w-9 h-9 rounded-xl flex items-center justify-center transition-colors',
                    cloning ? 'bg-violet-100' : 'bg-violet-50')}>
                    {cloning ? <Loader2 size={18} className="text-violet-600 animate-spin" /> : <Copy size={18} className="text-violet-600" />}
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">{cloning ? 'Cloning in Progress…' : 'Clone Menu Profile'}</h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {cloning
                        ? (cloneProgress < 30 ? 'Preparing data…' : cloneProgress < 60 ? 'Copying categories & items…' : cloneProgress < 90 ? 'Linking images & options…' : cloneProgress < 100 ? 'Loading profile…' : 'Complete!')
                        : <>From <span className="font-semibold text-slate-700">"{cloneSource.name || 'Untitled'}"</span> ({cloneSource.brandName})</>}
                    </p>
                  </div>
                </div>
                {!cloning && <button onClick={() => setCloneSource(null)} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"><X size={16} className="text-slate-400" /></button>}
              </div>
            </div>

            {/* Progress Bar — always rendered, hidden when not cloning */}
            {cloning && (
              <div className="px-6 pb-3 space-y-2">
                <div className="relative w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={cn('absolute inset-y-0 left-0 rounded-full transition-all duration-300 ease-out',
                      cloneProgress >= 100 ? 'bg-emerald-500' : 'bg-gradient-to-r from-violet-500 to-indigo-500')}
                    style={{ width: `${Math.min(cloneProgress, 100)}%` }}
                  />
                  {cloneProgress < 100 && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {[{l:'Prepare',t:30},{l:'Copy',t:60},{l:'Images',t:85},{l:'Load',t:100}].map(s => (
                      <div key={s.l} className="flex items-center gap-1">
                        <div className={cn('w-2 h-2 rounded-full transition-colors',
                          cloneProgress >= s.t ? 'bg-emerald-400' : cloneProgress >= s.t - 20 ? 'bg-violet-400 animate-pulse' : 'bg-slate-200')} />
                        <span className={cn('text-[9px] font-bold uppercase tracking-wider',
                          cloneProgress >= s.t ? 'text-emerald-600' : cloneProgress >= s.t - 20 ? 'text-violet-600' : 'text-slate-400')}>{s.l}</span>
                      </div>
                    ))}
                  </div>
                  <span className={cn('text-sm font-bold tabular-nums', cloneProgress >= 100 ? 'text-emerald-600' : 'text-violet-600')}>
                    {Math.round(cloneProgress)}%
                  </span>
                </div>
              </div>
            )}

            {/* Form — hidden during cloning */}
            {!cloning && (
              <div className="px-6 pb-2 space-y-3">
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">New Profile Name *</label>
                    <input
                      value={cloneName} onChange={e => setCloneName(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-300 focus:border-violet-400 outline-none"
                      placeholder="e.g. Takeaway Menu" autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Tag</label>
                    <input
                      value={cloneTag} onChange={e => setCloneTag(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-300 focus:border-violet-400 outline-none"
                      placeholder="e.g. Takeaway"
                    />
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <p className="text-[11px] text-amber-700 leading-relaxed">
                    <strong>Note:</strong> Images are linked to original files. To use a different image for this menu, simply upload a new one in the editor.
                  </p>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-2">
              {!cloning && (
                <>
                  <button onClick={() => setCloneSource(null)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                  <button
                    onClick={handleClone} disabled={!cloneName.trim()}
                    className="flex items-center gap-1.5 px-5 py-2 text-sm font-bold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 rounded-lg shadow-sm transition-colors"
                  >
                    <Copy size={14} /> Clone Profile
                  </button>
                </>
              )}
              {cloning && cloneProgress >= 100 && (
                <div className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle2 size={16} />
                  <span className="text-sm font-bold">Opening profile…</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Profile Detail View (view + inline CRUD)
// ═══════════════════════════════════════════════════════════════════════════════

function ProfileDetailView({ profile, allProfiles, brands, user, highlightMinistry, allTags, startInEditMode, deepLinkItemId, deepLinkCatId, highlightField, requireApproval, teleportOrigin, onBack, onPublish, onArchive, onRestore, onDelete, onSave, onSaveDraft, onReady }: {
  profile: MenuProfile; allProfiles?: MenuProfile[]; brands: Brand[]; user: User | null; highlightMinistry: boolean;
  allTags: string[]; startInEditMode?: boolean; deepLinkItemId?: string | null;
  deepLinkCatId?: string | null; highlightField?: string | null;
  requireApproval?: boolean;
  teleportOrigin?: { profileId: string; type: string; source?: string } | null;
  onBack: () => void;
  onPublish: (p: MenuProfile) => void; onArchive: (p: MenuProfile) => void;
  onRestore: (p: MenuProfile) => void; onDelete: (id: string) => void; onSave: (p: MenuProfile) => void;
  onSaveDraft: (p: MenuProfile) => void;
  onReady?: () => void;
}) {
  const isAdmin = hasTabVariant(user, 'menu-manage', 'profiles', 'admin');
  const teleportNav = useNavigate();
  /** Strict role check: only tenant ADMIN or platform staff can delete profiles / manage connections */
  const canDeleteProfile = user?.role === UserRole.ADMIN || isPlatformRole(user?.role as UserRole);
  const canEditConnections = canDeleteProfile;

  // ── Entity-level pending lock detection ──
  const isGodMode = isPlatformRole(user?.role as UserRole);
  const requiresCreationApproval = !!requireApproval && !isAdmin && !isGodMode && user?.role === UserRole.MANAGER;
  const pending = usePendingChanges();

  // Server-confirmed pending IDs
  const serverPendingIds = useMemo(() => {
    const set = new Set<string>();
    for (const pe of (profile?.pendingEntities || [])) set.add(pe.entityId);
    return set;
  }, [profile?.pendingEntities]);

  // Reconcile optimistic IDs when server data arrives
  useEffect(() => { pending.reconcile(serverPendingIds); }, [serverPendingIds]);

  // Combined: server-confirmed + optimistic (not yet confirmed)
  const pendingEntityIds = useMemo(() => {
    const combined = new Set(serverPendingIds);
    for (const id of pending.optimisticIds) combined.add(id);
    return combined;
  }, [serverPendingIds, pending.optimisticIds]);

  const isProfileCreationPending = useMemo(() => {
    if (profile.pendingChangeType === 'CREATE_PROFILE') return true;
    return !!profile.pendingEntities?.some((pendingEntity) => pendingEntity.entityId === profile.id && pendingEntity.changeType === 'CREATE_PROFILE');
  }, [profile.id, profile.pendingChangeType, profile.pendingEntities]);

  const approvalProtectedLiveMenu = !isAdmin && !!requireApproval && !!(profile as any).liveData;

  const parsePendingRecord = useCallback((value: string | null | undefined): Record<string, unknown> | null => {
    if (!value) return null;
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : null;
    } catch {
      return null;
    }
  }, []);

  const serverPendingChanges = useMemo(() => {
    const next = new Map<string, ProposedChange>();
    for (const pendingEntity of (profile.pendingEntities || [])) {
      const entityId = pendingEntity.entityId;
      if (!entityId) continue;
      const proposed = parsePendingRecord(pendingEntity.entityData) ?? {};
      const previous = parsePendingRecord(pendingEntity.previousData);
      next.set(entityId, {
        entityId,
        entityType: (pendingEntity.entityType as ProposedChange['entityType']) || 'item',
        changeType: (pendingEntity.changeType as ProposedChange['changeType']) || 'MODIFY',
        proposed,
        previous,
        addedAt: 0,
        changedFields: computeChangedFields(proposed, previous),
      });
    }
    return next;
  }, [profile.pendingEntities, parsePendingRecord]);

  const mergedPendingChanges = useMemo(() => {
    const next = new Map(serverPendingChanges);
    for (const [entityId, change] of pending.proposedChanges) next.set(entityId, change);
    return next;
  }, [serverPendingChanges, pending.proposedChanges]);

  const getPendingChange = useCallback((entityId: string | null | undefined): ProposedChange | undefined => {
    if (!entityId) return undefined;
    return mergedPendingChanges.get(entityId);
  }, [mergedPendingChanges]);

  const isEntityLocked = useCallback((entityId: string | null | undefined): boolean => {
    if (!entityId || isGodMode) return false;
    return pendingEntityIds.has(entityId);
  }, [pendingEntityIds, isGodMode]);

  /** Granular lock: returns which fields are locked for a given entity */
  const getEntityLock = useCallback((entityId: string | null | undefined): { isLocked: boolean; lockedFields: string[] } => {
    if (!entityId || isGodMode) return { isLocked: false, lockedFields: [] };
    if (!pendingEntityIds.has(entityId)) return { isLocked: false, lockedFields: [] };
    const proposed = getPendingChange(entityId);
    return { isLocked: true, lockedFields: proposed?.changedFields || [] };
  }, [pendingEntityIds, isGodMode, getPendingChange]);

  /** Check if item is locked directly OR any of its option groups are under observation.
   *  Returns granular info: which groups are locked vs the item itself. */
  const getItemLockState = useCallback((item: MenuItem | null | undefined): {
    itemDirectLocked: boolean;
    lockedGroupIds: string[];
    anyLocked: boolean;
    itemLockedFields: string[];
  } => {
    if (!item || isGodMode) return { itemDirectLocked: false, lockedGroupIds: [], anyLocked: false, itemLockedFields: [] };
    const itemLock = getEntityLock(item.id);
    const lockedGroupIds: string[] = [];
    for (const grp of (item.optionGroups || [])) {
      if (pendingEntityIds.has(grp.id)) lockedGroupIds.push(grp.id);
    }
    return {
      itemDirectLocked: itemLock.isLocked,
      lockedGroupIds,
      anyLocked: itemLock.isLocked || lockedGroupIds.length > 0,
      itemLockedFields: itemLock.lockedFields,
    };
  }, [pendingEntityIds, isGodMode, getEntityLock]);

  /** Backward-compat boolean helpers */
  const isItemOrChildLocked = useCallback((item: MenuItem | null | undefined) => {
    return getItemLockState(item).anyLocked;
  }, [getItemLockState]);
  /** Legacy profile-level lock kept for backward compat (when no entity info) */
  const isLocked = !isGodMode && (isProfileCreationPending || (!!profile?.pendingChangeRequest && pendingEntityIds.size === 0));
  const isNewProfile = !(profile as any).hasDraft && !(profile as any).liveData && !profile.pendingChangeRequest;
  const profileConfirmLabel = requiresCreationApproval && isNewProfile ? 'Submit Profile for Approval' : 'Confirm';
  const profileConfirmClass = requiresCreationApproval && isNewProfile ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-600 hover:bg-teal-700';
  const repo = getRepository();
  const toast = useToast();
  const bg = useBackgroundTask();
  const loc = useMenuLocalization();
  const [financialSettings, setFinancialSettings] = useState<FinancialSettingsSlice>(DEFAULT_FINANCIAL_SETTINGS);
  const financialVisibility = useFinancialVisibility(user, financialSettings);
  const [exportModalProfile, setExportModalProfile] = useState<MenuProfile | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importState, setImportState] = useState<MenuImportState | null>(null);

  const applyFinancialSettings = useCallback((raw?: Partial<MenuSettings> | Record<string, unknown> | null) => {
    setFinancialSettings(toFinancialSettingsSlice(raw || DEFAULT_MENU_SETTINGS));
  }, []);

  const loadFinancialSettings = useCallback(() => {
    if (isRemoteMode()) {
      fetchMenuSettings()
        .then(raw => applyFinancialSettings(raw))
        .catch(() => applyFinancialSettings(DEFAULT_MENU_SETTINGS));
      return;
    }
    repo.getAll<MenuSettings>('menu-settings')
      .then(all => applyFinancialSettings(all[0] || DEFAULT_MENU_SETTINGS))
      .catch(() => applyFinancialSettings(DEFAULT_MENU_SETTINGS));
  }, [applyFinancialSettings, repo]);

  useEffect(() => { loadFinancialSettings(); }, [loadFinancialSettings]);
  useEffect(() => {
    const handleSettingsUpdated = (event: Event) => {
      const raw = getSettingsFromEvent(event);
      if (raw) {
        applyFinancialSettings(raw);
        return;
      }
      loadFinancialSettings();
    };
    window.addEventListener(MENU_SETTINGS_UPDATED, handleSettingsUpdated);
    return () => window.removeEventListener(MENU_SETTINGS_UPDATED, handleSettingsUpdated);
  }, [applyFinancialSettings, loadFinancialSettings]);

  // AI feature gating
  const [aiVisible, setAiVisible] = useState(false);
  useEffect(() => { checkAiEnabled().then(setAiVisible).catch(() => setAiVisible(false)); }, []);

  // ── Draft tracking: detect unsaved local changes ─────────────────────────
  const [isDirty, setIsDirty] = useState(false);
  const [showExitGuard, setShowExitGuard] = useState(false);
  const savedRef = useRef(profile);
  const pendingGuardAction = useRef<(() => void) | null>(null);
  const pendingReorderRef = useRef<MenuProfile | null>(null);

  // Wrap onSave to track dirty state + auto-save draft to server
  const updateLocal = useCallback((p: MenuProfile) => {
    // Ensure Unassigned bucket always exists
    const safe = { ...p, categories: ensureUnassignedBucket(p.categories) };
    // ── Status Reversion: if PUBLISHED, auto-revert to DRAFT on any edit ──
    if (!approvalProtectedLiveMenu && safe.status === MenuProfileStatus.PUBLISHED) {
      safe.status = MenuProfileStatus.DRAFT;
    }
    if (approvalProtectedLiveMenu) {
      safe.status = profile.status;
    }
    onSave(safe);
    if (approvalProtectedLiveMenu) {
      setIsDirty(false);
      return;
    }
    // ── Auto-Draft: fire background save to server immediately ──
    // isDirty is set briefly for exit guard safety; cleared after BG save completes
    setIsDirty(true);
    onSaveDraft(safe);
  }, [onSave, onSaveDraft, approvalProtectedLiveMenu, profile.status]);

  // Generic guard: auto-draft means no blocking — always execute immediately
  const guardAction = useCallback((action: () => void) => {
    action();
  }, []);

  // Guard back navigation
  const guardedBack = useCallback(() => {
    guardAction(onBack);
  }, [guardAction, onBack]);

  // ── Drawer state (replaces global edit mode) ─────────────────────────────
  type DrawerState =
    | null
    | { type: 'profile' }
    | { type: 'category'; catId: string | null }
    | { type: 'product'; catId: string; itemId: string | null }
    | { type: 'optionGroup'; catId: string; itemId: string; grpId: string | null };
  type ProductDrawerState = Extract<NonNullable<DrawerState>, { type: 'product' }>;
  const [drawer, setDrawer] = useState<DrawerState>(null);
  // Snapshot refs for deep-comparison dirty-checking
  const profileFormSnap = useRef<any>(null);
  const catFormSnap = useRef<any>(null);
  const prodFormSnap = useRef<any>(null);
  const prodPrimaryCatSnap = useRef<string>('');
  const prodLinkedCatsSnap = useRef<string[]>([]);
  const grpFormSnap = useRef<any>(null);
  const bulkCheckedSnap = useRef<Set<string>>(new Set());
  const stagedImageSnap = useRef<File | null>(null);
  const [showDrawerGuard, setShowDrawerGuard] = useState(false);
  const [drawerSaving, setDrawerSaving] = useState(false);
  const pendingDrawerRef = useRef<DrawerState>(null);
  const parentProductRef = useRef<{ drawer: ProductDrawerState; prodForm: MenuItem; expandedGrps: Set<string> } | null>(null);
  const returnToAuditRef = useRef(false);
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  // Ref to capture latest profile for background upload (avoids stale closure)
  const profileRef = useRef(profile);
  profileRef.current = profile;

  // Teleport highlight: pulsing border on the target field
  const teleportCls = (field: string) =>
    highlightField === field ? 'ring-2 ring-rose-400 ring-offset-1 animate-pulse' : '';

  // ── Deep-link: sync product drawer ↔ ?edit= URL param ───────────────────
  const [editParam, setEditParam] = usePersistentState('edit');

  // ── Profile form (for profile drawer) ────────────────────────────────────
  const safeConnMods = (v: unknown): string[] => { if (Array.isArray(v)) return v; if (typeof v === 'string') { try { const p = JSON.parse(v); if (Array.isArray(p)) return p; } catch {} } return []; };
  const [profileForm, setProfileForm] = useState({ name: profile?.name || '', brandId: profile?.brandId || '', brandName: profile?.brandName || '', branchIds: profile?.branchIds || [], tag: profile?.tag || '', type: profile?.type, connectedModules: safeConnMods(profile?.connectedModules), availabilitySchedule: profile?.availabilitySchedule });
  const [tagInput, setTagInput] = useState(profile?.tag || '');
  const [showTagDD, setShowTagDD] = useState(false);
  const [branchConflict, setBranchConflict] = useState<{ conflictingProfiles: MenuProfile[], nextForm: any } | null>(null);

  // ── Category form ────────────────────────────────────────────────────────
  const [catForm, setCatForm] = useState<{ nameEN: string; nameAR: string; code?: string; availabilitySchedule?: AvailabilitySchedule }>({ nameEN: '', nameAR: '' });

  // ── Product form ─────────────────────────────────────────────────────────
  const [prodForm, setProdForm] = useState<MenuItem>({ id: '', nameEN: '', nameAR: '', basePrice: 0, cost: undefined, options: [], optionGroups: [], sortOrder: 0, categoryIds: [] });
  const [productDrawerTab, setProductDrawerTab] = useState<'details' | 'recipe'>('details');
  useEffect(() => {
    if (drawer?.type !== 'product') {
      setProductDrawerTab('details');
      return;
    }
    if (!financialVisibility.showCostData && productDrawerTab === 'recipe') {
      setProductDrawerTab('details');
    }
  }, [drawer?.type, financialVisibility.showCostData, productDrawerTab]);

  /** Granular lock state for the currently-open product drawer */
  const drawerLock = React.useMemo(() => {
    if (drawer?.type !== 'product' || !prodForm.id) return { isLocked: false, lockedFields: [] as string[] };
    return getEntityLock(prodForm.id);
  }, [drawer?.type, prodForm.id, getEntityLock]);
  const isFieldPending = React.useCallback((field: string) => drawerLock.lockedFields.includes(field), [drawerLock.lockedFields]);

  const [stagedImage, setStagedImage] = useState<File | null>(null);
  const stagedImageUrl = React.useMemo(() => stagedImage ? URL.createObjectURL(stagedImage) : null, [stagedImage]);
  React.useEffect(() => () => { if (stagedImageUrl) URL.revokeObjectURL(stagedImageUrl); }, [stagedImageUrl]);

  // ── Category linking state for Product Drawer ────────────────────────────
  const [catLinkSearch, setCatLinkSearch] = useState('');
  const [prodPrimaryCat, setProdPrimaryCat] = useState<string>('');
  const [prodLinkedCats, setProdLinkedCats] = useState<string[]>([]);

  // ── Option Group form ────────────────────────────────────────────────────
  const [grpForm, setGrpForm] = useState<OptionGroup>({ id: '', nameEN: '', nameAR: '', options: [], minSelection: 0, maxSelection: 0 });
  const [optReorderMode, setOptReorderMode] = useState(false);
  const optDragIdxRef = useRef<number | null>(null);

  // ── Bulk product assignment for option groups ────────────────────────────
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkChecked, setBulkChecked] = useState<Set<string>>(new Set());
  const [bulkSearch, setBulkSearch] = useState('');

  // ── Option clipboard & bulk selection ────────────────────────────────────
  const { clipboard, copyToClipboard } = useMenuClipboard();
  const [selectedOptIds, setSelectedOptIds] = useState<Set<string>>(new Set());
  const toggleOptSel = (id: string) => setSelectedOptIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOptsSelected = grpForm.options.length > 0 && grpForm.options.every(o => selectedOptIds.has(o.id));
  const toggleAllOpts = () => { if (allOptsSelected) setSelectedOptIds(new Set()); else setSelectedOptIds(new Set(grpForm.options.map(o => o.id))); };
  // Reset selection when drawer changes
  useEffect(() => { setSelectedOptIds(new Set()); setOptReorderMode(false); }, [drawer]);
  // Return-to-audit: navigate back after confirm closes drawer
  useEffect(() => { if (returnToAuditRef.current && !drawer) { returnToAuditRef.current = false; onBackRef.current(); } }, [drawer]);

  // ── Deep-comparison dirty-checking (replaces boolean setDrawerDirty) ────
  const drawerDirty = useMemo(() => {
    if (!drawer) return false;
    if (drawer.type === 'profile') return hasDataChanged(profileFormSnap.current, profileForm);
    if (drawer.type === 'category') return hasDataChanged(catFormSnap.current, catForm);
    if (drawer.type === 'product') {
      const formDirty = hasDataChanged(prodFormSnap.current, prodForm) || stagedImage !== stagedImageSnap.current;
      const catDirty = prodPrimaryCat !== prodPrimaryCatSnap.current
        || hasDataChanged([...prodLinkedCats].sort(), [...prodLinkedCatsSnap.current].sort());
      return formDirty || catDirty;
    }
    if (drawer.type === 'optionGroup') {
      const formDirty = hasDataChanged(grpFormSnap.current, grpForm);
      const bulkArr = [...bulkChecked].sort();
      const snapArr = [...bulkCheckedSnap.current].sort();
      const bulkDirty = bulkArr.length !== snapArr.length || bulkArr.some((v, i) => v !== snapArr[i]);
      return formDirty || bulkDirty;
    }
    return false;
  }, [drawer, profileForm, catForm, prodForm, grpForm, stagedImage, prodPrimaryCat, prodLinkedCats, bulkChecked]);
  // No-op setter — keeps existing setDrawerDirty(true/false) calls harmless
  const setDrawerDirty = (_v: boolean) => {};

  // ── Delete confirmation ──────────────────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState | null>(null);
  const [unlinkConfirm, setUnlinkConfirm] = useState<{ grpName: string; onConfirm: () => void } | null>(null);

  // ── Tree state ───────────────────────────────────────────────────────────
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedProdGrps, setExpandedProdGrps] = useState<Set<string>>(new Set());
  const [grpReorderMode, setGrpReorderMode] = useState(false);
  const [selectedCatId, setSelectedCatId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = usePersistentState<'products' | 'options'>('subtab', 'products');

  // ── Sync selectedCatId to shared context for scroll-to-sync ─────────────
  const { state: sharedCtx, setActiveCatId: setSharedCatId } = React.useContext(MenuPreviewCtx);
  const profiles = sharedCtx.profiles;
  useEffect(() => { setSharedCatId(selectedCatId); }, [selectedCatId]);

  // ── Shared toolbar ───────────────────────────────────────────────────────
  const [tabSearch, setTabSearch] = usePersistentState('search');
  const [showTabFilter, setShowTabFilter] = useState(false);
  const [tabFilterCat, setTabFilterCat] = usePersistentState('cat');
  const [showTabMenu, setShowTabMenu] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [pendingReorderProfile, setPendingReorderProfile] = useState<MenuProfile | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  useEffect(() => { pendingReorderRef.current = pendingReorderProfile; }, [pendingReorderProfile]);
  const [selectedOptGrpId, setSelectedOptGrpId] = useState<string | null>(null);
  const [focusedItemId, setFocusedItemId] = useState<string | null>(null);
  const dragIdRef = useRef<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  const pendingDisplay = useMemo(() => {
    const baseProfile = pendingReorderProfile ?? profile;
    const categories: MenuCategory[] = baseProfile.categories.map((category) => ({
      ...category,
      items: category.items.map((item) => ({
        ...item,
        optionGroups: (item.optionGroups || []).map((group) => ({
          ...group,
          options: group.options.map((option) => ({ ...option })),
        })),
      })),
    }));
    const ghostCategoryIds = new Set<string>();
    const ghostItemIds = new Set<string>();

    for (const change of mergedPendingChanges.values()) {
      if (change.entityType !== 'category' || change.changeType !== 'ADD') continue;
      if (categories.some((category) => category.id === change.entityId)) {
        ghostCategoryIds.add(change.entityId);
        continue;
      }
      const proposedCategory = change.proposed as Partial<MenuCategory>;
      categories.push({
        id: String(proposedCategory.id ?? change.entityId),
        nameEN: String(proposedCategory.nameEN ?? ''),
        nameAR: String(proposedCategory.nameAR ?? ''),
        code: proposedCategory.code,
        items: Array.isArray(proposedCategory.items) ? proposedCategory.items.map((item) => ({
          ...item,
          optionGroups: (item.optionGroups || []).map((group) => ({
            ...group,
            options: (group.options || []).map((option) => ({ ...option })),
          })),
        })) : [],
        sortOrder: typeof proposedCategory.sortOrder === 'number'
          ? proposedCategory.sortOrder
          : categories.filter((category) => !isUnassignedCat(category.id)).length,
        hidden: !!proposedCategory.hidden,
        availabilitySchedule: proposedCategory.availabilitySchedule,
      } as MenuCategory);
      ghostCategoryIds.add(change.entityId);
    }

    for (const change of mergedPendingChanges.values()) {
      if (change.entityType !== 'item' || change.changeType !== 'ADD') continue;
      const proposedItem = change.proposed as Partial<MenuItem>;
      const targetCatIds = Array.isArray(proposedItem.categoryIds) && proposedItem.categoryIds.length > 0
        ? proposedItem.categoryIds.map(String)
        : [categories.find((category) => !isUnassignedCat(category.id))?.id].filter(Boolean) as string[];
      if (targetCatIds.length === 0) continue;

      for (const targetCatId of targetCatIds) {
        const targetCategory = categories.find((category) => category.id === targetCatId);
        if (!targetCategory || targetCategory.items.some((item) => item.id === change.entityId)) continue;
        targetCategory.items.push({
          ...(proposedItem as MenuItem),
          id: String(proposedItem.id ?? change.entityId),
          categoryIds: targetCatIds,
          optionGroups: (proposedItem.optionGroups || []).map((group) => ({
            ...group,
            options: (group.options || []).map((option) => ({ ...option })),
          })),
          sortOrder: typeof proposedItem.sortOrder === 'number' ? proposedItem.sortOrder : targetCategory.items.length,
        });
      }
      ghostItemIds.add(change.entityId);
    }

    return {
      profile: { ...baseProfile, categories },
      ghostCategoryIds,
      ghostItemIds,
    };
  }, [pendingReorderProfile, profile, mergedPendingChanges]);

  // The "active" profile is the pending reorder version plus any pending additions.
  const activeProfile = pendingDisplay.profile;
  const ghostCategoryIds = pendingDisplay.ghostCategoryIds;
  const ghostItemIds = pendingDisplay.ghostItemIds;

  // ── Deep-link navigation: Options → Products ────────────────────────────
  const navigateToItem = useCallback((catId: string, itemId: string) => {
    setDetailTab('products');
    setSelectedCatId(catId);
    setTabSearch('');
    setTabFilterCat('');
    setReorderMode(false);
    setFocusedItemId(itemId);
    // Clear glow after animation
    setTimeout(() => setFocusedItemId(null), 2200);
    // Scroll into view after React render
    setTimeout(() => {
      const el = document.getElementById(`menu-item-${itemId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
  }, []);

  // ── Generic drag-drop reorder helper ─────────────────────────────────────
  const makeDrag = (id: string) => ({
    onDragStart: (e: React.DragEvent) => {
      if (!reorderMode) return;
      dragIdRef.current = id;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', id);
    },
    onDragEnd: () => {
      dragIdRef.current = null;
      setDropTargetId(null);
    },
  });
  const makeDrop = <T extends { id: string },>(arr: T[], id: string, onReorder: (fromIndex: number, toIndex: number) => void) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!reorderMode) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    },
    onDragEnter: (e: React.DragEvent) => {
      if (!reorderMode) return;
      e.preventDefault();
      setDropTargetId(id);
    },
    onDrop: (e: React.DragEvent) => {
      if (!reorderMode) return;
      e.preventDefault();
      e.stopPropagation();
      const sourceId = dragIdRef.current;
      const fromIndex = arr.findIndex((entry) => entry.id === sourceId);
      const toIndex = arr.findIndex((entry) => entry.id === id);
      if (sourceId && fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
        onReorder(fromIndex, toIndex);
      }
      dragIdRef.current = null;
      setDropTargetId(null);
    },
  });

  // ── Ministry vault ───────────────────────────────────────────────────────
  const [showVaultLinker, setShowVaultLinker] = useState(false);
  const [showFileGallery, setShowFileGallery] = useState(false);
  const [vaultFiles, setVaultFiles] = useState<MinistryFile[]>([]);
  useEffect(() => { getRepository().getAll<MinistryFile>('ministry-files').then(setVaultFiles); }, []);

  // ── Availability index (master-link sync) ────────────────────────────────
  const [availIndex, setAvailIndex] = useState<AvailabilityIndex>({ globalHiddenSet: new Set(), libraryMap: new Map() });
  const [disabledOptIds, setDisabledOptIds] = useState<Set<string>>(new Set());
  const loadAvailIndex = useCallback(() => {
    getRepository().getAll<LibraryModifier>('modifier-library').then(mods => {
      const scoped = profile.brandId ? mods.filter(m => !m.brandId || m.brandId === profile.brandId) : mods;
      setAvailIndex(buildAvailabilityIndex(scoped));
    });
  }, [profile.brandId]);
  useEffect(() => { loadAvailIndex(); }, [loadAvailIndex]);
  useEffect(() => {
    const handler = () => loadAvailIndex();
    window.addEventListener(MENU_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(MENU_DATA_UPDATED_EVENT, handler);
  }, [loadAvailIndex]);

  /** Availability Map: Record<libraryOptionId, boolean(hidden)> — single source of truth */
  const availabilityMap = useMemo<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    for (const id of availIndex.globalHiddenSet) map[id] = true;
    return map;
  }, [availIndex]);

  /** Check if an option is globally hidden via master library */
  const isGloballyHidden = useCallback((opt: MenuItemOption, grp: OptionGroup) => {
    if (!grp.linkedLibraryId || !opt.libraryOptionId) return false;
    return !!availabilityMap[opt.libraryOptionId];
  }, [availabilityMap]);

  // Reset when profile changes
  useEffect(() => {
    setProfileForm({ name: profile.name, brandId: profile.brandId, brandName: profile.brandName, branchIds: profile.branchIds || [], tag: profile.tag || '', type: profile.type, connectedModules: safeConnMods(profile.connectedModules), availabilitySchedule: profile.availabilitySchedule });
    setTagInput(profile.tag || '');
    const firstVisibleCat = profile.categories.find(c => !isUnassignedCat(c.id));
    if (firstVisibleCat) setSelectedCatId(firstVisibleCat.id);
    else setSelectedCatId(null);
    setTabSearch('');
    setDrawer(null);
    setDrawerDirty(false);
  }, [profile.id]);

  useEffect(() => {
    if (!onReady) return;
    void waitForNextPaint().then(() => waitForNextPaint()).then(() => onReady());
  }, [onReady, profile.id]);

  // Deep-link: auto-open item drawer when deepLinkItemId is provided
  useEffect(() => {
    if (!deepLinkItemId) return;
    for (const cat of profile.categories) {
      const item = cat.items.find(i => i.id === deepLinkItemId);
      if (item) {
        setSelectedCatId(cat.id);
        setDetailTab('products');
        openDrawer({ type: 'product', catId: cat.id, itemId: item.id });
        break;
      }
    }
  }, [deepLinkItemId, profile.id]);

  // Deep-link: auto-open category drawer when deepLinkCatId is provided (no item)
  useEffect(() => {
    if (!deepLinkCatId || deepLinkItemId) return;
    const cat = profile.categories.find(c => c.id === deepLinkCatId);
    if (cat) {
      setSelectedCatId(cat.id);
      setDetailTab('products');
      openDrawer({ type: 'category', catId: cat.id });
    }
  }, [deepLinkCatId, deepLinkItemId, profile.id]);

  // Deep-link: auto-open product drawer from ?edit=<itemId> URL param
  const editParamHandledRef = useRef(false);
  useEffect(() => {
    if (!editParam || editParamHandledRef.current || drawer || deepLinkItemId) return;
    for (const cat of profile.categories) {
      const item = cat.items.find(i => i.id === editParam);
      if (item) {
        editParamHandledRef.current = true;
        setSelectedCatId(cat.id);
        setDetailTab('products');
        openDrawer({ type: 'product', catId: cat.id, itemId: item.id });
        break;
      }
    }
  }, [editParam, profile.id]);

  const toggleExpand = (setFn: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) => {
    setFn(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const totalItems = activeProfile.categories.filter(c => !isUnassignedCat(c.id)).reduce((a, c) => a + c.items.length, 0);
  const hasMinistry = profile.ministryFileIds.length > 0;
  const ub = user?.assignedBrands;
  const aBrands = (ub && ub.length > 0) ? brands.filter(b => ub.includes(b.id)) : brands;

  // ── Drawer open helpers ──────────────────────────────────────────────────
  const tryOpenDrawer = (next: DrawerState) => {
    if (isLocked) { toast.error('Locked', 'This menu has a pending change request. Editing is disabled until it is approved or rejected.'); return; }
    if (approvalProtectedLiveMenu && next?.type === 'profile') {
      toast.error('Approval Required', 'Profile settings on a live menu can only be changed by an Admin while manual approval is enabled.');
      return;
    }
    // Entity-level lock check (covers server-confirmed AND optimistic)
    const entityId = next?.type === 'product' ? next.itemId : next?.type === 'category' ? next.catId : next?.type === 'optionGroup' ? next.grpId : null;
    if (entityId && isEntityLocked(entityId)) {
      toast.error('Change Pending', 'This item has a proposed modification awaiting Admin approval. Editing is disabled.');
      return;
    }
    // Granular: if only option groups are locked (not the item itself), ALLOW opening the drawer.
    // The drawer will handle disabling locked group sections internally.
    if (drawer && drawerDirty) { pendingDrawerRef.current = next; setShowDrawerGuard(true); return; }
    openDrawer(next);
  };
  const openDrawer = (next: DrawerState) => {
    if (!next) { setDrawer(null); setDrawerDirty(false); setEditParam(''); return; }
    // Sync ?edit= URL param for product deep-links
    if (next.type === 'product' && next.itemId) setEditParam(next.itemId);
    else if (next.type !== 'optionGroup') setEditParam('');
    if (next.type === 'profile') {
      const snap = { name: profile.name, brandId: profile.brandId, brandName: profile.brandName, branchIds: profile.branchIds || [], tag: profile.tag || '', type: profile.type, connectedModules: safeConnMods(profile.connectedModules), availabilitySchedule: profile.availabilitySchedule };
      setProfileForm(snap);
      profileFormSnap.current = JSON.parse(JSON.stringify(snap));
      setTagInput(profile.tag || '');
    } else if (next.type === 'category') {
      const cat = next.catId ? profile.categories.find(c => c.id === next.catId) : null;
      const snap = { nameEN: cat?.nameEN || '', nameAR: cat?.nameAR || '', code: cat?.code || '', availabilitySchedule: cat?.availabilitySchedule };
      setCatForm(snap);
      catFormSnap.current = JSON.parse(JSON.stringify(snap));
    } else if (next.type === 'product') {
      const cat = profile.categories.find(c => c.id === next.catId);
      const item = next.itemId ? cat?.items.find(it => it.id === next.itemId) : null;
      const form = item ? JSON.parse(JSON.stringify(item)) : { id: '', nameEN: '', nameAR: '', basePrice: 0, cost: undefined, options: [], optionGroups: [], sortOrder: 0, categoryIds: [next.catId] };
      setProdForm(form);
      prodFormSnap.current = JSON.parse(JSON.stringify(form));
      // Initialize category linking state
      const itemCatIds = form.categoryIds && form.categoryIds.length > 0 ? form.categoryIds : [next.catId];
      setProdPrimaryCat(itemCatIds[0]);
      setProdLinkedCats(itemCatIds.slice(1));
      prodPrimaryCatSnap.current = itemCatIds[0];
      prodLinkedCatsSnap.current = itemCatIds.slice(1);
      setCatLinkSearch('');
    } else if (next.type === 'optionGroup') {
      const cat = profile.categories.find(c => c.id === next.catId);
      const item = cat?.items.find(it => it.id === next.itemId);
      const grp = next.grpId ? (item?.optionGroups || []).find(g => g.id === next.grpId) : null;
      const snap = grp ? JSON.parse(JSON.stringify(grp)) : { id: '', nameEN: '', nameAR: '', options: [], minSelection: 0, maxSelection: 0 };
      setGrpForm(snap);
      grpFormSnap.current = JSON.parse(JSON.stringify(snap));
      // Snapshot initial product selections for dirty-checking
      const initChecked = new Set<string>();
      if (next.grpId) {
        const libId = grp?.linkedLibraryId || next.grpId;
        for (const c of profile.categories) {
          if (isUnassignedCat(c.id)) continue;
          for (const it of c.items) {
            if ((it.optionGroupIds || []).includes(libId) ||
                (it.optionGroups || []).some(g => g.id === next.grpId || g.linkedLibraryId === libId)) {
              initChecked.add(it.id);
            }
          }
        }
      }
      setBulkChecked(initChecked);
      bulkCheckedSnap.current = new Set(initChecked);
      setBulkSearch(''); setShowBulkAssign(false);
    }
    setStagedImage(null);
    stagedImageSnap.current = null;
    setDrawer(next);
    setDrawerDirty(false);
  };
  const closeDrawer = () => {
    if (drawerDirty) { pendingDrawerRef.current = null; setShowDrawerGuard(true); return; }
    // If returning from OG drawer back to parent product drawer
    if (drawer?.type === 'optionGroup' && parentProductRef.current) {
      const p = parentProductRef.current;
      parentProductRef.current = null;
      setProdForm(p.prodForm);
      setExpandedProdGrps(p.expandedGrps);
      setDrawer(p.drawer);
      setDrawerDirty(true);
      return;
    }
    setDrawer(null); setDrawerDirty(false); setEditParam('');
  };
  const confirmProfileDrawer = () => {
    if (!profileForm.name.trim()) { toast.error('Name Required', 'Enter a profile name.'); return; }

    const tag = profileForm.tag;
    const branches = profileForm.branchIds;
    if (tag && branches.length > 0 && allProfiles) {
      const activeStats = [MenuProfileStatus.PUBLISHED, 'LIVE'];
      const conflicts = allProfiles.filter(p => {
        if (p.id === profile.id || p.tag !== tag) return false;
        if (!activeStats.includes(p.status as any)) return false;
        return p.branchIds?.some(bId => branches.includes(bId));
      });
      if (conflicts.length > 0) {
        setBranchConflict({ conflictingProfiles: conflicts, nextForm: profileForm });
        return;
      }
    }

    executeProfileSaved(profileForm);
  };

  const executeProfileSaved = async (form: any, overrideConflicts?: MenuProfile[]) => {
    const shouldOptimisticallyLockCreation = requiresCreationApproval && !profile.pendingChangeRequest && !(profile as any).liveData && !(profile as any).hasDraft;
    updateLocal({
      ...profile,
      name: form.name,
      brandId: form.brandId,
      brandName: form.brandName,
      branchIds: form.branchIds,
      tag: form.tag,
      type: form.type,
      connectedModules: form.connectedModules,
      availabilitySchedule: form.availabilitySchedule,
      ...(shouldOptimisticallyLockCreation ? {
        pendingChangeRequest: 'pending-create',
        pendingChangeType: 'CREATE_PROFILE' as const,
      } : {}),
    });
    if (overrideConflicts && overrideConflicts.length > 0) {
      for (const p of overrideConflicts) {
        const safeBranchIds = (p.branchIds || []).filter((id: string) => !form.branchIds.includes(id));
        await repo.save('menu-profiles', p.orgId || 'org1', { ...p, branchIds: safeBranchIds, updatedAt: Date.now() });
      }
      toast.success('Conflicts Resolved', 'Other profiles updated to remove overlapping branches.');
    }
    setDrawer(null); setDrawerDirty(false); setBranchConflict(null);
    bg.run({ label: form.name || 'Profile', entityId: profile.id, work: async () => {} });
  };

  // ── Atomic Change Request: entity-level submit for non-admin users ────────
  const atomicSubmitEntity = useCallback(async (
    entityType: 'item' | 'category' | 'optionGroup',
    entityId: string,
    entityData: unknown,
    previousEntityData: unknown,
    changeType: 'MODIFY' | 'DELETE' | 'ADD',
    changeLabel: string,
  ) => {
    if (isAdmin || !requireApproval) return;

    // ── Optimistic lock: add to pending BEFORE API call ──
    pending.addPending(entityId, {
      entityId,
      entityType,
      changeType,
      proposed: (entityData as Record<string, unknown>) || {},
      previous: (previousEntityData as Record<string, unknown>) || null,
    });

    try {
      const p = profileRef.current;
      await submitChangeRequest(
        p.id, p.name || 'Untitled',
        JSON.stringify(entityData), // changes (entity snapshot)
        JSON.stringify(previousEntityData), // previous data
        entityId, entityType, changeType,
        JSON.stringify(entityData), // entity_data for atomic merge
      );
      window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
    } catch (err: any) {
      // 409 = duplicate — item is already pending, keep optimistic lock and swallow error
      if (err?.status === 409) {
        toast.warning('Already Pending', 'A change request for this item is already awaiting approval.');
        window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
        return; // Don't re-throw — bg task should not show error for duplicates
      }
      // Other failures: rollback optimistic lock
      pending.removePending(entityId);
      window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
      throw err;
    }
  }, [isAdmin, requireApproval, pending]);

  /** Find entity in liveData for 'previous' snapshot */
  const getLiveEntity = useCallback((entityType: 'item' | 'category' | 'optionGroup', entityId: string) => {
    const liveCats: MenuCategory[] = (profile as any).liveData || profile.categories;
    if (entityType === 'category') return liveCats.find((c: MenuCategory) => c.id === entityId) || null;
    if (entityType === 'optionGroup') {
      for (const cat of liveCats) {
        for (const item of (cat.items || [])) {
          const grp = (item.optionGroups || []).find((g: any) => g.id === entityId);
          if (grp) return grp;
        }
      }
      return null;
    }
    for (const cat of liveCats) {
      const item = (cat.items || []).find((it: MenuItem) => it.id === entityId);
      if (item) return item;
    }
    return null;
  }, [profile]);

  const submitInlineEntityChange = useCallback((
    entityType: 'item' | 'category' | 'optionGroup',
    entityId: string,
    entityData: Record<string, unknown>,
    changeLabel: string,
  ): boolean => {
    if (!approvalProtectedLiveMenu) return false;
    const previousEntityData = getLiveEntity(entityType, entityId);
    bg.run({
      label: `Submit: ${changeLabel}`,
      entityId,
      work: async () => {
        await atomicSubmitEntity(entityType, entityId, entityData, previousEntityData, 'MODIFY', changeLabel);
      },
    });
    return true;
  }, [approvalProtectedLiveMenu, bg, atomicSubmitEntity, getLiveEntity]);

  const confirmCategoryDrawer = () => {
    if (!catForm.nameEN.trim()) { toast.error('Name Required', 'Enter a category name.'); return; }
    if (drawer?.type !== 'category') return;
    const dupCat = profile.categories.find(c => c.id !== drawer.catId && c.nameEN.trim().toLowerCase() === catForm.nameEN.trim().toLowerCase());
    if (dupCat) { toast.error('Duplicate Category', `"${catForm.nameEN}" already exists.`); return; }
    const isNew = !drawer.catId;
    const catId = drawer.catId || genId();
    let updated: typeof profile;
    if (!isNew) {
      const autoCode = catForm.code || generateCategoryCode(profile.brandName, profile.categories);
      updated = { ...profile, categories: profile.categories.map(c => c.id === catId ? { ...c, nameEN: catForm.nameEN, nameAR: catForm.nameAR, code: autoCode, availabilitySchedule: catForm.availabilitySchedule } : c) };
    } else {
      const autoCode = generateCategoryCode(profile.brandName, profile.categories);
      const newCat: MenuCategory = { id: catId, nameEN: catForm.nameEN, nameAR: catForm.nameAR, code: catForm.code || autoCode, items: [], sortOrder: profile.categories.filter(c => !isUnassignedCat(c.id)).length, availabilitySchedule: catForm.availabilitySchedule };
      updated = { ...profile, categories: [...profile.categories, newCat] };
      setSelectedCatId(newCat.id);
    }
    updateLocal(updated);
    setDrawer(null); setDrawerDirty(false);
    // Entity-level atomic submit
    if (!isAdmin && requireApproval) {
      const catData = updated.categories.find(c => c.id === catId);
      const prevCat = getLiveEntity('category', catId);
      bg.run({ label: `Submit: ${catForm.nameEN}`, entityId: catId, work: async () => {
        await atomicSubmitEntity('category', catId, catData, prevCat, isNew ? 'ADD' : 'MODIFY', catForm.nameEN);
      }});
    }
  };
  const deleteCategoryDrawer = () => {
    if (drawer?.type !== 'category' || !drawer.catId) return;
    const cat = profile.categories.find(c => c.id === drawer.catId);
    if (cat && cat.items.length > 0) { toast.error('Cannot Delete', 'Remove all products first.'); return; }
    if (!isAdmin && requireApproval) {
      // Manager: submit entity-level deletion request
      const prevCat = getLiveEntity('category', drawer.catId);
      setDrawer(null); setDrawerDirty(false);
      bg.run({
        label: `Delete Request: ${cat?.nameEN || 'Category'}`,
        entityId: drawer.catId,
        work: async () => {
          await atomicSubmitEntity('category', drawer.catId!, cat, prevCat, 'DELETE', cat?.nameEN || 'Category');
        },
      });
      return;
    }
    updateLocal({ ...profile, categories: profile.categories.filter(c => c.id !== drawer.catId) });
    if (selectedCatId === drawer.catId) setSelectedCatId(profile.categories.find(c => c.id !== drawer.catId && !isUnassignedCat(c.id))?.id || null);
    setDrawer(null); setDrawerDirty(false);
    bg.run({ label: cat?.nameEN || 'Category', entityId: drawer.catId, work: async () => {} });
  };
  const confirmProductDrawer = () => {
    if (!prodForm.nameEN.trim()) { toast.error('Name Required', 'Enter a product name.'); return; }
    if (drawer?.type !== 'product') return;
    if (drawerSaving) return; // Prevent double-click race condition

    // ── 1. DRAFT COLLECTION: Build atomic finalPayload from ALL current state ──
    const allCatIds = [prodPrimaryCat, ...prodLinkedCats.filter(id => id !== prodPrimaryCat)].filter(Boolean);
    // Extract relational optionGroupIds from hydrated groups
    const resolvedGroupIds = (prodForm.optionGroups || []).map(g => g.linkedLibraryId || g.id);
    // Auto-generate item code if missing
    const autoItemCode = prodForm.code || generateItemCode(profile.brandName, profile.categories);
    const finalPayload: MenuItem = {
      ...prodForm,
      code: autoItemCode,
      categoryIds: allCatIds.length > 0 ? allCatIds : [drawer.catId],
      // Relational pointers — source of truth
      optionGroupIds: resolvedGroupIds,
      // Deep-clone optionGroups for propagation (hydrated view)
      optionGroups: deepCloneItemGroups(prodForm.optionGroups || []),
    };

    // ── 2. ASSET CHECK: Capture staged image before clearing drawer state ──
    const pendingImage = stagedImage;
    const capturedPreviewUrl = stagedImageUrl;

    // If image pending, use blob: URL optimistically; real URL applied after upload
    const optimisticPayload = pendingImage
      ? { ...finalPayload, image: capturedPreviewUrl ?? finalPayload.image }
      : finalPayload;

    // Assign ID for new items
    const isNewItem = !drawer.itemId;
    const itemId = isNewItem ? genId() : drawer.itemId!;
    const readyPayload = isNewItem
      ? { ...optimisticPayload, id: itemId, sortOrder: (profile.categories.find(c => c.id === drawer.catId)?.items.length || 0) }
      : optimisticPayload;

    // ── 3. ONE-SHOT LOCAL UPDATE: Apply complete payload to profile ──
    let updatedProfile: typeof profile;
    if (!isNewItem) {
      const updatedCats = updateItemCategories(profile.categories, itemId, allCatIds[0] || drawer.catId, allCatIds.slice(1));
      let finalCats = updatedCats.map(c => ({
        ...c,
        items: c.items.map(it => it.id === itemId ? { ...readyPayload } : it),
      }));
      for (const grp of (readyPayload.optionGroups || [])) {
        finalCats = propagateGroupEdit(finalCats, grp);
      }

      // ── ORPHAN RESCUE: Move removed groups to Unassigned bucket ──
      const originalItem = profile.categories.flatMap(c => c.items).find(it => it.id === itemId);
      if (originalItem) {
        const origIds = (originalItem.optionGroupIds || (originalItem.optionGroups || []).map(g => g.linkedLibraryId || g.id));
        const newIds = readyPayload.optionGroupIds || [];
        const removedIds = origIds.filter((rid: string) => !newIds.includes(rid));
        if (removedIds.length > 0) {
          const orphanGroups: OptionGroup[] = [];
          for (const rid of removedIds) {
            let stillUsed = false;
            for (const c of finalCats) {
              if (isUnassignedCat(c.id)) continue;
              for (const it of c.items) {
                if (it.id === itemId) continue;
                if ((it.optionGroupIds || []).includes(rid) ||
                    (it.optionGroups || []).some(g => g.id === rid || g.linkedLibraryId === rid)) {
                  stillUsed = true; break;
                }
              }
              if (stillUsed) break;
            }
            if (!stillUsed) {
              const full = (originalItem.optionGroups || []).find(g => g.id === rid || g.linkedLibraryId === rid);
              if (full) orphanGroups.push(full);
            }
          }
          if (orphanGroups.length > 0) {
            finalCats = ensureUnassignedBucket(finalCats);
            finalCats = finalCats.map(c => !isUnassignedCat(c.id) ? c : {
              ...c,
              items: c.items.map(it => it.id !== UNASSIGNED_ITEM_ID ? it : {
                ...it,
                optionGroups: [...(it.optionGroups || []), ...orphanGroups],
              }),
            });
          }
        }
      }

      // ── UNASSIGNED CLEANUP: Remove groups from Unassigned bucket if now assigned ──
      const assignedIds = new Set<string>();
      for (const c of finalCats) {
        if (isUnassignedCat(c.id)) continue;
        for (const it of c.items) {
          for (const id of (it.optionGroupIds || [])) assignedIds.add(id);
          for (const g of (it.optionGroups || [])) { assignedIds.add(g.id); if (g.linkedLibraryId) assignedIds.add(g.linkedLibraryId); }
        }
      }
      finalCats = finalCats.map(c => !isUnassignedCat(c.id) ? c : {
        ...c,
        items: c.items.map(it => it.id !== UNASSIGNED_ITEM_ID ? it : {
          ...it,
          optionGroups: (it.optionGroups || []).filter(g => {
            const key = g.linkedLibraryId || g.id;
            return !assignedIds.has(key) && !assignedIds.has(g.id);
          }),
        }),
      });

      updatedProfile = { ...profile, categories: finalCats };
      updateLocal(updatedProfile);
    } else {
      let cats = profile.categories.map(c => c.id !== (allCatIds[0] || drawer.catId) ? c : { ...c, items: [...c.items, readyPayload] });
      for (const catId of allCatIds.slice(1)) {
        cats = cats.map(c => c.id !== catId ? c : { ...c, items: c.items.some(it => it.id === readyPayload.id) ? c.items : [...c.items, { ...readyPayload, sortOrder: c.items.length }] });
      }
      // ── UNASSIGNED CLEANUP for new items: remove groups from Unassigned if now assigned ──
      const newItemIds = new Set<string>();
      for (const id of (readyPayload.optionGroupIds || [])) newItemIds.add(id);
      for (const g of (readyPayload.optionGroups || [])) { newItemIds.add(g.id); if (g.linkedLibraryId) newItemIds.add(g.linkedLibraryId); }
      if (newItemIds.size > 0) {
        cats = cats.map(c => !isUnassignedCat(c.id) ? c : {
          ...c,
          items: c.items.map(it => it.id !== UNASSIGNED_ITEM_ID ? it : {
            ...it,
            optionGroups: (it.optionGroups || []).filter(g => {
              const key = g.linkedLibraryId || g.id;
              return !newItemIds.has(key) && !newItemIds.has(g.id);
            }),
          }),
        });
      }
      updatedProfile = { ...profile, categories: cats };
      updateLocal(updatedProfile);
    }

    // ── 4. CLEANUP: Close drawer ──
    setStagedImage(null); setDrawer(null); setDrawerDirty(false);
    const catCount = allCatIds.length;
    const prevItem = getLiveEntity('item', itemId);

    // ── 5. BACKGROUND IMAGE UPLOAD + Entity-level atomic submit ──
    if (pendingImage) {
      const itemName = finalPayload.nameEN || 'Item';
      setDrawerSaving(true);
      bg.run({ label: itemName, entityId: itemId, work: async () => {
        try {
          const url = await uploadFile(pendingImage, { moduleId: 'menu-manage' });
          // Read LATEST profile from ref — NOT the stale closure
          const freshProfile = profileRef.current;
          updateLocal({ ...freshProfile, categories: freshProfile.categories.map(c => ({ ...c, items: c.items.map(it =>
            it.id === itemId ? { ...it, image: url } : it
          ) })) });
          // Submit with final image URL
          if (!isAdmin && requireApproval) {
            await atomicSubmitEntity('item', itemId, { ...readyPayload, image: url }, prevItem, isNewItem ? 'ADD' : 'MODIFY', itemName);
          }
        } finally {
          setDrawerSaving(false);
        }
      }});
    } else if (!isAdmin && requireApproval) {
      // No image pending — submit immediately
      bg.run({ label: `Submit: ${prodForm.nameEN}`, entityId: itemId, work: async () => {
        await atomicSubmitEntity('item', itemId, readyPayload, prevItem, isNewItem ? 'ADD' : 'MODIFY', prodForm.nameEN);
      }});
    }
    if (!pendingImage && (isAdmin || !requireApproval)) {
      bg.run({ label: prodForm.nameEN || 'Product', entityId: itemId, work: async () => {} });
    }
  };
  const deleteProductDrawer = () => {
    if (drawer?.type !== 'product' || !drawer.itemId) return;
    const itemName = prodForm.nameEN || 'Product';
    if (!isAdmin && requireApproval) {
      // Manager: submit entity-level deletion request
      const prevItem = getLiveEntity('item', drawer.itemId);
      setDrawer(null); setDrawerDirty(false);
      bg.run({
        label: `Delete Request: ${itemName}`,
        entityId: drawer.itemId,
        work: async () => {
          await atomicSubmitEntity('item', drawer.itemId!, prevItem, prevItem, 'DELETE', itemName);
        },
      });
      return;
    }
    // Admin: direct delete
    updateLocal({ ...profile, categories: profile.categories.map(c => ({ ...c, items: c.items.filter(it => it.id !== drawer.itemId) })) });
    setDrawer(null); setDrawerDirty(false);
    bg.run({ label: itemName, entityId: drawer.itemId, work: async () => {} });
  };
  const confirmOptionGroupDrawer = () => {
    if (!grpForm.nameEN.trim()) { toast.error('Name Required', 'Enter a group name.'); return; }
    if (drawer?.type !== 'optionGroup') return;
    // If we came from Product Drawer → return to it with the new group added
    if (parentProductRef.current && !drawer.grpId) {
      const p = parentProductRef.current;
      const dupInProd = (p.prodForm.optionGroups || []).find(g => g.nameEN.trim().toLowerCase() === grpForm.nameEN.trim().toLowerCase());
      if (dupInProd) { toast.error('Duplicate Group', `"${grpForm.nameEN}" already exists on this product.`); return; }
      parentProductRef.current = null;
      // Deep-clone: fresh ID + deep-cloned options to prevent shared references
      const autoCode = grpForm.groupCode || generateGroupCode(profile.brandName, profile.categories);
      const newGrp = { ...grpForm, id: genId(), linkedLibraryId: undefined, groupCode: autoCode, options: grpForm.options.map(o => ({ ...o, id: genId() })) };
      const updatedProdForm = { ...p.prodForm, optionGroups: [...(p.prodForm.optionGroups || []), newGrp] };
      setProdForm(updatedProdForm);
      setExpandedProdGrps(new Set(p.expandedGrps).add(newGrp.id));
      setDrawer(p.drawer);
      setDrawerDirty(true);
      toast.success('New Local Group Created', `"${grpForm.nameEN}" added to product. (Unlinked)`);
      return;
    }
    // Track the latest categories after edits (profile state is stale until next render)
    let latestCats = profile.categories;
    if (drawer.grpId) {
      // Edit existing — propagate details to all items sharing this group ID
      const autoCode = grpForm.groupCode || generateGroupCode(profile.brandName, profile.categories);
      const safeGrp = { ...grpForm, groupCode: autoCode, options: grpForm.options.map(o => ({ ...o })) };
      const cats = propagateGroupEdit(profile.categories, safeGrp);
      latestCats = cats;
      const updated = { ...profile, categories: cats };
      updateLocal(updated);
      // Entity-level atomic submit for option group edit
      if (!isAdmin && requireApproval) {
        const prevGrp = getLiveEntity('optionGroup', drawer.grpId);
        bg.run({ label: `Submit: ${grpForm.nameEN}`, entityId: drawer.grpId, work: async () => {
          await atomicSubmitEntity('optionGroup', drawer.grpId!, safeGrp, prevGrp, 'MODIFY', grpForm.nameEN);
        }});
      }
    } else {
      // Create new with bulk assignment
      const autoCode = grpForm.groupCode || generateGroupCode(profile.brandName, profile.categories);
      const newGrp = { ...grpForm, id: genId(), linkedLibraryId: undefined, groupCode: autoCode, options: grpForm.options.map(o => ({ ...o, id: genId() })) };
      if (bulkChecked.size > 0) {
        // Add to all selected products — RELATIONAL: same group ID = shared pointer
        const libId = newGrp.id; // This group ID becomes the library reference
        let cats = profile.categories.map(c => ({
          ...c,
          items: c.items.map(it => {
            if (!bulkChecked.has(it.id)) return it;
            if ((it.optionGroupIds || []).includes(libId) ||
                (it.optionGroups || []).some(g => g.nameEN.trim().toLowerCase() === newGrp.nameEN.trim().toLowerCase())) return it;
            return {
              ...it,
              optionGroupIds: [...(it.optionGroupIds || []), libId],
              optionGroups: [...(it.optionGroups || []), { ...newGrp, options: newGrp.options.map(o => ({ ...o })) }],
            };
          }),
        }));
        const updated = { ...profile, categories: cats };
        updateLocal(updated);
        // Entity-level atomic submit for new option group
        if (!isAdmin && requireApproval) {
          bg.run({ label: `Submit: ${grpForm.nameEN}`, entityId: newGrp.id, work: async () => {
            await atomicSubmitEntity('optionGroup', newGrp.id, newGrp, null, 'ADD', grpForm.nameEN);
          }});
        }
      } else {
        // Save as unassigned
        let cats = ensureUnassignedBucket(profile.categories);
        cats = cats.map(c => c.id !== UNASSIGNED_CAT_ID ? c : { ...c, items: c.items.map(it => it.id !== UNASSIGNED_ITEM_ID ? it : { ...it, optionGroups: [...(it.optionGroups || []), newGrp] }) });
        const updated = { ...profile, categories: cats };
        updateLocal(updated);
        toast.success('New Local Group Created', `"${grpForm.nameEN}" saved as Unassigned. Assign to a product later.`);
      }
    }
    // If we came from Product Drawer → return to it with updated product form
    if (parentProductRef.current) {
      const p = parentProductRef.current;
      parentProductRef.current = null;
      // Use latestCats (post-edit) instead of stale profile.categories
      const sourceDrawer = p.drawer;
      const freshCat = latestCats.find(c => c.id === sourceDrawer.catId);
      const freshItem = freshCat?.items.find(it => it.id === sourceDrawer.itemId);
      const freshForm = freshItem ? JSON.parse(JSON.stringify(freshItem)) : p.prodForm;
      setProdForm(freshForm);
      prodFormSnap.current = JSON.parse(JSON.stringify(freshForm));
      setExpandedProdGrps(p.expandedGrps);
      setDrawer(p.drawer);
      setDrawerDirty(false);
      return;
    }
    setDrawer(null); setDrawerDirty(false);
  };

  // ── Bulk product assignment helpers ────────────────────────────────────────
  const openBulkAssign = () => {
    if (!drawer || drawer.type !== 'optionGroup') return;
    const grpId = drawer.grpId;
    const checked = new Set<string>();
    if (grpId) {
      // Find which products reference this group (by optionGroupIds or optionGroups)
      const libId = grpForm.linkedLibraryId || grpId;
      for (const c of profile.categories) {
        if (isUnassignedCat(c.id)) continue;
        for (const it of c.items) {
          if ((it.optionGroupIds || []).includes(libId) ||
              (it.optionGroups || []).some(g => g.id === grpId || g.linkedLibraryId === libId)) {
            checked.add(it.id);
          }
        }
      }
    }
    setBulkChecked(checked);
    setBulkSearch('');
    setShowBulkAssign(true);
  };

  const applyBulkAssign = () => {
    if (!drawer || drawer.type !== 'optionGroup') return;
    const grpId = drawer.grpId;
    if (!grpId) {
      // For new groups, just close modal — bulkChecked will be used on confirm
      setShowBulkAssign(false);
      return;
    }
    // Determine the library ID for this group
    const libId = grpForm.linkedLibraryId || grpId;
    // Find which products currently have this group linked
    const currentLinked = new Set<string>();
    for (const c of profile.categories) {
      for (const it of c.items) {
        if ((it.optionGroupIds || []).includes(libId) ||
            (it.optionGroups || []).some(g => g.id === grpId || g.linkedLibraryId === libId)) {
          currentLinked.add(it.id);
        }
      }
    }
    const toAdd = [...bulkChecked].filter(id => !currentLinked.has(id));
    const toRemove = [...currentLinked].filter(id => !bulkChecked.has(id) && !isUnassignedCat(profile.categories.find(c => c.items.some(it => it.id === id))?.id || ''));
    if (toAdd.length === 0 && toRemove.length === 0) { setShowBulkAssign(false); return; }

    // Relational update: add/remove the library ID from each product's optionGroupIds
    const hydratedGrp = modLibMap.get(libId);
    let cats = profile.categories.map(c => ({
      ...c,
      items: c.items.map(it => {
        if (toRemove.includes(it.id)) {
          const newIds = (it.optionGroupIds || []).filter(id => id !== libId);
          const newGroups = (it.optionGroups || []).filter(g => g.id !== grpId && g.linkedLibraryId !== libId);
          return { ...it, optionGroupIds: newIds, optionGroups: newGroups };
        }
        if (toAdd.includes(it.id)) {
          // Guard: skip if product already has this group
          if ((it.optionGroupIds || []).includes(libId)) return it;
          const newIds = [...(it.optionGroupIds || []), libId];
          const newGroups = [...(it.optionGroups || [])];
          if (hydratedGrp) {
            newGroups.push({
              id: hydratedGrp.id, nameEN: hydratedGrp.nameEN, nameAR: hydratedGrp.nameAR,
              options: hydratedGrp.options.map(o => ({ ...o, libraryOptionId: o.id })),
              minSelection: hydratedGrp.minSelection, maxSelection: hydratedGrp.maxSelection,
              linkedLibraryId: hydratedGrp.id,
            });
          }
          return { ...it, optionGroupIds: newIds, optionGroups: newGroups };
        }
        return it;
      }),
    }));
    // Remove from unassigned bucket if now linked to real products
    if (bulkChecked.size > 0) {
      cats = cats.map(c => c.id !== UNASSIGNED_CAT_ID ? c : {
        ...c, items: c.items.map(it => ({
          ...it,
          optionGroupIds: (it.optionGroupIds || []).filter(id => id !== libId),
          optionGroups: (it.optionGroups || []).filter(g => g.id !== grpId && g.linkedLibraryId !== libId),
        })),
      });
    }
    updateLocal({ ...profile, categories: cats });
    // Update drawer to point to first linked item
    if (bulkChecked.size > 0) {
      const firstId = [...bulkChecked][0];
      for (const c of cats) {
        const it = c.items.find(i => i.id === firstId);
        if (it) { setDrawer({ ...drawer, catId: c.id, itemId: it.id }); break; }
      }
    }
    const msgs: string[] = [];
    if (toAdd.length > 0) msgs.push(`Added to ${toAdd.length} product${toAdd.length > 1 ? 's' : ''}`);
    if (toRemove.length > 0) msgs.push(`Removed from ${toRemove.length} product${toRemove.length > 1 ? 's' : ''}`);
    toast.success('🔗 Product Links Updated', msgs.join('. ') + '.');
    setShowBulkAssign(false);
    setDrawerDirty(true);
  };

  const deleteOptionGroupDrawer = () => {
    if (drawer?.type !== 'optionGroup' || !drawer.grpId) return;
    const srcCat = profile.categories.find(c => c.id === drawer.catId);
    const srcItem = srcCat?.items.find(it => it.id === drawer.itemId);
    const grpToDelete = (srcItem?.optionGroups || []).find(g => g.id === drawer.grpId);
    if (!grpToDelete) return;

    // ── Non-admin + approval required → submit deletion request ──
    if (!isAdmin && requireApproval) {
      const prevGrp = getLiveEntity('optionGroup', drawer.grpId);
      setDrawer(null); setDrawerDirty(false);
      bg.run({
        label: `Delete Request: ${grpToDelete.nameEN || 'Option Group'}`,
        entityId: drawer.grpId,
        work: async () => {
          await atomicSubmitEntity('optionGroup', drawer.grpId!, grpToDelete, prevGrp, 'DELETE', grpToDelete.nameEN || 'Option Group');
        },
      });
      return;
    }

    const isFromOptionsTab = detailTab === 'options';
    const isOrphan = isUnassignedCat(drawer.catId);
    const libId = grpToDelete.linkedLibraryId || grpToDelete.id;

    if (isFromOptionsTab || isOrphan) {
      // Options Tab context: permanent deletion from Library
      // Count how many real products still reference this group
      let linkedProductCount = 0;
      for (const c of profile.categories) {
        if (isUnassignedCat(c.id)) continue;
        for (const it of c.items) {
          if ((it.optionGroupIds || []).includes(libId) ||
              (it.optionGroups || []).some(g => g.id === drawer.grpId || g.linkedLibraryId === libId)) {
            linkedProductCount++;
          }
        }
      }

      if (linkedProductCount > 0) {
        toast.error('Cannot Delete', `This group is linked to ${linkedProductCount} product${linkedProductCount > 1 ? 's' : ''}. Use "Force Unlink All" or remove links from the "Select Products" panel first.`);
        return;
      }

      // Safe to permanently delete — remove from ALL locations
      const grpId = drawer.grpId;
      const cats = profile.categories.map(c => ({
        ...c,
        items: c.items.map(it => ({
          ...it,
          optionGroupIds: (it.optionGroupIds || []).filter(id => id !== libId && id !== grpId),
          optionGroups: (it.optionGroups || []).filter(g => g.id !== grpId && g.linkedLibraryId !== libId),
        })),
      }));
      const updatedProfile = { ...profile, categories: cats };
      updateLocal(updatedProfile);
      onSaveDraft(updatedProfile);
      setDrawer(null); setDrawerDirty(false);
      setSelectedOptGrpId(null);
      setIsDirty(false);
      apiFetch(`/api/menu/option-groups/${grpId}`, { method: 'DELETE' })
        .then(() => toast.success('Permanently Deleted', `"${grpToDelete.nameEN}" permanently removed from Library.`))
        .catch((err: any) => {
          if (err?.status === 404 || String(err?.message || '').includes('404')) {
            toast.success('Permanently Deleted', `"${grpToDelete.nameEN}" permanently removed from Library.`);
          } else {
            toast.error('Delete Failed', 'Could not delete from database. The group was removed locally and will be cleaned on next save.');
          }
        });
      return;
    }

    // Product Drawer context: UNLINK only — remove the ID reference from this product.
    // The group stays in the Library for use by other products.
    let cats = profile.categories.map(c => c.id !== drawer.catId ? c : {
      ...c, items: c.items.map(it => it.id !== drawer.itemId ? it : {
        ...it,
        optionGroupIds: (it.optionGroupIds || []).filter(id => id !== libId && id !== drawer.grpId),
        optionGroups: (it.optionGroups || []).filter(g => g.id !== drawer.grpId),
      }),
    });

    // ── ORPHAN RESCUE: If no other real product references this group, move to Unassigned ──
    let groupOrphaned = true;
    for (const c of cats) {
      if (isUnassignedCat(c.id)) continue;
      for (const it of c.items) {
        if ((it.optionGroupIds || []).includes(libId) ||
            (it.optionGroups || []).some(g => g.id === drawer.grpId || g.linkedLibraryId === libId)) {
          groupOrphaned = false; break;
        }
      }
      if (!groupOrphaned) break;
    }
    if (groupOrphaned) {
      cats = ensureUnassignedBucket(cats);
      cats = cats.map(c => !isUnassignedCat(c.id) ? c : {
        ...c,
        items: c.items.map(it => it.id !== UNASSIGNED_ITEM_ID ? it : {
          ...it,
          optionGroups: [...(it.optionGroups || []), grpToDelete],
        }),
      });
    }

    const updatedProfile = { ...profile, categories: cats };
    updateLocal(updatedProfile);
    onSaveDraft(updatedProfile);
    setDrawer(null); setDrawerDirty(false);
    setIsDirty(false);
    toast.success('🔗 Unlinked', `"${grpToDelete.nameEN}" removed from this product.${groupOrphaned ? ' Moved to Unassigned in Options tab.' : ' The group remains linked to other products.'}`);
  };

  // ── Product form helpers ─────────────────────────────────────────────────
  const pf = {
    up: (u: Partial<MenuItem>) => { setProdForm(p => ({ ...p, ...u })); setDrawerDirty(true); },
    addGrp: () => { const g = createCleanOptionGroup(); setProdForm(p => ({ ...p, optionGroups: [...(p.optionGroups || []), g] })); setExpandedProdGrps(s => new Set(s).add(g.id)); setDrawerDirty(true); toast.success('New Local Group Created', 'Fresh option group added. Will be promoted to Library on save.'); },
    rmGrp: (gid: string) => {
      // UNLINK ONLY — remove from this product's form. The group stays in the Library.
      setProdForm(p => {
        const grp = (p.optionGroups || []).find(g => g.id === gid);
        const libId = grp?.linkedLibraryId || gid;
        return {
          ...p,
          optionGroupIds: (p.optionGroupIds || []).filter(id => id !== libId && id !== gid),
          optionGroups: (p.optionGroups || []).filter(g => g.id !== gid),
        };
      }); setDrawerDirty(true);
      toast.success('🔗 Unlinked', 'Option group unlinked from this product. It remains in the Library.');
    },
    upGrp: (gid: string, u: Partial<OptionGroup>) => { setProdForm(p => ({ ...p, optionGroups: (p.optionGroups || []).map(g => g.id === gid ? { ...g, ...u } : g) })); setDrawerDirty(true); },
    addOpt: (gid: string) => { const o = createCleanOption(); setProdForm(p => ({ ...p, optionGroups: (p.optionGroups || []).map(g => g.id !== gid ? g : { ...g, options: [...g.options, o] }) })); setDrawerDirty(true); },
    rmOpt: (gid: string, oid: string) => { setProdForm(p => ({ ...p, optionGroups: (p.optionGroups || []).map(g => g.id !== gid ? g : { ...g, options: g.options.filter(o => o.id !== oid) }) })); setDrawerDirty(true); },
    upOpt: (gid: string, oid: string, u: Partial<MenuItemOption>) => { setProdForm(p => ({ ...p, optionGroups: (p.optionGroups || []).map(g => g.id !== gid ? g : { ...g, options: g.options.map(o => o.id === oid ? { ...o, ...u } : o) }) })); setDrawerDirty(true); },
    handleImg: (f: File) => { if (f.size > 5 * 1024 * 1024) { toast.error('Too Large', 'Max 5 MB.'); return; } setStagedImage(f); setDrawerDirty(true); },
  };

  // ── Add Group via drawer (stash product state, open OG drawer) ─────────
  const addGroupViaDrawer = () => {
    if (drawer?.type !== 'product') return;
    // Stash current product drawer state so we can return to it
    parentProductRef.current = { drawer, prodForm: JSON.parse(JSON.stringify(prodForm)), expandedGrps: new Set(expandedProdGrps) };
    // Open a new Option Group drawer (catId + itemId from current product context)
    const itemId = drawer.itemId || prodForm.id || '';
    const snap = { ...createCleanOptionGroup(), id: '' };
    setGrpForm(snap);
    grpFormSnap.current = JSON.parse(JSON.stringify(snap));
    setDrawer({ type: 'optionGroup', catId: drawer.catId, itemId, grpId: null });
    setDrawerDirty(false);
  };

  // ── Option group form helpers ────────────────────────────────────────────
  const gf = {
    up: (u: Partial<OptionGroup>) => { setGrpForm(p => ({ ...p, ...u })); setDrawerDirty(true); },
    addOpt: () => { const o = createCleanOption(); setGrpForm(p => ({ ...p, options: [...p.options, o] })); setDrawerDirty(true); },
    rmOpt: (oid: string) => { setGrpForm(p => ({ ...p, options: p.options.filter(o => o.id !== oid) })); setDrawerDirty(true); },
    upOpt: (oid: string, u: Partial<MenuItemOption>) => { setGrpForm(p => ({ ...p, options: p.options.map(o => o.id === oid ? { ...o, ...u } : o) })); setDrawerDirty(true); },
    pasteOpts: (opts: MenuItemOption[]) => {
      const fresh = opts.map(o => ({ ...o, id: genId() }));
      setGrpForm(p => ({ ...p, options: [...p.options, ...fresh] }));
      setDrawerDirty(true);
    },
    copyOpt: (opt: MenuItemOption) => {
      copyToClipboard({ type: 'option', label: opt.nameEN || 'Option', data: JSON.parse(JSON.stringify(opt)) });
      toast.success('Copied', `"${opt.nameEN || 'Option'}" copied to clipboard.`);
    },
    copySelectedOpts: () => {
      const sel = grpForm.options.filter(o => selectedOptIds.has(o.id));
      if (sel.length === 0) return;
      copyToClipboard({ type: 'options', label: `${sel.length} option${sel.length > 1 ? 's' : ''}`, data: JSON.parse(JSON.stringify(sel)) });
      toast.success('Copied', `${sel.length} option${sel.length > 1 ? 's' : ''} copied. You can now paste them into any group.`);
      setSelectedOptIds(new Set());
    },
  };

  // ── Quick visibility toggles (work on live profile) ──────────────────────
  const toggleCatVis = (cid: string) => {
    const category = profile.categories.find(c => c.id === cid);
    if (!category) return;
    const updatedCategory = { ...category, hidden: !category.hidden };
    updateLocal({ ...profile, categories: profile.categories.map(c => c.id === cid ? updatedCategory : c) });
    submitInlineEntityChange('category', cid, updatedCategory as Record<string, unknown>, updatedCategory.nameEN || 'Category');
  };
  const toggleItemVis = (cid: string, iid: string) => {
    const category = profile.categories.find(c => c.id === cid);
    const item = category?.items.find(entry => entry.id === iid);
    if (!item) return;
    const updatedItem = { ...item, hidden: !item.hidden };
    updateLocal({
      ...profile,
      categories: profile.categories.map(c => c.id !== cid ? c : { ...c, items: c.items.map(entry => entry.id === iid ? updatedItem : entry) }),
    });
    submitInlineEntityChange('item', iid, updatedItem as Record<string, unknown>, updatedItem.nameEN || 'Product');
  };
  const toggleGrpVis = (cid: string, iid: string, gid: string) => {
    const category = profile.categories.find(c => c.id === cid);
    const item = category?.items.find(entry => entry.id === iid);
    const group = (item?.optionGroups || []).find(entry => entry.id === gid);
    if (!group) return;
    const updatedGroup = { ...group, hidden: !group.hidden };
    updateLocal({
      ...profile,
      categories: profile.categories.map(c => c.id !== cid ? c : {
        ...c,
        items: c.items.map(entry => entry.id !== iid ? entry : {
          ...entry,
          optionGroups: (entry.optionGroups || []).map(optionGroup => optionGroup.id === gid ? updatedGroup : optionGroup),
        }),
      }),
    });
    submitInlineEntityChange('optionGroup', gid, updatedGroup as Record<string, unknown>, updatedGroup.nameEN || 'Modifier Group');
  };

  /** Smart toggle: if option is library-linked → update master record globally; else → local toggle */
  const toggleOptVis = async (cid: string, iid: string, gid: string, oid: string) => {
    // Find the group and option
    let targetGrp: OptionGroup | undefined;
    let targetOpt: MenuItemOption | undefined;
    for (const cat of profile.categories) {
      if (cat.id !== cid) continue;
      for (const item of cat.items) {
        if (item.id !== iid) continue;
        targetGrp = (item.optionGroups || []).find(g => g.id === gid);
        if (targetGrp) targetOpt = targetGrp.options.find(o => o.id === oid);
        break;
      }
      break;
    }
    if (!targetGrp || !targetOpt) return;

    const updatedGroup = {
      ...targetGrp,
      options: targetGrp.options.map(option => option.id === oid ? { ...option, hidden: !option.hidden } : option),
    };

    if (approvalProtectedLiveMenu) {
      updateLocal({
        ...profile,
        categories: profile.categories.map(c => c.id !== cid ? c : {
          ...c,
          items: c.items.map(it => it.id !== iid ? it : {
            ...it,
            optionGroups: (it.optionGroups || []).map(g => g.id !== gid ? g : updatedGroup),
          }),
        }),
      });
      submitInlineEntityChange('optionGroup', gid, updatedGroup as Record<string, unknown>, updatedGroup.nameEN || 'Modifier Group');
      return;
    }

    // If linked → global toggle via master library (optimistic + PATCH)
    if (targetGrp.linkedLibraryId && targetOpt.libraryOptionId) {
      const libOptId = targetOpt.libraryOptionId;
      const libModId = targetGrp.linkedLibraryId;
      // Prevent double-click: skip if already locked
      if (disabledOptIds.has(libOptId)) return;
      const libMod = availIndex.libraryMap.get(libModId);
      if (libMod) {
        const wasHidden = !!availabilityMap[libOptId];
        const updated = toggleGlobalOptionAvailability(libMod, libOptId);

        // ── 1. Optimistic: flip availIndex immediately ──
        setAvailIndex(prev => {
          const newMap = new Map(prev.libraryMap);
          newMap.set(updated.id, updated);
          const newHidden = new Set<string>();
          for (const mod of newMap.values()) {
            for (const hid of (mod.globalHiddenOptionIds || [])) newHidden.add(hid);
          }
          return { globalHiddenSet: newHidden, libraryMap: newMap };
        });

        // ── 2. Lock switches for 500ms ──
        setDisabledOptIds(prev => { const s = new Set(prev); s.add(libOptId); return s; });
        setTimeout(() => setDisabledOptIds(prev => { const s = new Set(prev); s.delete(libOptId); return s; }), 500);

        // ── 3. PATCH in background — dispatch event only AFTER server confirms ──
        apiFetch(`/api/menu/modifiers/${libModId}/options/${libOptId}/toggle`, { method: 'PATCH' })
        .then(() => {
          // Server confirmed — notify other components (e.g. Library tab)
          window.dispatchEvent(new CustomEvent(MENU_DATA_UPDATED_EVENT));
        }).catch(() => {
          // Rollback the optimistic flip
          setAvailIndex(prev => {
            const newMap = new Map(prev.libraryMap);
            newMap.set(libMod.id, libMod);
            const newHidden = new Set<string>();
            for (const mod of newMap.values()) {
              for (const hid of (mod.globalHiddenOptionIds || [])) newHidden.add(hid);
            }
            return { globalHiddenSet: newHidden, libraryMap: newMap };
          });
          toast.error('Sync Failed', 'Could not update availability — reverted.');
        });
        return;
      }
    }

    // Not linked → local toggle (existing behavior)
    updateLocal({ ...profile, categories: profile.categories.map(c => c.id !== cid ? c : { ...c, items: c.items.map(it => it.id !== iid ? it : { ...it, optionGroups: (it.optionGroups || []).map(g => g.id !== gid ? g : { ...g, options: g.options.map(o => o.id === oid ? { ...o, hidden: !o.hidden } : o) }) }) }) });
  };

  // ── Import / Export ──────────────────────────────────────────────────────
  const toggleExcelBar = () => {
    if (showImportDialog) {
      if (importState && !['done', 'error'].includes(importState.stage)) return;
      setShowImportDialog(false);
      setImportState(null);
    } else {
      setImportState(null);
      setShowImportDialog(true);
    }
  };

  const closeExcelBar = () => {
    if (importState && !['done', 'error'].includes(importState.stage)) return;
    setShowImportDialog(false);
    setImportState(null);
  };

  const handleImportFile = async (file: File, strategy: import('./components/ImportExcelDialog').ImportStrategy) => {
    const report = await runMenuImportWorkflow({
      file,
      existingCategories: profile.categories,
      strategy,
      setState: setImportState,
      applyCategories: async (categories) => {
        await Promise.resolve(updateLocal({ ...profile, categories }));
      },
    });

    if (report && !report.errors.length) {
      const cats = report.categoriesCreated + report.categoriesUpdated;
      const items = report.itemsCreated + report.itemsUpdated;
      const groups = report.groupsCreated;
      const imgCount = report.imageFilenames.size;
      const imgSuffix = imgCount > 0 ? `, ${imgCount} images uploaded` : '';
      toast.success('Import Complete', `${cats} categories, ${items} items, ${groups} option groups synchronized${imgSuffix}.`);
    } else if (report?.errors.length) {
      toast.warning('Imported With Warnings', `${report.errors.length} issue(s) need review.`);
    }
  };

  // ── Library link helper (API-first — Modifier Library is the truth) ────
  const [showLibraryPicker, setShowLibraryPicker] = useState<{ cid: string; iid: string } | null>(null);
  const [libraryMods, setLibraryMods] = useState<(LibraryModifier & { sourceMenuName?: string })[]>([]);
  /** Master Map: library modifier ID → full modifier. Used for hydration everywhere. */
  const [modLibMap, setModLibMap] = useState<Map<string, LibraryModifier>>(new Map());
  const [libSearch, setLibSearch] = useState('');
  const [infoMod, setInfoMod] = useState<string | null>(null);
  const [bulkSelected, setBulkSelected] = useState<string[]>([]);
  const [libDiscoveryMode, setLibDiscoveryMode] = useState(false);

  const loadLibraryMods = useCallback(async () => {
    // 1. Always scan profile first — it has the actual names & options
    const warehouseMods: (LibraryModifier & { sourceMenuName?: string })[] = [];
    const seen = new Set<string>();
    for (const cat of profile.categories) {
      for (const item of cat.items) {
        for (const grp of item.optionGroups || []) {
          const key = grp.linkedLibraryId || grp.id;
          if (seen.has(key)) continue;
          seen.add(key);
          warehouseMods.push({
            id: grp.linkedLibraryId || grp.id,
            nameEN: grp.nameEN, nameAR: grp.nameAR,
            options: grp.options.map(o => ({ ...o })),
            minSelection: grp.minSelection, maxSelection: grp.maxSelection,
            updatedAt: Date.now(), brandId: profile.brandId,
          });
        }
      }
    }

    // 2. In remote mode, merge API extras (globalHiddenOptionIds, etc.)
    if (isRemoteMode()) {
      try {
        const res = await apiFetch<{ data: LibraryModifier[] }>('/api/menu/modifiers');
        const apiMods = (res.data || []).filter((m: LibraryModifier) => profile.brandId && m.brandId === profile.brandId);
        const apiMap = new Map(apiMods.map(m => [m.id, m]));
        // Merge API fields into profile-scanned mods
        for (const mod of warehouseMods) {
          const apiMod = apiMap.get(mod.id);
          if (apiMod) {
            if ((apiMod as any).globalHiddenOptionIds) (mod as any).globalHiddenOptionIds = (apiMod as any).globalHiddenOptionIds;
          }
        }
        // Add any API-only mods not found in profile (e.g. unassigned)
        for (const apiMod of apiMods) {
          if (!seen.has(apiMod.id)) {
            seen.add(apiMod.id);
            warehouseMods.push(apiMod);
          }
        }
      } catch { /* API unavailable — profile scan is sufficient */ }
    }

    setLibraryMods(warehouseMods);
    setModLibMap(new Map(warehouseMods.map(m => [m.id, m])));
    return warehouseMods;
  }, [profile]);

  useEffect(() => { loadLibraryMods(); }, [loadLibraryMods]);
  useEffect(() => { if (showLibraryPicker) { loadLibraryMods(); setBulkSelected([]); } }, [showLibraryPicker]);

  const linkFromLibrary = (mod: LibraryModifier) => {
    // Check: already linked by ID?
    const alreadyOnItem = (prodForm.optionGroupIds || []).includes(mod.id);

    if (alreadyOnItem) {
      toast.error('Already Linked', `"${mod.nameEN}" is already linked to this product.`);
      return;
    }

    // Relational link: push library ID — NO cloning
    const result = linkBulkModifiers(prodForm, [mod], modLibMap);
    if (result.linked.length > 0) {
      setProdForm(result.updated);
      setDrawerDirty(true);
      setShowLibraryPicker(null); setLibSearch(''); setInfoMod(null); setBulkSelected([]);
      toast.success('🔗 Master Link Created', `"${mod.nameEN}" linked by reference. Changes will sync globally.`);
    } else {
      toast.error('Already Linked', `"${mod.nameEN}" is already linked to this product.`);
    }
  };
  const handleBulkLink = () => {
    const selected = bulkSelected.map(id => libraryMods.find(m => m.id === id)).filter(Boolean) as typeof libraryMods;
    if (selected.length === 0) return;
    const result = linkBulkModifiers(prodForm, selected, modLibMap);
    setProdForm(result.updated);
    setDrawerDirty(true);
    setShowLibraryPicker(null);
    setLibSearch('');
    setInfoMod(null);
    setBulkSelected([]);
    if (result.linked.length > 0) {
      toast.success('🔗 Bulk Linked', `${result.linked.length} group${result.linked.length !== 1 ? 's' : ''} linked by reference.${result.skipped.length > 0 ? ` ${result.skipped.length} skipped (already linked).` : ''}`);
    } else {
      toast.warning('All Skipped', 'All selected groups were already linked.');
    }
  };

  const linkFile = (fid: string) => { updateLocal({ ...profile, ministryFileIds: [...new Set([...profile.ministryFileIds, fid])], permitSyncRequired: false }); setShowVaultLinker(false); toast.success('Linked', 'Ministry file linked.'); };

  // ── Render Guard (Null Shield) ───────────────────────────────────────────
  if (!profile) {
    return (
      <div className="flex h-[calc(100vh-140px)] bg-slate-50 overflow-hidden animate-pulse border border-slate-200 rounded-xl relative z-10 w-full mb-8">
        <div className="hidden lg:flex w-[280px] bg-white border-r border-slate-200 flex-col shrink-0">
          <div className="px-5 py-4 border-b border-slate-200 bg-slate-50/50 h-[65px]" />
          <div className="p-3">
             <div className="h-9 bg-slate-100 rounded-lg w-full mb-2" />
             <div className="h-6 bg-slate-100 rounded mb-2 w-3/4 mx-3" />
             <div className="h-6 bg-slate-100 rounded mb-2 w-5/6 mx-3" />
          </div>
        </div>
        <div className="flex-1 flex flex-col min-w-0 bg-slate-50">
          <div className="h-[65px] bg-white border-b border-slate-200 flex items-center px-4 justify-between">
            <div className="w-1/3 h-5 bg-slate-100 rounded" />
            <div className="w-1/4 h-8 bg-slate-100 rounded-lg" />
          </div>
          <div className="flex-1 p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="h-[104px] bg-white border border-slate-200 rounded-xl p-3 flex gap-3">
                <div className="w-[80px] h-[80px] bg-slate-100 rounded-lg shrink-0" />
                 <div className="flex-1 flex flex-col justify-center gap-2">
                   <div className="h-3.5 bg-slate-100 rounded w-11/12" />
                   <div className="h-3 bg-slate-50 rounded w-2/3" />
                 </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Navigation Guard Modal (3-option) ── */}
      {showExitGuard && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-amber-500" />
              <h3 className="text-lg font-bold text-slate-900">Unpublished Changes</h3>
            </div>
            <p className="text-sm text-slate-600">Your draft changes are auto-saved. What would you like to do before leaving?</p>
            <div className="flex flex-col gap-2">
              <button onClick={() => { setIsDirty(false); setPendingReorderProfile(null); setReorderMode(false); setShowExitGuard(false); const a = pendingGuardAction.current; pendingGuardAction.current = null; a?.(); }}
                className="w-full px-4 py-2.5 bg-slate-100 border border-slate-200 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-200">Keep as Draft</button>
              {isAdmin && profile.status === MenuProfileStatus.DRAFT && (
                <button onClick={() => { onPublish(profile); setIsDirty(false); setPendingReorderProfile(null); setReorderMode(false); setShowExitGuard(false); const a = pendingGuardAction.current; pendingGuardAction.current = null; a?.(); }}
                  className="w-full px-4 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-bold hover:bg-emerald-700">Go Live Now</button>
              )}
              {(profile as any).liveData && (
                <button onClick={async () => {
                  setShowExitGuard(false);
                  try {
                    if (isRemoteMode()) { await discardMenuDraft(profile.id); }
                    setIsDirty(false);
                    window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
                  } catch { /* silent */ }
                  setPendingReorderProfile(null); setReorderMode(false);
                  const a = pendingGuardAction.current; pendingGuardAction.current = null; a?.();
                }}
                  className="w-full px-4 py-2.5 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm font-bold hover:bg-red-100">Discard Changes</button>
              )}
              <button onClick={() => { setShowExitGuard(false); pendingGuardAction.current = null; }}
                className="w-full px-4 py-2 text-slate-400 text-xs font-medium hover:text-slate-600">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="flex items-center gap-3">
        <button onClick={guardedBack} className="p-2 hover:bg-slate-100 rounded-xl"><ArrowLeft size={18} className="text-slate-600" /></button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold tracking-tight truncate">{profile.name || 'Untitled'}</h2>
            {!isLocked && <button onClick={() => tryOpenDrawer({ type: 'profile' })} className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors" title="Edit profile details"><Edit2 size={14} className="text-indigo-500" /></button>}
            {isLocked && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border border-amber-300 bg-amber-50 text-amber-700"><Lock size={10} /> Pending Approval</span>}
            {bg.isSaving(profile.id) && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border border-indigo-300 bg-indigo-50 text-indigo-600 animate-pulse"><Activity size={10} /> Auto-saving…</span>}
            {isDirty && !bg.isSaving(profile.id) && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border border-teal-300 bg-teal-50 text-teal-700"><Check size={10} /> Draft Saved</span>}
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-sm text-slate-500">{profile.brandName}</span>
            {(() => {
              const brand = brands.find(b => b.id === profile.brandId);
              const branchNames = (profile.branchIds ?? []).map(bid => {
                const br = brand?.branches?.find(b => b.id === bid);
                return br ? br.name : null;
              }).filter(Boolean);
              if (branchNames.length === 0) return null;
              return branchNames.map((name, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-slate-100 text-slate-600 border border-slate-200">{name}</span>
              ));
            })()}
            {profile.tag && <span className={cn('px-2 py-0.5 rounded text-[9px] font-bold border', tagColor(profile.tag))}>{profile.tag}</span>}
            {approvalProtectedLiveMenu && pendingEntityIds.size > 0 ? (
              <span className="px-2 py-0.5 rounded-full text-[9px] font-bold border bg-amber-50 text-amber-700 border-amber-200">🟠 LIVE — Pending Review</span>
            ) : profile.status === MenuProfileStatus.PUBLISHED ? (
              <span className="px-2 py-0.5 rounded-full text-[9px] font-bold border bg-emerald-50 text-emerald-700 border-emerald-200">🟢 LIVE</span>
            ) : profile.status === MenuProfileStatus.DRAFT && (profile as any).liveData ? (
              <span className="px-2 py-0.5 rounded-full text-[9px] font-bold border bg-amber-50 text-amber-700 border-amber-200">🟡 DRAFT — Changes Pending</span>
            ) : (
              <span className={cn('px-2 py-0.5 rounded-full text-[9px] font-bold border', STATUS_STYLES[profile.status])}>{profile.status}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
        </div>
      </div>

      {/* ── Pending Approval Lock Banner ── */}
      {isLocked && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
          <Lock size={16} className="text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-800">Editing Locked — Pending Approval</p>
            <p className="text-xs text-amber-600">A change request for this menu is awaiting admin review. Editing is disabled until the request is approved or rejected.</p>
          </div>
        </div>
      )}
      {pendingEntityIds.size > 0 && !isLocked && isAdmin && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
          <AlertCircle size={16} className="text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-800">{pendingEntityIds.size} item{pendingEntityIds.size > 1 ? 's' : ''} with proposed modifications</p>
            <p className="text-xs text-amber-600">Highlighted items have pending change requests. They remain live and visible to customers while awaiting Admin approval.</p>
          </div>
        </div>
      )}
      {pendingEntityIds.size > 0 && !isLocked && !isAdmin && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
          <Clock size={16} className="text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-800">Submitted changes are awaiting Admin approval</p>
            <p className="text-xs text-amber-600">The live menu remains unchanged until approval. Amber rows show your pending additions and edits.</p>
          </div>
        </div>
      )}

      {/* Ministry section */}
      <div
        className={cn('rounded-xl border p-4 transition-all', highlightMinistry && !hasMinistry ? 'border-amber-400 ring-2 ring-amber-300 ring-offset-2 animate-pulse bg-amber-50' : 'border-slate-200 bg-white', hasMinistry && 'cursor-pointer hover:border-indigo-300 hover:shadow-sm')}
        onClick={hasMinistry ? () => setShowFileGallery(true) : () => setShowVaultLinker(true)}
      >
        <div className="flex items-center gap-3">
          <Shield size={18} className={hasMinistry ? 'text-emerald-600' : 'text-amber-500'} />
          <div className="flex-1">
            <p className="text-sm font-bold text-slate-800">Ministry Attachment</p>
            <p className="text-xs text-slate-500">{hasMinistry ? `${profile.ministryFileIds.length} file(s) linked.` : 'No ministry permit linked.'}</p>
          </div>
          {hasMinistry ? (
            <Eye size={16} className="text-indigo-400" />
          ) : (
            <span className="px-3 py-1 bg-amber-100 border border-amber-300 rounded-lg text-[10px] font-bold text-amber-700">Link File</span>
          )}
        </div>
      </div>

      {/* Ministry File Gallery Popup */}
      {showFileGallery && hasMinistry && (() => {
        const linkedFiles = vaultFiles.filter(f => profile.ministryFileIds.includes(f.id));
        return (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={() => setShowFileGallery(false)}>
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <div className="relative w-full max-w-3xl mx-4 max-h-[85vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-200">
                <Shield size={20} className="text-emerald-600" />
                <div className="flex-1">
                  <h3 className="text-base font-bold text-slate-800">Ministry Attachments</h3>
                  <p className="text-xs text-slate-500">{linkedFiles.length} file(s) linked to this profile</p>
                </div>
                <button onClick={() => setShowFileGallery(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                  <X size={18} className="text-slate-400" />
                </button>
              </div>

              {/* File List */}
              <div className="flex-1 overflow-y-auto p-6">
                {linkedFiles.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">No files found.</p>
                ) : (
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50">
                          <th className="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wide">File</th>
                          <th className="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wide">Upload Date</th>
                          <th className="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wide text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {linkedFiles.map(f => {
                          const isImage = f.fileType?.startsWith('image/');
                          const isPdf = f.fileType === 'application/pdf';
                          const ext = f.fileType?.includes('/') ? '.' + f.fileType.split('/')[1].replace('jpeg', 'jpg') : '';
                          const src = f.fileData?.includes(',') ? f.fileData : `data:${f.fileType};base64,${f.fileData}`;
                          return (
                            <tr key={f.id} className="hover:bg-slate-50/60 transition-colors">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', isPdf ? 'bg-red-100' : 'bg-indigo-100')}>
                                    {isPdf ? <FileText size={14} className="text-red-600" /> : <ImageIcon size={14} className="text-indigo-600" />}
                                  </div>
                                  <span className="text-sm font-semibold text-slate-700 truncate">{f.name}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(f.uploadedAt)}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-end gap-1">
                                  {isImage && (
                                    <button onClick={() => window.open(src, '_blank')} className="p-1.5 hover:bg-indigo-50 rounded-lg" title="Open in new tab"><Eye size={14} className="text-indigo-600" /></button>
                                  )}
                                  {isPdf && (
                                    <button onClick={() => {
                                      let raw = f.fileData; if (raw.includes(',')) raw = raw.split(',')[1];
                                      const byteChars = atob(raw);
                                      const arr = new Uint8Array(byteChars.length);
                                      for (let i = 0; i < byteChars.length; i++) arr[i] = byteChars.charCodeAt(i);
                                      const blob = new Blob([arr], { type: 'application/pdf' });
                                      window.open(URL.createObjectURL(blob), '_blank');
                                    }} className="p-1.5 hover:bg-red-50 rounded-lg" title="Open PDF"><Eye size={14} className="text-red-600" /></button>
                                  )}
                                  <button onClick={() => base64Download(f.fileData, f.fileType, `${f.name}${ext}`)} className="p-1.5 hover:bg-emerald-50 rounded-lg" title="Download"><Download size={14} className="text-emerald-600" /></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* General Assets linked to this profile */}
      {(() => {
        const generalAssets = vaultFiles.filter(f => f.fileCategory === 'general' && f.linkedProfileIds.includes(profile.id));
        if (generalAssets.length === 0) return null;
        return (
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-3 mb-3">
              <FolderOpen size={18} className="text-teal-600" />
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-800">General Assets</p>
                <p className="text-xs text-slate-500">{generalAssets.length} file{generalAssets.length !== 1 ? 's' : ''} linked</p>
              </div>
            </div>
            <div className="space-y-1">
              {generalAssets.map(f => {
                const isPdf = f.fileType === 'application/pdf';
                return (
                  <div key={f.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                    <div className={cn('w-6 h-6 rounded-md flex items-center justify-center shrink-0', isPdf ? 'bg-red-50' : 'bg-indigo-50')}>
                      {isPdf ? <FileText size={11} className="text-red-500" /> : <ImageIcon size={11} className="text-indigo-500" />}
                    </div>
                    <span className="flex-1 text-xs font-medium text-slate-700 truncate">{f.name}</span>
                    <button onClick={() => {
                      if (!f.fileData) return;
                      const ext = f.fileType === 'application/pdf' ? '.pdf' : '.img';
                      base64Download(f.fileData, f.fileType, `${f.name}${ext}`);
                    }} className="p-1 hover:bg-emerald-50 rounded" title="Download"><Download size={11} className="text-emerald-600" /></button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Categories', value: profile.categories.filter(c => !isUnassignedCat(c.id)).length, icon: FolderOpen },
          { label: 'Items', value: totalItems, icon: Package },
          { label: 'Options', value: profile.categories.filter(c => !isUnassignedCat(c.id)).reduce((a, c) => a + c.items.reduce((b, it) => b + (it.optionGroups || []).reduce((g, grp) => g + grp.options.length, 0), 0), 0), icon: Layers },
          { label: 'Updated', value: fmtDate(profile.updatedAt), icon: Timer },
        ].map(s => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
            <s.icon size={16} className="text-slate-400" />
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{s.label}</p>
              <p className="text-lg font-bold">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Version History (Data Vault) — Admin / Platform only ── */}
      {canDeleteProfile && (
        <VersionHistoryPanel profileId={profile.id} onRestore={(restoredId) => {
          toast.success('Menu Restored', `Restored from history version. Refreshing...`);
          setTimeout(() => window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT)), 400);
        }} />
      )}

      {/* Products / Options Tab Bar + Unified Toolbar */}
      <div className="flex items-center gap-2 bg-white/90 backdrop-blur-md border border-slate-200 rounded-xl px-3 py-2 sticky top-[100px] z-30 shadow-sm">
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5 shrink-0">
          {(['products', 'options'] as const).map(tab => (
            <button key={tab} onClick={() => { if (tab === detailTab) return; guardAction(() => { setDetailTab(tab); setTabSearch(''); setTabFilterCat(''); setShowTabFilter(false); setShowTabMenu(false); setReorderMode(false); }); }}
              className={cn('px-4 py-1.5 rounded-md text-xs font-bold transition-colors capitalize',
                detailTab === tab ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
              {tab}
            </button>
          ))}
        </div>
        {/* Search */}
        <div className="relative flex-1 ml-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
          <input value={tabSearch} onChange={e => setTabSearch(e.target.value)} placeholder={detailTab === 'products' ? 'Search products...' : 'Search option groups...'}
            className="w-full pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-indigo-400" />
        </div>
        {/* Action buttons — Staging Control Bar */}
        <div className="flex items-center gap-1.5 shrink-0">
          {isAdmin && !isLocked && <button onClick={toggleExcelBar} className={cn('flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition', showImportDialog ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100')}><FileSpreadsheet size={12} /> Excel Tools</button>}
          {isAdmin && !isLocked && profile.status !== MenuProfileStatus.ARCHIVED && (
            <div className="flex items-center gap-2 px-3 py-1 bg-slate-50 border border-slate-200 rounded-lg">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Public Menu</span>
              <LiveToggleSwitch
                isLive={profile.status === MenuProfileStatus.PUBLISHED}
                size="sm"
                showLabel={false}
                onToggle={(goLive) => {
                  if (goLive) {
                    onPublish(profile);
                    setIsDirty(false);
                  } else {
                    // Unpublish: set to DRAFT
                    const u = { ...profile, status: MenuProfileStatus.DRAFT, updatedAt: Date.now() };
                    onSave(u);
                    if (isRemoteMode()) {
                      unpublishMenu(profile.id).catch(() => {});
                    }
                    window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
                  }
                }}
              />
            </div>
          )}
          {isAdmin && !isLocked && (
            <>
              {profile.status === MenuProfileStatus.ARCHIVED
                ? <button onClick={() => onRestore(profile)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-xs font-bold hover:bg-blue-100"><RotateCcw size={12} /> Restore</button>
                : <button onClick={() => onArchive(profile)} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 text-slate-600 rounded-lg text-xs font-bold hover:bg-slate-100"><Archive size={12} /> Archive</button>}
            </>
          )}
        </div>
        {/* Filter by category (both tabs) */}
        <div className="relative shrink-0">
          <button onClick={() => { setShowTabFilter(!showTabFilter); setShowTabMenu(false); }}
            className={cn('flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-bold transition-colors',
              tabFilterCat ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')}>
            <Filter size={12} /> {tabFilterCat ? (profile.categories.find(c => c.id === tabFilterCat)?.nameEN || 'Filter') : 'Filter'}
          </button>
          {showTabFilter && (
            <div className="absolute right-0 mt-1 w-52 bg-white border border-slate-200 rounded-xl shadow-lg z-30 py-1 max-h-60 overflow-auto">
              <button onClick={() => { setTabFilterCat(''); setShowTabFilter(false); }}
                className={cn('w-full text-left px-3 py-2 text-xs hover:bg-slate-50', !tabFilterCat && 'font-bold text-indigo-600')}>All Categories</button>
              {profile.categories.filter(c => !isUnassignedCat(c.id)).map(c => (
                <button key={c.id} onClick={() => { setTabFilterCat(c.id); setShowTabFilter(false); if (detailTab === 'products') setSelectedCatId(c.id); }}
                  className={cn('w-full text-left px-3 py-2 text-xs hover:bg-slate-50', tabFilterCat === c.id && 'font-bold text-indigo-600')}>
                  {c.nameEN || 'Unnamed'} <span className="text-slate-400">({c.items.length} items)</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* 3-dot menu */}
        <div className="relative shrink-0">
          <button onClick={() => { setShowTabMenu(!showTabMenu); setShowTabFilter(false); }}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
            <MoreVertical size={16} className="text-slate-500" />
          </button>
          {showTabMenu && (
            <div className="absolute right-0 mt-1 w-44 bg-white border border-slate-200 rounded-xl shadow-lg z-30 py-1">
              <button onClick={() => { setShowTabMenu(false); setReorderMode(r => !r); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50">
                <GripVertical size={13} className="text-slate-400" /> {reorderMode ? 'Exit Reorder' : 'Reorder'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Excel Toolbar (slide-down below action bar) ── */}
      <ExcelToolbar
        isOpen={showImportDialog}
        status={importState}
        onClose={closeExcelBar}
        onProcessFile={handleImportFile}
        onDownloadTemplate={exportBlankTemplate}
        onExportCurrent={() => setExportModalProfile(profile)}
        onRecipeBook={async () => {
          const logo = brands.find(b => b.id === profile.brandId)?.logoUrl;
          const result = await generateRecipeBook({ profileId: profile.id, brandName: profile.brandName, brandLogo: logo, profileName: profile.name });
          if (!result.success) toast.error('Recipe Book', result.error || 'Failed');
          else toast.success('Recipe Book', `${result.count} recipes opened in new tab.`);
        }}
      />

      {/* Reorder mode indicator */}
      {reorderMode && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
          <GripVertical size={13} className="text-amber-600" />
          <span className="text-[11px] font-bold text-amber-700">Reorder mode — drag items to rearrange</span>
        </div>
      )}

      {/* Floating reorder save bar */}
      {pendingReorderProfile && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 px-5 py-3 bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-700 animate-slide-up">
          <AlertTriangle size={16} className="text-amber-400 shrink-0" />
          <span className="text-sm font-semibold">Order changed</span>
          <button
            onClick={() => { onPublish(pendingReorderProfile); setPendingReorderProfile(null); setReorderMode(false); }}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-teal-500 hover:bg-teal-600 text-white rounded-xl text-xs font-bold transition-colors">
            <Check size={13} /> Save Reorder
          </button>
          <button
            onClick={() => setPendingReorderProfile(null)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-xl text-xs font-bold transition-colors">
            <RotateCcw size={12} /> Reset
          </button>
        </div>
      )}

      {detailTab === 'products' ? (
        <div className="flex gap-0 border border-slate-200 rounded-xl overflow-hidden bg-white" style={{ height: 'calc(100vh - 166px)', position: 'sticky', top: '154px' }}>
          {/* ── Left: Category Sidebar ── */}
          <div className="w-56 shrink-0 border-r border-slate-200 bg-slate-50 flex flex-col">
            <div className="px-3 pt-3 pb-2">
              <button onClick={() => tryOpenDrawer({ type: 'category', catId: null })} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-teal-600 border border-dashed border-teal-300 hover:bg-teal-50 rounded-lg transition-colors">
                <Plus size={12} /> Add Category
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {activeProfile.categories.filter(c => !isUnassignedCat(c.id)).length === 0 ? (
                <div className="px-3 py-8 text-center">
                  <FolderOpen size={24} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-[11px] text-slate-400">No categories yet</p>
                </div>
              ) : (
                activeProfile.categories.filter(c => !isUnassignedCat(c.id)).map((cat, catIdx) => {
                  const isSelected = selectedCatId === cat.id;
                  const catHidden = !!cat.hidden;
                  const catLocked = isEntityLocked(cat.id);
                  const catPendingChange = getPendingChange(cat.id);
                  const catPendingAdd = ghostCategoryIds.has(cat.id) || catPendingChange?.changeType === 'ADD';
                  return (
                    <div key={cat.id}
                      draggable={reorderMode}
                      {...makeDrag(cat.id)}
                      {...makeDrop(activeProfile.categories, cat.id, (fromIndex, toIndex) => setPendingReorderProfile(prev => { const base = prev ?? profile; return { ...base, categories: reorderCategories(base.categories, fromIndex, toIndex) }; }))}
                      onClick={() => { if (!reorderMode) setSelectedCatId(cat.id); }}
                      className={cn(
                        'group flex items-center gap-2 px-3 py-2.5 cursor-pointer border-l-[3px] transition-colors',
                        isSelected ? 'bg-white border-l-indigo-500' : 'border-l-transparent hover:bg-slate-100',
                        catHidden && 'opacity-50',
                        catPendingAdd && 'bg-amber-50/70 border-l-amber-400',
                        catLocked && 'opacity-60 grayscale pointer-events-auto',
                        reorderMode && 'cursor-grab active:cursor-grabbing',
                        reorderMode && dropTargetId === cat.id && 'ring-2 ring-amber-300 bg-amber-50/60'
                      )}>
                      {reorderMode && <GripVertical size={12} className="text-slate-300 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className={cn('text-xs font-semibold truncate flex items-center gap-1', isSelected ? 'text-slate-800' : 'text-slate-600')}>
                          {cat.nameEN || 'Unnamed'}
                          {!reorderMode && cat.availabilitySchedule && !cat.availabilitySchedule.alwaysOn && <Timer size={9} className={cn(isAvailableNow(cat.availabilitySchedule) ? 'text-emerald-500' : 'text-red-400')} />}
                        </p>
                        {catLocked ? (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-bold bg-amber-400 text-amber-950 mt-0.5">
                            {catPendingAdd ? '🟡 Pending Addition' : catPendingChange?.changeType === 'DELETE' ? '🗑 Pending Delete' : '📝 Change Pending'}
                          </span>
                        ) : (
                          <p className="text-[10px] text-slate-400">{cat.items.length} product{cat.items.length !== 1 ? 's' : ''}</p>
                        )}
                      </div>
                      {!reorderMode && (
                        <button onClick={e => { e.stopPropagation(); toggleCatVis(cat.id); }}
                          disabled={catLocked}
                          className={cn('relative inline-flex h-4 w-7 items-center rounded-full transition-colors shrink-0',
                            catLocked && 'opacity-40 cursor-not-allowed',
                            catHidden ? 'bg-red-300' : 'bg-emerald-400')}
                          title={catHidden ? 'Hidden' : 'Live'}>
                          <span className={cn('inline-block h-2.5 w-2.5 rounded-full bg-white shadow-sm transition-transform',
                            catHidden ? 'translate-x-0.5' : 'translate-x-[14px]')} />
                        </button>
                      )}
                      {!reorderMode && <ChevronRight size={14} className={cn('shrink-0', isSelected ? 'text-indigo-500' : 'text-slate-300')} />}
                    </div>
                  );
                })
              )}
            </div>
            {/* Summary */}
            <div className="px-3 py-2 border-t border-slate-200 text-[10px] text-slate-400 space-y-0.5">
              <p><strong className="text-slate-600">{activeProfile.categories.filter(c => !isUnassignedCat(c.id)).length}</strong> categories · <strong className="text-slate-600">{totalItems}</strong> items</p>
            </div>
          </div>

          {/* ── Right: Content Panel ── */}
          <div className="flex-1 flex flex-col min-w-0">
            {(() => {
              const selectedCat = activeProfile.categories.find(c => c.id === selectedCatId);
              if (!selectedCat) {
                return (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <Package size={32} className="mx-auto text-slate-200 mb-3" />
                      <p className="text-sm text-slate-400">{activeProfile.categories.filter(c => !isUnassignedCat(c.id)).length === 0 ? 'Add a category to get started' : 'Select a category'}</p>
                    </div>
                  </div>
                );
              }
              const selectedCatLocked = isEntityLocked(selectedCat.id);
              const selectedCatPendingChange = getPendingChange(selectedCat.id);
              return (
                <>
                  {/* Content header */}
                  <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100">
                    <h3 className="text-base font-bold text-slate-800">{selectedCat.nameEN || 'Unnamed'}</h3>
                    <button onClick={() => tryOpenDrawer({ type: 'category', catId: selectedCat.id })} disabled={selectedCatLocked} className={cn('p-1 rounded-lg transition-colors', selectedCatLocked ? 'opacity-40 cursor-not-allowed' : 'hover:bg-indigo-50')} title="Edit category"><Edit2 size={13} className="text-indigo-400" /></button>
                    <div className="flex-1" />
                    {selectedCat.nameAR && <span className="text-xs text-slate-400" dir={loc.secondaryDir}>{selectedCat.nameAR}</span>}
                    <span className="text-xs text-slate-400">{selectedCat.items.length} product{selectedCat.items.length !== 1 ? 's' : ''}</span>
                    {selectedCatLocked && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                        {selectedCatPendingChange?.changeType === 'ADD' ? 'Pending Addition' : selectedCatPendingChange?.changeType === 'DELETE' ? 'Pending Delete' : 'Pending Review'}
                      </span>
                    )}
                    {clipboard && clipboard.type === 'item' && (
                      <button onClick={() => {
                        if (approvalProtectedLiveMenu) {
                          toast.error('Approval Required', 'Use Add Product to submit new additions while manual approval is enabled.');
                          return;
                        }
                        const cloned = smartCopyItem(clipboard.data as MenuItem);
                        updateLocal({ ...profile, categories: profile.categories.map(c => c.id !== selectedCat.id ? c : { ...c, items: [...c.items, { ...cloned, sortOrder: c.items.length }] }) });
                        toast.success('Pasted (Shared Groups)', `"${cloned.nameEN}" pasted. Option groups are shared — edits sync across all linked items.`);
                      }} className="flex items-center gap-1.5 px-3 py-1.5 border-2 border-dashed border-indigo-300 text-indigo-600 text-xs font-bold rounded-lg hover:bg-indigo-50 transition-colors animate-pulse">
                        <ClipboardPaste size={12} /> Paste "{clipboard.label}"
                      </button>
                    )}
                    <button onClick={() => tryOpenDrawer({ type: 'product', catId: selectedCat.id, itemId: null })} disabled={isEntityLocked(selectedCat.id)} className={cn('flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors', isEntityLocked(selectedCat.id) ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700')}>
                      <Plus size={12} /> Add Product
                    </button>
                  </div>

                  {/* Item list */}
                  <div className="flex-1 overflow-y-auto p-5 space-y-2">
                    {(selectedCat.items.length === 0) ? (
                      <div className="py-12 text-center">
                        <Package size={28} className="mx-auto text-slate-200 mb-2" />
                        <p className="text-sm text-slate-400">No products in this category</p>
                        {<button onClick={() => tryOpenDrawer({ type: 'product', catId: selectedCat.id, itemId: null })} disabled={isEntityLocked(selectedCat.id)} className={cn('mt-3 text-xs font-bold', isEntityLocked(selectedCat.id) ? 'text-slate-400 cursor-not-allowed' : 'text-teal-600 hover:underline')}>+ Add first product</button>}
                      </div>
                    ) : (
                      (() => {
                        const lq = tabSearch.toLowerCase();
                        const filteredItems = lq ? selectedCat.items.filter(it => (it.nameEN || '').toLowerCase().includes(lq) || (it.nameAR || '').toLowerCase().includes(lq)) : selectedCat.items;
                        return filteredItems.map((item, itemIdx) => {
                          const groups = item.optionGroups || [];
                          const hasGroups = groups.length > 0;
                          const isHidden = !!item.hidden;
                          const itemOpen = expandedItems.has(item.id);
                          const lockState = getItemLockState(item);
                          const itemDirectLocked = lockState.itemDirectLocked;
                          const proposedChange = itemDirectLocked ? getPendingChange(item.id) : undefined;
                          const pendingItemAdd = ghostItemIds.has(item.id) || proposedChange?.changeType === 'ADD';
                          return (
                            <div key={item.id}
                              id={`menu-item-${item.id}`}
                              draggable={reorderMode}
                              {...makeDrag(item.id)}
                              {...makeDrop(selectedCat.items, item.id, (fromIndex, toIndex) => setPendingReorderProfile(prev => { const base = prev ?? profile; return { ...base, categories: base.categories.map(c => c.id !== selectedCat.id ? c : { ...c, items: reorderItems(selectedCat.items, fromIndex, toIndex) }) }; }))}
                              className={cn('border rounded-xl overflow-hidden transition-all', isHidden ? 'border-slate-200 opacity-50' : 'border-slate-200', itemDirectLocked && 'border-amber-300 ring-1 ring-amber-100', pendingItemAdd && 'bg-amber-50/50 border-dashed', !itemDirectLocked && lockState.lockedGroupIds.length > 0 && 'border-amber-100', reorderMode && 'cursor-grab active:cursor-grabbing', reorderMode && dropTargetId === item.id && 'ring-2 ring-amber-300 bg-amber-50/40', focusedItemId === item.id && 'ring-2 ring-indigo-400 bg-indigo-50/40 animate-pulse')}>
                              
                              {/* Proposed Modification badge — only for ITEM-DIRECT lock */}
                              {itemDirectLocked && (
                                <div className="flex items-center justify-center gap-2 px-4 py-1.5 bg-amber-400 overflow-hidden transition-all duration-500">
                                  <span className="text-[10px] font-bold text-amber-950">{pendingItemAdd ? '🟡 PENDING ADDITION' : proposedChange?.changeType === 'DELETE' ? '🗑 DELETION PENDING' : '📝 CHANGE PENDING'}</span>
                                  <span className="text-[9px] text-amber-800">Awaiting Admin approval</span>
                                </div>
                              )}
                              {/* Group-only lock indicator — subtle banner when only groups are pending */}
                              {!itemDirectLocked && lockState.lockedGroupIds.length > 0 && (
                                <div className="flex items-center gap-2 px-4 py-1.5 bg-amber-50 border-b border-amber-200 overflow-hidden transition-all duration-500">
                                  <span className="text-[10px] font-semibold text-amber-700">🟠 {lockState.lockedGroupIds.length} modifier group{lockState.lockedGroupIds.length !== 1 ? 's' : ''} pending</span>
                                  <span className="text-[9px] text-amber-500">— item fields remain editable</span>
                                </div>
                              )}

                              {/* Operational Strip — high-contrast Old→New change preview */}
                              {itemDirectLocked && proposedChange && proposedChange.changeType !== 'DELETE' && (
                                <div className="px-4 py-2 bg-yellow-300 border-b-2 border-yellow-400 space-y-0.5 overflow-hidden transition-all duration-500">
                                  {proposedChange.proposed.basePrice !== undefined && proposedChange.previous?.basePrice !== undefined && String(proposedChange.proposed.basePrice) !== String(proposedChange.previous.basePrice) && (
                                    <p className="text-[11px] text-yellow-950 font-semibold"><span className="font-bold">Price:</span> <span className="line-through opacity-70">{loc.currencySymbol} {Number(proposedChange.previous.basePrice).toFixed(2)}</span> <span className="mx-1">→</span> <span className="font-extrabold">{loc.currencySymbol} {Number(proposedChange.proposed.basePrice).toFixed(2)}</span></p>
                                  )}
                                  {proposedChange.proposed.cost !== undefined && proposedChange.previous?.cost !== undefined && String(proposedChange.proposed.cost) !== String(proposedChange.previous.cost) && (
                                    <p className="text-[11px] text-yellow-950 font-semibold"><span className="font-bold">Cost:</span> <span className="line-through opacity-70">{loc.currencySymbol} {Number(proposedChange.previous.cost).toFixed(2)}</span> <span className="mx-1">→</span> <span className="font-extrabold">{loc.currencySymbol} {Number(proposedChange.proposed.cost).toFixed(2)}</span></p>
                                  )}
                                  {!!proposedChange.proposed.nameEN && !!proposedChange.previous?.nameEN && proposedChange.proposed.nameEN !== proposedChange.previous.nameEN && (
                                    <p className="text-[11px] text-yellow-950 font-semibold"><span className="font-bold">Name:</span> <span className="line-through opacity-70">{String(proposedChange.previous.nameEN)}</span> <span className="mx-1">→</span> <span className="font-extrabold">{String(proposedChange.proposed.nameEN)}</span></p>
                                  )}
                                  {!!proposedChange.proposed.nameAR && !!proposedChange.previous?.nameAR && proposedChange.proposed.nameAR !== proposedChange.previous.nameAR && (
                                    <p className="text-[11px] text-yellow-950 font-semibold" dir="rtl"><span className="font-bold">الاسم:</span> <span className="line-through opacity-70">{String(proposedChange.previous.nameAR)}</span> <span className="mx-1">←</span> <span className="font-extrabold">{String(proposedChange.proposed.nameAR)}</span></p>
                                  )}
                                  {!!proposedChange.proposed.descriptionEN && !!proposedChange.previous?.descriptionEN && String(proposedChange.proposed.descriptionEN) !== String(proposedChange.previous.descriptionEN) && (
                                    <p className="text-[10px] text-yellow-950"><span className="font-bold">Desc:</span> <span className="line-through opacity-70">{String(proposedChange.previous.descriptionEN).slice(0, 40)}…</span> <span className="mx-1">→</span> <span className="font-semibold">{String(proposedChange.proposed.descriptionEN).slice(0, 40)}…</span></p>
                                  )}
                                  {proposedChange.changeType === 'ADD' && (
                                    <p className="text-[11px] font-bold text-emerald-900">+ New item pending approval</p>
                                  )}
                                </div>
                              )}
                              {itemDirectLocked && proposedChange?.changeType === 'DELETE' && (
                                <div className="px-4 py-1.5 bg-red-100 border-b-2 border-red-300 overflow-hidden transition-all duration-500">
                                  <p className="text-[11px] font-bold text-red-800">🗑 Deletion pending approval</p>
                                </div>
                              )}
                              
                              {/* Item row */}
                              <div className="group flex items-center gap-3 px-4 py-3 hover:bg-slate-50/60 transition-colors cursor-pointer"
                                onClick={() => { if (!reorderMode) { if (itemDirectLocked) { toast.error('Change Pending', 'This item has a proposed modification awaiting Admin approval.'); return; } tryOpenDrawer({ type: 'product', catId: selectedCat.id, itemId: item.id }); } }}>
                                {/* Drag handle */}
                                {reorderMode && <GripVertical size={16} className="text-slate-300 shrink-0" />}
                                {/* Image */}
                                {item.image ? (
                                  <img loading="lazy" src={getMediaUrl(item.image)} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0 border border-slate-200" />
                                ) : (
                                  <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Package size={16} className="text-slate-300" /></div>
                                )}
                                {/* Name + description + See Options link */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <p className={cn('text-sm font-semibold truncate', isHidden ? 'text-slate-400' : 'text-slate-800')}>{item.nameEN || '—'}</p>
                                    {!reorderMode && item.categoryIds && item.categoryIds.length > 1 && (
                                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-violet-200 bg-violet-50 text-[8px] font-bold text-violet-600 shrink-0" title={`Listed in ${item.categoryIds.length} categories`}>
                                        <Link2 size={8} /> {item.categoryIds.length}
                                      </span>
                                    )}
                                    {!reorderMode && item.systemManaged && (
                                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-[8px] font-bold text-amber-700 shrink-0" title={`Managed by ${getModuleName(item.systemManaged.sourceModule)}`}>
                                        <Lock size={8} className="text-amber-600" /> {getModuleName(item.systemManaged.sourceModule)}
                                      </span>
                                    )}
                                    {!reorderMode && item.campaignMeta && (
                                      <span className="relative group/ci shrink-0">
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-teal-200 bg-teal-50 text-[8px] font-bold text-teal-700 cursor-help">
                                          <Info size={8} /> Campaign
                                        </span>
                                        <span className="absolute left-0 top-full mt-1 z-50 w-64 p-3 bg-white rounded-xl shadow-xl border border-slate-200 text-[11px] text-slate-700 hidden group-hover/ci:block">
                                          <p className="font-bold text-slate-900 mb-1">🏷️ {item.campaignMeta.campaignName}</p>
                                          <p>Original: <span className="font-semibold">{loc.currencySymbol} {item.campaignMeta.originalPrice.toFixed(2)}</span> → Promo: <span className="font-bold text-emerald-600">{loc.currencySymbol} {item.campaignMeta.promoPrice.toFixed(2)}</span></p>
                                          <p>{discountLabel(item, loc.currencySymbol)} · {fundingLabel(item)}</p>
                                          <p className="mt-1 text-slate-500">Start: {new Date(item.campaignMeta.startDate).toLocaleDateString()}</p>
                                          <p className="text-slate-500">End: {new Date(item.campaignMeta.endDate).toLocaleDateString()}</p>
                                          {isPromoExpired(item) && <p className="mt-1 font-bold text-red-600">⚠️ Expired</p>}
                                        </span>
                                      </span>
                                    )}
                                  </div>
                                  {!reorderMode && item.nameAR && <p className="text-[11px] text-slate-400 truncate" dir={loc.secondaryDir}>{item.nameAR}</p>}
                                  {!reorderMode && item.descriptionEN && <p className="text-[11px] text-slate-400 truncate mt-0.5">{item.descriptionEN}</p>}
                                  {!reorderMode && hasGroups && (
                                    <button onClick={e => { e.stopPropagation(); toggleExpand(setExpandedItems, item.id); }}
                                      className="text-[11px] font-semibold text-indigo-500 hover:text-indigo-700 mt-0.5 flex items-center gap-0.5">
                                      See Options <ChevronRight size={10} className={cn('transition-transform', itemOpen && 'rotate-90')} />
                                    </button>
                                  )}
                                </div>
                                {/* Toggle + price + edit pencil — hidden in reorder mode */}
                                {!reorderMode && (
                                <div className="flex items-center gap-3 shrink-0">
                                  <button onClick={e => { e.stopPropagation(); toggleItemVis(selectedCat.id, item.id); }}
                                    disabled={itemDirectLocked}
                                    className={cn('relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
                                      itemDirectLocked && 'opacity-40 cursor-not-allowed',
                                      isHidden ? 'bg-red-300' : 'bg-emerald-400')}
                                    title={isHidden ? 'Hidden' : 'Live'}>
                                    <span className={cn('inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform',
                                      isHidden ? 'translate-x-1' : 'translate-x-[18px]')} />
                                  </button>
                                  <span className="text-sm font-bold text-slate-700 min-w-[70px] text-right">
                                    {item.campaignMeta && !isPromoExpired(item) ? (
                                      <span className="inline-flex flex-col items-end leading-tight">
                                        <span className="text-[10px] text-slate-400 line-through">{loc.currencySymbol} {item.campaignMeta.originalPrice.toFixed(2)}</span>
                                        <span className="text-sm font-bold text-emerald-600">{loc.currencySymbol} {item.campaignMeta.promoPrice.toFixed(2)}</span>
                                      </span>
                                    ) : shouldHidePrice(item) ? (
                                      <span className="text-[10px] text-slate-400 font-normal">{(() => { const fp = formatCardPrice(item, loc.currencySymbol); return fp.text ? `${fp.prefix ? fp.prefix + ' ' : ''}${fp.text}` : (fp.badge || '—'); })()}</span>
                                    ) : (
                                      <>{loc.currencySymbol} {item.basePrice.toFixed(2)}</>
                                    )}
                                  </span>
                                  {item.campaignMeta && !isPromoExpired(item) && (
                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-[8px] font-bold text-emerald-700 shrink-0 whitespace-nowrap">
                                      {discountLabel(item, loc.currencySymbol)} — {fundingLabel(item)}
                                    </span>
                                  )}
                                  {item.campaignMeta && isPromoExpired(item) && (
                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-red-200 bg-red-50 text-[8px] font-bold text-red-600 shrink-0">Expired</span>
                                  )}
                                  <button onClick={e => { e.stopPropagation(); copyToClipboard({ type: 'item', label: item.nameEN || 'Product', data: JSON.parse(JSON.stringify(item)) }); toast.success('Copied (Shared)', `"${item.nameEN || 'Product'}" copied — option groups will be shared, not duplicated.`); }}
                                    className="p-1.5 hover:bg-indigo-100 rounded-lg transition-colors border border-transparent hover:border-indigo-200 shrink-0" title="Copy item (option groups are shared — no duplicates)">
                                    <Copy size={14} className="text-indigo-500" />
                                  </button>
                                  <Edit2 size={13} className={cn('text-indigo-400 transition-opacity', itemDirectLocked ? 'opacity-30 pointer-events-none' : 'opacity-0 group-hover:opacity-100')} />
                                </div>
                                )}
                              </div>

                              {/* Option groups under item (read mode expand) */}
                              {itemOpen && hasGroups && !reorderMode && (
                                <div className="border-t border-slate-100 bg-slate-50/50">
                                  {groups.map((grp) => {
                                    const grpKey = `${item.id}:${grp.id}`;
                                    const grpOpen = expandedGroups.has(grpKey);
                                    const min = grp.minSelection ?? 0;
                                    const max = grp.maxSelection ?? 0;
                                    const grpHidden = !!grp.hidden;
                                    const grpShared = isSharedGroup(grp.id, profile.categories, grp.linkedLibraryId);
                                    const grpLocked = lockState.lockedGroupIds.includes(grp.id);
                                    return (
                                      <div key={grp.id} className={cn('transition-all', grpHidden && 'opacity-50', grpLocked && 'opacity-60 bg-amber-50/40')}>
                                        {grpLocked && (
                                          <div className="flex items-center gap-2 px-6 py-1 bg-yellow-200 border-b border-yellow-300">
                                            <span className="text-[9px] font-bold text-yellow-900">🟠 Proposed Modification</span>
                                            <span className="text-[8px] text-yellow-800">Awaiting Admin approval</span>
                                          </div>
                                        )}
                                        <div className="w-full flex items-center gap-3 px-6 py-2.5 hover:bg-indigo-50/30 transition-colors">
                                          <button onClick={() => toggleExpand(setExpandedGroups, grpKey)} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                                            <div className={cn('w-4 h-4 flex items-center justify-center transition-transform', grpOpen && 'rotate-90')}>
                                              <ChevronRight size={12} className="text-slate-400" />
                                            </div>
                                            <Layers size={13} className={grpShared ? 'text-amber-500 shrink-0' : 'text-indigo-400 shrink-0'} />
                                            <span className="text-xs font-bold text-slate-700 flex-1">{grp.nameEN || 'Unnamed Group'}</span>
                                            {grpShared && <span className="text-[8px] px-1.5 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 rounded font-bold shrink-0">🔗 Master</span>}
                                          </button>
                                          <div className="flex items-center gap-2 shrink-0">
                                            {min > 0 && <span className="text-[9px] px-1.5 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 rounded font-bold">Min {min}</span>}
                                            {max > 0 && <span className="text-[9px] px-1.5 py-0.5 bg-sky-50 text-sky-600 border border-sky-200 rounded font-bold">Max {max}</span>}
                                            <span className="text-[10px] text-slate-400">{grp.options.length} options</span>
                                            <button onClick={e => { e.stopPropagation(); toggleGrpVis(selectedCat.id, item.id, grp.id); }}
                                              disabled={grpLocked}
                                              className={cn('relative inline-flex h-3.5 w-6 items-center rounded-full transition-colors',
                                                grpLocked && 'opacity-40 cursor-not-allowed',
                                                grpHidden ? 'bg-red-300' : 'bg-emerald-400')}
                                              title={grpHidden ? 'Hidden' : 'Live'}>
                                              <span className={cn('inline-block h-2 w-2 rounded-full bg-white shadow-sm transition-transform',
                                                grpHidden ? 'translate-x-0.5' : 'translate-x-[14px]')} />
                                            </button>
                                          </div>
                                        </div>
                                        {grpOpen && (
                                          <div className="px-6 pb-1">
                                            {grp.options.map((opt) => {
                                              const optHidden = !!opt.hidden;
                                              const optGlobalOOS = isGloballyHidden(opt, grp);
                                              const optLocked = grpLocked || !!(opt.libraryOptionId && disabledOptIds.has(opt.libraryOptionId));
                                              return (
                                              <div key={opt.id} className={cn('flex items-center gap-3 py-2 border-t border-slate-100/60 pl-8 transition-colors duration-200', (optHidden || optGlobalOOS) && 'opacity-60')}>
                                                <div className={cn('w-1.5 h-1.5 rounded-full shrink-0', (optHidden || optGlobalOOS) ? 'bg-slate-300' : 'bg-indigo-300')} />
                                                <span className={cn('text-xs flex-1 transition-colors duration-200', (optHidden || optGlobalOOS) ? 'text-slate-400 line-through' : 'text-slate-700')}>{opt.nameEN || '—'}</span>
                                                {optGlobalOOS && <span className="text-[8px] px-1 py-0.5 bg-red-50 text-red-500 border border-red-200 rounded font-bold shrink-0">OOS</span>}
                                                {opt.nameAR && <span className="text-[11px] text-slate-400 mr-2" dir={loc.secondaryDir}>{opt.nameAR}</span>}
                                                {opt.extraPrice > 0 ? (
                                                  <span className="text-xs font-bold text-indigo-600">+{loc.currencySymbol} {opt.extraPrice.toFixed(2)}</span>
                                                ) : (
                                                  <span className="text-[10px] text-slate-400">Free</span>
                                                )}
                                                <button onClick={() => toggleOptVis(selectedCat.id, item.id, grp.id, opt.id)}
                                                  disabled={optLocked}
                                                  className={cn('relative inline-flex h-5 w-9 items-center rounded-full shrink-0 transition-colors duration-200',
                                                    optLocked && 'opacity-50 cursor-not-allowed',
                                                    (optHidden || optGlobalOOS) ? 'bg-slate-300' : 'bg-emerald-400')}
                                                  title={optGlobalOOS ? 'Globally Out of Stock — click to restore' : optHidden ? 'Hidden' : 'Live'}>
                                                  <span className={cn('inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform duration-200',
                                                    (optHidden || optGlobalOOS) ? 'translate-x-0.5' : 'translate-x-[18px]')} />
                                                </button>
                                              </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Option groups reorder mode */}
                              {reorderMode && hasGroups && (
                                <div className="border-t border-slate-100 bg-amber-50/30">
                                  {groups.map((grp) => (
                                    <div key={grp.id}
                                      draggable
                                      onDragStart={(e) => {
                                        if (!reorderMode) return;
                                        e.stopPropagation();
                                        dragIdRef.current = grp.id;
                                        e.dataTransfer.effectAllowed = 'move';
                                        e.dataTransfer.setData('text/plain', grp.id);
                                      }}
                                      onDragEnd={() => { dragIdRef.current = null; setDropTargetId(null); }}
                                      {...makeDrop(groups, grp.id, (fromIndex, toIndex) => setPendingReorderProfile(prev => {
                                        const base = prev ?? profile;
                                        return { ...base, categories: base.categories.map(c => c.id !== selectedCat.id ? c : {
                                          ...c, items: c.items.map(it => it.id !== item.id ? it : {
                                            ...it, optionGroups: reorderGroups(groups, fromIndex, toIndex)
                                          })
                                        })};
                                      }))}
                                      className={cn('flex items-center gap-3 px-6 py-2.5 border-b border-slate-100/60 cursor-grab active:cursor-grabbing transition-all',
                                        dropTargetId === grp.id && 'ring-2 ring-amber-300 bg-amber-50/60')}>
                                      <GripVertical size={12} className="text-slate-300 shrink-0" />
                                      <Layers size={13} className="text-indigo-400 shrink-0" />
                                      <span className="text-xs font-bold text-slate-700 flex-1 truncate">{grp.nameEN || 'Unnamed Group'}</span>
                                      <span className="text-[10px] text-slate-400 shrink-0">{grp.options.length} options</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      ) : (
        /* ── Options Tab ── */
        <div className="flex gap-0 border border-slate-200 rounded-xl overflow-hidden bg-white" style={{ height: 'calc(100vh - 166px)', position: 'sticky', top: '154px' }}>
          {/* ── Left: Option Group Sidebar ── */}
          <div className="w-64 shrink-0 border-r border-slate-200 bg-slate-50 flex flex-col">
            <div className="px-3 pt-3 pb-2">
              <button onClick={() => {
                // Always create new option groups in Unassigned bucket first
                const cats = ensureUnassignedBucket(profile.categories);
                if (cats !== profile.categories) updateLocal({ ...profile, categories: cats });
                tryOpenDrawer({ type: 'optionGroup', catId: UNASSIGNED_CAT_ID, itemId: UNASSIGNED_ITEM_ID, grpId: null });
              }} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-teal-600 border border-dashed border-teal-300 hover:bg-teal-50 rounded-lg transition-colors">
                <Plus size={12} /> Add option group
              </button>
              {/* Purge Unassigned button — only shown when unassigned groups exist */}
              {!approvalProtectedLiveMenu && (() => {
                const uCat = profile.categories.find(c => c.id === UNASSIGNED_CAT_ID);
                const uItem = uCat?.items.find(it => it.id === UNASSIGNED_ITEM_ID);
                const uCount = (uItem?.optionGroups || []).length;
                if (uCount === 0) return null;
                return (
                  <button onClick={() => setDeleteConfirm({
                    message: `Permanently delete all ${uCount} unassigned option group${uCount !== 1 ? 's' : ''}? This cannot be undone.`,
                    onConfirm: () => {
                      // Remove from local state
                      const cats = profile.categories.map(c => c.id !== UNASSIGNED_CAT_ID ? c : { ...c, items: c.items.map(it => it.id !== UNASSIGNED_ITEM_ID ? it : { ...it, optionGroups: [] }) });
                      const updatedProfile = { ...profile, categories: cats };
                      updateLocal(updatedProfile);
                      // CRITICAL: persist draft immediately so purged groups don't reappear
                      onSaveDraft(updatedProfile);
                      setIsDirty(false);
                      // Fire backend purge (best-effort)
                      apiFetch(`/api/menu/option-groups/purge-unassigned/${profile.id}`, { method: 'DELETE' })
                        .then((r: any) => toast.success('Purge Complete', `${r?.purged ?? uCount} unassigned group(s) permanently removed.`))
                        .catch(() => toast.error('Purge Failed', 'Groups removed locally but backend cleanup failed. They will be cleaned on next save.'));
                    }
                  })} className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 mt-1 text-[10px] font-bold text-amber-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 size={10} /> Clean Up {uCount} Unassigned
                  </button>
                );
              })()}
            </div>
            <div className="flex-1 overflow-y-auto">
              {(() => {
                let allGroups: { cat: MenuCategory; item: MenuItem; grp: OptionGroup }[] = [];
                profile.categories.forEach(cat => cat.items.forEach(item => (item.optionGroups || []).forEach(grp => allGroups.push({ cat, item, grp }))));
                if (tabFilterCat) allGroups = allGroups.filter(g => g.cat.id === tabFilterCat);
                const sq = tabSearch.toLowerCase();
                if (sq) allGroups = allGroups.filter(g => (g.grp.nameEN || '').toLowerCase().includes(sq) || (g.grp.nameAR || '').toLowerCase().includes(sq) || (g.item.nameEN || '').toLowerCase().includes(sq) || (g.cat.nameEN || '').toLowerCase().includes(sq));

                // Deduplicate: linked groups with same linkedLibraryId appear once; shared groups with same ID appear once
                const seenLibIds = new Set<string>();
                const seenIds = new Set<string>();
                const dedupedGroups: { cat: MenuCategory; item: MenuItem; grp: OptionGroup; linkedCount: number }[] = [];
                // Count usages per linkedLibraryId AND per groupId across ALL profile groups (not just filtered)
                const libUsageMap = new Map<string, number>();
                const idUsageMap = new Map<string, number>();
                profile.categories.forEach(cat => cat.items.forEach(item => (item.optionGroups || []).forEach(grp => {
                  if (grp.linkedLibraryId) libUsageMap.set(grp.linkedLibraryId, (libUsageMap.get(grp.linkedLibraryId) || 0) + 1);
                  idUsageMap.set(grp.id, (idUsageMap.get(grp.id) || 0) + 1);
                })));
                for (const entry of allGroups) {
                  const libId = entry.grp.linkedLibraryId;
                  const gId = entry.grp.id;
                  if (libId) {
                    if (seenLibIds.has(libId)) continue;
                    seenLibIds.add(libId);
                    dedupedGroups.push({ ...entry, linkedCount: libUsageMap.get(libId) || 1 });
                  } else {
                    if (seenIds.has(gId)) continue;
                    seenIds.add(gId);
                    const count = idUsageMap.get(gId) || 1;
                    dedupedGroups.push({ ...entry, linkedCount: count > 1 ? count : 0 });
                  }
                }

                if (dedupedGroups.length === 0) return (
                  <div className="px-3 py-8 text-center">
                    <Layers size={24} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-[11px] text-slate-400">{tabSearch || tabFilterCat ? 'No matches' : 'No option groups yet'}</p>
                  </div>
                );
                // auto-select first if none selected
                if (!selectedOptGrpId || !dedupedGroups.find(g => g.grp.id === selectedOptGrpId)) {
                  setTimeout(() => setSelectedOptGrpId(dedupedGroups[0]?.grp.id || null), 0);
                }
                return dedupedGroups.map(({ cat, item, grp, linkedCount }) => {
                  const isSelected = selectedOptGrpId === grp.id;
                  const grpHidden = !!grp.hidden;
                  const isOrphan = isUnassignedCat(cat.id);
                  return (
                    <div key={grp.id}
                      onClick={() => setSelectedOptGrpId(grp.id)}
                      className={cn(
                        'group flex items-center gap-2 px-3 py-2.5 cursor-pointer border-l-[3px] transition-colors',
                        isSelected ? 'bg-white border-l-indigo-500' : 'border-l-transparent hover:bg-slate-100',
                        grpHidden && 'opacity-50',
                        isOrphan && !isSelected && 'bg-amber-50/40'
                      )}>
                      <div className="flex-1 min-w-0">
                        <p className={cn('text-xs font-semibold truncate', isSelected ? 'text-slate-800' : 'text-slate-600')}>{grp.nameEN || 'Unnamed'}</p>
                        <div className="flex items-center flex-wrap gap-1 mt-0.5">
                          <span className="text-[10px] text-slate-400">{grp.options.length} Option{grp.options.length !== 1 ? 's' : ''}</span>
                          {grp.linkedLibraryId && linkedCount > 1 && <span title={`Shared by ${linkedCount} items`}><Link2 size={9} className="text-indigo-400" /></span>}
                          {grp.linkedLibraryId && linkedCount <= 1 && <Library size={9} className="text-indigo-400" />}
                          {grp.linkedLibraryId && linkedCount > 0 && <span className="text-[10px] text-indigo-500">· {linkedCount} item{linkedCount !== 1 ? 's' : ''}</span>}
                          {isOrphan && <span className="px-1.5 py-0.5 bg-amber-100 border border-amber-200 text-amber-700 text-[8px] font-bold rounded">UNASSIGNED</span>}
                        </div>
                      </div>
                      <span className="w-5 h-5 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center text-[9px] font-bold text-indigo-500 shrink-0">{grp.options.length}</span>
                      <ChevronRight size={14} className={cn('shrink-0', isSelected ? 'text-indigo-500' : 'text-slate-300')} />
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          {/* ── Right: Content Panel ── */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Selected group content */}
            {(() => {
              let allGroups: { cat: MenuCategory; item: MenuItem; grp: OptionGroup }[] = [];
              profile.categories.forEach(cat => cat.items.forEach(item => (item.optionGroups || []).forEach(grp => allGroups.push({ cat, item, grp }))));
              const match = allGroups.find(g => g.grp.id === selectedOptGrpId);
              if (!match) return (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <Layers size={32} className="mx-auto text-slate-200 mb-3" />
                    <p className="text-sm text-slate-400">Select an option group</p>
                  </div>
                </div>
              );
              const { cat: selCat, item: selItem, grp: selGrp } = match;
              const selGrpLocked = isEntityLocked(selGrp.id);
              const selGrpPendingChange = getPendingChange(selGrp.id);
              return (
                <>
                  {/* Group header */}
                  <div className="px-5 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-bold text-slate-800">{selGrp.nameEN || 'Unnamed Group'}:</h3>
                      <button onClick={() => tryOpenDrawer({ type: 'optionGroup', catId: selCat.id, itemId: selItem.id, grpId: selGrp.id })} disabled={selGrpLocked} className={cn('p-1 rounded-lg transition-colors', selGrpLocked ? 'opacity-40 cursor-not-allowed' : 'hover:bg-indigo-50')} title="Edit option group"><Edit2 size={13} className="text-indigo-400" /></button>
                      {selGrp.groupCode && <span className="shrink-0 px-1.5 py-0.5 bg-violet-50 border border-violet-200 text-violet-600 text-[9px] font-bold font-mono rounded" title={`Group Code: ${selGrp.groupCode}`}>{selGrp.groupCode}</span>}
                      {selGrp.linkedLibraryId && <span className="text-[8px] px-1.5 py-0.5 bg-indigo-50 text-indigo-500 border border-indigo-200 rounded font-bold flex items-center gap-1"><Library size={9} /> Library</span>}
                      {selGrpLocked && <span className="text-[8px] px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded font-bold">{selGrpPendingChange?.changeType === 'ADD' ? 'Pending Addition' : selGrpPendingChange?.changeType === 'DELETE' ? 'Pending Delete' : 'Pending Review'}</span>}
                    </div>
                    {/* Linked to breadcrumb — clickable to navigate to product */}
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      {isUnassignedCat(selCat.id) ? (
                        <>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 border border-amber-200 rounded text-[10px] font-bold text-amber-700">
                            <AlertTriangle size={10} /> Unassigned — not linked to any product
                          </span>
                          <button onClick={() => tryOpenDrawer({ type: 'optionGroup', catId: selCat.id, itemId: selItem.id, grpId: selGrp.id })} className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-50 border border-teal-200 rounded-lg text-[10px] font-bold text-teal-700 hover:bg-teal-100 transition-colors">
                            <MapPin size={10} /> Assign to Product
                          </button>
                        </>
                      ) : selGrp.linkedLibraryId ? (
                        /* ── Library-linked: show all products referencing this master group ── */
                        (() => {
                          const linkedProds = getLinkedProducts(selGrp.linkedLibraryId!, profiles);
                          const libMod = availIndex.libraryMap.get(selGrp.linkedLibraryId!);
                          return (
                            <>
                              <span className="text-[10px] text-slate-400">Linked to:</span>
                              {linkedProds.slice(0, 8).map(lp => (
                                <button key={`${lp.profileId}-${lp.itemId}`}
                                  onClick={() => navigateToItem(lp.categoryId, lp.itemId)}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 border border-indigo-200 rounded text-[10px] font-semibold text-indigo-600 hover:bg-indigo-100 transition-colors cursor-pointer"
                                  title={`${lp.profileName} › ${lp.itemName}`}>
                                  <Package size={9} /> {lp.itemName || 'Unnamed'}
                                </button>
                              ))}
                              {linkedProds.length > 8 && <span className="text-[9px] text-slate-400">+{linkedProds.length - 8} more</span>}
                              <span className="text-[9px] px-1.5 py-0.5 bg-violet-50 text-violet-600 border border-violet-200 rounded font-bold">
                                Used in {linkedProds.length} Product{linkedProds.length !== 1 ? 's' : ''}
                              </span>
                              {libMod && (
                                <>
                                  <ChevronRight size={10} className="text-slate-300" />
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 border border-indigo-200 rounded text-[10px] font-semibold text-indigo-600">
                                    <Library size={10} className="text-indigo-400" /> {libMod.nameEN || 'Library'}
                                  </span>
                                </>
                              )}
                            </>
                          );
                        })()
                      ) : (
                        <>
                          <span className="text-[10px] text-slate-400">Linked to:</span>
                          <button onClick={() => navigateToItem(selCat.id, selItem.id)} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-semibold text-slate-600 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 transition-colors cursor-pointer" title="Go to category">
                            <FolderOpen size={10} className="text-slate-400" /> {selCat.nameEN || 'Unnamed'}
                          </button>
                          <ChevronRight size={10} className="text-slate-300" />
                          <button onClick={() => navigateToItem(selCat.id, selItem.id)} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-semibold text-slate-600 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 transition-colors cursor-pointer" title="Go to product">
                            <Package size={10} className="text-slate-400" /> {selItem.nameEN || 'Unnamed'}
                          </button>
                        </>
                      )}
                    </div>
                    {/* Min/Max badges */}
                    <div className="flex items-center gap-2 mt-2">
                      {(selGrp.minSelection ?? 0) > 0 && <span className="text-[9px] px-1.5 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 rounded font-bold">Min {selGrp.minSelection}</span>}
                      {(selGrp.maxSelection ?? 0) > 0 && <span className="text-[9px] px-1.5 py-0.5 bg-sky-50 text-sky-600 border border-sky-200 rounded font-bold">Max {selGrp.maxSelection}</span>}
                      {selGrp.linkedLibraryId && (
                        <button
                          disabled={approvalProtectedLiveMenu}
                          onClick={() => {
                            if (approvalProtectedLiveMenu) return;
                            setUnlinkConfirm({
                              grpName: selGrp.nameEN || 'Unnamed Group',
                              onConfirm: () => {
                                const unlinked = unlinkGroup(selGrp);
                                updateLocal({
                                  ...profile,
                                  categories: profile.categories.map(c => c.id !== selCat.id ? c : {
                                    ...c, items: c.items.map(it => it.id !== selItem.id ? it : {
                                      ...it, optionGroups: (it.optionGroups || []).map(g => g.id !== selGrp.id ? g : unlinked)
                                    })
                                  })
                                });
                                toast.success('Unlinked', `"${selGrp.nameEN}" is now a local copy. Changes here will no longer affect other products.`);
                              },
                            });
                          }}
                          className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold transition-colors', approvalProtectedLiveMenu ? 'bg-slate-100 border border-slate-200 text-slate-400 cursor-not-allowed' : 'bg-orange-50 border border-orange-200 text-orange-600 hover:bg-orange-100')}
                          title="Convert to local copy — disconnect from Library">
                          <Unlink size={9} /> Unlink & Make Local
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Options list */}
                  <div className="flex-1 overflow-y-auto p-5 space-y-2">
                    {selGrp.options.length === 0 ? (
                      <div className="py-12 text-center">
                        <Package size={28} className="mx-auto text-slate-200 mb-2" />
                        <p className="text-sm text-slate-400">No options in this group</p>
                      </div>
                    ) : (
                      selGrp.options.map((opt, optIdx) => {
                        const optHidden = !!opt.hidden;
                        const optGlobalOOS = isGloballyHidden(opt, selGrp);
                        const optLocked = !!(opt.libraryOptionId && disabledOptIds.has(opt.libraryOptionId));
                        return (
                          <div key={opt.id}
                            draggable={reorderMode}
                            {...makeDrag(opt.id)}
                            {...makeDrop(selGrp.options, opt.id, (fromIndex, toIndex) => setPendingReorderProfile(prev => { const base = prev ?? profile; return { ...base, categories: base.categories.map(c => c.id !== selCat.id ? c : { ...c, items: c.items.map(it => it.id !== selItem.id ? it : { ...it, optionGroups: (it.optionGroups || []).map(g => g.id !== selGrp.id ? g : { ...g, options: reorderOptions(selGrp.options, fromIndex, toIndex) }) }) }) }; }))}
                            className={cn('flex items-center gap-3 px-4 py-3 border border-slate-200 rounded-xl transition-colors duration-200', (optHidden || optGlobalOOS) && 'opacity-60', reorderMode && 'cursor-grab active:cursor-grabbing', reorderMode && dropTargetId === opt.id && 'ring-2 ring-amber-300 bg-amber-50/40')}>
                            {reorderMode && <GripVertical size={16} className="text-slate-300 shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <p className={cn('text-sm font-semibold transition-colors duration-200', (optHidden || optGlobalOOS) ? 'text-slate-400 line-through' : 'text-slate-800')}>{opt.nameEN || '—'}</p>
                              <p className="text-[11px] text-slate-400">+{loc.currencySymbol} {opt.extraPrice.toFixed(2)}</p>
                              {optGlobalOOS && <p className="text-[9px] text-red-500 font-bold mt-0.5">Globally Out of Stock</p>}
                            </div>
                            {opt.nameAR && <span className="text-xs text-slate-400" dir={loc.secondaryDir}>{opt.nameAR}</span>}
                            {selGrp.linkedLibraryId && opt.libraryOptionId && <span className="text-[8px] px-1 py-0.5 bg-indigo-50 text-indigo-400 border border-indigo-100 rounded font-bold shrink-0" title="Global toggle — affects all linked products">🌐</span>}
                            <button onClick={() => toggleOptVis(selCat.id, selItem.id, selGrp.id, opt.id)}
                              disabled={optLocked || selGrpLocked}
                              className={cn('relative inline-flex h-5 w-9 items-center rounded-full shrink-0 transition-colors duration-200',
                                (optLocked || selGrpLocked) && 'opacity-50 cursor-not-allowed',
                                (optHidden || optGlobalOOS) ? 'bg-slate-300' : 'bg-emerald-400')}
                              title={optGlobalOOS ? 'Globally Out of Stock — click to restore' : optHidden ? 'Hidden locally' : 'Live'}>
                              <span className={cn('inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform duration-200',
                                (optHidden || optGlobalOOS) ? 'translate-x-1' : 'translate-x-[18px]')} />
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* ═══ SIDE DRAWERS ═══ */}
      <AnimatePresence>
        {drawer && (
          <div className="fixed inset-0 z-50 flex">
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
              className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={closeDrawer} />
            {/* Panel */}
            <motion.div
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 30, stiffness: 250, mass: 1 }}
              className="ml-auto relative w-full max-w-xl h-full bg-white shadow-2xl border-l border-slate-200 flex flex-col"
              onClick={e => e.stopPropagation()}>
              {/* Drawer header */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 shrink-0 bg-slate-50/50">
                <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-white shadow-sm border border-slate-200 shrink-0">
                  {drawer.type === 'profile' ? <SettingsIcon size={18} className="text-slate-600" /> :
                   drawer.type === 'category' ? <FolderOpen size={18} className="text-teal-600" /> :
                   drawer.type === 'product' ? <Package size={18} className="text-indigo-600" /> :
                   <Layers size={18} className="text-violet-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-slate-900 truncate">
                    {drawer.type === 'profile' ? 'Profile Settings' :
                     drawer.type === 'category' ? (drawer.catId ? 'Edit Category' : 'New Category') :
                     drawer.type === 'product' ? (drawer.itemId ? 'Edit Product' : 'New Product') :
                     (drawer.grpId ? 'Edit Option Group' : 'New Option Group')}
                  </h3>
                  <p className="text-[11px] text-slate-500 font-medium truncate">
                    {drawer.type === 'profile' ? profile.name || 'Untitled' :
                     drawer.type === 'category' ? (catForm.nameEN || 'Define category details') :
                     drawer.type === 'product' ? (prodForm.nameEN || 'Define product details') :
                     (grpForm.nameEN || 'Define modifier options')}
                  </p>
                </div>
                {teleportOrigin && (
                  <button
                    onClick={() => {
                      if (drawerDirty && drawer) {
                        returnToAuditRef.current = true;
                        if (drawer.type === 'profile') confirmProfileDrawer();
                        else if (drawer.type === 'category') confirmCategoryDrawer();
                        else if (drawer.type === 'product') confirmProductDrawer();
                        else if (drawer.type === 'optionGroup') confirmOptionGroupDrawer();
                      } else { closeDrawer(); onBack(); }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-[11px] font-bold hover:bg-emerald-700 transition-all shrink-0 shadow-sm"
                  >
                    <CheckCircle2 size={13} /> Done, Return to Audit
                  </button>
                )}
                <button onClick={closeDrawer} className="p-2 hover:bg-slate-200 rounded-xl transition-colors shrink-0">
                  <X size={18} className="text-slate-400" />
                </button>
              </div>

            {/* Drawer body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* ── Profile Drawer ── */}
              {drawer.type === 'profile' && (
                <>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Profile Name *</label>
                    <input value={profileForm.name} onChange={e => { setProfileForm(p => ({ ...p, name: e.target.value })); setDrawerDirty(true); }} placeholder="e.g. Dine-In Menu" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400" />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Brand *</label>
                    <CustomSelect value={profileForm.brandId} onChange={v => { const b = aBrands.find(br => br.id === v); if (b) { setProfileForm(p => ({ ...p, brandId: b.id, brandName: b.name })); setDrawerDirty(true); } }} placeholder="Select brand" options={aBrands.map(b => ({ value: b.id, label: b.name }))} />
                  </div>
                  {/* ── Branch Assignment ── */}
                  {(() => {
                    const selBrand = aBrands.find(b => b.id === profileForm.brandId);
                    const brList = selBrand?.branches || [];
                    if (brList.length === 0) return null;
                    const allSel = brList.length > 0 && brList.every(br => (profileForm.branchIds || []).includes(br.id));
                    const someSel = brList.some(br => (profileForm.branchIds || []).includes(br.id)) && !allSel;
                    return (
                      <div>
                        <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Branches</label>
                        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                          <div className="flex items-center gap-2 p-2.5 bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors"
                            onClick={() => {
                              const ids = allSel ? [] : brList.map(br => br.id);
                              setProfileForm(p => ({ ...p, branchIds: ids }));
                              setDrawerDirty(true);
                            }}>
                            {allSel ? <CheckCircle2 size={14} className="text-indigo-600" /> : someSel ? <MinusSquare size={14} className="text-indigo-600" /> : <Square size={14} className="text-slate-400" />}
                            <span className="text-xs font-bold text-slate-700">All Branches ({brList.length})</span>
                          </div>
                          <div className="p-1.5 space-y-0.5 max-h-40 overflow-y-auto">
                            {brList.map(br => {
                              const checked = (profileForm.branchIds || []).includes(br.id);
                              return (
                                <div key={br.id} className={cn('flex items-center gap-2 px-2.5 py-1.5 rounded-md cursor-pointer transition-colors', checked ? 'bg-indigo-50/50' : 'hover:bg-slate-50')}
                                  onClick={() => {
                                    const ids = checked ? (profileForm.branchIds || []).filter((id: string) => id !== br.id) : [...(profileForm.branchIds || []), br.id];
                                    setProfileForm(p => ({ ...p, branchIds: ids }));
                                    setDrawerDirty(true);
                                  }}>
                                  {checked ? <CheckCircle2 size={13} className="text-indigo-600" /> : <Square size={13} className="text-slate-300" />}
                                  <span className={cn('text-xs font-medium truncate', checked ? 'text-indigo-900' : 'text-slate-600')}>{br.name}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="relative">
                    <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Tag</label>
                    <input value={tagInput} onChange={e => { setTagInput(e.target.value); setProfileForm(p => ({ ...p, tag: e.target.value })); setShowTagDD(true); setDrawerDirty(true); }} onFocus={() => setShowTagDD(true)} onBlur={() => setTimeout(() => setShowTagDD(false), 150)} placeholder="e.g. Regular, Catering…" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400" />
                    {showTagDD && allTags.filter(t => t.toLowerCase().includes(tagInput.toLowerCase())).length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-32 overflow-auto">
                        {allTags.filter(t => t.toLowerCase().includes(tagInput.toLowerCase())).map(t => (
                          <button key={t} onMouseDown={() => { setTagInput(t); setProfileForm(p => ({ ...p, tag: t })); setShowTagDD(false); setDrawerDirty(true); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50">{t}</button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Main Operating Hours */}
                  <div className="space-y-2">
                    <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><Timer size={13} className="text-indigo-500" /> Main Operating Hours</p>
                    <p className="text-[10px] text-slate-400">Set the global schedule for this menu. When closed, all categories and items become unavailable.</p>
                    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
                      <ScheduleEditor value={profileForm.availabilitySchedule} onChange={s => { setProfileForm(p => ({ ...p, availabilitySchedule: s })); setDrawerDirty(true); }} />
                    </div>
                  </div>

                  {/* ── Module Connections (Admin only) ── */}
                  {canEditConnections && (
                    <div>
                      <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Module Connections</label>
                      <p className="text-[10px] text-slate-400 mb-2">Select modules that can send data to this menu.</p>
                      <div className="space-y-1.5">
                        {PLATFORM_MODULES.filter(m => m.id !== 'menu-manage' && m.id !== 'system-tools').map(mod => {
                          const connected = profileForm.connectedModules.includes(mod.id);
                          return (
                            <button key={mod.id} type="button" onClick={() => { setProfileForm(p => ({ ...p, connectedModules: connected ? p.connectedModules.filter(id => id !== mod.id) : [...p.connectedModules, mod.id] })); setDrawerDirty(true); }}
                              className={cn('w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left text-xs font-semibold transition-all',
                                connected ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300')}>
                              <div className={cn('w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
                                connected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300')}>
                                {connected && <Check size={10} className="text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="block truncate">{mod.name}</span>
                                <span className="block text-[9px] font-normal text-slate-400 truncate">Can push items into this menu</span>
                              </div>
                              {connected && <Link2 size={12} className="text-indigo-400 shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ── Category Drawer ── */}
              {drawer.type === 'category' && (
                <>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Category Name (EN) *</label>
                    <input value={catForm.nameEN} onChange={e => { setCatForm(p => ({ ...p, nameEN: e.target.value })); setDrawerDirty(true); }} placeholder="e.g. Burgers" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-[9px] font-bold text-slate-400 uppercase">Category Name ({loc.secondaryLangCode})</label>
                      {aiVisible && <TranslateBtn sourceText={catForm.nameEN} onResult={v => { setCatForm(p => ({ ...p, nameAR: v })); setDrawerDirty(true); }} onClear={() => { setCatForm(p => ({ ...p, nameAR: '' })); setDrawerDirty(true); }} currentValue={catForm.nameAR} />}
                    </div>
                    <input value={catForm.nameAR} onChange={e => { setCatForm(p => ({ ...p, nameAR: e.target.value })); setDrawerDirty(true); }} placeholder="الاسم بالعربي" dir={loc.secondaryDir} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-right focus:outline-none focus:border-indigo-400" />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Internal Code</label>
                    <input value={catForm.code || ''} onChange={e => { setCatForm(p => ({ ...p, code: e.target.value })); setDrawerDirty(true); }} disabled={!isAdmin} placeholder={generateCategoryCode(profile.brandName, profile.categories)} className={cn('w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-mono focus:outline-none focus:border-indigo-400', !isAdmin && 'opacity-60 cursor-not-allowed bg-slate-50')} />
                    <p className="text-[9px] text-slate-400 mt-0.5">Auto-generated on save. For POS / internal use.</p>
                  </div>
                  {/* Availability Schedule */}
                  <div className="space-y-2">
                    <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><Timer size={13} className="text-indigo-500" /> Availability Schedule</p>
                    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
                      <ScheduleEditor value={catForm.availabilitySchedule} onChange={s => { setCatForm(p => ({ ...p, availabilitySchedule: s })); setDrawerDirty(true); }} />
                    </div>
                  </div>
                </>
              )}

              {/* ── Product Drawer ── */}
              {drawer.type === 'product' && (
                <>
                  {/* Granular pending banner */}
                  {drawerLock.isLocked && (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-amber-200 bg-amber-50 text-xs">
                      <Clock size={14} className="text-amber-600 shrink-0" />
                      <div>
                        <p className="font-bold text-amber-800">📝 Proposed Modification Pending</p>
                        <p className="text-amber-600 text-[10px]">Fields marked with 🟠 are awaiting approval and temporarily read-only. Other fields remain editable.</p>
                      </div>
                    </div>
                  )}
                  {prodForm.systemManaged && (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-amber-200 bg-amber-50 text-xs">
                      <Lock size={14} className="text-amber-600 shrink-0" />
                      <div>
                        <p className="font-bold text-amber-800">System Managed Item</p>
                        <p className="text-amber-600"><Lock size={10} className="inline -mt-0.5" /> Locked by {getModuleName(prodForm.systemManaged.sourceModule)}. Name and price are read-only — update from the source module.</p>
                        {prodForm.campaignMeta && (
                          <div className="mt-1.5 pt-1.5 border-t border-amber-200 space-y-0.5">
                            <p className="font-semibold text-amber-800">🏷️ Campaign: {prodForm.campaignMeta.campaignName}</p>
                            <p className="text-amber-600">Original: {loc.currencySymbol} {prodForm.campaignMeta.originalPrice.toFixed(2)} → Promo: {loc.currencySymbol} {prodForm.campaignMeta.promoPrice.toFixed(2)} ({discountLabel(prodForm)})</p>
                            <p className="text-amber-600">Period: {new Date(prodForm.campaignMeta.startDate).toLocaleDateString()} — {new Date(prodForm.campaignMeta.endDate).toLocaleDateString()} · {fundingLabel(prodForm)}</p>
                            {isPromoExpired(prodForm) && <p className="font-bold text-red-600">⚠️ This promotion has expired</p>}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Translate All Fields button */}
                  {aiVisible && !prodForm.systemManaged && (prodForm.nameEN || prodForm.descriptionEN) && (
                    <TranslateAllBtn
                      fields={[
                        { key: 'name', source: prodForm.nameEN, onResult: v => pf.up({ nameAR: v }) },
                        ...(prodForm.descriptionEN ? [{ key: 'description', source: prodForm.descriptionEN, onResult: (v: string) => pf.up({ descriptionAR: v }) }] : []),
                      ]}
                    />
                  )}
                  {financialVisibility.showCostData && (
                    <div className="inline-flex items-center gap-1 rounded-xl bg-slate-100 p-1">
                      <button
                        type="button"
                        onClick={() => setProductDrawerTab('details')}
                        className={cn('px-3 py-1.5 rounded-lg text-[10px] font-bold transition-colors', productDrawerTab === 'details' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
                      >
                        Details
                      </button>
                      <button
                        type="button"
                        onClick={() => setProductDrawerTab('recipe')}
                        className={cn('px-3 py-1.5 rounded-lg text-[10px] font-bold transition-colors', productDrawerTab === 'recipe' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
                      >
                        Recipe
                      </button>
                    </div>
                  )}
                  <div className={cn('space-y-3', productDrawerTab === 'recipe' && 'hidden')}>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Name (EN) *{isFieldPending('nameEN') && <span className="inline-block w-2 h-2 rounded-full bg-amber-500 ml-1 align-middle" title="Pending approval" />}</label>
                      <input value={prodForm.nameEN} onChange={e => pf.up({ nameEN: e.target.value })} disabled={!!prodForm.systemManaged || isFieldPending('nameEN')} className={cn('w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400', (prodForm.systemManaged || isFieldPending('nameEN')) && 'opacity-60 cursor-not-allowed bg-slate-50', isFieldPending('nameEN') && 'border-amber-300 bg-amber-50/30')} />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-[9px] font-bold text-slate-400 uppercase">Name ({loc.secondaryLangCode}){isFieldPending('nameAR') && <span className="inline-block w-2 h-2 rounded-full bg-amber-500 ml-1 align-middle" title="Pending approval" />}</label>
                        {aiVisible && !prodForm.systemManaged && !isFieldPending('nameAR') && <TranslateBtn sourceText={prodForm.nameEN} onResult={v => pf.up({ nameAR: v })} onClear={() => pf.up({ nameAR: '' })} currentValue={prodForm.nameAR} />}
                      </div>
                      <input value={prodForm.nameAR || ''} onChange={e => pf.up({ nameAR: e.target.value })} disabled={!!prodForm.systemManaged || isFieldPending('nameAR')} dir={loc.secondaryDir} className={cn('w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-right focus:outline-none focus:border-indigo-400', (prodForm.systemManaged || isFieldPending('nameAR')) && 'opacity-60 cursor-not-allowed bg-slate-50', isFieldPending('nameAR') && 'border-amber-300 bg-amber-50/30', teleportCls('nameAR'))} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Internal Code</label>
                    <input value={prodForm.code || ''} onChange={e => pf.up({ code: e.target.value })} disabled={!isAdmin || !!prodForm.systemManaged} placeholder={generateItemCode(profile.brandName, profile.categories)} className={cn('w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-mono focus:outline-none focus:border-indigo-400', (!isAdmin || prodForm.systemManaged) && 'opacity-60 cursor-not-allowed bg-slate-50')} />
                    <p className="text-[9px] text-slate-400 mt-0.5">Auto-generated on save. For POS / internal use.</p>
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Original Price ({loc.currencySymbol}) *{isFieldPending('basePrice') && <span className="inline-block w-2 h-2 rounded-full bg-amber-500 ml-1 align-middle" title="Pending approval" />}</label>
                    <input type="number" min="0" step="0.01" value={prodForm.basePrice} onChange={e => pf.up({ basePrice: Number(e.target.value) })} disabled={!!prodForm.systemManaged || isFieldPending('basePrice')} className={cn('w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400', (prodForm.systemManaged || isFieldPending('basePrice')) && 'opacity-60 cursor-not-allowed bg-slate-50', isFieldPending('basePrice') && 'border-amber-300 bg-amber-50/30', teleportCls('basePrice'))} />
                  </div>
                  {financialVisibility.showCostData && (
                    <div>
                      <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Cost (Internal) ({loc.currencySymbol}){isFieldPending('cost') && <span className="inline-block w-2 h-2 rounded-full bg-amber-500 ml-1 align-middle" title="Pending approval" />}</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={prodForm.cost ?? ''}
                        onChange={e => pf.up({ cost: e.target.value === '' ? undefined : Number(e.target.value) })}
                        disabled={!financialVisibility.canEditCost || !!prodForm.systemManaged || isFieldPending('cost')}
                        className={cn('w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400', (!financialVisibility.canEditCost || prodForm.systemManaged || isFieldPending('cost')) && 'opacity-60 cursor-not-allowed bg-slate-50', isFieldPending('cost') && 'border-amber-300 bg-amber-50/30', teleportCls('cost'))}
                      />
                      <div className="mt-1 flex items-center justify-between gap-2 text-[10px]">
                        <span className="text-slate-400">{financialVisibility.canEditCost ? 'Editable for Admin and Super Admin only.' : 'Read-only for Managers.'}</span>
                        <span className="font-bold text-emerald-600">Profit: {loc.currencySymbol} {(prodForm.basePrice - (typeof prodForm.cost === 'number' ? prodForm.cost : 0)).toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Description (EN){isFieldPending('descriptionEN') && <span className="inline-block w-2 h-2 rounded-full bg-amber-500 ml-1 align-middle" title="Pending approval" />}</label>
                      <textarea value={prodForm.descriptionEN ?? ''} onChange={e => pf.up({ descriptionEN: e.target.value })} disabled={isFieldPending('descriptionEN')} rows={2} className={cn('w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:border-indigo-400', isFieldPending('descriptionEN') && 'opacity-60 cursor-not-allowed bg-amber-50/30 border-amber-300', teleportCls('descriptionEN'))} style={{ overflow: 'hidden' }} onInput={e => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px'; }} ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }} />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-[9px] font-bold text-slate-400 uppercase">Description ({loc.secondaryLangCode}){isFieldPending('descriptionAR') && <span className="inline-block w-2 h-2 rounded-full bg-amber-500 ml-1 align-middle" title="Pending approval" />}</label>
                        {aiVisible && !isFieldPending('descriptionAR') && <TranslateBtn sourceText={prodForm.descriptionEN ?? ''} onResult={v => pf.up({ descriptionAR: v })} onClear={() => pf.up({ descriptionAR: '' })} currentValue={prodForm.descriptionAR} />}
                      </div>
                      <textarea value={prodForm.descriptionAR ?? ''} onChange={e => pf.up({ descriptionAR: e.target.value })} disabled={isFieldPending('descriptionAR')} rows={2} dir={loc.secondaryDir} className={cn('w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-right resize-none focus:outline-none focus:border-indigo-400', isFieldPending('descriptionAR') && 'opacity-60 cursor-not-allowed bg-amber-50/30 border-amber-300', teleportCls('descriptionAR'))} style={{ overflow: 'hidden' }} onInput={e => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px'; }} ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }} />
                    </div>
                  </div>
                  {/* Image upload */}
                  <div>
                    <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 flex items-center gap-1">Image {isFieldPending('image') && <span className="inline-block w-2 h-2 rounded-full bg-amber-500" title="Pending approval" />} {drawerSaving && <Loader2 size={9} className="animate-spin text-indigo-500" />}</label>
                    <label className={cn('block w-28 h-28 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 hover:border-indigo-300 hover:bg-indigo-50/30 cursor-pointer transition-colors overflow-hidden', isFieldPending('image') && 'opacity-60 pointer-events-none border-amber-300', teleportCls('image'))}>
                      {(stagedImageUrl || prodForm.image) ? (
                        <img loading="lazy" src={stagedImageUrl ?? getMediaUrl(prodForm.image)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-slate-400">
                          <Upload size={16} />
                          <span className="text-[9px] font-bold">Upload</span>
                        </div>
                      )}
                      <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files?.[0]) pf.handleImg(e.target.files[0]); }} />
                    </label>
                  </div>

                  {/* Availability Schedule */}
                  <div className="space-y-2">
                    <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><Timer size={13} className="text-indigo-500" /> Availability Schedule</p>
                    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
                      <ScheduleEditor value={prodForm.availabilitySchedule} onChange={s => pf.up({ availabilitySchedule: s })} />
                    </div>
                  </div>

                  {/* 📝 Allow Special Requests */}
                  <div className="flex items-center justify-between py-2 px-3 border border-slate-200 rounded-xl bg-slate-50/50">
                    <div>
                      <p className="text-xs font-bold text-slate-600">📝 Special Requests</p>
                      <p className="text-[9px] text-slate-400 mt-0.5">Allow customers to add notes when ordering this item</p>
                    </div>
                    <button type="button" onClick={() => pf.up({ allowSpecialRequests: !prodForm.allowSpecialRequests })}
                      className={cn('relative w-9 h-5 rounded-full transition-colors', prodForm.allowSpecialRequests !== false ? 'bg-indigo-600' : 'bg-slate-300')}>
                      <span className={cn('absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform', prodForm.allowSpecialRequests !== false ? 'translate-x-4' : 'translate-x-0')} />
                    </button>
                  </div>

                  {/* ── Category Management ── */}
                  <div className="space-y-3 pt-3 border-t border-slate-200">
                    <p className="text-xs font-bold text-slate-600">Category Management</p>
                    {/* Primary Category Dropdown */}
                    <div>
                      <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Move to Category *</label>
                      <CustomSelect
                        value={prodPrimaryCat}
                        onChange={v => { setProdPrimaryCat(v); setDrawerDirty(true); }}
                        placeholder="Select primary category"
                        options={profile.categories.filter(c => !isUnassignedCat(c.id)).map(c => ({ value: c.id, label: c.nameEN || 'Unnamed' }))}
                      />
                    </div>
                    {/* Link to Additional Categories */}
                    <div>
                      <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Link to Additional Categories</label>
                      <div className="flex items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded-xl min-h-[38px] flex-wrap">
                        {prodLinkedCats.map(catId => {
                          const cat = profile.categories.find(c => c.id === catId);
                          return (
                            <span key={catId} className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-800 text-white text-[10px] font-bold rounded-lg">
                              {cat?.nameEN || 'Unknown'}
                              <button onClick={() => { setProdLinkedCats(prev => prev.filter(id => id !== catId)); setDrawerDirty(true); }}
                                className="hover:bg-white/20 rounded-full p-0.5 transition-colors"><X size={10} /></button>
                            </span>
                          );
                        })}
                        <div className="relative flex-1 min-w-[120px]">
                          <input
                            value={catLinkSearch}
                            onChange={e => setCatLinkSearch(e.target.value)}
                            placeholder={prodLinkedCats.length === 0 ? 'Search categories…' : ''}
                            className="w-full bg-transparent text-xs focus:outline-none py-0.5 px-1"
                          />
                          {catLinkSearch && (() => {
                            const q = catLinkSearch.toLowerCase();
                            const available = profile.categories.filter(c =>
                              c.id !== prodPrimaryCat &&
                              !prodLinkedCats.includes(c.id) &&
                              !isUnassignedCat(c.id) &&
                              (c.nameEN.toLowerCase().includes(q) || c.nameAR.toLowerCase().includes(q))
                            );
                            if (available.length === 0) return null;
                            return (
                              <div className="absolute left-0 top-full mt-1 w-52 bg-white border border-slate-200 rounded-xl shadow-lg z-30 py-1 max-h-40 overflow-auto">
                                {available.map(c => (
                                  <button key={c.id} onMouseDown={() => { setProdLinkedCats(prev => [...prev, c.id]); setCatLinkSearch(''); setDrawerDirty(true); }}
                                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 text-slate-700">{c.nameEN}</button>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                        {prodLinkedCats.length > 0 && (
                          <button onClick={() => { setProdLinkedCats([]); setDrawerDirty(true); }}
                            className="p-1 hover:bg-red-50 rounded-lg transition-colors shrink-0" title="Clear all">
                            <XCircle size={14} className="text-slate-400 hover:text-red-500" />
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Listed in feedback */}
                    {(() => {
                      const allIds = [prodPrimaryCat, ...prodLinkedCats].filter(Boolean);
                      if (allIds.length === 0) return null;
                      const names = allIds.map(id => profile.categories.find(c => c.id === id)?.nameEN).filter(Boolean);
                      return <p className="text-[11px] text-slate-400">Listed in: {names.join(', ')}</p>;
                    })()}
                  </div>

                  {/* Option groups in product drawer */}
                  <div className="space-y-3 pt-3 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5">Option Groups <span className="text-slate-400 font-normal">({(prodForm.optionGroups || []).length})</span>
                        {drawerSaving && <Loader2 size={10} className="animate-spin text-indigo-500" />}
                      </p>
                      <div className="flex items-center gap-2">
                        {(prodForm.optionGroups || []).length > 1 && (
                          <button onClick={() => setGrpReorderMode(m => !m)}
                            className={cn('flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold transition-colors', grpReorderMode ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
                            {grpReorderMode ? <><Check size={10} /> Done</> : <><ArrowUpDown size={10} /> Reorder</>}
                          </button>
                        )}
                        <button onClick={() => { if (drawer.type === 'product') setShowLibraryPicker({ cid: drawer.catId, iid: drawer.itemId || '' }); }} className="text-[10px] font-bold text-indigo-600 hover:underline flex items-center gap-1"><Library size={11} /> Link from Library</button>
                        <button onClick={addGroupViaDrawer} className="flex items-center gap-1 px-2.5 py-1 bg-teal-50 border border-teal-200 text-teal-700 rounded-lg text-[10px] font-bold hover:bg-teal-100 transition-colors"><Plus size={10} /> Add Group</button>
                      </div>
                    </div>
                    {(prodForm.optionGroups || []).length === 0 && (
                      <div className="py-6 text-center border-2 border-dashed border-slate-200 rounded-xl">
                        <Layers size={20} className="mx-auto text-slate-300 mb-1" />
                        <p className="text-xs text-slate-400">No option groups yet</p>
                      </div>
                    )}
                    {(prodForm.optionGroups || []).map((grp, grpIdx) => {
                      const isOpen = expandedProdGrps.has(grp.id);
                      const isMasterLink = !!grp.linkedLibraryId;
                      const linkedCount = isMasterLink ? countCategoryLinkedProducts(grp.linkedLibraryId!, profile.categories) : 0;
                      const shared = linkedCount > 1 || isSharedGroup(grp.id, profile.categories, grp.linkedLibraryId);
                      const grpLocked = isEntityLocked(grp.id);
                      return (
                        <div key={grp.id}
                          draggable={grpReorderMode}
                          onDragStart={grpReorderMode ? (e) => { e.stopPropagation(); dragIdRef.current = grp.id; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', grp.id); } : undefined}
                          onDragEnd={grpReorderMode ? () => { dragIdRef.current = null; setDropTargetId(null); } : undefined}
                          {...(grpReorderMode ? {
                            onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; },
                            onDragEnter: (e: React.DragEvent) => { e.preventDefault(); setDropTargetId(grp.id); },
                            onDrop: (e: React.DragEvent) => {
                              e.preventDefault(); e.stopPropagation();
                              const srcId = dragIdRef.current;
                              const groups = prodForm.optionGroups || [];
                              const fromIdx = groups.findIndex(g => g.id === srcId);
                              const toIdx = groups.findIndex(g => g.id === grp.id);
                              if (srcId && fromIdx >= 0 && toIdx >= 0 && fromIdx !== toIdx) {
                                setProdForm(p => ({ ...p, optionGroups: reorderGroups(groups, fromIdx, toIdx) }));
                                setDrawerDirty(true);
                              }
                              dragIdRef.current = null; setDropTargetId(null);
                            },
                          } : {})}
                          className={cn('border rounded-xl overflow-hidden transition-all',
                            grpReorderMode && dropTargetId === grp.id && 'ring-2 ring-indigo-300 bg-indigo-50/40',
                            grpReorderMode && 'cursor-grab active:cursor-grabbing',
                            grpLocked ? 'border-amber-300 ring-1 ring-amber-100 opacity-60' : shared ? 'border-amber-300 ring-1 ring-amber-100' : grp.linkedLibraryId ? 'border-indigo-300 ring-1 ring-indigo-100' : 'border-slate-200')}>
                          {/* Group header */}
                          <div className={cn('flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-slate-100/80 transition-colors', grpLocked ? 'bg-amber-50/40' : 'bg-slate-50')}
                            onClick={() => !grpReorderMode && toggleExpand(setExpandedProdGrps, grp.id)}>
                            {grpReorderMode && <GripVertical size={13} className="text-slate-300 shrink-0" />}
                            {grpReorderMode && <span className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center text-[10px] font-black text-white shrink-0">{grpIdx + 1}</span>}
                            {!grpReorderMode && <ChevronRight size={13} className={cn('text-slate-400 transition-transform shrink-0', isOpen && 'rotate-90')} />}
                            <Layers size={12} className="text-indigo-500 shrink-0" />
                            <span className="text-xs font-bold text-slate-700 flex-1 truncate">{grp.nameEN || 'Unnamed Group'}</span>
                            {grpLocked && <span className="text-[8px] px-1.5 py-0.5 bg-yellow-200 text-yellow-900 border border-yellow-300 rounded font-bold flex items-center gap-0.5"><Clock size={9} /> Change Pending</span>}
                            <span className="text-[9px] text-slate-400">{grp.options.length} opt{grp.options.length !== 1 ? 's' : ''}</span>
                            {isMasterLink && <span className="text-[8px] px-1.5 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded font-bold flex items-center gap-0.5">🔗 Master Link</span>}
                            {shared && <span className="text-[8px] px-1.5 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 rounded font-bold">{linkedCount} products</span>}
                            <button onClick={e => { e.stopPropagation(); if (grpLocked) return; setDeleteConfirm({ message: `Remove "${grp.nameEN || 'Unnamed'}" from this product? The group will be moved to Unassigned Modifiers.`, onConfirm: () => pf.rmGrp(grp.id) }); }} className={cn('p-1 hover:bg-red-50 rounded transition-colors', grpLocked && 'opacity-40 pointer-events-none')} title="Remove from Product"><Unlink size={11} className="text-red-400" /></button>
                          </div>
                          {/* Group body (expanded) — always read-only, edit via Options tab */}
                          {isOpen && (
                              <div className={cn('p-3 space-y-3', shared ? 'bg-amber-50/20' : grp.linkedLibraryId ? 'bg-indigo-50/30' : 'bg-slate-50/50')}>
                                <div className="flex items-center gap-2 text-[10px] text-slate-500 font-semibold flex-wrap">
                                  {isMasterLink && shared ? <><span className="text-amber-600">⚠️ Notice: You are editing a Master Group. This change will affect {linkedCount} product{linkedCount !== 1 ? 's' : ''} instantly.</span></> : isMasterLink ? <><Library size={11} className="text-indigo-500" /> <span className="text-indigo-500">🔗 Master Link — from Modifier Library</span></> : <><Layers size={11} /> Custom Group</>}
                                  <button onClick={() => {
                                    if (drawer?.type !== 'product') return;
                                    parentProductRef.current = { drawer, prodForm: JSON.parse(JSON.stringify(prodForm)), expandedGrps: new Set(expandedProdGrps) };
                                    const cat = profile.categories.find(c => c.items.some(it => (it.optionGroups || []).some(g => g.id === grp.id)));
                                    const item = cat?.items.find(it => (it.optionGroups || []).some(g => g.id === grp.id));
                                    openDrawer({ type: 'optionGroup', catId: cat?.id || drawer.catId, itemId: item?.id || drawer.itemId || '', grpId: grp.id });
                                  }} className="text-indigo-500 hover:text-indigo-700 underline underline-offset-2 cursor-pointer transition-colors">
                                    — click to edit ✎
                                  </button>
                                  {shared && (
                                    <button onClick={() => {
                                      const detached = detachGroupCopy(grp);
                                      setProdForm(p => ({ ...p, optionGroups: (p.optionGroups || []).map(g => g.id === grp.id ? detached : g) }));
                                      setDrawerDirty(true);
                                      toast.success('Link Broken', `"${grp.nameEN}" is now an independent local copy. Other items keep their original.`);
                                    }} className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 border border-orange-200 text-orange-600 rounded text-[9px] font-bold hover:bg-orange-100 transition-colors ml-auto" title="Break shared link — create independent copy for this item only">
                                      ✂️ Break Link & Make Local
                                    </button>
                                  )}
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div><span className="block text-[9px] font-bold text-slate-400 uppercase mb-0.5">Name (EN)</span><span className="text-xs text-slate-700">{grp.nameEN || '—'}</span></div>
                                  <div><span className="block text-[9px] font-bold text-slate-400 uppercase mb-0.5">Name ({loc.secondaryLangCode})</span><span className="text-xs text-slate-700" dir={loc.secondaryDir}>{grp.nameAR || '—'}</span></div>
                                </div>
                                <div className="flex items-center gap-4">
                                  {(grp.minSelection ?? 0) > 0 && <span className="text-[9px] px-1.5 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 rounded font-bold">Min {grp.minSelection}</span>}
                                  {(grp.maxSelection ?? 0) > 0 && <span className="text-[9px] px-1.5 py-0.5 bg-sky-50 text-sky-600 border border-sky-200 rounded font-bold">Max {grp.maxSelection}</span>}
                                </div>
                                {grp.options.length > 0 && (
                                  <div className="space-y-1.5 pt-2 border-t border-slate-200/60">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase">Options ({grp.options.length})</p>
                                    {grp.options.map((opt, oi) => {
                                      const optGlobalOOS = isGloballyHidden(opt, grp);
                                      return (
                                      <div key={opt.id} className={cn('flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2.5 py-2 transition-colors duration-200', optGlobalOOS && 'opacity-60')}>
                                        <span className="w-5 h-5 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center text-[9px] font-bold text-indigo-500 shrink-0">{oi + 1}</span>
                                        <span className={cn('text-xs font-semibold flex-1 truncate transition-colors duration-200', optGlobalOOS ? 'text-slate-400 line-through' : 'text-slate-700')}>{opt.nameEN || 'Unnamed'}</span>
                                        {optGlobalOOS && <span className="text-[8px] px-1 py-0.5 bg-red-50 text-red-500 border border-red-200 rounded font-bold shrink-0">OOS</span>}
                                        {opt.extraPrice > 0 && <span className="text-[10px] font-bold text-emerald-600">+{opt.extraPrice} {loc.currencySymbol}</span>}
                                      </div>
                                      );
                                    })}
                                  </div>
                                )}
                                {grp.options.length === 0 && (
                                  <p className="text-[11px] text-slate-400 text-center py-3">No options yet — add via Options tab</p>
                                )}
                              </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  </div>
                  {financialVisibility.showCostData && productDrawerTab === 'recipe' && (
                    <div className="space-y-3">
                      {(prodForm.id || drawer.itemId) ? (
                        <RecipeTab
                          profileId={profile.id}
                          itemId={prodForm.id || drawer.itemId || ''}
                          itemName={prodForm.nameEN || 'Untitled Item'}
                          itemImage={prodForm.image}
                          basePrice={prodForm.basePrice}
                          orgId={user?.orgId ?? 'default'}
                          canViewCost={financialVisibility.showCostData}
                          canEdit={financialVisibility.canEditCost}
                          onCostUpdate={(totalCost) => {
                            if (!financialVisibility.canEditCost) return;
                            pf.up({ cost: Number(totalCost.toFixed(2)) });
                          }}
                          brandName={profile.brandName}
                          brandLogo={brands.find(brand => brand.id === profile.brandId)?.logoUrl}
                          currencySymbol={loc.currencySymbol}
                        />
                      ) : (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
                          Save this item once, then reopen it to manage recipe ingredients and costing.
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* ── Option Group Drawer ── */}
              {drawer.type === 'optionGroup' && (
                <>
                  {/* Linked Products Header */}
                  {(() => {
                    const isNew = !drawer.grpId;
                    const isFromParent = !!parentProductRef.current && isNew;
                    // Compute linked products for existing groups
                    const linkedItems: { catId: string; catName: string; itemId: string; itemName: string }[] = [];
                    if (drawer.grpId) {
                      for (const c of profile.categories) {
                        if (isUnassignedCat(c.id)) continue;
                        for (const it of c.items) {
                          if ((it.optionGroups || []).some(g => g.id === drawer.grpId)) {
                            linkedItems.push({ catId: c.id, catName: c.nameEN || 'Unnamed', itemId: it.id, itemName: it.nameEN || 'Unnamed' });
                          }
                        }
                      }
                    }
                    const count = isNew ? bulkChecked.size : linkedItems.length;

                    // Creating from product drawer — simple breadcrumb
                    if (isFromParent) {
                      const parentProd = parentProductRef.current!.prodForm;
                      return (
                        <div className="flex items-center gap-1.5 flex-wrap bg-indigo-50/50 border border-indigo-100 rounded-lg px-3 py-2">
                          <span className="text-[10px] text-slate-400 font-bold">Will be added to:</span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-slate-200 rounded text-[10px] font-semibold text-slate-600">
                            <Package size={10} className="text-slate-400" /> {parentProd.nameEN || 'Current product'}
                          </span>
                        </div>
                      );
                    }

                    // For new groups: items selected via bulk picker
                    const selectedItems: { id: string; name: string }[] = [];
                    if (isNew && bulkChecked.size > 0) {
                      for (const c of profile.categories) {
                        if (isUnassignedCat(c.id)) continue;
                        for (const it of c.items) {
                          if (bulkChecked.has(it.id)) selectedItems.push({ id: it.id, name: it.nameEN || 'Unnamed' });
                        }
                      }
                    }

                    return (
                      <div className={cn('rounded-xl p-3 space-y-2', count === 0 ? 'bg-amber-50/60 border border-amber-200' : 'bg-emerald-50/60 border border-emerald-200')}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Link2 size={12} className={count === 0 ? 'text-amber-600' : 'text-emerald-600'} />
                            <p className={cn('text-[10px] font-bold uppercase tracking-wide', count === 0 ? 'text-amber-700' : 'text-emerald-700')}>
                              {count === 0 ? 'No Products Linked' : `Linked to ${count} Product${count > 1 ? 's' : ''}`}
                            </p>
                          </div>
                          <span className={cn('px-2 py-0.5 rounded text-[9px] font-bold border',
                            count === 0 ? 'bg-amber-100 border-amber-200 text-amber-700' : 'bg-emerald-100 border-emerald-200 text-emerald-700')}>
                            {count === 0 ? 'UNASSIGNED' : 'LINKED'}
                          </span>
                        </div>
                        {/* Show linked product chips */}
                        {!isNew && linkedItems.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {linkedItems.slice(0, 3).map(li => (
                              <span key={li.itemId} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-emerald-200 rounded text-[9px] font-medium text-slate-600">
                                <Package size={9} className="text-emerald-400" /> {li.itemName}
                              </span>
                            ))}
                            {linkedItems.length > 3 && (
                              <span className="px-2 py-0.5 bg-emerald-100 border border-emerald-200 rounded text-[9px] font-bold text-emerald-700">+{linkedItems.length - 3} more</span>
                            )}
                          </div>
                        )}
                        {isNew && selectedItems.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {selectedItems.slice(0, 3).map(it => (
                              <span key={it.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-emerald-200 rounded text-[9px] font-medium text-slate-600">
                                <Package size={9} className="text-emerald-400" /> {it.name}
                              </span>
                            ))}
                            {selectedItems.length > 3 && (
                              <span className="px-2 py-0.5 bg-emerald-100 border border-emerald-200 rounded text-[9px] font-bold text-emerald-700">+{selectedItems.length - 3} more</span>
                            )}
                          </div>
                        )}
                        <button onClick={openBulkAssign}
                          className={cn('w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-bold transition-colors',
                            count === 0 ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-200' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-200')}>
                          <Layers size={12} /> Manage Product Links
                        </button>
                        {count === 0 && (
                          <p className="text-[10px] text-amber-600 flex items-center gap-1">
                            <Info size={10} /> {isNew ? 'Select products or save as unassigned.' : 'This group is unassigned. Link to products above.'}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                  {/* Group details section */}
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Group Details</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Name (EN) *</label>
                        <input value={grpForm.nameEN} onChange={e => gf.up({ nameEN: e.target.value })} placeholder="e.g. Size, Extras" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400" />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-[9px] font-bold text-slate-400 uppercase">Name ({loc.secondaryLangCode})</label>
                          {aiVisible && <TranslateBtn sourceText={grpForm.nameEN} onResult={v => gf.up({ nameAR: v })} onClear={() => gf.up({ nameAR: '' })} currentValue={grpForm.nameAR} />}
                        </div>
                        <input value={grpForm.nameAR || ''} onChange={e => gf.up({ nameAR: e.target.value })} dir={loc.secondaryDir} placeholder="الاسم" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-right focus:outline-none focus:border-indigo-400" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Min Selection</label>
                        <input type="number" min="0" value={grpForm.minSelection ?? 0} onChange={e => gf.up({ minSelection: Number(e.target.value) })} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400" />
                      </div>
                      <div>
                        <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Max Selection</label>
                        <input type="number" min="0" value={grpForm.maxSelection ?? 0} onChange={e => gf.up({ maxSelection: Number(e.target.value) })} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400" />
                      </div>
                    </div>
                    {/* Group Code (Relational Modifier Code) */}
                    <div>
                      <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Group Code</label>
                      <div className="flex items-center gap-2">
                        <input value={grpForm.groupCode || ''} onChange={e => isAdmin ? gf.up({ groupCode: e.target.value.toUpperCase() }) : undefined} readOnly={!isAdmin} placeholder={!drawer.grpId ? 'Auto-generated on save' : 'No code'} className={cn('flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-mono tracking-wide focus:outline-none', isAdmin ? 'focus:border-indigo-400' : 'bg-slate-100 text-slate-500 cursor-default')} />
                        {grpForm.groupCode && <span className="shrink-0 px-2 py-1 bg-indigo-50 border border-indigo-200 rounded text-[9px] font-bold text-indigo-600 font-mono">{grpForm.groupCode}</span>}
                      </div>
                      <p className="text-[9px] text-slate-400 mt-1">{isAdmin ? 'Editable identifier for aggregator linking.' : 'Read-only code assigned by Admin.'}</p>
                    </div>
                    {/* Selection rule badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {(grpForm.minSelection ?? 0) >= 1 && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-red-50 border border-red-200 rounded text-[9px] font-bold text-red-600"><AlertTriangle size={9} /> REQUIRED</span>
                      )}
                      {(grpForm.minSelection ?? 0) === 0 && (
                        <span className="px-2 py-0.5 bg-slate-50 border border-slate-200 rounded text-[9px] font-bold text-slate-400">OPTIONAL</span>
                      )}
                      {(grpForm.maxSelection ?? 0) === 1 && <span className="px-2 py-0.5 bg-blue-50 border border-blue-200 rounded text-[9px] font-bold text-blue-600">Single Select</span>}
                      {(grpForm.maxSelection ?? 0) > 1 && <span className="px-2 py-0.5 bg-blue-50 border border-blue-200 rounded text-[9px] font-bold text-blue-600">Multi Select (up to {grpForm.maxSelection})</span>}
                    </div>
                    {/* Allow Multiple Quantities toggle */}
                    <div className="flex items-center justify-between py-2 border-t border-slate-100">
                      <div>
                        <p className="text-[11px] font-semibold text-slate-700">Allow Multiple Quantities</p>
                        <p className="text-[9px] text-slate-400">When ON, customers use quantity steppers [-] 0 [+] instead of radio/checkbox.</p>
                      </div>
                      <button onClick={() => gf.up({ allowMultipleQty: !grpForm.allowMultipleQty })}
                        className={cn('relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
                          grpForm.allowMultipleQty ? 'bg-indigo-600' : 'bg-slate-300')}>
                        <span className={cn('inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow',
                          grpForm.allowMultipleQty ? 'translate-x-4.5' : 'translate-x-0.5')} />
                      </button>
                    </div>
                  </div>

                  {/* Options section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-bold text-slate-600">Options <span className="text-slate-400 font-normal">({grpForm.options.length})</span></p>
                        {grpForm.options.length > 0 && !optReorderMode && (
                          <button onClick={toggleAllOpts}
                            className={cn('flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold transition-colors border',
                              allOptsSelected ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white border-slate-200 text-slate-500 hover:border-indigo-200')}>
                            {allOptsSelected ? <CheckCircle2 size={10} /> : <Hash size={10} />} {allOptsSelected ? 'Deselect All' : 'Select All'}
                          </button>
                        )}
                        {/* Translate All Options button */}
                        {aiVisible && grpForm.options.length > 0 && !optReorderMode && (
                          <TranslateAllBtn fields={grpForm.options.filter(o => o.nameEN?.trim()).map(o => ({ key: o.id, source: o.nameEN, onResult: (v: string) => gf.upOpt(o.id, { nameAR: v }) }))} />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {grpForm.options.length >= 2 && (
                          <button onClick={() => setOptReorderMode(m => !m)}
                            className={cn('flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold transition-colors border',
                              optReorderMode ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100' : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100')}>
                            {optReorderMode ? <><Check size={10} /> Done</> : <><ArrowUpDown size={10} /> Reorder</>}
                          </button>
                        )}
                        {!optReorderMode && (
                          <button onClick={gf.addOpt} className="flex items-center gap-1 px-2.5 py-1 bg-teal-50 border border-teal-200 text-teal-700 rounded-lg text-[10px] font-bold hover:bg-teal-100 transition-colors"><Plus size={10} /> Add Option</button>
                        )}
                      </div>
                    </div>
                    {grpForm.options.length === 0 && (
                      <div className="py-8 text-center border-2 border-dashed border-slate-200 rounded-xl">
                        <Package size={22} className="mx-auto text-slate-300 mb-1" />
                        <p className="text-xs text-slate-400">No options yet</p>
                        <button onClick={gf.addOpt} className="mt-2 text-[10px] font-bold text-teal-600 hover:underline">+ Add first option</button>
                      </div>
                    )}

                    {/* Reorder mode — simplified draggable rows */}
                    {optReorderMode && grpForm.options.map((opt, oi) => (
                      <div key={opt.id}
                        draggable
                        onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(oi)); optDragIdxRef.current = oi; }}
                        onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; e.currentTarget.classList.add('ring-2', 'ring-amber-300', 'bg-amber-50/60'); }}
                        onDragLeave={e => { e.currentTarget.classList.remove('ring-2', 'ring-amber-300', 'bg-amber-50/60'); }}
                        onDrop={e => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('ring-2', 'ring-amber-300', 'bg-amber-50/60');
                          const from = optDragIdxRef.current;
                          if (from !== null && from !== oi) {
                            setGrpForm(prev => {
                              const opts = [...prev.options];
                              const [moved] = opts.splice(from, 1);
                              opts.splice(oi, 0, moved);
                              return { ...prev, options: opts };
                            });
                            setDrawerDirty(true);
                          }
                          optDragIdxRef.current = null;
                        }}
                        className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-3 py-2.5 cursor-grab active:cursor-grabbing transition-all">
                        <GripVertical size={14} className="text-slate-300 shrink-0" />
                        <span className="w-5 h-5 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center text-[9px] font-bold text-indigo-500 shrink-0">{oi + 1}</span>
                        <span className="text-xs font-semibold text-slate-700 flex-1 truncate">{opt.nameEN || 'Unnamed option'}</span>
                        {opt.extraPrice > 0 && <span className="text-[10px] font-bold text-indigo-600 shrink-0">+{loc.currencySymbol} {opt.extraPrice.toFixed(2)}</span>}
                      </div>
                    ))}

                    {/* Normal mode — full option cards */}
                    {!optReorderMode && grpForm.options.map((opt, oi) => (
                      <div key={opt.id} className={cn('bg-white border rounded-xl p-3 space-y-2.5 transition-colors',
                        selectedOptIds.has(opt.id) ? 'border-indigo-300 bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300')}>
                        <div className="flex items-center gap-2">
                          <button onClick={() => toggleOptSel(opt.id)}
                            className={cn('w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
                              selectedOptIds.has(opt.id) ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300 hover:border-indigo-300')}>
                            {selectedOptIds.has(opt.id) && <Check size={10} className="text-white" />}
                          </button>
                          <span className="w-5 h-5 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center text-[9px] font-bold text-indigo-500 shrink-0">{oi + 1}</span>
                          <span className="text-xs font-semibold text-slate-700 flex-1 truncate">{opt.nameEN || 'New option'}</span>
                          {opt.pinned && <span className="px-1.5 py-0.5 bg-amber-50 border border-amber-200 rounded text-[8px] font-bold text-amber-600">PINNED</span>}
                          {(grpForm.minSelection ?? 0) >= 1 && (
                            <button onClick={() => {
                              const isPinned = !opt.pinned;
                              // Clear all pins in the current group form
                              grpForm.options.forEach(o => { if (o.id !== opt.id && o.pinned) gf.upOpt(o.id, { pinned: false }); });
                              gf.upOpt(opt.id, { pinned: isPinned });
                              // Also clear pins in OTHER groups on the same item (one pin per item)
                              if (isPinned && drawer?.type === 'optionGroup' && drawer.itemId) {
                                const itemId = drawer.itemId;
                                const thisGrpId = grpForm.id;
                                updateLocal({
                                  ...profile,
                                  categories: profile.categories.map(c => ({
                                    ...c,
                                    items: c.items.map(it => it.id !== itemId ? it : {
                                      ...it,
                                      optionGroups: (it.optionGroups || []).map(g =>
                                        g.id === thisGrpId ? g : { ...g, options: g.options.map(o => o.pinned ? { ...o, pinned: false } : o) }
                                      ),
                                    }),
                                  })),
                                });
                              }
                            }} className={cn('p-1 rounded-lg transition-colors', opt.pinned ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-slate-100')} title={opt.pinned ? 'Unpin price' : 'Pin as card price (1 per item)'}>
                              <MapPin size={12} className={opt.pinned ? 'text-amber-500' : 'text-slate-300'} />
                            </button>
                          )}
                          <button onClick={() => gf.copyOpt(opt)} className="p-1 hover:bg-indigo-50 rounded-lg transition-colors" title="Copy option"><Copy size={12} className="text-indigo-400" /></button>
                          <button onClick={() => setDeleteConfirm({ message: `Delete option "${opt.nameEN || 'Unnamed'}"?`, onConfirm: () => gf.rmOpt(opt.id) })} className="p-1 hover:bg-red-50 rounded-lg transition-colors" title="Remove option"><Trash2 size={12} className="text-red-400" /></button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="block text-[8px] font-bold text-slate-400 uppercase mb-0.5">Name (EN)</label>
                            <input value={opt.nameEN} onChange={e => gf.upOpt(opt.id, { nameEN: e.target.value })} placeholder="Option name" className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-indigo-400 focus:bg-white" />
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-0.5">
                              <label className="block text-[8px] font-bold text-slate-400 uppercase">Name ({loc.secondaryLangCode})</label>
                              {aiVisible && <TranslateBtn sourceText={opt.nameEN} onResult={v => gf.upOpt(opt.id, { nameAR: v })} onClear={() => gf.upOpt(opt.id, { nameAR: '' })} currentValue={opt.nameAR} size="xs" />}
                            </div>
                            <input value={opt.nameAR || ''} onChange={e => gf.upOpt(opt.id, { nameAR: e.target.value })} placeholder="الاسم" dir={loc.secondaryDir} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-right focus:outline-none focus:border-indigo-400 focus:bg-white" />
                          </div>
                          <div>
                            <label className="block text-[8px] font-bold text-slate-400 uppercase mb-0.5">Extra Price ({loc.currencySymbol})</label>
                            <input type="number" min="0" step="0.5" value={opt.extraPrice} onChange={e => gf.upOpt(opt.id, { extraPrice: Number(e.target.value) })} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-indigo-400 focus:bg-white" />
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Paste Option(s) button — shown when clipboard holds option(s) */}
                    {!optReorderMode && clipboard && (clipboard.type === 'option' || clipboard.type === 'options') && (
                      <button onClick={() => {
                        const opts = clipboard.type === 'options' ? (clipboard.data as MenuItemOption[]) : [clipboard.data as MenuItemOption];
                        gf.pasteOpts(opts);
                        toast.success('Pasted', `${opts.length} option${opts.length > 1 ? 's' : ''} added to this group.`);
                      }} className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-indigo-300 rounded-xl text-xs font-bold text-indigo-600 hover:bg-indigo-50 transition-colors animate-pulse">
                        <ClipboardPaste size={14} /> Paste {clipboard.type === 'options' ? `${(clipboard.data as MenuItemOption[]).length} Options` : `"${clipboard.label}"`}
                      </button>
                    )}

                    {/* Floating bulk copy bar */}
                    {!optReorderMode && selectedOptIds.size > 0 && (
                      <div className="sticky bottom-0 z-10 flex items-center justify-between gap-2 bg-indigo-600 text-white rounded-xl px-4 py-2.5 shadow-lg">
                        <span className="text-xs font-bold">{selectedOptIds.size} option{selectedOptIds.size > 1 ? 's' : ''} selected</span>
                        <div className="flex items-center gap-2">
                          <button onClick={() => setSelectedOptIds(new Set())} className="px-2.5 py-1 bg-white/20 rounded-lg text-[10px] font-bold hover:bg-white/30 transition-colors">Clear</button>
                          <button onClick={gf.copySelectedOpts} className="flex items-center gap-1 px-3 py-1 bg-white text-indigo-700 rounded-lg text-[10px] font-bold hover:bg-indigo-50 transition-colors"><Copy size={11} /> Copy {selectedOptIds.size} Selected</button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Drawer footer */}
            <div className="flex items-center gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
              {/* Delete button (only for existing entities, hidden for system-managed items) */}
              {((drawer.type === 'category' && drawer.catId) || (drawer.type === 'product' && drawer.itemId && !prodForm.systemManaged) || (drawer.type === 'optionGroup' && drawer.grpId)) && (() => {
                const isOGFromOptionsTab = drawer.type === 'optionGroup' && detailTab === 'options';
                const isOGFromProductTab = drawer.type === 'optionGroup' && detailTab === 'products';
                const btnLabel = isOGFromOptionsTab ? 'Delete Permanently' : isOGFromProductTab ? 'Remove from Product' : 'Delete';
                const confirmMsg = isOGFromOptionsTab
                  ? 'Are you sure you want to PERMANENTLY DELETE this option group? This will remove it from the database and cannot be undone.'
                  : isOGFromProductTab
                    ? 'This will unlink the option group from this product and move it to the Unassigned bucket.'
                    : `Are you sure you want to delete this ${drawer.type === 'category' ? 'category' : 'product'}? This action cannot be undone.`;
                return (
                  <button onClick={() => { setDeleteConfirm({ message: confirmMsg, onConfirm: () => { if (drawer.type === 'category') deleteCategoryDrawer(); else if (drawer.type === 'product') deleteProductDrawer(); else if (drawer.type === 'optionGroup') deleteOptionGroupDrawer(); } }); }}
                    className={cn('flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold transition-colors', isOGFromOptionsTab ? 'text-red-700 bg-red-50 hover:bg-red-100 border border-red-200' : 'text-red-600 hover:bg-red-50')}>
                    {isOGFromProductTab ? <Unlink size={12} /> : <Trash2 size={12} />} {btnLabel}</button>
                );
              })()}
              <div className="flex-1" />
              {!drawerDirty && !drawerSaving && <span className="text-[10px] text-slate-400 mr-1">No changes detected</span>}
              <button onClick={closeDrawer} className="px-4 py-2 bg-slate-100 text-slate-700 border border-slate-200 rounded-xl text-sm font-bold hover:bg-slate-200">Cancel</button>
              {/* Hide Confirm/Save for system-managed products */}
              {!(drawer.type === 'product' && prodForm.systemManaged) && (
              <button onClick={() => { if (drawer.type === 'profile') confirmProfileDrawer(); else if (drawer.type === 'category') confirmCategoryDrawer(); else if (drawer.type === 'product') confirmProductDrawer(); else if (drawer.type === 'optionGroup') confirmOptionGroupDrawer(); }}
                disabled={drawerSaving || !drawerDirty}
                className={cn('flex items-center gap-2 px-4 py-2 text-white rounded-xl text-sm font-bold', drawer.type === 'profile' ? profileConfirmClass : 'bg-teal-600 hover:bg-teal-700', (drawerSaving || !drawerDirty) && 'opacity-50 cursor-not-allowed')}>
                {drawerSaving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Check size={14} /> {drawer.type === 'profile' ? profileConfirmLabel : 'Confirm'}</>}</button>
              )}
            </div>
          </motion.div>
        </div>
        )}
      </AnimatePresence>
      {/* Bulk Product Selector Modal */}
      {showBulkAssign && drawer?.type === 'optionGroup' && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={() => setShowBulkAssign(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Layers size={18} className="text-indigo-500" /> Select Products</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Choose which products should have this option group</p>
                </div>
                <button onClick={() => setShowBulkAssign(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={16} className="text-slate-400" /></button>
              </div>
              <div className="relative mt-3">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={bulkSearch} onChange={e => setBulkSearch(e.target.value)} placeholder="Search products..." className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400" autoFocus />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
              {profile.categories.filter(c => !isUnassignedCat(c.id)).map(cat => {
                const q = bulkSearch.toLowerCase();
                const filteredItems = cat.items.filter(it => !it.hidden && (!q || (it.nameEN || '').toLowerCase().includes(q) || (it.nameAR || '').toLowerCase().includes(q)));
                if (filteredItems.length === 0) return null;
                const allChecked = filteredItems.every(it => bulkChecked.has(it.id));
                const someChecked = filteredItems.some(it => bulkChecked.has(it.id));
                return (
                  <div key={cat.id} className="border border-slate-200 rounded-xl overflow-hidden">
                    <button onClick={() => { const next = new Set(bulkChecked); if (allChecked) filteredItems.forEach(it => next.delete(it.id)); else filteredItems.forEach(it => next.add(it.id)); setBulkChecked(next); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className={cn('w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
                        allChecked ? 'border-indigo-600 bg-indigo-600' : someChecked ? 'border-indigo-400 bg-indigo-100' : 'border-slate-300')}>
                        {allChecked && <Check size={10} className="text-white" />}
                        {someChecked && !allChecked && <Minus size={10} className="text-indigo-600" />}
                      </div>
                      <FolderOpen size={14} className="text-slate-400" />
                      <span className="text-xs font-bold text-slate-700 flex-1 text-left">{cat.nameEN || 'Unnamed'}</span>
                      <span className="text-[10px] text-slate-400">{filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}</span>
                    </button>
                    {filteredItems.map(it => (
                      <button key={it.id} onClick={() => { const next = new Set(bulkChecked); if (next.has(it.id)) next.delete(it.id); else next.add(it.id); setBulkChecked(next); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 pl-9 hover:bg-indigo-50/50 transition-colors border-t border-slate-100">
                        <div className={cn('w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
                          bulkChecked.has(it.id) ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300 hover:border-indigo-300')}>
                          {bulkChecked.has(it.id) && <Check size={10} className="text-white" />}
                        </div>
                        <Package size={12} className="text-slate-400" />
                        <span className="text-xs text-slate-700 flex-1 text-left truncate">{it.nameEN || 'Unnamed'}</span>
                        {it.nameAR && <span className="text-[10px] text-slate-400 truncate max-w-[100px]" dir="rtl">{it.nameAR}</span>}
                      </button>
                    ))}
                  </div>
                );
              })}
              {profile.categories.filter(c => !isUnassignedCat(c.id)).every(c => c.items.filter(it => !it.hidden).length === 0) && (
                <div className="py-8 text-center">
                  <Package size={24} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-400">No products found</p>
                  <p className="text-xs text-slate-400">Create products first, then link option groups.</p>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
              <span className="text-xs text-slate-500">{bulkChecked.size} product{bulkChecked.size !== 1 ? 's' : ''} selected</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowBulkAssign(false)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-100">Cancel</button>
                <button onClick={applyBulkAssign} className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700">
                  <Check size={14} /> Apply to {bulkChecked.size} Product{bulkChecked.size !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drawer Dirty Guard Modal */}
      {showDrawerGuard && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><AlertTriangle size={20} className="text-amber-600" /></div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Unsaved Changes</h3>
                <p className="text-sm text-slate-500">You have unsaved changes that will be lost.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowDrawerGuard(false)} className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-200">Cancel</button>
              <button onClick={() => {
                setShowDrawerGuard(false); setDrawerDirty(false); setStagedImage(null);
                const next = pendingDrawerRef.current; pendingDrawerRef.current = null;
                if (next === null) {
                  // Discard & close — check if returning to parent product
                  if (drawer?.type === 'optionGroup' && parentProductRef.current) {
                    const p = parentProductRef.current; parentProductRef.current = null;
                    setProdForm(p.prodForm); setExpandedProdGrps(p.expandedGrps);
                    setDrawer(p.drawer); setDrawerDirty(true);
                  } else { setDrawer(null); }
                } else { openDrawer(next); }
              }}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700">Discard</button>
            </div>
          </div>
        </div>
      )}

      {/* Vault Linker Modal */}
      {showVaultLinker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full space-y-4">
            <h3 className="text-lg font-bold text-slate-900">Link Ministry File</h3>
            {vaultFiles.length === 0 ? (
              <p className="text-sm text-slate-500">No files available in the vault.</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-auto">
                {vaultFiles.map(f => (
                  <button key={f.id} onClick={() => linkFile(f.id)} className="w-full text-left px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg hover:bg-indigo-50 hover:border-indigo-200 text-sm">
                    <p className="font-semibold text-slate-700">{f.name}</p>
                    <p className="text-xs text-slate-400">{new Date(f.uploadedAt).toLocaleDateString()}</p>
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => setShowVaultLinker(false)} className="w-full px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-200">Close</button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteDialog state={deleteConfirm} onClose={() => setDeleteConfirm(null)} />

      {/* Unlink Warning Modal */}
      {unlinkConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full space-y-4 mx-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <Unlink size={20} className="text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Unlink Option Group?</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  <strong>"{unlinkConfirm.grpName}"</strong> will be converted to a local copy. Future changes to the Master Library version will no longer sync to this product.
                </p>
              </div>
            </div>
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 font-medium">
              ⚠ This action cannot be undone. You will need to re-link manually if you change your mind.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setUnlinkConfirm(null)}
                className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-200 transition-colors"
              >Cancel</button>
              <button
                onClick={() => { unlinkConfirm.onConfirm(); setUnlinkConfirm(null); }}
                className="flex-1 px-4 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-600 transition-colors"
              >Unlink</button>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Modifier Library Selector (Bulk-enabled, per-brand warehouse) */}
      {showLibraryPicker && (() => {
        // Compute usage: how many items use each modifier (linked OR standalone)
        const usageMap = new Map<string, { count: number; items: { catName: string; itemName: string }[] }>();
        for (const cat of profile.categories) {
          for (const item of cat.items) {
            for (const grp of item.optionGroups || []) {
              const key = grp.linkedLibraryId || grp.id;
              const prev = usageMap.get(key) || { count: 0, items: [] };
              prev.count++;
              prev.items.push({ catName: cat.nameEN, itemName: item.nameEN });
              usageMap.set(key, prev);
            }
          }
        }
        const q = libSearch.toLowerCase();
        const filtered = libraryMods.filter(m => {
          if (!q) return true;
          return m.nameEN.toLowerCase().includes(q) || m.nameAR.includes(libSearch) || m.options.some(o => o.nameEN.toLowerCase().includes(q));
        });
        const toggleBulk = (id: string) => {
          setBulkSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
        };
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowLibraryPicker(null); setLibSearch(''); setInfoMod(null); setBulkSelected([]); }}>
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-lg w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="px-4 py-2.5 border-b border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600"><Library size={14} /></div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-slate-900">Modifier Warehouse</h3>
                    <p className="text-[10px] text-slate-500">Option groups in <strong>{profile.name}</strong></p>
                  </div>
                  <button onClick={() => { setShowLibraryPicker(null); setLibSearch(''); setInfoMod(null); setBulkSelected([]); }} className="p-1 hover:bg-slate-100 rounded-lg transition shrink-0"><X size={14} className="text-slate-400" /></button>
                </div>
                <div className="relative">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={libSearch} onChange={e => setLibSearch(e.target.value)} placeholder="Search modifiers…"
                    className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300" autoFocus />
                </div>
              </div>

              {/* List */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {filtered.length === 0 ? (
                  <div className="text-center py-6 space-y-2">
                    <p className="text-xs text-slate-400">{libraryMods.length === 0 ? 'No option groups in this menu yet.' : 'No modifiers match your search.'}</p>
                  </div>
                ) : filtered.map(mod => {
                  const usage = usageMap.get(mod.id);
                  const isLinked = (prodForm.optionGroupIds || []).includes(mod.id);
                  const isChecked = bulkSelected.includes(mod.id);
                  const seqNum = isChecked ? bulkSelected.indexOf(mod.id) + 1 : 0;
                  return (
                    <div key={mod.id} className={cn('rounded-lg border px-2.5 py-2 transition', isLinked ? 'border-emerald-200 bg-emerald-50/50' : isChecked ? 'border-indigo-300 bg-indigo-50/30 ring-1 ring-indigo-200' : 'border-slate-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/30')}>
                      <div className="flex items-start gap-2">
                        {/* Sequence Badge / Checkbox */}
                        <button onClick={() => !isLinked && toggleBulk(mod.id)} disabled={isLinked}
                          className={cn('mt-0.5 shrink-0 transition flex items-center justify-center',
                            isLinked ? 'w-4 h-4 rounded border-2 border-emerald-300 bg-emerald-100 cursor-not-allowed' :
                            isChecked ? 'w-5 h-5 rounded-full bg-indigo-600 shadow-sm' :
                            'w-4 h-4 rounded border-2 border-slate-300 hover:border-indigo-400')}>
                          {isLinked ? <Check size={10} className="text-emerald-600" /> :
                           isChecked ? <span className="text-[10px] font-black text-white leading-none">{seqNum}</span> : null}
                        </button>
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => !isLinked && toggleBulk(mod.id)}>
                          <div className="flex items-center gap-1.5">
                            <p className="text-xs font-bold text-slate-800 truncate">{mod.nameEN}</p>
                            {mod.nameAR && <p className="text-[10px] text-slate-400 truncate" dir="rtl">{mod.nameAR}</p>}
                            {isLinked && <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded border border-emerald-200 bg-emerald-50 text-[9px] font-bold text-emerald-600"><CheckCircle2 size={8} /> Linked</span>}
                          </div>
                          {/* Option pills */}
                          <div className="flex flex-wrap gap-0.5 mt-1">
                            {mod.options.slice(0, 8).map(opt => (
                              <span key={opt.id} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full border border-slate-200 bg-slate-50 text-[9px] font-medium text-slate-600">
                                {opt.nameEN}{opt.extraPrice > 0 && <span className="text-emerald-600">+{opt.extraPrice}</span>}
                              </span>
                            ))}
                            {mod.options.length > 8 && <span className="px-1 text-[9px] text-slate-400">+{mod.options.length - 8} more</span>}
                          </div>
                          {/* Rules + Usage */}
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded border border-indigo-100 bg-indigo-50 text-[9px] font-bold text-indigo-600">
                              Min {mod.minSelection ?? 0} / Max {mod.maxSelection ?? 0}
                            </span>
                            {usage && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded border border-slate-100 bg-slate-50 text-[9px] font-medium text-slate-500">
                                {usage.count} product{usage.count !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          {/* Attached items */}
                          {usage && usage.count > 0 && (
                            <div className="flex flex-wrap gap-0.5 mt-1">
                              {usage.items.slice(0, 6).map((u, i) => (
                                <span key={i} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded border border-violet-200 bg-violet-50 text-[9px] font-medium text-violet-700">
                                  {u.itemName}
                                </span>
                              ))}
                              {usage.items.length > 6 && <span className="px-1 text-[9px] text-slate-400">+{usage.items.length - 6} more</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer with Bulk Link */}
              <div className="px-4 py-2 border-t border-slate-100 flex items-center gap-2">
                <button onClick={() => { setShowLibraryPicker(null); setLibSearch(''); setInfoMod(null); setBulkSelected([]); }}
                  className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-xs font-bold hover:bg-slate-200">Close</button>
                <div className="flex-1" />
                {bulkSelected.length > 0 && (
                  <button onClick={handleBulkLink}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-colors">
                    <Link2 size={12} /> Link {bulkSelected.length} Group{bulkSelected.length !== 1 ? 's' : ''} in Selected Order
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}
      {exportModalProfile && (
        <ExportModal
          isOpen={true}
          data={menuProfileToMenuData(exportModalProfile, availIndex)}
          profile={exportModalProfile}
          onClose={() => setExportModalProfile(null)}
          onExportTemplate1={() => { exportRelationalTemplate1(exportModalProfile, `Menu_T1_${safeExportName(exportModalProfile.brandName)}_${safeExportName(exportModalProfile.name)}`, loc.currencySymbol); toast.success('Generating Export', 'Template 1 — Detailed download will start shortly.'); setExportModalProfile(null); }}
          onExportTemplate2={() => { exportRelationalTemplate2(exportModalProfile, `Menu_T2_${safeExportName(exportModalProfile.brandName)}_${safeExportName(exportModalProfile.name)}`, loc.currencySymbol); toast.success('Generating Export', 'Template 2 — Aggregator download will start shortly.'); setExportModalProfile(null); }}
          onExportBoth={() => { exportRelationalBoth(exportModalProfile, `Menu_${safeExportName(exportModalProfile.brandName)}_${safeExportName(exportModalProfile.name)}`, loc.currencySymbol); toast.success('Generating Export', 'Both Templates download will start shortly.'); setExportModalProfile(null); }}
        />
      )}

      {/* Branch Conflict Modal */}
      {branchConflict && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full space-y-4 mx-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Conflict Detected</h3>
                <p className="text-sm font-medium text-slate-600">Other active profiles share these branches and tag "{branchConflict.nextForm.tag}".</p>
              </div>
            </div>
            <div className="p-3 bg-red-50 border border-red-100 rounded-lg max-h-40 overflow-y-auto space-y-2">
              {branchConflict.conflictingProfiles.map(cp => (
                <div key={cp.id} className="text-xs">
                  <span className="font-bold text-slate-700">{cp.name}</span>
                  <span className="text-slate-500"> — {cp.branchIds?.filter(id => branchConflict.nextForm.branchIds.includes(id)).length} overlapping branch(es)</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-600 font-medium">Do you want to remove the overlapping branches from the other profiles and assign them here instead?</p>
            <div className="flex gap-3 pt-2 w-full">
              <button 
                onClick={() => setBranchConflict(null)} 
                className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-200 transition-colors"
              >Cancel</button>
              <button 
                onClick={() => executeProfileSaved(branchConflict.nextForm, branchConflict.conflictingProfiles)} 
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 transition-colors"
              >Yes, Replace</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Builder Drawer (Edit mode + Nav Guard + Ministry Sync alert)
// ═══════════════════════════════════════════════════════════════════════════════

function BuilderDrawer({ profile, brands, allTags, user, onSave, onClose }: {
  profile: MenuProfile; brands: Brand[]; allTags: string[]; user: User | null;
  onSave: (p: MenuProfile) => void; onClose: () => void;
}) {
  const toast = useToast();
  const loc = useMenuLocalization();
  const bg = useBackgroundTask();
  const [form, setForm] = useState<MenuProfile>(JSON.parse(JSON.stringify(profile)));
  const [expandCat, setExpandCat] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [showGuard, setShowGuard] = useState(false);
  const [tagInput, setTagInput] = useState(form.tag || '');
  const [showTagDD, setShowTagDD] = useState(false);
  const [exportModalProfile, setExportModalProfile] = useState<MenuProfile | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importState, setImportState] = useState<MenuImportState | null>(null);
  const [builderStagedImages, setBuilderStagedImages] = useState<Map<string, { file: File; url: string }>>(new Map());

  // Ministry vault linker
  const [showVaultLinker, setShowVaultLinker] = useState(false);
  const [vaultFiles, setVaultFiles] = useState<MinistryFile[]>([]);

  useEffect(() => {
    getRepository().getAll<MinistryFile>('ministry-files').then(setVaultFiles);
  }, []);

  // Cleanup staged object URLs on unmount
  useEffect(() => () => { builderStagedImages.forEach(v => URL.revokeObjectURL(v.url)); }, []);

  const mk = () => { if (!dirty) setDirty(true); };
  const up = (u: Partial<MenuProfile>) => { setForm(prev => ({ ...prev, ...u })); mk(); };

  const ub = user?.assignedBrands;
  const aBrands = (ub && ub.length > 0) ? brands.filter(b => ub.includes(b.id)) : brands;
  const hasMinistry = form.ministryFileIds.length > 0;

  // Category ops
  const addCat = () => { const c: MenuCategory = { id: genId(), nameEN: '', nameAR: '', items: [], sortOrder: form.categories.length }; setForm(p => ({ ...p, categories: [...p.categories, c] })); setExpandCat(c.id); mk(); };
  const upCat = (id: string, u: Partial<MenuCategory>) => { setForm(p => ({ ...p, categories: p.categories.map(c => c.id === id ? { ...c, ...u } : c) })); mk(); };
  const rmCat = (id: string) => { setForm(p => ({ ...p, categories: p.categories.filter(c => c.id !== id) })); mk(); };
  const moveCat = (i: number, d: -1 | 1) => { const j = i + d; if (j < 0 || j >= form.categories.length) return; setForm(p => { const cs = [...p.categories]; [cs[i], cs[j]] = [cs[j], cs[i]]; return { ...p, categories: cs.map((c, k) => ({ ...c, sortOrder: k })) }; }); mk(); };

  // Item ops
  const addItem = (cid: string) => { const it: MenuItem = { id: genId(), nameEN: '', nameAR: '', basePrice: 0, options: [], optionGroups: [], sortOrder: 0 }; setForm(p => ({ ...p, categories: p.categories.map(c => c.id !== cid ? c : { ...c, items: [...c.items, { ...it, sortOrder: c.items.length }] }) })); mk(); };
  const upItem = (cid: string, iid: string, u: Partial<MenuItem>) => { setForm(p => ({ ...p, categories: p.categories.map(c => c.id !== cid ? c : { ...c, items: c.items.map(it => it.id === iid ? { ...it, ...u } : it) }) })); mk(); };
  const rmItem = (cid: string, iid: string) => { setForm(p => ({ ...p, categories: p.categories.map(c => c.id !== cid ? c : { ...c, items: c.items.filter(it => it.id !== iid) }) })); mk(); };

  // Option Group ops (with min/max)
  const addGrp = (cid: string, iid: string) => {
    const g = createCleanOptionGroup();
    const cat = form.categories.find(c => c.id === cid);
    const item = cat?.items.find(it => it.id === iid);
    if (item) upItem(cid, iid, { optionGroups: [...(item.optionGroups || []), g] });
  };
  const upGrp = (cid: string, iid: string, gid: string, u: Partial<OptionGroup>) => {
    const item = form.categories.find(c => c.id === cid)?.items.find(it => it.id === iid);
    if (item) upItem(cid, iid, { optionGroups: (item.optionGroups || []).map(g => g.id === gid ? { ...g, ...u } : g) });
  };
  const rmGrp = (cid: string, iid: string, gid: string) => {
    // REMOVE LINK ONLY — do NOT move to Unassigned (that created duplicate copies).
    const item = form.categories.find(c => c.id === cid)?.items.find(it => it.id === iid);
    if (item) upItem(cid, iid, { optionGroups: (item.optionGroups || []).filter(g => g.id !== gid) });
  };

  // Option ops
  const addOpt = (cid: string, iid: string, gid: string) => {
    const o = createCleanOption();
    const grp = form.categories.find(c => c.id === cid)?.items.find(it => it.id === iid)?.optionGroups?.find(g => g.id === gid);
    if (grp) upGrp(cid, iid, gid, { options: [...grp.options, o] });
  };
  const upOpt = (cid: string, iid: string, gid: string, oid: string, u: Partial<MenuItemOption>) => {
    const grp = form.categories.find(c => c.id === cid)?.items.find(it => it.id === iid)?.optionGroups?.find(g => g.id === gid);
    if (grp) upGrp(cid, iid, gid, { options: grp.options.map(o => o.id === oid ? { ...o, ...u } : o) });
  };
  const rmOpt = (cid: string, iid: string, gid: string, oid: string) => {
    const grp = form.categories.find(c => c.id === cid)?.items.find(it => it.id === iid)?.optionGroups?.find(g => g.id === gid);
    if (grp) upGrp(cid, iid, gid, { options: grp.options.filter(o => o.id !== oid) });
  };

  const handleImg = (cid: string, iid: string, f: File) => {
    if (f.size > 5 * 1024 * 1024) { toast.error('Too Large', 'Max 5 MB.'); return; }
    const objUrl = URL.createObjectURL(f);
    setBuilderStagedImages(prev => { const m = new Map<string, { file: File; url: string }>(prev); const old = m.get(iid); if (old) URL.revokeObjectURL(old.url); m.set(iid, { file: f, url: objUrl }); return m; });
    upItem(cid, iid, { image: objUrl });
  };

  const openImportDialog = () => {
    setImportState(null);
    setShowImportDialog(true);
  };

  const closeImportDialog = () => {
    if (importState && !['done', 'error'].includes(importState.stage)) return;
    setShowImportDialog(false);
    setImportState(null);
  };

  const handleImportFile = async (file: File, strategy: import('./components/ImportExcelDialog').ImportStrategy) => {
    const report = await runMenuImportWorkflow({
      file,
      existingCategories: form.categories,
      strategy,
      setState: setImportState,
      applyCategories: async (categories) => {
        setForm((prev) => ({ ...prev, categories }));
        mk();
      },
    });

    if (report && !report.errors.length) {
      const cats = report.categoriesCreated + report.categoriesUpdated;
      const items = report.itemsCreated + report.itemsUpdated;
      const groups = report.groupsCreated;
      const imgCount = report.imageFilenames.size;
      const imgSuffix = imgCount > 0 ? `, ${imgCount} images uploaded` : '';
      toast.success('Import Complete', `${cats} categories, ${items} items, ${groups} option groups synchronized${imgSuffix}.`);
    } else if (report?.errors.length) {
      toast.warning('Imported With Warnings', `${report.errors.length} issue(s) need review.`);
    }
  };

  const handleSave = () => {
    if (!form.name.trim()) { toast.error('Name Required', 'Enter a profile name.'); return; }
    const staged = new Map<string, { file: File; url: string }>(builderStagedImages);
    const saveForm = { ...form, tag: tagInput || form.tag };
    // Pass form through immediately (with staged object URLs as placeholders)
    onSave(saveForm);
    setBuilderStagedImages(new Map());
    setDirty(false);
    // Upload staged images in background & patch the saved profile
    if (staged.size > 0) {
      bg.run({ label: saveForm.name || 'Profile', entityId: saveForm.id, work: async () => {
        for (const [iid, { file, url }] of staged) {
          const realUrl = await uploadFile(file, { moduleId: 'menu-manage' });
          // Patch the item image in the saved profile by re-saving
          saveForm.categories = saveForm.categories.map(c => ({ ...c, items: c.items.map(it => it.id === iid && it.image === url ? { ...it, image: realUrl } : it) }));
        }
        onSave(saveForm);
      }});
    }
  };

  const handleClose = () => { if (dirty) { handleSave(); } onClose(); };

  // Link ministry file
  const linkFile = (fid: string) => {
    up({ ministryFileIds: [...new Set([...form.ministryFileIds, fid])], permitSyncRequired: false });
    setShowVaultLinker(false);
    toast.success('Linked', 'Ministry file linked to this profile.');
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm" onClick={handleClose}>
      <div className="bg-white w-full max-w-4xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2">
              <BookOpen size={18} className="text-teal-600" />
              {profile.createdAt === profile.updatedAt ? 'New Profile' : 'Edit Profile'}
              {dirty && <span className="w-2 h-2 rounded-full bg-amber-500" />}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setExportModalProfile(form)} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg text-xs font-bold hover:bg-emerald-100"><Download size={12} /> Export</button>
            <button onClick={openImportDialog} className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg text-xs font-bold hover:bg-amber-100"><Upload size={12} /> Import</button>
            <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700"><Check size={14} /> Save</button>
            <button onClick={handleClose} className="p-2 hover:bg-slate-100 rounded-xl"><X size={20} className="text-slate-500" /></button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Ministry Sync Alert (Req 4) */}
          {!hasMinistry && (
            <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-300 rounded-xl text-sm">
              <AlertTriangle size={16} className="text-amber-600 shrink-0" />
              <span className="text-amber-800 font-medium flex-1">Notice: This menu is not currently linked to a valid Ministry Permit.</span>
              <button onClick={() => setShowVaultLinker(true)} className="flex items-center gap-1 px-3 py-1.5 bg-amber-200 text-amber-900 rounded-lg text-xs font-bold hover:bg-amber-300"><Link2 size={12} /> Link File</button>
            </div>
          )}

          {/* Meta */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Profile Name *</label>
                <input value={form.name} onChange={e => up({ name: e.target.value })} className="w-full px-3 py-2 bg-white border border-indigo-200 rounded-lg text-sm focus:outline-none focus:border-teal-500" placeholder="e.g. Main Menu" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Brand *</label>
                <CustomSelect value={form.brandId} onChange={v => { const b = aBrands.find(br => br.id === v); if (b) up({ brandId: b.id, brandName: b.name }); }}
                  options={aBrands.map(b => ({ value: b.id, label: b.name }))} placeholder="Select brand…" />
              </div>
            </div>
            <div className="relative">
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Tag</label>
              <input value={tagInput} onChange={e => { setTagInput(e.target.value); setShowTagDD(true); mk(); }}
                onFocus={() => setShowTagDD(true)} onBlur={() => setTimeout(() => setShowTagDD(false), 200)}
                className="w-full px-3 py-2 bg-white border border-indigo-200 rounded-lg text-sm focus:outline-none focus:border-teal-500" placeholder="e.g. Regular, Ramadan…" />
              {showTagDD && allTags.length > 0 && (
                <div className="absolute z-20 top-full mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {allTags.filter(t => t.toLowerCase().includes(tagInput.toLowerCase())).map(t => (
                    <button key={t} onMouseDown={() => { setTagInput(t); setShowTagDD(false); mk(); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">{t}</button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Categories ({form.categories.filter(c => !isUnassignedCat(c.id)).length})</p>
              <button onClick={addCat} className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-bold hover:bg-teal-700"><Plus size={12} /> Add Category</button>
            </div>

            {form.categories.length === 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
                <FolderOpen size={28} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-400">No categories yet.</p>
              </div>
            )}

            {form.categories.map((cat, ci) => {
              if (isUnassignedCat(cat.id)) return null;
              return (
              <div key={cat.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200 cursor-pointer" onClick={() => setExpandCat(expandCat === cat.id ? null : cat.id)}>
                  <GripVertical size={14} className="text-slate-300" />
                  {expandCat === cat.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span className="text-sm font-bold">{cat.nameEN || `Category ${ci + 1}`}</span>
                  {cat.nameAR && <span className="text-xs text-slate-400" dir={loc.secondaryDir}>{cat.nameAR}</span>}
                  <span className="text-[10px] text-slate-400 ml-auto mr-2">{cat.items.length} items</span>
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    <button onClick={() => moveCat(ci, -1)} disabled={ci === 0} className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronUp size={12} /></button>
                    <button onClick={() => moveCat(ci, 1)} disabled={ci === form.categories.length - 1} className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronDown size={12} /></button>
                    <button onClick={() => rmCat(cat.id)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={12} className="text-red-500" /></button>
                  </div>
                </div>

                {expandCat === cat.id && (
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Name (EN) *</label>
                        <input value={cat.nameEN} onChange={e => upCat(cat.id, { nameEN: e.target.value })} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400" />
                      </div>
                      <div>
                        <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Name ({loc.secondaryLangCode})</label>
                        <input value={cat.nameAR} onChange={e => upCat(cat.id, { nameAR: e.target.value })} dir={loc.secondaryDir} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-right focus:outline-none focus:border-teal-400" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-[9px] font-bold text-slate-400 uppercase">Items ({cat.items.length})</p>
                        <button onClick={() => addItem(cat.id)} className="flex items-center gap-1 px-2 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-[10px] font-bold hover:bg-teal-100"><Plus size={10} /> Add Item</button>
                      </div>

                      {cat.items.length === 0 && <p className="text-xs text-slate-300 text-center py-4">No items.</p>}

                      {cat.items.map((item, ii) => (
                        <ItemRow key={item.id} item={item} catId={cat.id} idx={ii}
                          onUpdate={u => upItem(cat.id, item.id, u)}
                          onRemove={() => rmItem(cat.id, item.id)}
                          onImg={f => handleImg(cat.id, item.id, f)}
                          onAddGrp={() => addGrp(cat.id, item.id)}
                          onUpGrp={(gid, u) => upGrp(cat.id, item.id, gid, u)}
                          onRmGrp={gid => rmGrp(cat.id, item.id, gid)}
                          onAddOpt={gid => addOpt(cat.id, item.id, gid)}
                          onUpOpt={(gid, oid, u) => upOpt(cat.id, item.id, gid, oid, u)}
                          onRmOpt={(gid, oid) => rmOpt(cat.id, item.id, gid, oid)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ); })}
          </div>
        </div>
      </div>

      {/* Nav Guard */}
      {showGuard && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={() => setShowGuard(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center"><AlertTriangle size={20} className="text-amber-600" /></div>
              <div>
                <h3 className="text-lg font-bold">Unsaved Changes</h3>
                <p className="text-xs text-slate-500">You have unsaved changes. What would you like to do?</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { handleSave(); setShowGuard(false); onClose(); }} className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700">Save</button>
              <button onClick={() => { setShowGuard(false); setDirty(false); onClose(); }} className="flex-1 px-4 py-2.5 bg-red-50 text-red-700 border border-red-200 rounded-xl text-sm font-bold hover:bg-red-100">Discard</button>
              <button onClick={() => setShowGuard(false)} className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 border border-slate-200 rounded-xl text-sm font-bold hover:bg-slate-200">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Vault Linker Modal */}
      {showVaultLinker && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={() => setShowVaultLinker(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold flex items-center gap-2"><Link2 size={18} className="text-teal-600" /> Link Ministry File</h3>
            {vaultFiles.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No ministry files uploaded yet. Upload one from the Ministry Vault tab.</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {vaultFiles.map(f => (
                  <button key={f.id} onClick={() => linkFile(f.id)}
                    className={cn('w-full flex items-center gap-3 px-4 py-3 border rounded-xl text-left transition-all hover:border-teal-400',
                      form.ministryFileIds.includes(f.id) ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200')}>
                    <FileText size={16} className="text-slate-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{f.name}</p>
                      <p className="text-[10px] text-slate-400">{fmtDate(f.uploadedAt)}</p>
                    </div>
                    {form.ministryFileIds.includes(f.id) && <CheckCircle2 size={14} className="text-emerald-500" />}
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => setShowVaultLinker(false)} className="w-full px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-200">Close</button>
          </div>
        </div>
      )}
      <ImportExcelDialog
        isOpen={showImportDialog}
        status={importState}
        onClose={closeImportDialog}
        onProcessFile={handleImportFile}
        onDownloadTemplate={exportBlankTemplate}
        onExportCurrent={() => {
          setShowImportDialog(false);
          setImportState(null);
          setExportModalProfile(form);
        }}
      />
      {exportModalProfile && (
        <ExportModal
          isOpen={true}
          data={menuProfileToMenuData(exportModalProfile)}
          profile={exportModalProfile}
          onClose={() => setExportModalProfile(null)}
          onExportTemplate1={() => exportRelationalTemplate1(exportModalProfile, `Menu_T1_${safeExportName(exportModalProfile.brandName)}_${safeExportName(exportModalProfile.name)}`, loc.currencySymbol)}
          onExportTemplate2={() => exportRelationalTemplate2(exportModalProfile, `Menu_T2_${safeExportName(exportModalProfile.brandName)}_${safeExportName(exportModalProfile.name)}`, loc.currencySymbol)}
          onExportBoth={() => exportRelationalBoth(exportModalProfile, `Menu_${safeExportName(exportModalProfile.brandName)}_${safeExportName(exportModalProfile.name)}`, loc.currencySymbol)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Item Editor Row (with Option Groups + min/max)
// ═══════════════════════════════════════════════════════════════════════════════

const ItemRow = React.memo(function ItemRow({ item, catId, idx, onUpdate, onRemove, onImg, onAddGrp, onUpGrp, onRmGrp, onAddOpt, onUpOpt, onRmOpt }: {
  key?: React.Key; item: MenuItem; catId: string; idx: number;
  onUpdate: (u: Partial<MenuItem>) => void; onRemove: () => void;
  onImg: (f: File) => void;
  onAddGrp: () => void; onUpGrp: (gid: string, u: Partial<OptionGroup>) => void; onRmGrp: (gid: string) => void;
  onAddOpt: (gid: string) => void; onUpOpt: (gid: string, oid: string, u: Partial<MenuItemOption>) => void; onRmOpt: (gid: string, oid: string) => void;
}) {
  const loc = useMenuLocalization();
  const [exp, setExp] = useState(false);

  return (
    <div className="border border-slate-100 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50/50 cursor-pointer" onClick={() => setExp(!exp)}>
        <span className="text-[10px] text-slate-400 w-5 text-center">{idx + 1}</span>
        {item.image ? <img loading="lazy" src={getMediaUrl(item.image)} alt="" className="w-7 h-7 rounded object-cover" /> : <div className="w-7 h-7 rounded bg-slate-100 flex items-center justify-center"><ImageIcon size={11} className="text-slate-300" /></div>}
        <span className="flex-1 text-sm font-medium truncate">{item.nameEN || 'Untitled'}</span>
        {shouldHidePrice(item) ? (
          <span className="text-[10px] text-slate-400">{(() => { const fp = formatCardPrice(item, loc.currencySymbol); return fp.text ? `${fp.prefix ? fp.prefix + ' ' : ''}${fp.text}` : fp.badge; })()}</span>
        ) : (
          <span className="text-xs font-bold text-teal-700">{loc.currencySymbol} {item.basePrice.toFixed(2)}</span>
        )}
        {(item.optionGroups || []).length > 0 && <span className="px-1.5 py-0.5 bg-indigo-50 border border-indigo-200 rounded text-[9px] text-indigo-600 font-bold">{(item.optionGroups || []).length} grps</span>}
        <button onClick={e => { e.stopPropagation(); onRemove(); }} className="p-1 hover:bg-red-50 rounded"><Trash2 size={12} className="text-red-400" /></button>
        {exp ? <ChevronUp size={12} className="text-slate-400" /> : <ChevronDown size={12} className="text-slate-400" />}
      </div>

      {exp && (
        <div className="bg-slate-50/50 px-4 py-3 space-y-3 border-t border-slate-100">
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Name (EN) *</label>
              <input value={item.nameEN} onChange={e => onUpdate({ nameEN: e.target.value })} className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none" /></div>
            <div><label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Name ({loc.secondaryLangCode})</label>
              <input value={item.nameAR} onChange={e => onUpdate({ nameAR: e.target.value })} dir={loc.secondaryDir} className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-right focus:outline-none" /></div>
            <div><label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Price ({loc.currencySymbol}) *</label>
              <input type="number" min="0" step="0.01" value={item.basePrice} onChange={e => onUpdate({ basePrice: Number(e.target.value) })} className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Desc (EN)</label>
              <textarea value={item.descriptionEN ?? ''} onChange={e => onUpdate({ descriptionEN: e.target.value })} rows={2} className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-sm resize-none focus:outline-none" /></div>
            <div><label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Desc ({loc.secondaryLangCode})</label>
              <textarea value={item.descriptionAR ?? ''} onChange={e => onUpdate({ descriptionAR: e.target.value })} rows={2} dir={loc.secondaryDir} className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-right resize-none focus:outline-none" /></div>
          </div>
          <div>
            <label className="flex items-center gap-2 px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-500 cursor-pointer hover:bg-slate-50 w-fit">
              <Upload size={12} /> {item.image ? 'Change image' : 'Upload image'}
              <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files?.[0]) onImg(e.target.files[0]); }} />
            </label>
          </div>

          {/* Option Groups with min/max */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[9px] font-bold text-slate-400 uppercase">Option Groups ({(item.optionGroups || []).length})</p>
              <button onClick={onAddGrp} className="text-[9px] font-bold text-teal-600 hover:underline">+ Add Group</button>
            </div>
            {(item.optionGroups || []).map(grp => (
              <div key={grp.id} className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Layers size={12} className="text-indigo-500 shrink-0" />
                  <input value={grp.nameEN} onChange={e => onUpGrp(grp.id, { nameEN: e.target.value })} placeholder="Group Name (EN)" className="flex-1 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs focus:outline-none" />
                  <input value={grp.nameAR} onChange={e => onUpGrp(grp.id, { nameAR: e.target.value })} placeholder={loc.secondaryLangCode} dir={loc.secondaryDir} className="w-24 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs text-right focus:outline-none" />
                  <button onClick={() => onRmGrp(grp.id)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={11} className="text-red-400" /></button>
                </div>
                {/* Min / Max selection inputs */}
                <div className="flex items-center gap-3 pl-5">
                  <div className="flex items-center gap-1">
                    <label className="text-[9px] font-bold text-slate-400">Min:</label>
                    <input type="number" min="0" value={grp.minSelection ?? 0} onChange={e => onUpGrp(grp.id, { minSelection: Number(e.target.value) })}
                      className="w-12 px-1 py-0.5 bg-slate-50 border border-slate-200 rounded text-xs text-center focus:outline-none" />
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="text-[9px] font-bold text-slate-400">Max:</label>
                    <input type="number" min="0" value={grp.maxSelection ?? 0} onChange={e => onUpGrp(grp.id, { maxSelection: Number(e.target.value) })}
                      className="w-12 px-1 py-0.5 bg-slate-50 border border-slate-200 rounded text-xs text-center focus:outline-none" />
                  </div>
                  <span className="text-[9px] text-slate-400 italic">
                    {(grp.minSelection ?? 0) > 0 ? `Required (min ${grp.minSelection})` : 'Optional'}
                    {(grp.maxSelection ?? 0) > 0 ? `, max ${grp.maxSelection}` : ''}
                  </span>
                </div>
                {grp.options.map(opt => (
                  <div key={opt.id} className="flex items-center gap-2 pl-5">
                    <span className="text-slate-300">└</span>
                    <input value={opt.nameEN} onChange={e => onUpOpt(grp.id, opt.id, { nameEN: e.target.value })} placeholder="Option (EN)" className="flex-1 px-1.5 py-1 bg-slate-50 border border-slate-100 rounded text-xs focus:outline-none" />
                    <input value={opt.nameAR} onChange={e => onUpOpt(grp.id, opt.id, { nameAR: e.target.value })} placeholder={loc.secondaryLangCode} dir={loc.secondaryDir} className="w-20 px-1.5 py-1 bg-slate-50 border border-slate-100 rounded text-xs text-right focus:outline-none" />
                    <span className="text-[9px] text-slate-400">+</span>
                    <input type="number" min="0" step="0.5" value={opt.extraPrice} onChange={e => onUpOpt(grp.id, opt.id, { extraPrice: Number(e.target.value) })} className="w-14 px-1.5 py-1 bg-slate-50 border border-slate-100 rounded text-xs text-right focus:outline-none" />
                    <button onClick={() => onRmOpt(grp.id, opt.id)} className="p-0.5 hover:bg-red-50 rounded"><X size={10} className="text-red-400" /></button>
                  </div>
                ))}
                <button onClick={() => onAddOpt(grp.id)} className="text-[9px] font-bold text-teal-600 hover:underline ml-5">+ Add Option</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════════
// MODIFIER LIBRARY TAB — Reusable modifier groups warehouse + Global Availability
// ═══════════════════════════════════════════════════════════════════════════════

function ModifierLibraryTab({ user }: { user: User | null }) {
  const canEdit = hasTabVariant(user, 'menu-manage', 'modifiers', 'admin');
  const repo = getRepository();
  const toast = useToast();
  const loc = useMenuLocalization();
  const [mods, setMods] = useState<LibraryModifier[]>([]);
  const [profiles, setProfiles] = useState<MenuProfile[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [editMod, setEditMod] = useState<LibraryModifier | null>(null);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [libView, setLibView] = useState<'groups' | 'search'>('groups');
  const [bulkSearch, setBulkSearch] = useState('');
  const [lockedOptIds, setLockedOptIds] = useState<Set<string>>(new Set());

  // AI feature gating
  const [aiVisible, setAiVisible] = useState(false);
  useEffect(() => { checkAiEnabled().then(setAiVisible).catch(() => setAiVisible(false)); }, []);

  const ub = user?.assignedBrands;
  const MOD_CACHE_KEY = 'menu_modifiers_cache';
  const load = useCallback(async () => {
    const [all, allProfiles, allBrands] = await Promise.all([
      repo.getAll<LibraryModifier>('modifier-library'),
      repo.getAll<MenuProfile>('menu-profiles'),
      repo.getAll<Brand>('brands'),
    ]);
    const scoped = (!ub || ub.length === 0) ? all : all.filter(m => !m.brandId || ub.includes(m.brandId));
    setMods(scoped);
    setProfiles(allProfiles);
    setBrands(allBrands);
    setIsLoading(false);
    try { sessionStorage.setItem(MOD_CACHE_KEY, JSON.stringify({ mods: scoped, profiles: allProfiles, brands: allBrands, ts: Date.now() })); } catch { /* quota */ }
  }, [ub]);
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(MOD_CACHE_KEY);
      if (cached) {
        const { mods: cm, profiles: cp, brands: cb, ts } = JSON.parse(cached);
        if (Date.now() - ts < 5 * 60 * 1000) {
          setMods(cm); setProfiles(cp); setBrands(cb); setIsLoading(false);
        }
      }
    } catch { /* ignore */ }
    load();
  }, [load]);

  /** Count how many profiles have at least one group linked to this modifier */
  const linkedCount = useCallback((modId: string) => {
    return profiles.filter(p => p.categories.some(c => c.items.some(it => (it.optionGroups || []).some(g => g.linkedLibraryId === modId)))).length;
  }, [profiles]);

  const handleSave = async (mod: LibraryModifier) => {
    const isNew = !mods.some(m => m.id === mod.id);
    await repo.save('modifier-library', user?.orgId || 'org1', mod);
    // Sync linked groups across all profiles
    const profiles = await repo.getAll<MenuProfile>('menu-profiles');
    for (const p of profiles) {
      let changed = false;
      const updated = {
        ...p,
        categories: p.categories.map(c => ({
          ...c,
          items: c.items.map(it => ({
            ...it,
            optionGroups: (it.optionGroups || []).map(g => {
              if (g.linkedLibraryId === mod.id) {
                changed = true;
                return { ...g, nameEN: mod.nameEN, nameAR: mod.nameAR, options: mod.options.map(o => ({ ...o, id: genId(), libraryOptionId: o.id })), minSelection: mod.minSelection, maxSelection: mod.maxSelection };
              }
              return g;
            }),
          })),
        })),
      };
      if (changed) await repo.save('menu-profiles', p.orgId || 'org1', updated);
    }
    window.dispatchEvent(new CustomEvent(MENU_DATA_UPDATED_EVENT));
    setEditMod(null);
    toast.success(isNew ? 'Created' : 'Updated', `"${mod.nameEN || 'Untitled Group'}" ${isNew ? 'added to Library' : 'saved and synced'}.`);
    load();
  };

  const handleDelete = async (id: string) => {
    const lc = linkedCount(id);
    if (lc > 0) {
      toast.error('Cannot Delete', `This modifier is linked to ${lc} profile${lc !== 1 ? 's' : ''}. Unlink all products first.`);
      return;
    }
    await repo.delete('modifier-library', user?.orgId || 'org1', id);
    toast.success('Deleted', 'Modifier removed from library.');
    load();
  };

  const createNew = () => {
    setEditMod({ id: genId(), nameEN: '', nameAR: '', options: [], minSelection: 0, maxSelection: 0, updatedAt: Date.now() });
  };

  // ── Global Availability Toggle ─────────────────────────────────────────
  const handleToggleGlobalOption = async (mod: LibraryModifier, optionId: string) => {
    if (!canEdit || lockedOptIds.has(optionId)) return;
    const updated = toggleGlobalOptionAvailability(mod, optionId);
    // Optimistic local update
    setMods(prev => prev.map(m => m.id === updated.id ? updated : m));
    // Lock for 500ms to prevent double-clicks
    setLockedOptIds(prev => { const s = new Set(prev); s.add(optionId); return s; });
    setTimeout(() => setLockedOptIds(prev => { const s = new Set(prev); s.delete(optionId); return s; }), 500);
    // Persist via dedicated PATCH endpoint — dispatch event only AFTER server confirms
    apiFetch(`/api/menu/modifiers/${mod.id}/options/${optionId}/toggle`, { method: 'PATCH' })
    .then(() => {
      window.dispatchEvent(new CustomEvent(MENU_DATA_UPDATED_EVENT));
    }).catch(() => {
      // Rollback on failure
      setMods(prev => prev.map(m => m.id === mod.id ? mod : m));
      toast.error('Sync Failed', 'Could not update availability — reverted.');
    });
  };

  // ── Bulk Toggle by Name ────────────────────────────────────────────────
  const bulkMatches = useMemo(() => searchLibraryOptions(mods, bulkSearch), [mods, bulkSearch]);

  const handleBulkHideAll = async (optionName: string) => {
    if (!canEdit) return;
    const updated = bulkToggleByName(mods, optionName, true);
    for (const mod of updated) {
      if (mod !== mods.find(m => m.id === mod.id)) {
        await repo.save('modifier-library', user?.orgId || 'org1', mod);
      }
    }
    setMods(updated);
    window.dispatchEvent(new CustomEvent(MENU_DATA_UPDATED_EVENT));
    toast.success('Bulk Hidden', `All "${optionName}" options hidden globally.`);
  };

  const handleBulkShowAll = async (optionName: string) => {
    if (!canEdit) return;
    const updated = bulkToggleByName(mods, optionName, false);
    for (const mod of updated) {
      if (mod !== mods.find(m => m.id === mod.id)) {
        await repo.save('modifier-library', user?.orgId || 'org1', mod);
      }
    }
    setMods(updated);
    window.dispatchEvent(new CustomEvent(MENU_DATA_UPDATED_EVENT));
    toast.success('Bulk Restored', `All "${optionName}" options restored globally.`);
  };

  const filtered = mods.filter(m => m.nameEN.toLowerCase().includes(search.toLowerCase()));

  if (isLoading) return <ModifierLibrarySkeleton />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Option Library</h2>
          <p className="text-sm text-slate-500">Reusable option groups with master-link sync. Toggle availability globally.</p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button onClick={createNew} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700">
              <Plus size={14} /> New Modifier
            </button>
          )}
        </div>
      </div>

      {/* View Toggle: Groups vs Cross-Group Search */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
          <button onClick={() => setLibView('groups')} className={cn('px-3 py-1.5 rounded-md text-xs font-bold transition-all', libView === 'groups' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500')}>
            <Layers size={12} className="inline mr-1" /> Modifier Groups
          </button>
          <button onClick={() => setLibView('search')} className={cn('px-3 py-1.5 rounded-md text-xs font-bold transition-all', libView === 'search' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500')}>
            <Search size={12} className="inline mr-1" /> Bulk Toggle
          </button>
        </div>
      </div>

      {libView === 'search' ? (
        /* ── BULK TOGGLE VIEW ── */
        <div className="space-y-4">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={bulkSearch} onChange={e => setBulkSearch(e.target.value)} placeholder="Search option name across all groups (e.g. Potato, Cheese)…"
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400" />
          </div>

          {bulkSearch.trim() && bulkMatches.length > 0 ? (
            <div className="space-y-3">
              {/* Summary bar */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-indigo-700">{bulkMatches.length} match{bulkMatches.length !== 1 ? 'es' : ''}</span>
                  <span className="text-xs text-indigo-500">across {new Set(bulkMatches.map(m => m.modifier.id)).size} group{new Set(bulkMatches.map(m => m.modifier.id)).size !== 1 ? 's' : ''}</span>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleBulkHideAll(bulkSearch)} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700">
                      <EyeOff size={12} /> Hide All Matches
                    </button>
                    <button onClick={() => handleBulkShowAll(bulkSearch)} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700">
                      <Eye size={12} /> Show All Matches
                    </button>
                  </div>
                )}
              </div>

              {/* Matches list */}
              <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
                {bulkMatches.map((match, i) => (
                  <div key={`${match.modifier.id}-${match.option.id}`} className={cn('flex items-center gap-3 px-4 py-3', match.isGloballyHidden && 'bg-red-50/50')}>
                    <div className={cn('w-2 h-2 rounded-full shrink-0', match.isGloballyHidden ? 'bg-red-400' : 'bg-emerald-400')} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">{match.option.nameEN}</p>
                      <p className="text-[10px] text-slate-400">in <span className="font-bold text-slate-500">{match.modifier.nameEN}</span>{match.option.extraPrice > 0 && ` · +${match.option.extraPrice}`}</p>
                    </div>
                    {match.isGloballyHidden ? (
                      <span className="flex items-center gap-1 px-2 py-1 bg-red-100 border border-red-200 rounded-full text-[9px] font-bold text-red-600"><EyeOff size={9} /> Out of Stock</span>
                    ) : (
                      <span className="flex items-center gap-1 px-2 py-1 bg-emerald-100 border border-emerald-200 rounded-full text-[9px] font-bold text-emerald-600"><Eye size={9} /> Available</span>
                    )}
                    {canEdit && (
                      <button onClick={() => handleToggleGlobalOption(match.modifier, match.option.id)}
                        disabled={lockedOptIds.has(match.option.id)}
                        className={cn('p-1.5 rounded-lg transition-colors duration-200', lockedOptIds.has(match.option.id) && 'opacity-50 cursor-not-allowed', match.isGloballyHidden ? 'hover:bg-emerald-100' : 'hover:bg-red-100')}
                        title={match.isGloballyHidden ? 'Restore globally' : 'Hide globally'}>
                        {match.isGloballyHidden ? <Eye size={14} className="text-emerald-600" /> : <EyeOff size={14} className="text-red-500" />}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : bulkSearch.trim() && bulkMatches.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <Search size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm font-semibold text-slate-400">No options matching "{bulkSearch}"</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <Search size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm font-semibold text-slate-400">Type an ingredient name to find it across all groups</p>
              <p className="text-xs text-slate-400 mt-1">e.g. "Potato" — then hide/show it everywhere with one click</p>
            </div>
          )}
        </div>
      ) : (
        /* ── GROUPS VIEW ── */
        <>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search modifiers…"
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400" />
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <Library size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm font-semibold text-slate-400">{mods.length === 0 ? 'No option groups yet. Create your first one!' : 'No results.'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(mod => {
                const hiddenSet = new Set(mod.globalHiddenOptionIds || []);
                const hiddenCount = hiddenSet.size;
                return (
                  <div key={mod.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-3">
                      <Layers size={16} className="text-indigo-500" />
                      <h3 className="text-sm font-bold text-slate-800 flex-1 truncate">{mod.nameEN || mod.nameAR || 'Untitled Group'}</h3>
                      {canEdit && (
                        <div className="flex items-center gap-1">
                          <button onClick={() => setEditMod(JSON.parse(JSON.stringify(mod)))} className="p-1.5 hover:bg-indigo-50 rounded-lg"><Edit2 size={12} className="text-indigo-500" /></button>
                          <button onClick={() => handleDelete(mod.id)} className={cn('p-1.5 rounded-lg', linkedCount(mod.id) > 0 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-red-50')} title={linkedCount(mod.id) > 0 ? `Linked to ${linkedCount(mod.id)} profile(s) — unlink first` : 'Delete modifier'}><Trash2 size={12} className="text-red-400" /></button>
                        </div>
                      )}
                    </div>
                    {mod.nameAR && <p className="text-xs text-slate-400 mb-2" dir={loc.secondaryDir}>{mod.nameAR}</p>}
                    <div className="flex items-center gap-2 flex-wrap text-[10px] text-slate-500 mb-2">
                      <span>{mod.options.length} option{mod.options.length !== 1 ? 's' : ''}</span>
                      {hiddenCount > 0 && (
                        <span className="px-1.5 py-0.5 bg-red-50 text-red-600 border border-red-200 rounded font-bold flex items-center gap-0.5"><EyeOff size={8} /> {hiddenCount} hidden</span>
                      )}
                      {(mod.minSelection ?? 0) > 0 && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 rounded font-bold">Min {mod.minSelection}</span>}
                      {(mod.maxSelection ?? 0) > 0 && <span className="px-1.5 py-0.5 bg-sky-50 text-sky-600 border border-sky-200 rounded font-bold">Max {mod.maxSelection}</span>}
                      {mod.brandId && brands.find(b => b.id === mod.brandId) && (
                        <span className="px-1.5 py-0.5 bg-violet-50 text-violet-600 border border-violet-200 rounded font-bold">{brands.find(b => b.id === mod.brandId)!.name}</span>
                      )}
                      {linkedCount(mod.id) > 0 ? (
                        <span className="px-1.5 py-0.5 bg-teal-50 text-teal-600 border border-teal-200 rounded font-bold flex items-center gap-0.5"><Link2 size={8} /> {linkedCount(mod.id)} linked</span>
                      ) : (
                        <span className="px-1.5 py-0.5 bg-slate-50 text-slate-400 border border-slate-200 rounded font-bold">UNUSED</span>
                      )}
                    </div>
                    {/* Options list with availability toggles */}
                    <div className="space-y-1">
                      {mod.options.map(o => {
                        const isHidden = hiddenSet.has(o.id);
                        const isLocked = lockedOptIds.has(o.id);
                        return (
                          <div key={o.id} className={cn('flex items-center gap-2 text-xs rounded-lg px-1.5 py-1 -mx-1.5 transition-colors duration-200', isHidden && 'bg-red-50/60')}>
                            {canEdit ? (
                              <button onClick={() => handleToggleGlobalOption(mod, o.id)}
                                disabled={isLocked}
                                className={cn('w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors duration-200',
                                  isLocked && 'opacity-50 cursor-not-allowed',
                                  isHidden ? 'bg-red-100 border-red-300 hover:bg-red-200' : 'bg-emerald-100 border-emerald-300 hover:bg-emerald-200')}
                                title={isHidden ? 'Restore (make available)' : 'Hide globally (out of stock)'}>
                                {isHidden ? <EyeOff size={10} className="text-red-600" /> : <Eye size={10} className="text-emerald-600" />}
                              </button>
                            ) : (
                              <div className={cn('w-1.5 h-1.5 rounded-full shrink-0', isHidden ? 'bg-red-400' : 'bg-indigo-300')} />
                            )}
                            <span className={cn('flex-1 truncate transition-colors duration-200', isHidden ? 'text-slate-400 line-through' : 'text-slate-600')}>{o.nameEN}</span>
                            {isHidden && <span className="text-[8px] font-bold text-red-500 shrink-0">OUT OF STOCK</span>}
                            {o.extraPrice > 0 && <span className="text-indigo-600 font-bold shrink-0">+{o.extraPrice}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Edit Modal */}
      {editMod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-lg w-full space-y-4 max-h-[85vh] overflow-auto">
            <h3 className="text-lg font-bold text-slate-900">{editMod.id && mods.some(m => m.id === editMod.id) ? 'Edit Modifier' : 'New Modifier'}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Name (EN) *</label>
                <input value={editMod.nameEN} onChange={e => setEditMod({ ...editMod, nameEN: e.target.value })} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[9px] font-bold text-slate-400 uppercase">Name ({loc.secondaryLangCode})</label>
                  {aiVisible && <TranslateBtn sourceText={editMod.nameEN} onResult={v => setEditMod(prev => prev ? { ...prev, nameAR: v } : prev)} onClear={() => setEditMod(prev => prev ? { ...prev, nameAR: '' } : prev)} currentValue={editMod.nameAR} />}
                </div>
                <input value={editMod.nameAR || ''} onChange={e => setEditMod({ ...editMod, nameAR: e.target.value })} dir={loc.secondaryDir} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-right focus:outline-none focus:border-indigo-400" />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <label className="text-[9px] font-bold text-slate-400">Min:</label>
                <input type="number" min="0" value={editMod.minSelection ?? 0} onChange={e => setEditMod({ ...editMod, minSelection: Number(e.target.value) })} className="w-16 px-2 py-1 bg-white border border-slate-200 rounded text-xs text-center focus:outline-none" />
              </div>
              <div className="flex items-center gap-1">
                <label className="text-[9px] font-bold text-slate-400">Max:</label>
                <input type="number" min="0" value={editMod.maxSelection ?? 0} onChange={e => setEditMod({ ...editMod, maxSelection: Number(e.target.value) })} className="w-16 px-2 py-1 bg-white border border-slate-200 rounded text-xs text-center focus:outline-none" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[9px] font-bold text-slate-400 uppercase">Options ({editMod.options.length})</p>
                <button onClick={() => setEditMod({ ...editMod, options: [...editMod.options, { id: genId(), nameEN: '', nameAR: '', extraPrice: 0 }] })} className="text-[9px] font-bold text-teal-600 hover:underline">+ Add Option</button>
              </div>
              {editMod.options.map((opt, oi) => (
                <div key={opt.id} className="flex items-center gap-2">
                  <div className="flex flex-col items-center shrink-0">
                    <button onClick={() => { if (oi > 0) { const o = [...editMod.options]; [o[oi - 1], o[oi]] = [o[oi], o[oi - 1]]; setEditMod({ ...editMod, options: o }); } }} disabled={oi === 0} className="p-0 disabled:opacity-20"><ChevronUp size={8} /></button>
                    <GripVertical size={8} className="text-slate-200" />
                    <button onClick={() => { if (oi < editMod.options.length - 1) { const o = [...editMod.options]; [o[oi], o[oi + 1]] = [o[oi + 1], o[oi]]; setEditMod({ ...editMod, options: o }); } }} disabled={oi === editMod.options.length - 1} className="p-0 disabled:opacity-20"><ChevronDown size={8} /></button>
                  </div>
                  <input value={opt.nameEN} onChange={e => { const opts = [...editMod.options]; opts[oi] = { ...opts[oi], nameEN: e.target.value }; setEditMod({ ...editMod, options: opts }); }} placeholder="Name (EN)" className="flex-1 min-w-0 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs focus:outline-none" />
                  <div className="flex-1 min-w-0 flex items-center gap-0.5">
                    <input value={opt.nameAR || ''} onChange={e => { const opts = [...editMod.options]; opts[oi] = { ...opts[oi], nameAR: e.target.value }; setEditMod({ ...editMod, options: opts }); }} placeholder={`Name (${loc.secondaryLangCode})`} dir={loc.secondaryDir} className="flex-1 min-w-0 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs text-right focus:outline-none" />
                    {aiVisible && <TranslateBtn sourceText={opt.nameEN} onResult={v => { const opts = [...editMod.options]; opts[oi] = { ...opts[oi], nameAR: v }; setEditMod({ ...editMod, options: opts }); }} onClear={() => { const opts = [...editMod.options]; opts[oi] = { ...opts[oi], nameAR: '' }; setEditMod({ ...editMod, options: opts }); }} currentValue={opt.nameAR} size="xs" />}
                  </div>
                  <span className="text-[9px] text-slate-400">+</span>
                  <input type="number" min="0" step="0.5" value={opt.extraPrice} onChange={e => { const opts = [...editMod.options]; opts[oi] = { ...opts[oi], extraPrice: Number(e.target.value) }; setEditMod({ ...editMod, options: opts }); }} className="w-14 px-1.5 py-1 bg-slate-50 border border-slate-200 rounded text-xs text-right focus:outline-none" />
                  <button onClick={() => setEditMod({ ...editMod, options: editMod.options.filter((_, i) => i !== oi) })} className="p-0.5 hover:bg-red-50 rounded"><X size={10} className="text-red-400" /></button>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditMod(null)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-200">Cancel</button>
              <button onClick={() => { if (!editMod.nameEN.trim()) { toast.error('Name Required', 'Enter a name.'); return; } const dupLib = mods.find(m => m.id !== editMod.id && m.nameEN.trim().toLowerCase() === editMod.nameEN.trim().toLowerCase()); if (dupLib) { toast.error('Duplicate Name', `"${editMod.nameEN}" already exists in the library.`); return; } handleSave({ ...editMod, updatedAt: Date.now() }); }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700"><Check size={14} className="inline mr-1" /> Save & Sync</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 3. MINISTRY VAULT TAB — with Download
// ═══════════════════════════════════════════════════════════════════════════════

function MinistryVaultTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'menu-manage', 'ministry', 'admin');
  const repo = getRepository();
  const toast = useToast();
  const [files, setFiles] = useState<MinistryFile[]>([]);
  const [profiles, setProfiles] = useState<MenuProfile[]>([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: '', linkedProfileIds: [] as string[], fileCategory: 'permit' as VaultFileCategory });
  const [stagedVaultFile, setStagedVaultFile] = useState<{ file: File; previewUrl: string; type: string } | null>(null);
  const [editFile, setEditFile] = useState<MinistryFile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [vaultTab, setVaultTab] = useState<'permits' | 'general'>('permits');

  const load = useCallback(async () => {
    await seedDemoDataIfNeeded();
    // Load files first (fast), then profiles (may be slow) — Vault shows immediately
    const fPromise = repo.getAll<MinistryFile>('ministry-files');
    const pPromise = repo.getAll<MenuProfile>('menu-profiles');
    const f = await fPromise;
    const ub = user?.assignedBrands;
    const orgFiles = f.filter(fl => !user?.orgId || fl.orgId === user.orgId);
    // Show files immediately (profiles may still be loading)
    setFiles(orgFiles);
    setIsLoading(false);
    // Now wait for profiles for brand scoping + association column
    const p = await pPromise;
    const scopedFiles = (!ub || ub.length === 0) ? orgFiles : orgFiles.filter(fl => {
      const linkedBrandIds = fl.linkedProfileIds.map(pid => p.find(pr => pr.id === pid)?.brandId).filter(Boolean) as string[];
      return isWithinBrandScope(ub, linkedBrandIds);
    });
    setFiles(scopedFiles);
    setProfiles(isAdmin ? p : p.filter(pr => !ub || ub.length === 0 || ub.includes(pr.brandId)));
  }, [user, isAdmin]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (e: any) => {
      if ((import.meta as any).env?.DEV && e && e.type === 'PULSE_UPDATE') {
        console.log('💓 Heartbeat: Data Refreshed (Menu Profiles)');
      }
      load();
    };
    window.addEventListener(MENU_DATA_UPDATED_EVENT, handler);
    window.addEventListener('PULSE_UPDATE', handler);
    return () => {
      window.removeEventListener(MENU_DATA_UPDATED_EVENT, handler);
      window.removeEventListener('PULSE_UPDATE', handler);
    };
  }, [load]);
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Too Large', 'Max 5 MB.'); return; }
    if (stagedVaultFile) URL.revokeObjectURL(stagedVaultFile.previewUrl);
    setStagedVaultFile({ file: f, previewUrl: URL.createObjectURL(f), type: f.type });
  };

  const handleUpload = async () => {
    if (!uploadForm.name.trim() || !stagedVaultFile) { toast.error('Missing Fields', 'Name and file are required.'); return; }
    const pending = stagedVaultFile;
    const formData = { ...uploadForm };
    setShowUpload(false); setUploadForm({ name: '', linkedProfileIds: [], fileCategory: vaultTab === 'general' ? 'general' : 'permit' }); setStagedVaultFile(null);
    toast.success('Uploading…', `${formData.name} is being uploaded.`);
    try {
      const url = await uploadFile(pending.file, { moduleId: 'menu-manage' });
      const mf: MinistryFile = { id: genId(), name: formData.name, fileData: url, fileType: pending.type, linkedProfileIds: formData.linkedProfileIds, uploadedBy: user?.id ?? '', uploadedAt: Date.now(), orgId: user?.orgId, fileCategory: formData.fileCategory };
      await repo.save('ministry-files', user?.orgId ?? 'default', mf);
      for (const pid of formData.linkedProfileIds) {
        const pr = profiles.find(p => p.id === pid);
        if (pr) await repo.save('menu-profiles', user?.orgId ?? 'default', { ...pr, permitSyncRequired: false, ministryFileIds: [...new Set([...pr.ministryFileIds, mf.id])], updatedAt: Date.now() });
      }
      toast.success('Uploaded', `${mf.name} added.`);
      await load();
      window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
    } catch { toast.error('Upload Failed', 'Could not upload file.'); }
  };

  const handleEditSave = async (f: MinistryFile) => {
    await repo.save('ministry-files', user?.orgId ?? 'default', f);
    toast.success('Updated', `${f.name} updated.`);
    setEditFile(null); await load();
    window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
  };

  const handleDel = async (id: string) => {
    // Safety lock: check if file is linked to any active menu profiles
    const linkedProfiles = profiles.filter(p => p.ministryFileIds.includes(id) && p.status !== MenuProfileStatus.ARCHIVED);
    if (linkedProfiles.length > 0) {
      const names = linkedProfiles.map(p => `"${p.name}"`).join(', ');
      toast.error('Cannot Delete', `This file is linked to ${linkedProfiles.length} active profile(s): ${names}. Unlink it first.`);
      return;
    }
    await repo.delete('ministry-files', user?.orgId ?? 'default', id);
    toast.success('Deleted', 'File removed.'); await load();
    window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
  };

  // Filter files by category tab (must be before early return — hooks below depend on these)
  const permitFiles = files.filter(f => !f.fileCategory || f.fileCategory === 'permit');
  const generalFiles = files.filter(f => f.fileCategory === 'general');

  // Brand grid data for general files
  const brandGroups = useMemo(() => {
    const map = new Map<string, { brandName: string; brandId: string; files: MinistryFile[]; profileCount: number }>();
    for (const f of generalFiles) {
      const linked = profiles.filter(p => f.linkedProfileIds.includes(p.id));
      const brandNames = [...new Set(linked.map(p => p.brandName).filter(Boolean))];
      if (brandNames.length === 0) {
        const entry = map.get('_unlinked') ?? { brandName: 'Unlinked Files', brandId: '_unlinked', files: [], profileCount: 0 };
        entry.files.push(f);
        map.set('_unlinked', entry);
      } else {
        for (const bn of brandNames) {
          const bp = linked.find(p => p.brandName === bn);
          const entry = map.get(bn) ?? { brandName: bn, brandId: bp?.brandId ?? '', files: [], profileCount: 0 };
          entry.files.push(f);
          entry.profileCount = new Set([...entry.files.flatMap(ff => ff.linkedProfileIds)]).size;
          map.set(bn, entry);
        }
      }
    }
    return [...map.values()].sort((a, b) => a.brandName.localeCompare(b.brandName));
  }, [files, profiles]);

  if (isLoading) return <MinistryVaultSkeleton />;

  return (
    <div className="space-y-4">
      {/* Header with Sub-Tabs */}
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Ministry Vault</h2>
          <p className="text-sm text-slate-500 mt-1">Store ministry permits, compliance attachments & general files.</p>
        </div>
        <button onClick={() => { setUploadForm(prev => ({ ...prev, fileCategory: vaultTab === 'general' ? 'general' : 'permit' })); setShowUpload(true); }} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700"><Upload size={14} /> Upload File</button>
      </div>

      {/* Sub-Tab Navigation */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
        <button onClick={() => setVaultTab('permits')} className={cn('flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all', vaultTab === 'permits' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
          <Shield size={14} /> Permits <span className={cn('ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold', vaultTab === 'permits' ? 'bg-purple-100 text-purple-700' : 'bg-slate-200 text-slate-500')}>{permitFiles.length}</span>
        </button>
        <button onClick={() => setVaultTab('general')} className={cn('flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all', vaultTab === 'general' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
          <FolderOpen size={14} /> General Files <span className={cn('ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold', vaultTab === 'general' ? 'bg-teal-100 text-teal-700' : 'bg-slate-200 text-slate-500')}>{generalFiles.length}</span>
        </button>
      </div>

      {/* ── Permits Tab: Table View ── */}
      {vaultTab === 'permits' && (
        <>
          {permitFiles.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center"><Shield size={32} className="mx-auto text-slate-300 mb-3" /><p className="text-sm font-semibold text-slate-400">No permits yet. Upload a ministry permit to get started.</p></div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="px-5 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wide">File Name</th>
                    <th className="px-5 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wide">Linked Brand</th>
                    <th className="px-5 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wide">Associated Menus</th>
                    <th className="px-5 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wide">Upload Date</th>
                    <th className="px-5 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wide text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {permitFiles.sort((a, b) => b.uploadedAt - a.uploadedAt).map(f => {
                    const linked = profiles.filter(p => f.linkedProfileIds.includes(p.id));
                    const isPdf = f.fileType === 'application/pdf';
                    const brandNames = [...new Set(linked.map(p => p.brandName).filter(Boolean))];
                    return (
                      <tr key={f.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', isPdf ? 'bg-red-100' : 'bg-indigo-100')}>
                              {isPdf ? <FileText size={14} className="text-red-600" /> : <ImageIcon size={14} className="text-indigo-600" />}
                            </div>
                            <span className="text-sm font-semibold text-slate-800 truncate">{f.name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          {brandNames.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {brandNames.map(b => <span key={b} className="px-2 py-0.5 rounded text-[9px] font-bold bg-violet-50 text-violet-600 border border-violet-200">{b}</span>)}
                            </div>
                          ) : <span className="text-xs text-slate-400">—</span>}
                        </td>
                        <td className="px-5 py-3">
                          {linked.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {linked.map(p => <span key={p.id} className="px-2 py-0.5 rounded-full text-[9px] font-bold border bg-indigo-50 text-indigo-600 border-indigo-200">{p.name}</span>)}
                            </div>
                          ) : <span className="text-xs text-slate-400">No menu linked</span>}
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(f.uploadedAt)}</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => {
                              if (!f.fileData) { toast.warning('No Data', 'This demo file has no binary data.'); return; }
                              const ext = f.fileType === 'application/pdf' ? '.pdf' : '.img';
                              base64Download(f.fileData, f.fileType, `${f.name}${ext}`);
                            }} className="p-1.5 hover:bg-emerald-50 rounded-lg" title="Download"><Download size={14} className="text-emerald-600" /></button>
                            <button onClick={() => setEditFile(f)} className="p-1.5 hover:bg-indigo-50 rounded-lg" title="Edit"><Edit2 size={14} className="text-indigo-600" /></button>
                            <button onClick={() => handleDel(f.id)} className="p-1.5 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 size={14} className="text-red-400" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── General Files Tab: Brand Grid View ── */}
      {vaultTab === 'general' && (
        <>
          {generalFiles.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center"><FolderOpen size={32} className="mx-auto text-slate-300 mb-3" /><p className="text-sm font-semibold text-slate-400">No general files yet. Upload assets to organize by brand.</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {brandGroups.map(bg => (
                <div key={bg.brandId} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                  {/* Brand Card Header */}
                  <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-sm shrink-0">{bg.brandName.charAt(0)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-800 truncate">{bg.brandName}</p>
                      <p className="text-[10px] text-slate-400">{bg.files.length} file{bg.files.length !== 1 ? 's' : ''} · {bg.profileCount} profile{bg.profileCount !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  {/* File List in Card */}
                  <div className="divide-y divide-slate-50">
                    {bg.files.sort((a, b) => b.uploadedAt - a.uploadedAt).slice(0, 5).map(f => {
                      const isPdf = f.fileType === 'application/pdf';
                      return (
                        <div key={f.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50/60 transition-colors">
                          <div className={cn('w-7 h-7 rounded-md flex items-center justify-center shrink-0', isPdf ? 'bg-red-50' : 'bg-indigo-50')}>
                            {isPdf ? <FileText size={12} className="text-red-500" /> : <ImageIcon size={12} className="text-indigo-500" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-slate-700 truncate">{f.name}</p>
                            <p className="text-[10px] text-slate-400">{fmtDate(f.uploadedAt)}</p>
                          </div>
                          <div className="flex items-center gap-0.5">
                            <button onClick={() => {
                              if (!f.fileData) { toast.warning('No Data', 'This file has no binary data.'); return; }
                              const ext = f.fileType === 'application/pdf' ? '.pdf' : '.img';
                              base64Download(f.fileData, f.fileType, `${f.name}${ext}`);
                            }} className="p-1 hover:bg-emerald-50 rounded" title="Download"><Download size={12} className="text-emerald-600" /></button>
                            <button onClick={() => setEditFile(f)} className="p-1 hover:bg-indigo-50 rounded" title="Edit"><Edit2 size={12} className="text-indigo-500" /></button>
                            <button onClick={() => handleDel(f.id)} className="p-1 hover:bg-red-50 rounded" title="Delete"><Trash2 size={12} className="text-red-400" /></button>
                          </div>
                        </div>
                      );
                    })}
                    {bg.files.length > 5 && (
                      <div className="px-4 py-2 text-center"><span className="text-[10px] font-bold text-slate-400">+{bg.files.length - 5} more</span></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowUpload(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h3 className="text-lg font-bold flex items-center gap-2"><Upload size={18} className="text-teal-600" /> Upload {uploadForm.fileCategory === 'general' ? 'General File' : 'Ministry Permit'}</h3><button onClick={() => setShowUpload(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X size={18} /></button></div>
            <div className="space-y-3">
              {/* File Category Selector */}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">File Type</label>
                <div className="flex gap-2">
                  <button onClick={() => setUploadForm(p => ({ ...p, fileCategory: 'permit' }))} className={cn('flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border transition-all', uploadForm.fileCategory === 'permit' ? 'bg-purple-50 text-purple-700 border-purple-300' : 'bg-slate-50 text-slate-500 border-slate-200 hover:border-slate-300')}>
                    <Shield size={12} /> Permit
                  </button>
                  <button onClick={() => setUploadForm(p => ({ ...p, fileCategory: 'general' }))} className={cn('flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border transition-all', uploadForm.fileCategory === 'general' ? 'bg-teal-50 text-teal-700 border-teal-300' : 'bg-slate-50 text-slate-500 border-slate-200 hover:border-slate-300')}>
                    <FolderOpen size={12} /> General
                  </button>
                </div>
              </div>
              <div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Name *</label><input value={uploadForm.name} onChange={e => setUploadForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none" placeholder={uploadForm.fileCategory === 'general' ? 'e.g. Brand Logo 2026' : 'e.g. Food Permit 2026'} /></div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Link Profiles</label>
                <div className="max-h-32 overflow-y-auto bg-slate-50 border border-slate-200 rounded-lg p-2 space-y-1">
                  {profiles.map(p => (
                    <label key={p.id} className="flex items-center gap-2 px-2 py-1 hover:bg-white rounded cursor-pointer">
                      <input type="checkbox" checked={uploadForm.linkedProfileIds.includes(p.id)} onChange={() => setUploadForm(prev => ({ ...prev, linkedProfileIds: prev.linkedProfileIds.includes(p.id) ? prev.linkedProfileIds.filter(id => id !== p.id) : [...prev.linkedProfileIds, p.id] }))} className="rounded border-slate-300" />
                      <span className="text-xs">{p.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className={cn('flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed rounded-xl cursor-pointer transition-all', stagedVaultFile ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-teal-300')}>
                  {stagedVaultFile ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Upload size={16} className="text-slate-400" />}
                  <span className="text-sm font-medium">{stagedVaultFile ? 'File selected ✓' : 'Click to select file'}</span>
                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleFileSelect} />
                </label>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={handleUpload} disabled={!stagedVaultFile || !uploadForm.name} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700 disabled:opacity-50"><Upload size={14} /> Upload</button>
              <button onClick={() => setShowUpload(false)} className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-200">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editFile && <EditFileModal file={editFile} profiles={profiles} onSave={handleEditSave} onClose={() => setEditFile(null)} />}
    </div>
  );
}

function EditFileModal({ file, profiles, onSave, onClose }: { file: MinistryFile; profiles: MenuProfile[]; onSave: (f: MinistryFile) => void; onClose: () => void }) {
  const toast = useToast();
  const [name, setName] = useState(file.name);
  const [linked, setLinked] = useState(file.linkedProfileIds);
  const [stagedReplace, setStagedReplace] = useState<{ file: File; type: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const handleReplace = (e: React.ChangeEvent<HTMLInputElement>) => { const f = e.target.files?.[0]; if (!f || f.size > 5 * 1024 * 1024) return; setStagedReplace({ file: f, type: f.type }); };
  const handleSave = async () => {
    setSaving(true);
    try {
      let fileData = file.fileData;
      let fileType = file.fileType;
      if (stagedReplace) {
        const url = await uploadFile(stagedReplace.file, { moduleId: 'menu-manage' });
        fileData = url; fileType = stagedReplace.type;
      }
      onSave({ ...file, name, linkedProfileIds: linked, fileData, fileType });
    } catch { toast.error('Upload Failed', 'Could not upload replacement file.'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold flex items-center gap-2"><Edit2 size={18} className="text-teal-600" /> Edit File</h3>
        <div className="space-y-3">
          <div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Name</label><input value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none" /></div>
          <div>
            <label className={cn('flex items-center justify-center gap-2 px-3 py-3 border-2 border-dashed rounded-xl cursor-pointer', stagedReplace ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200')}>
              {stagedReplace ? <CheckCircle2 size={14} className="text-emerald-600" /> : <RefreshCw size={14} className="text-slate-400" />}
              <span className="text-xs">{stagedReplace ? 'New file selected' : 'Replace file (optional)'}</span>
              <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleReplace} />
            </label>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Linked Profiles</label>
            <div className="max-h-28 overflow-y-auto bg-slate-50 border border-slate-200 rounded-lg p-2 space-y-1">
              {profiles.map(p => (
                <label key={p.id} className="flex items-center gap-2 px-2 py-1 hover:bg-white rounded cursor-pointer">
                  <input type="checkbox" checked={linked.includes(p.id)} onChange={() => setLinked(prev => prev.includes(p.id) ? prev.filter(id => id !== p.id) : [...prev, p.id])} className="rounded border-slate-300" />
                  <span className="text-xs">{p.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700 disabled:opacity-50">{saving ? 'Uploading…' : 'Save'}</button>
          <button onClick={onClose} className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-200">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 4. PRO DIGITAL PREVIEW — Aggregator Style (Talabat/Snoonu)
// ═══════════════════════════════════════════════════════════════════════════════

interface CartLine { id: string; item: MenuItem; opts: { grp: string; opt: MenuItemOption }[]; qty: number; specialRequest?: string; }

function PreviewTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'menu-manage', 'preview', 'admin');
  const canBypassPreviewVisibilitySettings = user?.role === UserRole.ADMIN || isPlatformRole(user?.role as UserRole);
  const canUsePreviewCostInspector = user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN;
  const repo = getRepository();
  const loc = useMenuLocalization();
  const nav = useNavigate();
  const toast = useToast();
  const { state: shared } = React.useContext(MenuPreviewCtx);
  const [localProfiles, setLocalProfiles] = useState<MenuProfile[]>([]);
  const [previewBrands, setPreviewBrands] = useState<Brand[]>([]);
  const [selId, setSelId] = useState('');
  const [lang, setLang] = useState<'EN' | 'AR'>('EN');
  const [mode, setMode] = useState<'mobile' | 'desktop'>('desktop');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [drawer, setDrawer] = useState<MenuItem | null>(null);
  const [drawerSel, setDrawerSel] = useState<Map<string, Set<string>>>(new Map());
  const [drawerQtyMap, setDrawerQtyMap] = useState<Map<string, Map<string, number>>>(new Map());
  const [showCart, setShowCart] = useState(false);
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [previewView, setPreviewView] = useState<'visual' | 'list'>('visual');
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [detailItem, setDetailItem] = useState<MenuItem | null>(null);
  const [detailSpecialReq, setDetailSpecialReq] = useState('');
  const [previewLoading, setPreviewLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEnabled, setShareEnabled] = useState(true);
  const [shareExpiry, setShareExpiry] = useState('');
  const [shareSaving, setShareSaving] = useState(false);
  const [drawerSpecialReq, setDrawerSpecialReq] = useState('');
  const [drawerItemQty, setDrawerItemQty] = useState(1);
  const scrollSyncDone = useRef(false);
  const desktopMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const catNavDesktopRef = useRef<HTMLDivElement>(null);
  const catNavMobileRef = useRef<HTMLDivElement>(null);
  const desktopPreviewRef = useRef<HTMLDivElement>(null);
  const [previewH, setPreviewH] = useState<number>(0);
  const [emergencyShutdown, setEmergencyShutdown] = useState(false);
  const [pricingStrategy, setPricingStrategy] = useState<PricingStrategy>('LOWEST');
  const [previewSearchQuery, setPreviewSearchQuery] = useState('');
  const [allowPublicSharing, setAllowPublicSharing] = useState(false);
  const [allowNonAdminMenuExport, setAllowNonAdminMenuExport] = useState(false);
  const [financialSettings, setFinancialSettings] = useState<FinancialSettingsSlice>(DEFAULT_FINANCIAL_SETTINGS);
  const [costInspector, setCostInspector] = useState(false);
  const [costMap, setCostMap] = useState<Record<string, { baseCost: number; options: Record<string, number> }>>({});
  const [exportProfile, setExportProfile] = useState<MenuProfile | null>(null);
  const financialVisibility = useFinancialVisibility(user, financialSettings);

  const applyPreviewSettings = useCallback((raw?: Partial<MenuSettings> | Record<string, unknown> | null) => {
    const nextSettings = toMenuSettingsState(raw);
    setEmergencyShutdown(!!nextSettings.emergencyShutdown);
    setPricingStrategy(nextSettings.pricingStrategy || 'LOWEST');
    setAllowPublicSharing(!!nextSettings.allowPublicSharing);
    setAllowNonAdminMenuExport(!!nextSettings.allowNonAdminMenuExport);
    setFinancialSettings(toFinancialSettingsSlice(nextSettings));
  }, []);

  // ── Campaign Bridge state ──────────────────────────────────────
  type EligibleCampaign = { id: string; name: string; status: string; discountType: string; discountValue: number; fundingType: string; requiredItemCount?: number; aggregators: string[]; validFrom: number; validTo: number; importantRequirements?: string };
  type CampaignLink = { campaignId: string; campaignName: string; submissionStatus: string; currentStage: string | null };
  const [eligibleCampaigns, setEligibleCampaigns] = useState<EligibleCampaign[]>([]);
  const [campaignLinks, setCampaignLinks] = useState<Record<string, CampaignLink[]>>({});
  const [campaignPushItem, setCampaignPushItem] = useState<MenuItem | null>(null);
  const [campaignPushResult, setCampaignPushResult] = useState<{ submissionId: string; campaignName: string; itemName: string } | null>(null);
  const [pushingCampaignId, setPushingCampaignId] = useState<string | null>(null);
  const [linkBadgeItem, setLinkBadgeItem] = useState<string | null>(null);
  const [aggAccessible, setAggAccessible] = useState(false);

  // Cross-module visibility: org has aggregator (API reachable) + user has aggregator access
  const canPushToCampaign = aggAccessible && (
    !user?.assignedModules?.length || user.assignedModules.includes('aggregator' as any)
  );

  // Share/export bypass must follow actual admin roles, not preview tab variants.
  const canShare = canBypassPreviewVisibilitySettings || allowPublicSharing;
  const canExportMenu = canBypassPreviewVisibilitySettings || allowNonAdminMenuExport;

  const loadPreviewSettings = useCallback(() => {
    if (isRemoteMode()) {
      fetchMenuSettings().then(s => {
        applyPreviewSettings(s);
      }).catch(() => {
        applyPreviewSettings(DEFAULT_MENU_SETTINGS);
      });
      return;
    }
    repo.getAll<MenuSettings>('menu-settings').then(all => {
      applyPreviewSettings(all[0] || DEFAULT_MENU_SETTINGS);
    }).catch(() => {
      applyPreviewSettings(DEFAULT_MENU_SETTINGS);
    });
  }, [applyPreviewSettings, repo]);

  // Fetch emergency shutdown state + pricing strategy + sharing permission from settings
  useEffect(() => {
    loadPreviewSettings();
    // Also load brands for logo display
    repo.getAll<Brand>('brands').then(setPreviewBrands).catch(() => {});
  }, [loadPreviewSettings, repo]);

  // Re-fetch sharing permission when settings change
  useEffect(() => {
    const handleSettingsUpdated = (event: Event) => {
      const raw = getSettingsFromEvent(event);
      if (raw) {
        applyPreviewSettings(raw);
        return;
      }
      loadPreviewSettings();
    };
    window.addEventListener(MENU_SETTINGS_UPDATED, handleSettingsUpdated);
    return () => window.removeEventListener(MENU_SETTINGS_UPDATED, handleSettingsUpdated);
  }, [applyPreviewSettings, loadPreviewSettings]);

  useEffect(() => {
    if (!canExportMenu && exportProfile) {
      setExportProfile(null);
    }
  }, [canExportMenu, exportProfile]);

  // Local stub — preview tab doesn't load library availability data
  const isGloballyHidden = (_opt: MenuItemOption, _grp: OptionGroup) => false;

  const toggleExpand = (setFn: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) => {
    setFn(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Shared context instant load (0ms) or API fallback ──────────────────
  // Digital Preview only shows PUBLISHED profiles — DRAFT menus must not
  // appear in any customer-facing or preview context.
  const profiles = useMemo(() => {
    const source = shared.profiles.length > 0 ? shared.profiles : localProfiles;
    const ub = user?.assignedBrands;
    const scoped = isAdmin ? source : source.filter(pr => !ub || ub.length === 0 || ub.includes(pr.brandId));
    return scoped.filter(pr => pr.status === MenuProfileStatus.PUBLISHED);
  }, [shared.profiles, localProfiles, user, isAdmin]);

  // Fallback: fetch from API only when shared context is empty
  useEffect(() => {
    if (shared.profiles.length > 0) { setPreviewLoading(false); return; }
    (async () => {
      await seedDemoDataIfNeeded();
      const [p, b] = await Promise.all([repo.getAll<MenuProfile>('menu-profiles'), repo.getAll<Brand>('brands')]);
      setLocalProfiles(p);
      setPreviewBrands(b);
      setPreviewLoading(false);
    })();
  }, [shared.profiles.length]);

  // Listen for data updates to refresh fallback cache
  useEffect(() => {
    const handler = async () => {
      if (shared.profiles.length > 0) return; // context handles it
      const p = await repo.getAll<MenuProfile>('menu-profiles');
      setLocalProfiles(p);
    };
    window.addEventListener(MENU_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(MENU_DATA_UPDATED_EVENT, handler);
  }, [shared.profiles.length]);

  // Auto-select first profile
  useEffect(() => {
    if (profiles.length > 0 && !selId) setSelId(profiles[0].id);
  }, [profiles, selId]);

  // If user was editing a specific profile, auto-select it
  useEffect(() => {
    if (shared.activeProfile && profiles.some(p => p.id === shared.activeProfile!.id)) {
      setSelId(shared.activeProfile.id);
    }
  }, [shared.activeProfile?.id]);

  // ── Resolve profile: prefer live draft from shared context ─────────────
  const profile = useMemo(() => {
    if (shared.activeProfile && selId === shared.activeProfile.id) return shared.activeProfile;
    return profiles.find(p => p.id === selId);
  }, [profiles, selId, shared.activeProfile]);

  // Brand logo for the active profile
  const brandLogoUrl = useMemo(() => {
    if (!profile) return undefined;
    return previewBrands.find(b => b.id === profile.brandId)?.logoUrl || undefined;
  }, [profile, previewBrands]);

  const isAR = lang === 'AR';
  const nm = (en: string, ar: string) => isAR ? (ar || en) : (en || ar);

  // Preview cost starts OFF by default. Admin can turn it on manually.
  useEffect(() => {
    setCostInspector(false);
  }, [canUsePreviewCostInspector, financialVisibility.showCostData, profile?.id]);

  // ── Campaign Bridge: fetch eligible campaigns + linked items ──
  useEffect(() => {
    if (!profile?.brandId || !isRemoteMode()) { setEligibleCampaigns([]); setCampaignLinks({}); setAggAccessible(false); return; }
    apiFetch<{ data: EligibleCampaign[] }>(`/api/aggregator/campaigns/eligible/${profile.brandId}`)
      .then(j => { setAggAccessible(true); setEligibleCampaigns(j.data ?? []); })
      .catch(() => { setAggAccessible(false); setEligibleCampaigns([]); });
    apiFetch<{ data: Record<string, CampaignLink[]> }>(`/api/aggregator/campaign-links/${profile.id}`)
      .then(j => setCampaignLinks(j.data ?? {}))
      .catch(() => setCampaignLinks({}));
  }, [profile?.id, profile?.brandId]);

  // Campaign push handler
  const handleCampaignPush = async (campaign: EligibleCampaign) => {
    if (!campaignPushItem || !profile || pushingCampaignId) return;
    setPushingCampaignId(campaign.id);
    try {
      // Build imported options from current drawer selections
      const importedOptions = (campaignPushItem.optionGroups || []).map(grp => ({
        id: grp.id, nameEN: grp.nameEN, nameAR: grp.nameAR,
        minSelection: grp.minSelection, maxSelection: grp.maxSelection,
        options: grp.options.map(o => ({ id: o.id, nameEN: o.nameEN, nameAR: o.nameAR, extraPrice: o.extraPrice })),
      }));
      const json = await apiFetch<{ data: { submissionId: string; itemId: string; campaignId: string; campaignName: string; isNewSubmission: boolean } }>('/api/aggregator/quick-push', {
        method: 'POST',
        body: JSON.stringify({
          campaignId: campaign.id,
          campaignName: campaign.name,
          brandId: profile.brandId,
          brandName: profile.brandName,
          menuProfileId: profile.id,
          menuProfileName: profile.name,
          menuItemId: campaignPushItem.id,
          itemName: campaignPushItem.nameEN,
          itemNameAR: campaignPushItem.nameAR,
          itemPrice: campaignPushItem.basePrice,
          itemDescription: campaignPushItem.descriptionEN,
          itemDescriptionAR: campaignPushItem.descriptionAR,
          itemImage: campaignPushItem.image,
          importedOptions,
        }),
      });
      // Update links locally
      setCampaignLinks(prev => ({
        ...prev,
        [campaignPushItem.id]: [...(prev[campaignPushItem.id] || []), { campaignId: campaign.id, campaignName: campaign.name, submissionStatus: 'PENDING', currentStage: null }],
      }));
      setCampaignPushResult({ submissionId: json.data.submissionId, campaignName: campaign.name, itemName: campaignPushItem.nameEN });
      setCampaignPushItem(null);
    } catch (err: any) { toast.error('Push Failed', err?.message || 'Could not send item to campaign.'); }
    finally { setPushingCampaignId(null); }
  };

  // ── Memoized menu structure for rendering (Waterfall: Profile → Category → Item) ──
  const now = useMemo(() => new Date(), []);
  const profileAvailable = useMemo(() => !emergencyShutdown && isAvailableNow(profile?.availabilitySchedule, now), [profile, now, emergencyShutdown]);
  const profileNextSlot = useMemo(() => emergencyShutdown ? 'Emergency shutdown active' : nextSlotLabel(profile?.availabilitySchedule, now), [profile, now, emergencyShutdown]);
  const visibleCategories = useMemo(() => {
    if (!profile) return [];
    const cats = profile.categories.filter(c => !c.hidden && !isUnassignedCat(c.id)).map(cat => {
      const catAvail = profileAvailable && isAvailableNow(cat.availabilitySchedule, now);
      return {
        ...cat,
        visibleItems: cat.items.filter(i => !i.hidden).map(i => ({
          ...i,
          _available: catAvail && isAvailableNow(i.availabilitySchedule, now),
        })),
        _available: catAvail,
      };
    });
    // Move OFFERS category to the top (campaign-injected items)
    cats.sort((a, b) => {
      const aOffer = (a.nameEN || '').toUpperCase() === 'OFFERS' ? -1 : 0;
      const bOffer = (b.nameEN || '').toUpperCase() === 'OFFERS' ? -1 : 0;
      return aOffer - bOffer;
    });
    return cats;
  }, [profile, now, profileAvailable]);

  // Set first category as active when profile changes, or scroll-to-sync
  useEffect(() => {
    if (!profile) { setActiveCat(null); return; }
    if (!scrollSyncDone.current && shared.activeCatId && profile.categories.some(c => c.id === shared.activeCatId)) {
      setActiveCat(shared.activeCatId);
      scrollSyncDone.current = true;
      requestAnimationFrame(() => {
        const el = document.getElementById(`mob-cat-${shared.activeCatId}`) || document.getElementById(`prev-cat-${shared.activeCatId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } else if (profile.categories.filter(c => !isUnassignedCat(c.id)).length > 0) {
      const visCats = profile.categories.filter(c => !isUnassignedCat(c.id));
      setActiveCat(prev => prev && visCats.some(c => c.id === prev) ? prev : visCats[0].id);
    }
  }, [profile?.id]);

  // Calculate available height for the desktop preview so hero+category stay pinned
  useEffect(() => {
    const calc = () => {
      const el = desktopPreviewRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setPreviewH(window.innerHeight - top - 16);
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [profile?.id, previewView, mode]);

  // ── ScrollSpy: auto-highlight active category pill on scroll ──
  const scrollSpyCatIds = useMemo(() => visibleCategories.map(c => c.id), [visibleCategories]);
  const { scrollPillIntoView } = useScrollSpy({
    scrollRef: mode === 'mobile' ? mobileMenuRef : desktopMenuRef,
    navRef: mode === 'mobile' ? catNavMobileRef : catNavDesktopRef,
    categoryIds: scrollSpyCatIds,
    idPrefix: mode === 'mobile' ? 'mob-cat-' : 'prev-cat-',
    enabled: !!profile && previewView === 'visual',
    onActiveCatChange: setActiveCat,
  });
  useEffect(() => { if (activeCat) scrollPillIntoView(activeCat as string); }, [activeCat, mode]);

  // Hero banner data (Coffee Beanery style)
  const heroImage = useMemo(() => {
    if (!profile) return '';
    for (const cat of profile.categories) {
      for (const item of cat.items) { if (item.image) return item.image; }
    }
    return '';
  }, [profile]);

  const promoChips = useMemo(() => {
    if (!profile) return [];
    const chips: { label: string; color: string }[] = [];
    const seen = new Set<string>();
    for (const cat of profile.categories) {
      for (const item of cat.items) {
        if (item.campaignMeta && !isPromoExpired(item)) {
          const dl = discountLabel(item, loc?.currencySymbol ?? '');
          if (dl && !seen.has(dl)) { seen.add(dl); chips.push({ label: dl, color: 'bg-rose-500' }); }
        }
      }
    }
    return chips.slice(0, 3);
  }, [profile, loc.currencySymbol]);

  const totalItemCount = useMemo(() => profile ? profile.categories.filter(c => !isUnassignedCat(c.id)).reduce((a, c) => a + c.items.filter(i => !i.hidden).length, 0) : 0, [profile]);

  // Search-filtered categories for rendering
  const filteredPreviewCats = useMemo(() => {
    if (!previewSearchQuery.trim()) return visibleCategories;
    const q = previewSearchQuery.trim().toLowerCase();
    return visibleCategories.map(cat => {
      const matchedItems = cat.visibleItems.filter(i =>
        i.nameEN.toLowerCase().includes(q) || (i.nameAR || '').toLowerCase().includes(q) ||
        (i.descriptionEN || '').toLowerCase().includes(q) || (i.descriptionAR || '').toLowerCase().includes(q)
      );
      if (matchedItems.length > 0) return { ...cat, visibleItems: matchedItems };
      if ((cat.nameEN || '').toLowerCase().includes(q) || (cat.nameAR || '').toLowerCase().includes(q)) return cat;
      return null;
    }).filter(Boolean) as typeof visibleCategories;
  }, [visibleCategories, previewSearchQuery]);

  // Schedule label for display
  const profileScheduleText = useMemo(() => {
    if (!profile?.availabilitySchedule || profile.availabilitySchedule.alwaysOn) return null;
    return scheduleLabel(profile.availabilitySchedule);
  }, [profile?.availabilitySchedule]);

  // Sync sharing state when profile changes
  useEffect(() => {
    if (profile) {
      setShareEnabled(profile.publicLinkEnabled !== false);
      setShareExpiry(profile.publicLinkExpiry ? new Date(profile.publicLinkExpiry).toISOString().slice(0, 16) : '');
    }
  }, [profile?.id, profile?.publicLinkEnabled, profile?.publicLinkExpiry]);

  const publicLinkUrl = profile ? `${window.location.origin}/public-preview/${profile.id}` : '';

  const handleSaveSharing = async () => {
    if (!profile) return;
    setShareSaving(true);
    const payload = {
      publicLinkEnabled: shareEnabled,
      publicLinkExpiry: shareExpiry ? new Date(shareExpiry).getTime() : null,
    };
    try {
      await repo.save('menu-profiles', profile.id, { ...profile, ...payload });
      toast.success('Saved', 'Sharing settings updated.');
      // Refresh profiles
      if (shared.profiles.length === 0) {
        const p = await repo.getAll<MenuProfile>('menu-profiles');
        setLocalProfiles(p);
      }
    } catch { toast.error('Error', 'Failed to save sharing settings.'); }
    setShareSaving(false);
  };

  const handleDeactivateNow = async () => {
    if (!profile) return;
    setShareSaving(true);
    const payload = {
      publicLinkEnabled: false,
      publicLinkExpiry: Date.now(),
    };
    try {
      await repo.save('menu-profiles', profile.id, { ...profile, ...payload });
      setShareEnabled(false);
      setShareExpiry(new Date(Date.now()).toISOString().slice(0, 16));
      toast.success('Deactivated', 'Public link has been deactivated immediately.');
      if (shared.profiles.length === 0) {
        const p = await repo.getAll<MenuProfile>('menu-profiles');
        setLocalProfiles(p);
      }
    } catch { toast.error('Error', 'Failed to deactivate link.'); }
    setShareSaving(false);
  };

  // Drawer logic with min/max enforcement — always show detail first
  const openDrawer = (item: MenuItem) => {
    const groups = item.optionGroups || [];
    if (groups.length === 0) {
      // Show item detail drawer for ALL items (no direct-add)
      setDetailSpecialReq('');
      setDetailItem(item);
      return;
    }
    setDrawer(item);
    setDrawerSel(new Map());
    setDrawerQtyMap(new Map());
    setDrawerSpecialReq('');
    setDrawerItemQty(1);
  };

  // Add-to-cart from detail drawer (items with no options)
  const addFromDetail = () => {
    if (!detailItem) return;
    const sr = detailSpecialReq.trim() || undefined;
    setCart(prev => {
      // If no special request, merge with existing identical item
      if (!sr) {
        const ex = prev.find(c => c.item.id === detailItem.id && c.opts.length === 0 && !c.specialRequest);
        if (ex) return prev.map(c => c.id === ex.id ? { ...c, qty: c.qty + 1 } : c);
      }
      return [...prev, { id: genId(), item: detailItem, opts: [], qty: 1, specialRequest: sr }];
    });
    setDetailItem(null);
    setDetailSpecialReq('');
  };

  const toggleOpt = (grpId: string, optId: string, max: number) => {
    setDrawerSel(prev => {
      const next = new Map<string, Set<string>>(prev);
      const existing = next.get(grpId);
      const set = existing ? new Set<string>(existing) : new Set<string>();
      if (set.has(optId)) {
        set.delete(optId);
      } else {
        if (max === 1) { set.clear(); set.add(optId); }
        else if (max > 0 && set.size >= max) return prev;
        else set.add(optId);
      }
      next.set(grpId, set);
      return next;
    });
  };

  const changeOptQty = (grpId: string, optId: string, delta: number, max: number) => {
    setDrawerQtyMap(prev => {
      const next = new Map(prev);
      const grpQ = new Map(next.get(grpId) || []);
      const cur = grpQ.get(optId) || 0;
      const nv = Math.max(0, cur + delta);
      if (delta > 0 && max > 0) {
        let total = 0; grpQ.forEach(v => { total += v; });
        if (total >= max) return prev;
      }
      grpQ.set(optId, nv);
      next.set(grpId, grpQ);
      return next;
    });
  };

  const drawerValid = useMemo(() => {
    if (!drawer) return false;
    for (const grp of (drawer.optionGroups || [])) {
      const min = grp.minSelection ?? 0;
      if (grp.allowMultipleQty) {
        const qm = drawerQtyMap.get(grp.id);
        let total = 0; qm?.forEach(v => { total += v; });
        if (total < min) return false;
      } else {
        const selCount = drawerSel.get(grp.id)?.size ?? 0;
        if (selCount < min) return false;
      }
    }
    return true;
  }, [drawer, drawerSel, drawerQtyMap]);

  const drawerExtraPrice = useMemo(() => {
    if (!drawer) return 0;
    let total = 0;
    for (const grp of (drawer.optionGroups || [])) {
      if (grp.allowMultipleQty) {
        const qm = drawerQtyMap.get(grp.id);
        if (!qm) continue;
        qm.forEach((qty, oid) => { const opt = grp.options.find(o => o.id === oid); if (opt) total += opt.extraPrice * qty; });
      } else {
        const sel = drawerSel.get(grp.id);
        if (!sel) continue;
        for (const oid of sel) {
          const opt = grp.options.find(o => o.id === oid);
          if (opt) total += opt.extraPrice;
        }
      }
    }
    return total;
  }, [drawer, drawerSel, drawerQtyMap]);

  const refreshCostMap = useCallback(() => {
    if (!costInspector || !selId) { setCostMap({}); return; }
    apiFetch<{ data: Record<string, { baseCost: number; options: Record<string, number> }> }>(`/api/menu/recipes/cost-map/${selId}`)
      .then(res => setCostMap(res.data || {}))
      .catch(() => setCostMap({}));
  }, [costInspector, selId]);

  useEffect(() => { refreshCostMap(); }, [refreshCostMap]);
  useEffect(() => {
    window.addEventListener(MENU_DATA_UPDATED_EVENT, refreshCostMap);
    return () => window.removeEventListener(MENU_DATA_UPDATED_EVENT, refreshCostMap);
  }, [refreshCostMap]);

  // Compute real-time cost breakdown for selected drawer options
  const drawerCostBreakdown = useMemo(() => {
    if (!drawer || !costInspector) return null;
    const entry = costMap[drawer.id];
    if (!entry) return null;
    const baseCost = entry.baseCost;
    let optionsCost = 0;
    const optionDetails: { name: string; cost: number; sellPrice: number }[] = [];
    (drawer.optionGroups || []).forEach(grp => {
      const isQtyMode = grp.allowMultipleQty;
      grp.options.forEach(opt => {
        let qty = 0;
        if (isQtyMode) {
          const qm = drawerQtyMap.get(grp.id);
          qty = qm?.get(opt.id) || 0;
        } else {
          const sel = drawerSel.get(grp.id);
          qty = sel && sel.has(opt.id) ? 1 : 0;
        }
        if (qty > 0) {
          const optCost = (entry.options[opt.id] || 0) * qty;
          optionsCost += optCost;
          optionDetails.push({ name: opt.nameEN, cost: optCost, sellPrice: opt.extraPrice * qty });
        }
      });
    });
    const totalCost = baseCost + optionsCost;
    const totalSell = drawer.basePrice + drawerExtraPrice;
    const margin = totalSell > 0 ? ((totalSell - totalCost) / totalSell) * 100 : 0;
    const foodCostPct = totalSell > 0 ? (totalCost / totalSell) * 100 : 0;
    return { baseCost, optionDetails, totalCost, totalSell, margin, foodCostPct };
  }, [drawer, costInspector, costMap, drawerSel, drawerQtyMap, drawerExtraPrice]);

  // Cost breakdown for simple detail items (no option groups)
  const detailCostBreakdown = useMemo(() => {
    if (!detailItem || !costInspector) return null;
    const entry = costMap[detailItem.id];
    if (!entry) return null;
    const baseCost = entry.baseCost;
    const totalSell = detailItem.basePrice;
    const foodCostPct = totalSell > 0 ? (baseCost / totalSell) * 100 : 0;
    const margin = totalSell > 0 ? ((totalSell - baseCost) / totalSell) * 100 : 0;
    return { baseCost, totalCost: baseCost, totalSell, margin, foodCostPct };
  }, [detailItem, costInspector, costMap]);

  const confirmDrawer = () => {
    if (!drawer || !drawerValid) return;
    const opts: CartLine['opts'] = [];
    for (const grp of (drawer.optionGroups || [])) {
      if (grp.allowMultipleQty) {
        const qm = drawerQtyMap.get(grp.id);
        if (!qm) continue;
        qm.forEach((qty, oid) => { const opt = grp.options.find(o => o.id === oid); if (opt) { for (let i = 0; i < qty; i++) opts.push({ grp: grp.nameEN, opt }); } });
      } else {
        const sel = drawerSel.get(grp.id);
        if (!sel) continue;
        for (const oid of sel) {
          const opt = grp.options.find(o => o.id === oid);
          if (opt) opts.push({ grp: grp.nameEN, opt });
        }
      }
    }
    setCart(prev => [...prev, { id: genId(), item: drawer, opts, qty: drawerItemQty, specialRequest: drawerSpecialReq.trim() || undefined }]);
    setDrawer(null); setDrawerSel(new Map()); setDrawerQtyMap(new Map()); setDrawerSpecialReq(''); setDrawerItemQty(1);
  };

  const updateQty = (cid: string, d: number) => setCart(prev => prev.map(c => c.id === cid ? { ...c, qty: Math.max(0, c.qty + d) } : c).filter(c => c.qty > 0));

  const cartTotal = cart.reduce((s, c) => s + (c.item.basePrice + c.opts.reduce((a, o) => a + o.opt.extraPrice, 0)) * c.qty, 0);
  const cartCount = cart.reduce((a, c) => a + c.qty, 0);

  // ── Shared item card renderer ──
  const renderItemCard = (item: MenuItem, layout: 'grid' | 'list') => (
    <div key={item.id}
      className={cn(
        'bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-all active:scale-[0.99]',
        layout === 'list' && 'flex'
      )}
      onClick={() => openDrawer(item)}
    >
      {layout === 'grid' ? (
        <>
          <div className="w-full h-36 relative bg-gradient-to-br from-slate-100 to-slate-50">
            {item.image ? <PreviewImage src={item.image} className="w-full h-full" /> : <div className="w-full h-full flex items-center justify-center"><Package size={32} className="text-slate-200" /></div>}
            <button className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-lg hover:bg-indigo-700"><Plus size={16} /></button>
            {costInspector && (() => {
              const ce = costMap[item.id];
              if (!ce) return null;
              const sell = item.campaignMeta && !isPromoExpired(item) ? item.campaignMeta.promoPrice : item.basePrice;
              const fc = sell > 0 ? (ce.baseCost / sell) * 100 : 0;
              const color = fc <= 30 ? 'bg-emerald-500' : fc <= 40 ? 'bg-amber-500' : 'bg-red-500';
              return <div className={cn('absolute top-2 left-2 px-2 py-0.5 rounded-md text-[10px] font-bold text-white shadow-md', color)}>FC {fc.toFixed(0)}%</div>;
            })()}
            {canPushToCampaign && campaignLinks[item.id]?.length > 0 && (
              <div className="absolute top-2 right-10 px-2 py-0.5 rounded-md text-[9px] font-bold bg-violet-600 text-white shadow-md flex items-center gap-1">
                <PackageCheck size={9} /> {campaignLinks[item.id].length} Campaign{campaignLinks[item.id].length > 1 ? 's' : ''}
              </div>
            )}
          </div>
          <div className="p-4 space-y-1">
            <p className="text-sm font-bold text-slate-900 leading-tight truncate">{nm(item.nameEN, item.nameAR)}</p>
            {(isAR ? item.descriptionAR : item.descriptionEN) && <p className="text-[11px] text-slate-400 line-clamp-4">{nm(item.descriptionEN || '', item.descriptionAR || '')}</p>}
            {item.campaignMeta && !isPromoExpired(item) && (
              <span className="inline-block text-[8px] px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 font-bold">{item.campaignMeta.campaignName || 'CAMPAIGN'}</span>
            )}
            <div className="flex items-center gap-2 flex-wrap pt-1">
              {item.campaignMeta && !isPromoExpired(item) ? (
                <>
                  <span className="text-[10px] text-slate-400 line-through">{loc.currencySymbol} {item.campaignMeta.originalPrice.toFixed(2)}</span>
                  <span className="text-sm font-bold text-emerald-600">{loc.currencySymbol} {item.campaignMeta.promoPrice.toFixed(2)}</span>
                  <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold">{discountLabel(item, loc.currencySymbol)}</span>
                </>
              ) : (
                (() => { const fp = formatCardPrice(item, loc.currencySymbol, pricingStrategy); return fp.text ? <>{fp.prefix && <span className="text-[10px] font-normal text-slate-400 mr-1">{fp.prefix}</span>}<span className="text-sm font-bold text-indigo-600">{fp.text}</span></> : null; })()
              )}
              {(() => { const fp = formatCardPrice(item, loc.currencySymbol, pricingStrategy); return fp.badge ? <span className="text-[9px] px-2 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-full font-bold">{fp.badge}</span> : (item.optionGroups || []).length > 0 ? <span className="text-[9px] px-2 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-full font-bold">Customizable</span> : null; })()}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={cn('flex-1 p-4 flex flex-col justify-between min-w-0', isAR && 'items-end')}>
            <div>
              <p className="text-[15px] font-bold text-slate-900 leading-tight">{nm(item.nameEN, item.nameAR)}</p>
              {(isAR ? item.descriptionAR : item.descriptionEN) && <p className="text-xs text-slate-400 mt-1 line-clamp-4">{nm(item.descriptionEN || '', item.descriptionAR || '')}</p>}
              {item.campaignMeta && !isPromoExpired(item) && (
                <span className="inline-block text-[8px] px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 font-bold mt-1">{item.campaignMeta.campaignName || 'CAMPAIGN'}</span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-3">
              {item.campaignMeta && !isPromoExpired(item) ? (
                <>
                  <span className="text-[10px] text-slate-400 line-through">{loc.currencySymbol} {item.campaignMeta.originalPrice.toFixed(2)}</span>
                  <span className="text-sm font-bold text-emerald-600">{loc.currencySymbol} {item.campaignMeta.promoPrice.toFixed(2)}</span>
                  <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold">{discountLabel(item, loc.currencySymbol)}</span>
                </>
              ) : (
                (() => { const fp = formatCardPrice(item, loc.currencySymbol, pricingStrategy); return fp.text ? <>{fp.prefix && <span className="text-[10px] font-normal text-slate-400 mr-1">{fp.prefix}</span>}<span className="text-sm font-bold text-indigo-600">{fp.text}</span></> : null; })()
              )}
              {(() => { const fp = formatCardPrice(item, loc.currencySymbol, pricingStrategy); return fp.badge ? <span className="text-[10px] text-slate-400">{fp.badge}</span> : (item.optionGroups || []).length > 0 ? <span className="text-[10px] text-slate-400">Customizable</span> : null; })()}
            </div>
          </div>
          <div className="w-28 h-28 shrink-0 relative">
            {item.image ? <PreviewImage src={item.image} className="w-full h-full" /> : <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center"><Package size={24} className="text-slate-200" /></div>}
            <button className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-lg hover:bg-indigo-700"><Plus size={16} /></button>
          </div>
        </>
      )}
    </div>
  );

  // ── Modifier drawer content (shared between desktop & mobile) ──
  const renderModifierContent = () => {
    if (!drawer) return null;
    return (
      <>
        {drawer.image && <div className="w-full h-48 relative"><PreviewImage src={drawer.image} className="w-full h-full" /><button onClick={() => setDrawer(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center backdrop-blur-sm"><X size={16} /></button></div>}
        <div className="px-5 pt-4 pb-2">
          {!drawer.image && <div className="flex justify-end mb-2"><button onClick={() => setDrawer(null)} className="p-1.5 hover:bg-slate-100 rounded-full"><X size={18} /></button></div>}
          <h3 className="text-xl font-bold text-slate-900">{nm(drawer.nameEN, drawer.nameAR)}</h3>
          {(isAR ? drawer.descriptionAR : drawer.descriptionEN) && <p className="text-sm text-slate-500 mt-1">{nm(drawer.descriptionEN || '', drawer.descriptionAR || '')}</p>}
          {!shouldHidePrice(drawer) ? (
            <p className="text-lg font-bold text-indigo-600 mt-2">{loc.currencySymbol} {drawer.basePrice.toFixed(2)}</p>
          ) : (
            (() => { const fp = formatCardPrice(drawer, loc.currencySymbol, pricingStrategy); return fp.text ? <p className="text-lg font-bold text-indigo-600 mt-2">{fp.prefix && <span className="text-sm font-normal text-slate-400 mr-1">{fp.prefix}</span>}{fp.text}</p> : fp.badge ? <p className="text-sm text-slate-400 mt-2">{fp.badge}</p> : null; })()
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-5">
          {(drawer.optionGroups || []).filter(g => !g.hidden).map(grp => {
            const min = grp.minSelection ?? 0;
            const max = grp.maxSelection ?? 0;
            const selSet = drawerSel.get(grp.id) || new Set();
            const qm = drawerQtyMap.get(grp.id) || new Map<string, number>();
            const isQtyMode = grp.allowMultipleQty;
            let qtyTotal = 0; if (isQtyMode) qm.forEach(v => { qtyTotal += v; });
            const selCount = isQtyMode ? qtyTotal : selSet.size;
            const metMin = selCount >= min;
            const isReq = min > 0;
            return (
              <div key={grp.id}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-bold text-slate-800">{nm(grp.nameEN, grp.nameAR)}</p>
                    <p className="text-[11px] text-slate-400">{isReq ? 'Required' : 'Optional'}{min > 0 && ` · Min ${min}`}{max > 0 && ` · Max ${max}`}</p>
                  </div>
                  {isReq && !metMin && <span className="px-2 py-0.5 bg-red-50 border border-red-200 text-red-600 rounded text-[9px] font-bold">Required</span>}
                  {metMin && isReq && <span className="px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-600 rounded text-[9px] font-bold">✓</span>}
                </div>
                <div className="space-y-1.5">
                  {grp.options.filter(o => !o.hidden && !isGloballyHidden(o, grp)).map(opt => {
                    if (isQtyMode) {
                      const qty = qm.get(opt.id) || 0;
                      const atMax = max > 0 && qtyTotal >= max;
                      return (
                        <div key={opt.id} className={cn('w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all',
                          qty > 0 ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200')}>
                          <span className="text-sm font-medium text-slate-800">{nm(opt.nameEN, opt.nameAR)}</span>
                          <div className="flex items-center gap-2">
                            {opt.extraPrice > 0 && <span className="text-xs text-indigo-600 font-bold">+{loc.currencySymbol} {opt.extraPrice.toFixed(2)}{qty > 1 ? ` × ${qty}` : ''}</span>}
                            <div className="flex items-center gap-1">
                              <button onClick={() => changeOptQty(grp.id, opt.id, -1, max)} disabled={qty === 0}
                                className={cn('w-6 h-6 rounded-full border flex items-center justify-center transition-colors',
                                  qty === 0 ? 'border-slate-200 text-slate-300' : 'border-indigo-300 text-indigo-600 hover:bg-indigo-100')}><Minus size={12} /></button>
                              <span className="w-5 text-center text-xs font-bold text-slate-700">{qty}</span>
                              <button onClick={() => changeOptQty(grp.id, opt.id, 1, max)} disabled={atMax}
                                className={cn('w-6 h-6 rounded-full border flex items-center justify-center transition-colors',
                                  atMax ? 'border-slate-200 text-slate-300' : 'border-indigo-300 text-indigo-600 hover:bg-indigo-100')}><Plus size={12} /></button>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    const isSel = selSet.has(opt.id);
                    const disabled = !isSel && max > 0 && selCount >= max && max !== 1;
                    return (
                      <button key={opt.id} onClick={() => !disabled && toggleOpt(grp.id, opt.id, max)} disabled={disabled}
                        className={cn('w-full flex items-center justify-between px-4 py-3 rounded-xl border text-left transition-all',
                          isSel ? 'border-indigo-400 bg-indigo-50' : disabled ? 'border-slate-100 bg-slate-50 opacity-50' : 'border-slate-200 hover:border-indigo-300')}>
                        <span className="text-sm font-medium text-slate-800">{nm(opt.nameEN, opt.nameAR)}</span>
                        <div className="flex items-center gap-2">
                          {opt.extraPrice > 0 && <span className="text-xs text-indigo-600 font-bold">+{loc.currencySymbol} {opt.extraPrice.toFixed(2)}</span>}
                          <div className={cn('w-5 h-5 rounded-full border-2 flex items-center justify-center', isSel ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300')}>{isSel && <Check size={12} className="text-white" />}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        {/* Special Request */}
        {drawer.allowSpecialRequests !== false && (
          <div className="px-5 pb-3">
            <div className="flex items-center gap-2 mb-1.5">
              <p className="text-sm font-bold text-slate-700">Special Request</p>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 font-medium">Optional</span>
            </div>
            <textarea
              value={drawerSpecialReq}
              onChange={e => setDrawerSpecialReq(e.target.value.slice(0, 200))}
              placeholder="Leave any instructions to the restaurant"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
              rows={2}
            />
            <p className="text-[9px] text-slate-300 text-right mt-0.5">{drawerSpecialReq.length}/200</p>
          </div>
        )}
        <div className="px-4 pb-4 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 border border-slate-200 rounded-xl px-1.5 py-1">
              <button onClick={() => setDrawerItemQty(q => Math.max(1, q - 1))} disabled={drawerItemQty <= 1}
                className={cn('w-7 h-7 rounded-lg flex items-center justify-center transition-colors', drawerItemQty <= 1 ? 'text-slate-300' : 'text-slate-600 hover:bg-slate-100')}><Minus size={14} /></button>
              <span className="w-6 text-center text-sm font-bold text-slate-800">{drawerItemQty}</span>
              <button onClick={() => setDrawerItemQty(q => q + 1)}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors"><Plus size={14} /></button>
            </div>
            <button onClick={confirmDrawer} disabled={!drawerValid}
              className={cn('flex-1 py-2.5 rounded-2xl text-xs font-bold transition-all flex items-center justify-center gap-1.5',
                drawerValid ? 'bg-red-600 text-white hover:bg-red-700 shadow-lg' : 'bg-slate-200 text-slate-400 cursor-not-allowed')}>
              {drawerValid ? (
                <>
                  <span>{loc.currencySymbol} {((drawer.basePrice + drawerExtraPrice) * drawerItemQty).toFixed(2)}</span>
                  <span>Add to Cart</span>
                </>
              ) : 'Select required options'}
            </button>
          </div>
          {/* Campaign Bridge — compact drawer */}
          {canPushToCampaign && (
            <div className="mt-2 flex items-center gap-2">
              {campaignLinks[drawer.id]?.length ? (
                <button onClick={() => setLinkBadgeItem(linkBadgeItem === drawer.id ? null : drawer.id)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-2xl text-xs font-bold bg-violet-50 border-2 border-violet-200 text-violet-700 hover:bg-violet-100 transition-all relative">
                  <PackageCheck size={11} /> In {campaignLinks[drawer.id].length} Campaign{campaignLinks[drawer.id].length > 1 ? 's' : ''}
                  {linkBadgeItem === drawer.id && (
                    <div className="absolute bottom-full left-0 mb-2 bg-white border border-slate-200 rounded-xl shadow-xl p-3 w-72 z-50">
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-2">Campaign Submissions</p>
                      <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        {campaignLinks[drawer.id].map((lk, i) => (
                          <div key={i} className="flex items-center justify-between px-2.5 py-1.5 bg-slate-50 rounded-lg">
                            <span className="text-[11px] font-semibold text-slate-700 truncate mr-2">{lk.campaignName}</span>
                            <span className={cn('px-1.5 py-0.5 rounded text-[8px] font-bold uppercase',
                              lk.submissionStatus === 'APPROVED' ? 'bg-emerald-100 text-emerald-700' :
                              lk.submissionStatus === 'REJECTED' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-700'
                            )}>{lk.submissionStatus}{lk.currentStage ? ` · ${lk.currentStage}` : ''}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </button>
              ) : null}
              <button onClick={() => setCampaignPushItem(drawer)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-2xl text-xs font-bold border-2 border-indigo-400 text-indigo-600 bg-white hover:bg-indigo-50 transition-all">
                <Megaphone size={13} /> Select to Campaign
              </button>
            </div>
          )}
        </div>
      </>
    );
  };

  // ── Item Detail Content (for items with no modifiers — Talabat/Rafeeq style) ──
  const renderItemDetailContent = () => {
    if (!detailItem) return null;
    return (
      <>
        {detailItem.image ? (
          <div className="w-full h-56 relative bg-gradient-to-br from-slate-100 to-slate-50">
            <PreviewImage src={detailItem.image} className="w-full h-full" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            <button onClick={() => setDetailItem(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center backdrop-blur-sm hover:bg-black/60"><X size={16} /></button>
          </div>
        ) : (
          <div className="w-full h-40 relative bg-gradient-to-br from-indigo-50 to-slate-100 flex items-center justify-center">
            <Package size={48} className="text-indigo-200" />
            <button onClick={() => setDetailItem(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/20 text-white flex items-center justify-center backdrop-blur-sm hover:bg-black/40"><X size={16} /></button>
          </div>
        )}
        <div className="px-5 pt-4 pb-3">
          <h3 className="text-xl font-bold text-slate-900">{nm(detailItem.nameEN, detailItem.nameAR)}</h3>
          {(isAR ? detailItem.descriptionAR : detailItem.descriptionEN) && (
            <p className="text-sm text-slate-500 mt-2 leading-relaxed whitespace-pre-wrap">{nm(detailItem.descriptionEN || '', detailItem.descriptionAR || '')}</p>
          )}
          {detailItem.campaignMeta && !isPromoExpired(detailItem) && (
            <span className="inline-block mt-2 text-[9px] px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 font-bold">{detailItem.campaignMeta.campaignName || 'CAMPAIGN'}</span>
          )}
          <div className="flex items-center gap-3 mt-3">
            {detailItem.campaignMeta && !isPromoExpired(detailItem) ? (
              <>
                <span className="text-sm text-slate-400 line-through">{loc.currencySymbol} {detailItem.campaignMeta.originalPrice.toFixed(2)}</span>
                <span className="text-2xl font-bold text-emerald-600">{loc.currencySymbol} {detailItem.campaignMeta.promoPrice.toFixed(2)}</span>
                <span className="text-[9px] px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold">{discountLabel(detailItem, loc.currencySymbol)}</span>
              </>
            ) : !shouldHidePrice(detailItem) ? (
              <span className="text-2xl font-bold text-indigo-600">{loc.currencySymbol} {detailItem.basePrice.toFixed(2)}</span>
            ) : (
              (() => { const fp = formatCardPrice(detailItem, loc.currencySymbol, pricingStrategy); return fp.text ? <span className="text-2xl font-bold text-indigo-600">{fp.prefix && <span className="text-sm font-normal text-slate-400 mr-1">{fp.prefix}</span>}{fp.text}</span> : <span className="text-sm text-slate-400">{fp.badge}</span>; })()
            )}
          </div>
        </div>
        {/* Special Request — decoupled from option groups */}
        {detailItem.allowSpecialRequests !== false && (
          <div className="px-5 pb-3">
            <div className="flex items-center gap-2 mb-1.5">
              <p className="text-sm font-bold text-slate-700">Special Request</p>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 font-medium">Optional</span>
            </div>
            <textarea
              value={detailSpecialReq}
              onChange={e => setDetailSpecialReq(e.target.value.slice(0, 200))}
              placeholder="Leave any instructions to the restaurant"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
              rows={2}
            />
            <p className="text-[9px] text-slate-300 text-right mt-0.5">{detailSpecialReq.length}/200</p>
          </div>
        )}
        <div className="px-5 pb-5 pt-2 border-t border-slate-100 mt-auto space-y-2">
          {/* Cost Breakdown — simple item */}
          {financialVisibility.showCostData && costInspector && detailCostBreakdown && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-amber-100 flex items-center justify-center"><DollarSign size={12} className="text-amber-600" /></div>
                <div><p className="text-[10px] font-bold text-slate-800">Cost Breakdown</p></div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500">Cost</span>
                  <span className="text-[10px] font-bold text-red-600">{loc.currencySymbol} {detailCostBreakdown.totalCost.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500">Sell</span>
                  <span className="text-[10px] font-bold text-slate-800">{loc.currencySymbol} {detailCostBreakdown.totalSell.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500">Profit</span>
                  <span className="text-[10px] font-bold text-emerald-600">{loc.currencySymbol} {(detailCostBreakdown.totalSell - detailCostBreakdown.totalCost).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500">Food Cost %</span>
                  <span className={cn('text-[10px] font-bold', detailCostBreakdown.foodCostPct <= 30 ? 'text-emerald-600' : detailCostBreakdown.foodCostPct <= 40 ? 'text-amber-500' : 'text-red-500')}>{detailCostBreakdown.foodCostPct.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          )}
          <button onClick={addFromDetail}
            className="w-full py-3.5 rounded-2xl text-sm font-bold bg-red-600 text-white hover:bg-red-700 shadow-lg transition-all">
            {shouldHidePrice(detailItem) ? 'Add to Cart' : `${loc.currencySymbol} ${(detailItem.campaignMeta && !isPromoExpired(detailItem) ? detailItem.campaignMeta.promoPrice : detailItem.basePrice).toFixed(2)}  Add to Cart`}
          </button>
          {/* Campaign Bridge — simple item detail */}
          {canPushToCampaign && (
            <div className="flex items-center gap-2">
              {campaignLinks[detailItem.id]?.length ? (
                <button onClick={() => setLinkBadgeItem(linkBadgeItem === detailItem.id ? null : detailItem.id)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-2xl text-sm font-bold bg-violet-50 border-2 border-violet-200 text-violet-700 hover:bg-violet-100 transition-all relative">
                  <PackageCheck size={13} /> In {campaignLinks[detailItem.id].length} Campaign{campaignLinks[detailItem.id].length > 1 ? 's' : ''}
                  {linkBadgeItem === detailItem.id && (
                    <div className="absolute bottom-full left-0 mb-2 bg-white border border-slate-200 rounded-xl shadow-xl p-3 w-72 z-50">
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-2">Campaign Submissions</p>
                      <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        {campaignLinks[detailItem.id].map((lk, i) => (
                          <div key={i} className="flex items-center justify-between px-2.5 py-1.5 bg-slate-50 rounded-lg">
                            <span className="text-[11px] font-semibold text-slate-700 truncate mr-2">{lk.campaignName}</span>
                            <span className={cn('px-1.5 py-0.5 rounded text-[8px] font-bold uppercase',
                              lk.submissionStatus === 'APPROVED' ? 'bg-emerald-100 text-emerald-700' :
                              lk.submissionStatus === 'REJECTED' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-700'
                            )}>{lk.submissionStatus}{lk.currentStage ? ` · ${lk.currentStage}` : ''}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </button>
              ) : null}
              <button onClick={() => setCampaignPushItem(detailItem)}
                className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-2xl text-sm font-bold border-2 border-indigo-400 text-indigo-600 bg-white hover:bg-indigo-50 transition-all">
                <Megaphone size={14} /> Select to Campaign
              </button>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="w-full max-w-full min-w-0 overflow-hidden">
      {/* Header: title + tabs */}
      <div className="pb-3 space-y-3">
      <div className="flex items-end justify-between">
        <div><h2 className="text-2xl font-bold tracking-tight">Digital Preview</h2><p className="text-sm text-slate-500 mt-1">Aggregator-style menu simulation.</p></div>
      </div>

      {/* Sub-tab toggle */}
      <div className="flex items-center gap-2">
        <button onClick={() => setPreviewView('visual')} className={cn('flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all', previewView === 'visual' ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-indigo-300')}>
          <Monitor size={14} /> Visual Preview
        </button>
        <button onClick={() => setPreviewView('list')} className={cn('flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all', previewView === 'list' ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-indigo-300')}>
          <List size={14} /> List Menu
        </button>
      </div>

      {/* Controls bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-4 overflow-hidden">
        <CustomSelect value={selId} onChange={v => { setSelId(v); setCart([]); }} placeholder="Select a published menu…"
          options={[{ value: '', label: 'Select a published menu…' }, ...profiles.map(p => ({ value: p.id, label: `${p.name} — ${p.brandName}` }))]} className="flex-1 min-w-[200px]" />
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
          <button onClick={() => setLang('EN')} className={cn('px-3 py-1.5 rounded-md text-xs font-bold', lang === 'EN' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500')}>English</button>
          <button onClick={() => setLang('AR')} className={cn('px-3 py-1.5 rounded-md text-xs font-bold', lang === 'AR' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500')}>{loc.secondaryLangNative}</button>
        </div>
        {previewView === 'visual' && (
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => setMode('desktop')} className={cn('p-1.5 rounded-md', mode === 'desktop' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500')} title="Full Width"><Monitor size={14} /></button>
            <button onClick={() => setMode('mobile')} className={cn('p-1.5 rounded-md', mode === 'mobile' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500')} title="Mobile Preview"><Smartphone size={14} /></button>
          </div>
        )}
        {previewView === 'list' && profile && (
          <button onClick={() => { setExpandedCats(new Set(profile.categories.filter(c => !isUnassignedCat(c.id)).map(c => c.id))); setExpandedItems(new Set(profile.categories.filter(c => !isUnassignedCat(c.id)).flatMap(c => c.items.map(i => i.id)))); setExpandedGroups(new Set(profile.categories.filter(c => !isUnassignedCat(c.id)).flatMap(c => c.items.flatMap(i => (i.optionGroups || []).map(g => `${i.id}:${g.id}`))))); }}
            className="text-xs text-indigo-600 font-bold hover:underline">Expand All</button>
        )}
        {previewView === 'list' && (expandedCats.size > 0 || expandedItems.size > 0) && (
          <button onClick={() => { setExpandedCats(new Set()); setExpandedItems(new Set()); setExpandedGroups(new Set()); }}
            className="text-xs text-slate-500 font-bold hover:underline">Collapse All</button>
        )}
        {previewView === 'visual' && cart.length > 0 && <button onClick={() => setCart([])} className="text-xs text-red-600 font-bold hover:underline">Clear Cart</button>}
        {profile && (
          <>
            <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
              {/* Live indicator (pulsing) */}
              {profile.status === MenuProfileStatus.PUBLISHED && (
                <span className="flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-[10px] font-bold text-emerald-700">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  LIVE PREVIEW
                </span>
              )}
              <button onClick={() => canShare && profile.status === MenuProfileStatus.PUBLISHED && setShowShareModal(true)}
                disabled={!canShare || profile.status !== MenuProfileStatus.PUBLISHED}
                className={cn('flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border transition-all',
                  canShare && profile.status === MenuProfileStatus.PUBLISHED ? 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300 cursor-pointer' : 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed opacity-60')}
                title={profile.status !== MenuProfileStatus.PUBLISHED ? 'Publish this menu first to share it.' : canShare ? 'Share public link' : 'Permission Restricted: Only Administrators can generate public sharing links.'}>
                <ExternalLink size={13} /> Share Menu
              </button>
              {canUsePreviewCostInspector && (
                <button onClick={() => setCostInspector(v => !v)}
                  className={cn('flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border transition-all',
                    costInspector ? 'bg-amber-500 border-amber-500 text-white shadow-sm' : 'bg-white border-slate-200 text-slate-500 hover:border-amber-300 hover:text-amber-600')}
                  title="Toggle Cost Inspector">
                  <DollarSign size={13} /> {costInspector ? 'Cost ON' : 'Cost OFF'}
                </button>
              )}
              {isAdmin && profile.status === MenuProfileStatus.PUBLISHED && (() => {
                const offersCat = profile.categories.find(c => (c.nameEN || '').toLowerCase() === 'offers');
                const orphanCount = offersCat?.items?.filter((it: any) => it.systemManaged || it.system_managed || it.campaignMeta || it.campaign_meta).length ?? 0;
                if (!orphanCount) return null;
                return (
                  <button onClick={async () => {
                    if (!confirm(`Remove ${orphanCount} campaign-injected item${orphanCount > 1 ? 's' : ''} from the Offers category? This cannot be undone.`)) return;
                    try {
                      const res = await apiFetch(`/api/menu/profiles/${profile.id}/revoke`, { method: 'POST', body: JSON.stringify({ callingModule: 'aggregator' }) });
                      toast.success('Cleaned', `Removed ${(res as any)?.data?.removed ?? orphanCount} orphaned offer item(s).`);
                      // Refresh preview
                      const fresh = await repo.getAll<MenuProfile>('menu-profiles');
                      setLocalProfiles(fresh);
                    } catch (e: any) { toast.error('Failed', e.message || 'Could not clean offers.'); }
                  }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border transition-all bg-white border-red-200 text-red-600 hover:border-red-400 hover:bg-red-50"
                    title="Remove campaign-injected items from Offers category">
                    <Unlink size={13} /> Clean Offers ({orphanCount})
                  </button>
                );
              })()}
              {canExportMenu && (
                <button onClick={() => setExportProfile(profile)}
                  disabled={profile.status !== MenuProfileStatus.PUBLISHED}
                  className={cn('flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border transition-all',
                    profile.status === MenuProfileStatus.PUBLISHED ? 'bg-white border-slate-200 text-emerald-600 hover:border-emerald-300' : 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed opacity-60')}
                  title={profile.status !== MenuProfileStatus.PUBLISHED ? 'Publish this menu first to export.' : 'Export menu to Excel / ZIP'}>
                  <Download size={13} /> Export Menu
                </button>
              )}
            </div>
          </>
        )}
      </div>
      </div>{/* end header */}

      {/* Campaign Notice Bar — Active campaign invitations for this brand */}
      {canPushToCampaign && profile && eligibleCampaigns.length > 0 && (
        <div className="mt-3 bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
            <Bell size={16} className="text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-violet-800">
              {eligibleCampaigns.length} Active Campaign{eligibleCampaigns.length > 1 ? 's' : ''} Available
            </p>
            <p className="text-[10px] text-violet-600 mt-0.5 truncate">
              {eligibleCampaigns.slice(0, 3).map(c => `${c.name} (${c.discountValue}${c.discountType === 'PERCENTAGE' ? '%' : ` ${loc.currencySymbol}`} off)`).join(' · ')}
              {eligibleCampaigns.length > 3 && ` +${eligibleCampaigns.length - 3} more`}
            </p>
          </div>
          <span className="text-[9px] font-bold text-violet-500 bg-violet-100 px-2 py-1 rounded-lg whitespace-nowrap">
            Open any item → Select to Campaign
          </span>
        </div>
      )}

      {/* Preview content */}
      <div className="pt-3 w-full max-w-full min-w-0 overflow-hidden">
      {previewLoading ? (
        mode === 'mobile' ? <MobilePreviewSkeleton /> : (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center animate-pulse">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 mx-auto mb-4" />
            <div className="h-4 w-40 bg-slate-200 rounded mx-auto mb-2" />
            <div className="h-3 w-56 bg-slate-100 rounded mx-auto" />
          </div>
        )
      ) : !profile ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <Eye size={32} className="mx-auto text-slate-300 mb-3" />
          {profiles.length === 0 ? (
            <>
              <p className="text-sm font-semibold text-slate-500">No published menus available.</p>
              <p className="text-xs text-slate-400 mt-1">Only published menus appear in Digital Preview. Go to Products and publish a menu first.</p>
            </>
          ) : (
            <p className="text-sm font-semibold text-slate-400">Select a menu to preview.</p>
          )}
        </div>
      ) : (
        <>
          {previewView === 'list' ? (
        /* ═══════════════════════ LIST / TREE VIEW ═══════════════════════ */
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden relative" dir={isAR ? 'rtl' : 'ltr'}>
          {/* Profile header */}
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 text-white px-6 py-4">
            <div className={cn('flex items-center gap-3', isAR && 'flex-row-reverse')}>
              {brandLogoUrl ? (
                <img src={getMediaUrl(brandLogoUrl)} alt={profile.brandName} className="w-10 h-10 rounded-xl object-cover" />
              ) : (
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center text-lg font-bold">{profile.brandName.charAt(0)}</div>
              )}
              <div className={isAR ? 'text-right' : ''}>
                <h3 className="text-lg font-bold">{profile.brandName} — {profile.name}</h3>
                <p className="text-indigo-200 text-xs">{profile.categories.filter(c => !isUnassignedCat(c.id)).length} categories · {profile.categories.filter(c => !isUnassignedCat(c.id)).reduce((a, c) => a + c.items.length, 0)} items{profile.tag && ` · ${profile.tag}`}</p>
              </div>
            </div>
          </div>

          {/* 🚨 Profile-level CLOSED overlay (List view) */}
          {!profileAvailable && (
            <div className="px-6 py-4 bg-red-50 border-b border-red-200 flex items-center gap-3">
              <Timer size={16} className="text-red-500 shrink-0" />
              <div>
                <p className="text-xs font-bold text-red-700">Menu is currently CLOSED</p>
                <p className="text-[10px] text-red-500">{profileNextSlot || 'Check back later'}</p>
              </div>
            </div>
          )}

          {/* Tree content */}
          <div className="divide-y divide-slate-100">
            {visibleCategories.map((cat, ci) => {
              const catOpen = expandedCats.has(cat.id);
              return (
                <div key={cat.id}>
                  {/* Category row */}
                  <button onClick={() => toggleExpand(setExpandedCats, cat.id)}
                    className={cn('w-full flex items-center gap-3 px-6 py-3.5 hover:bg-slate-50 transition-colors text-left', !cat._available && 'opacity-50')}>
                    <div className={cn('w-5 h-5 flex items-center justify-center transition-transform', catOpen && 'rotate-90')}>
                      <ChevronRight size={16} className="text-indigo-500" />
                    </div>
                    <FolderOpen size={16} className="text-indigo-500 shrink-0" />
                    <span className="text-sm font-bold text-indigo-700 uppercase tracking-wide flex-1">{nm(cat.nameEN, cat.nameAR)}</span>
                    <ScheduleBadge schedule={cat.availabilitySchedule} />
                    <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{cat.visibleItems.length} items</span>
                  </button>

                  {/* Items under category */}
                  {catOpen && (
                    <div className="border-t border-slate-50">
                      {cat.visibleItems.map((item, ii) => {
                        const itemOpen = expandedItems.has(item.id);
                        const groups = item.optionGroups || [];
                        const hasGroups = groups.length > 0;
                        return (
                          <div key={item.id}>
                            {/* Item row */}
                            <button onClick={() => hasGroups && toggleExpand(setExpandedItems, item.id)}
                              className={cn('w-full flex items-center gap-3 pr-6 py-3 hover:bg-indigo-50/40 transition-colors text-left', isAR ? 'pr-14 pl-6' : 'pl-14 pr-6', !(item as any)._available && 'opacity-40')}
                              style={{ cursor: hasGroups ? 'pointer' : 'default' }}>
                              {hasGroups ? (
                                <div className={cn('w-5 h-5 flex items-center justify-center transition-transform', itemOpen && 'rotate-90')}>
                                  <ChevronRight size={14} className="text-slate-400" />
                                </div>
                              ) : (
                                <div className="w-5 h-5 flex items-center justify-center">
                                  <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                                </div>
                              )}
                              {item.image ? (
                                <PreviewImage src={item.image} className="w-10 h-10 rounded-lg shrink-0 border border-slate-200" />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Package size={14} className="text-slate-300" /></div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-800 truncate">{nm(item.nameEN, item.nameAR)}</p>
                                {(isAR ? item.descriptionAR : item.descriptionEN) && <p className="text-[11px] text-slate-400 truncate">{nm(item.descriptionEN || '', item.descriptionAR || '')}</p>}
                              </div>
                              <div className="flex items-center gap-3 shrink-0">
                                {hasGroups && <span className="text-[9px] px-2 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-full font-bold">{groups.length} group{groups.length > 1 ? 's' : ''}</span>}
                                {item.campaignMeta && !isPromoExpired(item) ? (
                                  <>
                                    <span className="text-[10px] text-slate-400 line-through">{loc.currencySymbol} {item.campaignMeta.originalPrice.toFixed(2)}</span>
                                    <span className="text-sm font-bold text-emerald-600">{loc.currencySymbol} {item.campaignMeta.promoPrice.toFixed(2)}</span>
                                    <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold">{discountLabel(item, loc.currencySymbol)}</span>
                                  </>
                                ) : shouldHidePrice(item) ? (
                                  (() => { const fp = formatCardPrice(item, loc.currencySymbol, pricingStrategy); return fp.text ? <span className="text-[10px] text-indigo-600 font-bold">{fp.prefix && <span className="font-normal text-slate-400 mr-0.5">{fp.prefix}</span>}{fp.text}</span> : fp.badge ? <span className="text-[10px] px-2 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-full font-bold">{fp.badge}</span> : null; })()
                                ) : (
                                  <span className="text-sm font-bold text-indigo-600">{loc.currencySymbol} {item.basePrice.toFixed(2)}</span>
                                )}
                                {item.hidden && <span className="text-[9px] px-2 py-0.5 bg-red-50 text-red-500 border border-red-200 rounded-full font-bold">Unavailable</span>}
                                <ScheduleBadge schedule={item.availabilitySchedule} />
                              </div>
                            </button>

                            {/* Option groups under item */}
                            {itemOpen && hasGroups && (
                              <div className="border-t border-slate-50 bg-slate-50/50">
                                {groups.filter(g => !g.hidden).map((grp) => {
                                  const grpKey = `${item.id}:${grp.id}`;
                                  const grpOpen = expandedGroups.has(grpKey);
                                  const min = grp.minSelection ?? 0;
                                  const max = grp.maxSelection ?? 0;
                                  return (
                                    <div key={grp.id}>
                                      {/* Group row */}
                                      <button onClick={() => toggleExpand(setExpandedGroups, grpKey)}
                                        className={cn('w-full flex items-center gap-3 py-2.5 hover:bg-indigo-50/30 transition-colors text-left', isAR ? 'pr-[5.5rem] pl-6' : 'pl-[5.5rem] pr-6')}>
                                        <div className={cn('w-4 h-4 flex items-center justify-center transition-transform', grpOpen && 'rotate-90')}>
                                          <ChevronRight size={12} className="text-slate-400" />
                                        </div>
                                        <Layers size={13} className="text-indigo-400 shrink-0" />
                                        <span className="text-xs font-bold text-slate-700 flex-1">{nm(grp.nameEN, grp.nameAR)}</span>
                                        <div className="flex items-center gap-2 shrink-0">
                                          {min > 0 && <span className="text-[9px] px-1.5 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 rounded font-bold">Min {min}</span>}
                                          {max > 0 && <span className="text-[9px] px-1.5 py-0.5 bg-sky-50 text-sky-600 border border-sky-200 rounded font-bold">Max {max}</span>}
                                          <span className="text-[10px] text-slate-400">{grp.options.length} options</span>
                                        </div>
                                      </button>

                                      {/* Options under group */}
                                      {grpOpen && (
                                        <div className={cn('pb-1', isAR ? 'pr-[7.5rem] pl-6' : 'pl-[7.5rem] pr-6')}>
                                          {grp.options.filter(o => !o.hidden && !isGloballyHidden(o, grp)).map((opt) => (
                                            <div key={opt.id} className="flex items-center gap-3 py-2 border-t border-slate-100/60">
                                              <div className="w-1.5 h-1.5 rounded-full bg-indigo-300 shrink-0" />
                                              <span className="text-xs text-slate-700 flex-1">{nm(opt.nameEN, opt.nameAR)}</span>
                                              {opt.extraPrice > 0 ? (
                                                <span className="text-xs font-bold text-indigo-600">+{loc.currencySymbol} {opt.extraPrice.toFixed(2)}</span>
                                              ) : (
                                                <span className="text-[10px] text-slate-400">Free</span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Summary footer */}
          <div className="bg-slate-50 border-t border-slate-200 px-6 py-3 flex flex-wrap items-center gap-4 text-xs text-slate-500">
            <span><strong className="text-slate-700">{profile.categories.filter(c => !isUnassignedCat(c.id)).length}</strong> categories</span>
            <span><strong className="text-slate-700">{profile.categories.filter(c => !isUnassignedCat(c.id)).reduce((a, c) => a + c.items.length, 0)}</strong> items</span>
            <span><strong className="text-slate-700">{profile.categories.filter(c => !isUnassignedCat(c.id)).reduce((a, c) => a + c.items.reduce((b, i) => b + (i.optionGroups || []).length, 0), 0)}</strong> option groups</span>
            <span><strong className="text-slate-700">{profile.categories.filter(c => !isUnassignedCat(c.id)).reduce((a, c) => a + c.items.reduce((b, i) => b + (i.optionGroups || []).reduce((d, g) => d + g.options.length, 0), 0), 0)}</strong> total options</span>
          </div>
        </div>
      ) : mode === 'desktop' ? (
        /* ═══════════════════════ DESKTOP / FULL-WIDTH VIEW ═══════════════════════ */
        <div ref={desktopPreviewRef} className="w-full bg-slate-50 border border-slate-200 rounded-2xl shadow-sm overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 120px)', position: 'sticky', top: '100px' }} dir={isAR ? 'rtl' : 'ltr'}>
          {/* Hero banner — pinned */}
          <div className="relative h-32 overflow-hidden shrink-0">
              {heroImage ? (
                <PreviewImage src={heroImage} className="absolute inset-0 w-full h-full" style={{ objectFit: 'cover', filter: 'blur(6px) brightness(0.35) saturate(1.2)' }} />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900" />
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
              <div className="relative z-10 h-full flex items-center px-6 gap-5 overflow-hidden">
                {/* Brand logo */}
                {brandLogoUrl ? (
                  <img src={getMediaUrl(brandLogoUrl)} alt={profile.brandName} className={cn('w-14 h-14 rounded-xl object-contain bg-white shadow-2xl shrink-0 ring-2 ring-white/30', isAR && 'order-last')} />
                ) : (
                  <div className={cn('w-14 h-14 bg-white rounded-xl shadow-2xl flex items-center justify-center text-2xl font-black text-slate-800 shrink-0 ring-2 ring-white/30', isAR && 'order-last')}>
                    {profile.brandName.charAt(0)}
                  </div>
                )}
                {/* Brand info */}
                <div className={cn('flex-1 min-w-0 space-y-1', isAR && 'text-right')}>
                  <h2 className="text-xl font-black text-white tracking-tight leading-none">{profile.brandName}</h2>
                  <div className={cn('flex items-center gap-2 flex-wrap', isAR && 'flex-row-reverse')}>
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-white/10 backdrop-blur-sm rounded-full">
                      <Star size={11} className="text-amber-400 fill-amber-400" />
                      <span className="text-[11px] font-bold text-white">4.8</span>
                    </div>
                    <span className="text-[11px] text-white/50">{visibleCategories.length} categories · {totalItemCount} items</span>
                    {profile.tag && <span className="text-[10px] px-2 py-0.5 bg-white/10 backdrop-blur-sm rounded-full text-white/70 font-medium">{profile.tag}</span>}
                    {profileScheduleText && (
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="text-[11px] text-white/70 font-medium">{profileScheduleText}</span>
                      </span>
                    )}
                  </div>
                  {promoChips.length > 0 && (
                    <div className={cn('flex items-center gap-1.5 flex-wrap', isAR && 'flex-row-reverse')}>
                      {promoChips.slice(0, 3).map((chip, ci) => (
                        <span key={ci} className={cn('px-2.5 py-1 rounded-full text-[9px] font-bold text-white shadow-lg backdrop-blur-sm whitespace-nowrap', chip.color)}>
                          <Zap size={8} className="inline mr-0.5 -mt-0.5" />{chip.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {/* Search */}
                <div className={cn('shrink-0', isAR && 'flex-row-reverse')}>
                  <div className="relative w-48 min-w-0">
                    <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/50" />
                    <input
                      type="text"
                      value={previewSearchQuery}
                      onChange={e => setPreviewSearchQuery(e.target.value)}
                      placeholder={`Search in ${profile.brandName}`}
                      className="w-full pl-10 pr-8 py-2 bg-white/10 backdrop-blur-xl border border-white/15 rounded-full text-xs text-white placeholder:text-white/40 focus:outline-none focus:bg-white/20 focus:border-white/30 transition-all"
                    />
                    {previewSearchQuery && (
                      <button onClick={() => setPreviewSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"><X size={13} /></button>
                    )}
                  </div>
                </div>
              </div>
            </div>

          {/* Category nav strip — pinned */}
          <div ref={catNavDesktopRef} className="relative bg-white border-b border-slate-100 px-5 overflow-x-auto shrink-0 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <div className="flex items-center gap-1 py-2">
              {filteredPreviewCats.map(cat => (
                <button key={cat.id} data-pill={cat.id} onClick={() => { setActiveCat(cat.id); document.getElementById(`prev-cat-${cat.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                  className={cn('px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all',
                    activeCat === cat.id ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700')}>
                  {nm(cat.nameEN, cat.nameAR)}
                </button>
              ))}
            </div>
          </div>

          {/* Menu items — scrollable */}
          <div ref={desktopMenuRef} className={cn('flex-1 min-h-0 overflow-y-auto p-6 bg-slate-50', isAR && 'text-right')}>
              {previewSearchQuery && filteredPreviewCats.length === 0 && (
                <div className="py-16 text-center">
                  <Search size={32} className="mx-auto text-slate-200 mb-3" />
                  <p className="text-sm font-semibold text-slate-500">No items match "{previewSearchQuery}"</p>
                  <p className="text-xs text-slate-400 mt-1">Try a different search term</p>
                </div>
              )}
            {filteredPreviewCats.map(cat => (
              <div key={cat.id} id={`prev-cat-${cat.id}`} className="mb-8">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-1 h-5 bg-indigo-500 rounded-full" />
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">{nm(cat.nameEN, cat.nameAR)}</h3>
                  <span className="text-[10px] text-slate-400 font-medium">{cat.visibleItems.length} items</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {cat.visibleItems.map(item => renderItemCard(item, 'grid'))}
                </div>
              </div>
            ))}
          </div>

          {/* Cart sidebar (always visible when items exist) */}
          {cartCount > 0 && (
              <div className="w-80 shrink-0 border-l border-slate-200 bg-slate-50 flex flex-col overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 bg-white flex items-center justify-between">
                  <h4 className="text-sm font-bold flex items-center gap-2"><ShoppingCart size={16} className="text-indigo-600" /> Cart ({cartCount})</h4>
                  <button onClick={() => setCart([])} className="text-[10px] text-red-500 font-bold hover:underline">Clear</button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {cart.map(c => {
                    const optP = c.opts.reduce((a, o) => a + o.opt.extraPrice, 0);
                    const lineT = (c.item.basePrice + optP) * c.qty;
                    return (
                      <div key={c.id} className="bg-white rounded-xl p-3 border border-slate-100 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold truncate">{nm(c.item.nameEN, c.item.nameAR)}</p>
                            {c.opts.length > 0 && <p className="text-[10px] text-slate-400 truncate">{c.opts.map(o => nm(o.opt.nameEN, o.opt.nameAR)).join(', ')}</p>}
                          </div>
                          <span className="text-sm font-bold text-indigo-600 whitespace-nowrap">{loc.currencySymbol} {lineT.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => updateQty(c.id, -1)} className="w-7 h-7 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center hover:bg-slate-100"><Minus size={12} /></button>
                          <span className="text-sm font-bold w-7 text-center">{c.qty}</span>
                          <button onClick={() => updateQty(c.id, 1)} className="w-7 h-7 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center hover:bg-slate-100"><Plus size={12} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="px-4 pb-4 pt-3 border-t border-slate-200 bg-white space-y-3">
                  <div className="flex items-center justify-between"><span className="text-sm font-bold text-slate-800">Total</span><span className="text-lg font-bold text-indigo-600">{loc.currencySymbol} {cartTotal.toFixed(2)}</span></div>
                  <button className="w-full py-3 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 shadow-lg">Checkout (Demo)</button>
                </div>
              </div>
            )}
        </div>
      ) : (
        /* ═══════════════════════ MOBILE PREVIEW ═══════════════════════ */
        <div className="flex justify-center">
          <div className="w-[390px] bg-white border-2 border-slate-300 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col relative" style={{ height: '780px' }}>
            {/* Phone notch */}
            <div className="bg-slate-900 h-7 flex items-center justify-center shrink-0"><div className="w-20 h-4 bg-slate-800 rounded-full" /></div>

            {/* Modern mobile hero */}
            <div className="relative h-44 shrink-0 overflow-hidden" dir={isAR ? 'rtl' : 'ltr'}>
              {heroImage ? (
                <PreviewImage src={heroImage} className="absolute inset-0 w-full h-full" style={{ objectFit: 'cover', filter: 'blur(4px) brightness(0.35) saturate(1.2)' }} />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900" />
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-transparent" />
              <div className="relative z-10 h-full px-4 py-3 flex flex-col justify-end">
                <div className={cn('flex items-end gap-3', isAR && 'flex-row-reverse')}>
                  {brandLogoUrl ? (
                    <img src={getMediaUrl(brandLogoUrl)} alt={profile.brandName} className="rounded-xl object-contain bg-white shadow-xl shrink-0 ring-1 ring-white/20" style={{ width: 52, height: 52 }} />
                  ) : (
                    <div className="w-13 h-13 bg-white rounded-xl shadow-xl flex items-center justify-center text-lg font-black text-slate-800 shrink-0 ring-1 ring-white/20" style={{ width: 52, height: 52 }}>
                      {profile.brandName.charAt(0)}
                    </div>
                  )}
                  <div className={cn('flex-1 min-w-0 space-y-0.5', isAR && 'text-right')}>
                    <h2 className="text-base font-black text-white tracking-tight leading-tight">{profile.brandName}</h2>
                    <div className={cn('flex items-center gap-1.5 flex-wrap', isAR && 'flex-row-reverse')}>
                      <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-white/10 rounded-full">
                        <Star size={9} className="text-amber-400 fill-amber-400" />
                        <span className="text-[10px] font-bold text-white">4.8</span>
                      </div>
                      <span className="text-[9px] text-white/50">{visibleCategories.length} categories · {totalItemCount} items</span>
                    </div>
                    {profileScheduleText && (
                      <div className={cn('flex items-center gap-1', isAR && 'flex-row-reverse')}>
                        <div className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="text-[9px] text-white/60 font-medium">{profileScheduleText}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 🚨 Profile-level CLOSED overlay */}
            {!profileAvailable && (
              <div className="absolute inset-0 z-30 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm rounded-[2.5rem]">
                <div className="text-center p-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center"><Timer size={28} className="text-red-500" /></div>
                  <h3 className="text-lg font-bold text-white mb-1">Menu is currently CLOSED</h3>
                  <p className="text-sm text-slate-300">{profileNextSlot || 'Check back later'}</p>
                </div>
              </div>
            )}

            {/* Category pills (scrollable) */}
            <div ref={catNavMobileRef} className="bg-white border-b border-slate-100 px-3 overflow-x-auto shrink-0">
              <div className="flex items-center gap-1 py-2">
                {filteredPreviewCats.map(cat => (
                  <button key={cat.id} data-pill={cat.id} onClick={() => { setActiveCat(cat.id); document.getElementById(`mob-cat-${cat.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                    className={cn('px-3 py-1.5 rounded-full text-[10px] font-bold whitespace-nowrap transition-all',
                      activeCat === cat.id ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 bg-slate-100')}>
                    {nm(cat.nameEN, cat.nameAR)}
                  </button>
                ))}
              </div>
            </div>

            {/* Scrollable menu content */}
            <div ref={mobileMenuRef} className={cn('flex-1 overflow-y-auto bg-gray-50', isAR && 'text-right')} dir={isAR ? 'rtl' : 'ltr'} style={{ paddingBottom: cartCount > 0 ? '70px' : '16px' }}>
              {filteredPreviewCats.map(cat => (
                <div key={cat.id} id={`mob-cat-${cat.id}`} className="py-3">
                  <div className="px-4 flex items-center gap-2 mb-2">
                    <div className="w-0.5 h-3.5 bg-indigo-500 rounded-full" />
                    <h3 className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">{nm(cat.nameEN, cat.nameAR)}</h3>
                  </div>
                  {cat.visibleItems.map(item => (
                    <div key={item.id}
                      className="bg-white mx-3 mb-2 rounded-2xl shadow-sm border border-slate-100 overflow-hidden cursor-pointer hover:shadow-md transition-all active:scale-[0.99]"
                      onClick={() => openDrawer(item)}
                    >
                      <div className={cn('flex', isAR && 'flex-row-reverse')}>
                        <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                          <div>
                            <p className="text-[13px] font-bold text-slate-900 leading-tight">{nm(item.nameEN, item.nameAR)}</p>
                            {(isAR ? item.descriptionAR : item.descriptionEN) && <p className="text-[10px] text-slate-400 mt-0.5 line-clamp-4">{nm(item.descriptionEN || '', item.descriptionAR || '')}</p>}
                            {item.campaignMeta && !isPromoExpired(item) && (
                              <span className="inline-block text-[7px] px-1 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 font-bold mt-0.5">{item.campaignMeta.campaignName || 'CAMPAIGN'}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            {item.campaignMeta && !isPromoExpired(item) ? (
                              <>
                                <span className="text-[10px] text-slate-400 line-through">{loc.currencySymbol} {item.campaignMeta.originalPrice.toFixed(2)}</span>
                                <span className="text-xs font-bold text-emerald-600">{loc.currencySymbol} {item.campaignMeta.promoPrice.toFixed(2)}</span>
                                <span className="text-[7px] px-1 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold">{discountLabel(item, loc.currencySymbol)}</span>
                              </>
                            ) : (
                              (() => { const fp = formatCardPrice(item, loc.currencySymbol, pricingStrategy); return fp.text ? <>{fp.prefix && <span className="text-[9px] font-normal text-slate-400 mr-1">{fp.prefix}</span>}<span className="text-xs font-bold text-indigo-600">{fp.text}</span></> : null; })()
                            )}
                            {(() => { const fp = formatCardPrice(item, loc.currencySymbol, pricingStrategy); return fp.badge ? <span className="text-[9px] text-slate-400">{fp.badge}</span> : (item.optionGroups || []).length > 0 ? <span className="text-[9px] text-slate-400">Customizable</span> : null; })()}
                          </div>
                        </div>
                        <div className="w-24 h-24 shrink-0 relative">
                          {item.image ? <PreviewImage src={item.image} className="w-full h-full" /> : <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center"><Package size={20} className="text-slate-200" /></div>}
                          <button className="absolute bottom-1.5 right-1.5 w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-lg"><Plus size={14} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Floating cart bar (inside phone) */}
            {cartCount > 0 && !showCart && !drawer && (
              <div className="absolute bottom-6 left-3 right-3 z-10">
                <button onClick={() => setShowCart(!showCart)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-indigo-600 text-white rounded-2xl shadow-xl hover:bg-indigo-700">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-indigo-500 rounded-lg flex items-center justify-center text-[10px] font-bold">{cartCount}</div>
                    <span className="text-xs font-bold">View Cart</span>
                  </div>
                  <span className="text-xs font-bold">{loc.currencySymbol} {cartTotal.toFixed(2)}</span>
                </button>
              </div>
            )}

            {/* ─── Modifier Drawer INSIDE phone ─── */}
            {drawer && (
              <div className="absolute inset-0 z-20 flex flex-col justify-end bg-black/40 rounded-[2.5rem] overflow-hidden" onClick={() => setDrawer(null)}>
                <div className="bg-white rounded-t-2xl max-h-[75%] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                  {renderModifierContent()}
                </div>
              </div>
            )}

            {/* ─── Item Detail Drawer INSIDE phone ─── */}
            {detailItem && !drawer && (
              <div className="absolute inset-0 z-20 flex flex-col justify-end bg-black/40 rounded-[2.5rem] overflow-hidden" onClick={() => setDetailItem(null)}>
                <div className="bg-white rounded-t-2xl max-h-[80%] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                  {renderItemDetailContent()}
                </div>
              </div>
            )}

            {/* ─── Cart slideout INSIDE phone ─── */}
            {showCart && (
              <div className="absolute inset-0 z-20 flex flex-col justify-end bg-black/40 rounded-[2.5rem] overflow-hidden" onClick={() => setShowCart(false)}>
                <div className="bg-white rounded-t-2xl max-h-[75%] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between shrink-0">
                    <h4 className="text-sm font-bold flex items-center gap-2"><ShoppingCart size={14} className="text-indigo-600" /> Your Cart</h4>
                    <button onClick={() => setShowCart(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X size={16} /></button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {cart.map(c => {
                      const optP = c.opts.reduce((a, o) => a + o.opt.extraPrice, 0);
                      const lineT = (c.item.basePrice + optP) * c.qty;
                      return (
                        <div key={c.id} className="flex items-center gap-2 bg-slate-50 rounded-xl p-2.5">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold truncate">{nm(c.item.nameEN, c.item.nameAR)}</p>
                            {c.opts.length > 0 && <p className="text-[9px] text-slate-400 truncate">{c.opts.map(o => nm(o.opt.nameEN, o.opt.nameAR)).join(', ')}</p>}
                          </div>
                          <div className="flex items-center gap-0.5">
                            <button onClick={() => updateQty(c.id, -1)} className="w-6 h-6 rounded-md bg-white border border-slate-200 flex items-center justify-center"><Minus size={10} /></button>
                            <span className="text-xs font-bold w-5 text-center">{c.qty}</span>
                            <button onClick={() => updateQty(c.id, 1)} className="w-6 h-6 rounded-md bg-white border border-slate-200 flex items-center justify-center"><Plus size={10} /></button>
                          </div>
                          <span className="text-xs font-bold text-indigo-600 whitespace-nowrap">{loc.currencySymbol} {lineT.toFixed(2)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="px-4 pb-4 pt-2 border-t border-slate-100 space-y-2 shrink-0">
                    <div className="flex items-center justify-between"><span className="text-sm font-bold">Total</span><span className="text-base font-bold text-indigo-600">{loc.currencySymbol} {cartTotal.toFixed(2)}</span></div>
                    <button className="w-full py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 shadow-lg">Checkout (Demo)</button>
                  </div>
                </div>
              </div>
            )}

            {/* Home bar */}
            <div className="bg-slate-100 h-5 flex items-center justify-center shrink-0"><div className="w-28 h-1 bg-slate-300 rounded-full" /></div>
          </div>
        </div>
      )}
        </>
      )}

      {/* ─── Modifier Drawer (desktop only — Pizza Hut side-by-side modal) ─── */}
      {drawer && mode === 'desktop' && (() => {
        const hasDrawerData = [...drawerSel.values()].some(s => s.size > 0) || drawerSpecialReq.trim().length > 0 || drawerItemQty > 1;
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => { if (!hasDrawerData) setDrawer(null); }}>
          <div className={cn('relative bg-white rounded-2xl w-full shadow-2xl flex flex-col overflow-hidden', costInspector && drawerCostBreakdown ? 'max-w-[1100px]' : 'max-w-3xl')} style={{ height: '85vh' }} onClick={e => e.stopPropagation()}>
            {/* Close button — top-right of card */}
            <button onClick={() => setDrawer(null)} className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-white/90 shadow-md hover:bg-white text-slate-600 hover:text-slate-900 flex items-center justify-center transition-all"><X size={18} /></button>
            {/* Side-by-side body — fills entire card */}
            <div className="flex flex-1 min-h-0">
              {/* Left: Image — locked to card height */}
              {drawer.image && (
                <div className={cn('shrink-0 relative bg-gradient-to-br from-slate-100 to-slate-50', costInspector && drawerCostBreakdown ? 'w-[38%]' : 'w-1/2')}>
                  <PreviewImage src={drawer.image} className="absolute inset-0 w-full h-full object-cover" />
                </div>
              )}
              {/* Middle: Content + footer — scrolls within card */}
              <div className={cn('flex flex-col min-h-0', drawer.image ? (costInspector && drawerCostBreakdown ? 'flex-1' : 'w-1/2') : 'w-full')}>
                <div className="px-5 pt-5 pb-3 shrink-0">
                  <h3 className="text-xl font-bold text-slate-900 pr-8">{nm(drawer.nameEN, drawer.nameAR)}</h3>
                  {(isAR ? drawer.descriptionAR : drawer.descriptionEN) && <p className="text-sm text-slate-500 mt-1">{nm(drawer.descriptionEN || '', drawer.descriptionAR || '')}</p>}
                  {drawer.campaignMeta && !isPromoExpired(drawer) && (
                    <span className="inline-block mt-1.5 text-[9px] px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 font-bold">{drawer.campaignMeta.campaignName || 'CAMPAIGN'}</span>
                  )}
                  {drawer.campaignMeta && !isPromoExpired(drawer) ? (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-sm text-slate-400 line-through">{loc.currencySymbol} {drawer.campaignMeta.originalPrice.toFixed(2)}</span>
                      <span className="text-lg font-bold text-emerald-600">{loc.currencySymbol} {drawer.campaignMeta.promoPrice.toFixed(2)}</span>
                      <span className="text-[9px] px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold">{discountLabel(drawer, loc.currencySymbol)}</span>
                    </div>
                  ) : !shouldHidePrice(drawer) ? (
                    <p className="text-lg font-bold text-indigo-600 mt-2">{loc.currencySymbol} {drawer.basePrice.toFixed(2)}</p>
                  ) : (
                    (() => { const fp = formatCardPrice(drawer, loc.currencySymbol, pricingStrategy); return fp.text ? <p className="text-lg font-bold text-indigo-600 mt-2">{fp.prefix && <span className="text-sm font-normal text-slate-400 mr-1">{fp.prefix}</span>}{fp.text}</p> : fp.badge ? <p className="text-sm text-slate-400 mt-2">{fp.badge}</p> : null; })()
                  )}
                </div>
                <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-5">
                  {(drawer.optionGroups || []).filter(g => !g.hidden).map(grp => {
                    const min = grp.minSelection ?? 0;
                    const max = grp.maxSelection ?? 0;
                    const selSet = drawerSel.get(grp.id) || new Set();
                    const qm = drawerQtyMap.get(grp.id) || new Map<string, number>();
                    const isQtyMode = grp.allowMultipleQty;
                    let qtyTotal = 0; if (isQtyMode) qm.forEach(v => { qtyTotal += v; });
                    const selCount = isQtyMode ? qtyTotal : selSet.size;
                    const metMin = selCount >= min;
                    const isReq = min > 0;
                    return (
                      <div key={grp.id}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-sm font-bold text-slate-800">{nm(grp.nameEN, grp.nameAR)}</p>
                            <p className="text-[11px] text-slate-400">{min > 0 && `Min ${min}`}{min > 0 && max > 0 && ' · '}{max > 0 && `Max ${max}`}{!min && !max && 'Optional'}</p>
                          </div>
                          {isReq && !metMin && <div className="w-5 h-5 rounded-full border-2 border-red-300 flex items-center justify-center"><span className="text-[9px] text-red-400">!</span></div>}
                          {metMin && isReq && <div className="w-5 h-5 rounded-full bg-slate-900 flex items-center justify-center"><Check size={11} className="text-white" /></div>}
                        </div>
                        <div className="space-y-1.5">
                          {grp.options.filter(o => !o.hidden && !isGloballyHidden(o, grp)).map(opt => {
                            if (isQtyMode) {
                              const qty = qm.get(opt.id) || 0;
                              const atMax = max > 0 && qtyTotal >= max;
                              return (
                                <div key={opt.id} className={cn('w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all',
                                  qty > 0 ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200')}>
                                  <span className="text-sm font-medium text-slate-800">{nm(opt.nameEN, opt.nameAR)}</span>
                                  <div className="flex items-center gap-2">
                                    {opt.extraPrice > 0 && <span className="text-xs text-indigo-600 font-bold">+{loc.currencySymbol} {opt.extraPrice.toFixed(2)}{qty > 1 ? ` × ${qty}` : ''}</span>}
                                    <div className="flex items-center gap-1">
                                      <button onClick={() => changeOptQty(grp.id, opt.id, -1, max)} disabled={qty === 0}
                                        className={cn('w-6 h-6 rounded-full border flex items-center justify-center transition-colors',
                                          qty === 0 ? 'border-slate-200 text-slate-300' : 'border-indigo-300 text-indigo-600 hover:bg-indigo-100')}><Minus size={12} /></button>
                                      <span className="w-5 text-center text-xs font-bold text-slate-700">{qty}</span>
                                      <button onClick={() => changeOptQty(grp.id, opt.id, 1, max)} disabled={atMax}
                                        className={cn('w-6 h-6 rounded-full border flex items-center justify-center transition-colors',
                                          atMax ? 'border-slate-200 text-slate-300' : 'border-indigo-300 text-indigo-600 hover:bg-indigo-100')}><Plus size={12} /></button>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            const isSel = selSet.has(opt.id);
                            const disabled = !isSel && max > 0 && selCount >= max && max !== 1;
                            return (
                              <button key={opt.id} onClick={() => !disabled && toggleOpt(grp.id, opt.id, max)} disabled={disabled}
                                className={cn('w-full flex items-center justify-between px-4 py-3 rounded-xl border text-left transition-all',
                                  isSel ? 'border-indigo-400 bg-indigo-50' : disabled ? 'border-slate-100 bg-slate-50 opacity-50' : 'border-slate-200 hover:border-indigo-300')}>
                                <span className="text-sm font-medium text-slate-800">{nm(opt.nameEN, opt.nameAR)}</span>
                                <div className="flex items-center gap-2">
                                  {opt.extraPrice > 0 && <span className="text-xs text-indigo-600 font-bold">+{loc.currencySymbol} {opt.extraPrice.toFixed(2)}</span>}
                                  <div className={cn('w-5 h-5 rounded-full border-2 flex items-center justify-center', isSel ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300')}>{isSel && <Check size={12} className="text-white" />}</div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {/* Special Request */}
                  {drawer.allowSpecialRequests !== false && (
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <p className="text-sm font-bold text-slate-700">Special Request</p>
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 font-medium">Optional</span>
                      </div>
                      <div className="relative">
                        <textarea
                          value={drawerSpecialReq}
                          onChange={e => setDrawerSpecialReq(e.target.value.slice(0, 200))}
                          placeholder="Leave any instructions to the restaurant"
                          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
                          rows={3}
                        />
                        <span className="absolute bottom-2 right-3 text-[9px] text-slate-300">{drawerSpecialReq.length}/200</span>
                      </div>
                    </div>
                  )}
                </div>
                {/* Footer: qty stepper + add to cart — inside content side only */}
                <div className="px-5 py-4 border-t border-slate-200 bg-white shrink-0 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-2 py-1.5">
                      <button onClick={() => setDrawerItemQty(q => Math.max(1, q - 1))} disabled={drawerItemQty <= 1}
                        className={cn('w-8 h-8 rounded-lg flex items-center justify-center transition-colors', drawerItemQty <= 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100')}><Minus size={16} /></button>
                      <span className="w-8 text-center text-base font-bold text-slate-800">{drawerItemQty}</span>
                      <button onClick={() => setDrawerItemQty(q => q + 1)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors"><Plus size={16} /></button>
                    </div>
                    <button onClick={confirmDrawer} disabled={!drawerValid}
                      className={cn('flex-1 py-3 rounded-2xl text-sm font-bold transition-all flex items-center justify-center gap-2',
                        drawerValid ? 'bg-red-600 text-white hover:bg-red-700 shadow-lg' : 'bg-slate-200 text-slate-400 cursor-not-allowed')}>
                      {drawerValid ? (
                        <>
                          <span>{loc.currencySymbol} {((drawer.basePrice + drawerExtraPrice) * drawerItemQty).toFixed(2)}</span>
                          <span>Add to Cart</span>
                        </>
                      ) : 'Select required options'}
                    </button>
                  </div>
                  {/* Campaign Push Row */}
                  {canPushToCampaign && (
                    <div className="mt-2 flex items-center gap-2">
                      {campaignLinks[drawer.id]?.length ? (
                        <div className="relative flex-1">
                          <button onClick={() => setLinkBadgeItem(linkBadgeItem === drawer.id ? null : drawer.id)}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-violet-50 border border-violet-200 text-xs font-bold text-violet-700 hover:bg-violet-100 transition-all">
                            <PackageCheck size={13} /> In {campaignLinks[drawer.id].length} Campaign{campaignLinks[drawer.id].length > 1 ? 's' : ''}
                          </button>
                          {linkBadgeItem === drawer.id && (
                            <div className="absolute bottom-full left-0 mb-2 bg-white border border-slate-200 rounded-xl shadow-xl p-3 w-72 z-50">
                              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-2">Campaign Submissions</p>
                              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                {campaignLinks[drawer.id].map((lk, i) => (
                                  <div key={i} className="flex items-center justify-between px-2.5 py-1.5 bg-slate-50 rounded-lg">
                                    <span className="text-[11px] font-semibold text-slate-700 truncate mr-2">{lk.campaignName}</span>
                                    <span className={cn('px-1.5 py-0.5 rounded text-[8px] font-bold uppercase',
                                      lk.submissionStatus === 'APPROVED' ? 'bg-emerald-100 text-emerald-700' :
                                      lk.submissionStatus === 'REJECTED' ? 'bg-red-100 text-red-700' :
                                      'bg-amber-100 text-amber-700'
                                    )}>{lk.submissionStatus}{lk.currentStage ? ` · ${lk.currentStage}` : ''}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}
                      <button onClick={() => setCampaignPushItem(drawer)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-xs font-bold hover:from-violet-700 hover:to-indigo-700 shadow-md transition-all whitespace-nowrap">
                        <Megaphone size={13} /> Select to Campaign
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Cost Inspector Sidebar */}
              {financialVisibility.showCostData && costInspector && drawerCostBreakdown && (
                <div className="w-[280px] shrink-0 border-l border-slate-200 bg-gradient-to-b from-slate-50 to-white flex flex-col">
                  <div className="px-4 py-3 border-b border-slate-200 bg-white">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center"><DollarSign size={14} className="text-amber-600" /></div>
                      <div><p className="text-xs font-bold text-slate-800">Cost Breakdown</p><p className="text-[9px] text-slate-400">Real-time analysis</p></div>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                    <div>
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Base Item</p>
                      <div className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-slate-100">
                        <span className="text-xs text-slate-600 truncate">{nm(drawer.nameEN, drawer.nameAR)}</span>
                        <span className="text-xs font-bold text-slate-800 whitespace-nowrap">{loc.currencySymbol} {drawerCostBreakdown.baseCost.toFixed(2)}</span>
                      </div>
                    </div>
                    {drawerCostBreakdown.optionDetails.length > 0 && (
                      <div>
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Selected Options</p>
                        <div className="space-y-1">
                          {drawerCostBreakdown.optionDetails.map((od, i) => (
                            <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-white rounded-lg border border-slate-100">
                              <span className="text-[11px] text-slate-600 truncate mr-2">{od.name}</span>
                              <div className="text-right shrink-0">
                                <span className="text-[11px] font-bold text-slate-800">{loc.currencySymbol} {od.cost.toFixed(2)}</span>
                                <span className="text-[9px] text-slate-400 ml-1">sell {loc.currencySymbol} {od.sellPrice.toFixed(2)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="border-t border-dashed border-slate-200" />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between"><span className="text-xs text-slate-500">Total Cost</span><span className="text-sm font-bold text-red-600">{loc.currencySymbol} {drawerCostBreakdown.totalCost.toFixed(2)}</span></div>
                      <div className="flex items-center justify-between"><span className="text-xs text-slate-500">Sell Price</span><span className="text-sm font-bold text-slate-800">{loc.currencySymbol} {drawerCostBreakdown.totalSell.toFixed(2)}</span></div>
                      <div className="flex items-center justify-between"><span className="text-xs text-slate-500">Profit</span><span className="text-sm font-bold text-emerald-600">{loc.currencySymbol} {(drawerCostBreakdown.totalSell - drawerCostBreakdown.totalCost).toFixed(2)}</span></div>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-100 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Food Cost %</span>
                        <span className={cn('text-lg font-black', drawerCostBreakdown.foodCostPct <= 30 ? 'text-emerald-600' : drawerCostBreakdown.foodCostPct <= 40 ? 'text-amber-500' : 'text-red-500')}>{drawerCostBreakdown.foodCostPct.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                        <div className={cn('h-full rounded-full transition-all duration-500', drawerCostBreakdown.foodCostPct <= 30 ? 'bg-emerald-500' : drawerCostBreakdown.foodCostPct <= 40 ? 'bg-amber-500' : 'bg-red-500')} style={{ width: `${Math.min(drawerCostBreakdown.foodCostPct, 100)}%` }} />
                      </div>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[8px] text-slate-400">0%</span>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500" /><span className="text-[8px] text-slate-400">Good</span></div>
                          <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-amber-500" /><span className="text-[8px] text-slate-400">Watch</span></div>
                          <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-red-500" /><span className="text-[8px] text-slate-400">High</span></div>
                        </div>
                        <span className="text-[8px] text-slate-400">100%</span>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-100 p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2"><TrendingUp size={14} className="text-emerald-500" /><span className="text-xs font-bold text-slate-600">Profit Margin</span></div>
                      <span className={cn('text-base font-black', drawerCostBreakdown.margin >= 60 ? 'text-emerald-600' : drawerCostBreakdown.margin >= 40 ? 'text-amber-500' : 'text-red-500')}>{drawerCostBreakdown.margin.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* ─── Item Detail Drawer (desktop only — Pizza Hut side-by-side modal) ─── */}
      {detailItem && !drawer && mode === 'desktop' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setDetailItem(null)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden relative" onClick={e => e.stopPropagation()}>
            <button onClick={() => setDetailItem(null)} className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/10 hover:bg-black/20 text-slate-600 flex items-center justify-center transition-colors"><X size={16} /></button>
            <div className="flex flex-col md:flex-row flex-1 min-h-0">
              {detailItem.image ? (
                <div className="w-full md:w-1/2 shrink-0">
                  <div className="aspect-square w-full relative bg-gradient-to-br from-slate-100 to-slate-50">
                    <PreviewImage src={detailItem.image} className="w-full h-full" />
                  </div>
                </div>
              ) : (
                <div className="w-full md:w-1/2 shrink-0">
                  <div className="aspect-square w-full bg-gradient-to-br from-indigo-50 to-slate-100 flex items-center justify-center">
                    <Package size={64} className="text-indigo-200" />
                  </div>
                </div>
              )}
              <div className={cn('flex flex-col flex-1 min-h-0', 'md:w-1/2')}>
                <div className="flex-1 overflow-y-auto px-6 pt-5 pb-4" style={{ maxHeight: '55vh' }}>
                  <h3 className="text-xl font-bold text-slate-900 pr-8">{nm(detailItem.nameEN, detailItem.nameAR)}</h3>
                  {(isAR ? detailItem.descriptionAR : detailItem.descriptionEN) && (
                    <p className="text-sm text-slate-500 mt-2 leading-relaxed whitespace-pre-wrap">{nm(detailItem.descriptionEN || '', detailItem.descriptionAR || '')}</p>
                  )}
                  <div className="flex items-center gap-3 mt-3">
                    {detailItem.campaignMeta && !isPromoExpired(detailItem) ? (
                      <>
                        <span className="text-sm text-slate-400 line-through">{loc.currencySymbol} {detailItem.campaignMeta.originalPrice.toFixed(2)}</span>
                        <span className="text-2xl font-bold text-emerald-600">{loc.currencySymbol} {detailItem.campaignMeta.promoPrice.toFixed(2)}</span>
                        <span className="text-[9px] px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold">{discountLabel(detailItem, loc.currencySymbol)}</span>
                      </>
                    ) : !shouldHidePrice(detailItem) ? (
                      <span className="text-2xl font-bold text-indigo-600">{loc.currencySymbol} {detailItem.basePrice.toFixed(2)}</span>
                    ) : (
                      (() => { const fp = formatCardPrice(detailItem, loc.currencySymbol, pricingStrategy); return fp.text ? <span className="text-2xl font-bold text-indigo-600">{fp.prefix && <span className="text-sm font-normal text-slate-400 mr-1">{fp.prefix}</span>}{fp.text}</span> : <span className="text-sm text-slate-400">{fp.badge}</span>; })()
                    )}
                  </div>
                  {detailItem.campaignMeta && !isPromoExpired(detailItem) && (
                    <span className="inline-block mt-2 text-[9px] px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 font-bold">{detailItem.campaignMeta.campaignName || 'CAMPAIGN'}</span>
                  )}
                  {/* Cost Breakdown */}
                  {financialVisibility.showCostData && costInspector && detailCostBreakdown && (
                    <div className="mt-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-lg bg-amber-100 flex items-center justify-center"><DollarSign size={12} className="text-amber-600" /></div>
                        <div><p className="text-xs font-bold text-slate-800">Cost Breakdown</p><p className="text-[9px] text-slate-400">Real-time analysis</p></div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between"><span className="text-xs text-slate-500">Cost</span><span className="text-xs font-bold text-red-600">{loc.currencySymbol} {detailCostBreakdown.totalCost.toFixed(2)}</span></div>
                        <div className="flex items-center justify-between"><span className="text-xs text-slate-500">Sell Price</span><span className="text-xs font-bold text-slate-800">{loc.currencySymbol} {detailCostBreakdown.totalSell.toFixed(2)}</span></div>
                        <div className="flex items-center justify-between"><span className="text-xs text-slate-500">Profit</span><span className="text-xs font-bold text-emerald-600">{loc.currencySymbol} {(detailCostBreakdown.totalSell - detailCostBreakdown.totalCost).toFixed(2)}</span></div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">Food Cost %</span>
                          <span className={cn('text-sm font-bold', detailCostBreakdown.foodCostPct <= 30 ? 'text-emerald-600' : detailCostBreakdown.foodCostPct <= 40 ? 'text-amber-500' : 'text-red-500')}>{detailCostBreakdown.foodCostPct.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Divider */}
                  <div className="border-t border-slate-100 my-4" />
                  {/* Special Request — decoupled from option groups */}
                  {detailItem.allowSpecialRequests !== false && (
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <p className="text-sm font-bold text-slate-700">Special Request</p>
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 font-medium">Optional</span>
                      </div>
                      <div className="relative">
                        <textarea
                          value={detailSpecialReq}
                          onChange={e => setDetailSpecialReq(e.target.value.slice(0, 200))}
                          placeholder="Leave any instructions to the restaurant"
                          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
                          rows={3}
                        />
                        <span className="absolute bottom-2 right-3 text-[9px] text-slate-300">{detailSpecialReq.length}/200</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 bg-white shrink-0 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
              <button onClick={addFromDetail}
                className="w-full py-3.5 rounded-2xl text-sm font-bold bg-red-600 text-white hover:bg-red-700 shadow-lg transition-all">
                {shouldHidePrice(detailItem) ? 'Add to Cart' : `${loc.currencySymbol} ${(detailItem.campaignMeta && !isPromoExpired(detailItem) ? detailItem.campaignMeta.promoPrice : detailItem.basePrice).toFixed(2)}  Add to Cart`}
              </button>
              {/* Campaign Bridge — desktop detail */}
              {canPushToCampaign && (
                <div className="flex items-center gap-2 mt-2">
                  {campaignLinks[detailItem.id]?.length ? (
                    <button onClick={() => setLinkBadgeItem(linkBadgeItem === detailItem.id ? null : detailItem.id)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-2xl text-sm font-bold bg-violet-50 border-2 border-violet-200 text-violet-700 hover:bg-violet-100 transition-all relative">
                      <PackageCheck size={13} /> In {campaignLinks[detailItem.id].length} Campaign{campaignLinks[detailItem.id].length > 1 ? 's' : ''}
                      {linkBadgeItem === detailItem.id && (
                        <div className="absolute bottom-full left-0 mb-2 bg-white border border-slate-200 rounded-xl shadow-xl p-3 w-72 z-50">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-2">Campaign Submissions</p>
                          <div className="space-y-1.5 max-h-40 overflow-y-auto">
                            {campaignLinks[detailItem.id].map((lk, i) => (
                              <div key={i} className="flex items-center justify-between px-2.5 py-1.5 bg-slate-50 rounded-lg">
                                <span className="text-[11px] font-semibold text-slate-700 truncate mr-2">{lk.campaignName}</span>
                                <span className={cn('px-1.5 py-0.5 rounded text-[8px] font-bold uppercase',
                                  lk.submissionStatus === 'APPROVED' ? 'bg-emerald-100 text-emerald-700' :
                                  lk.submissionStatus === 'REJECTED' ? 'bg-red-100 text-red-700' :
                                  'bg-amber-100 text-amber-700'
                                )}>{lk.submissionStatus}{lk.currentStage ? ` · ${lk.currentStage}` : ''}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </button>
                  ) : null}
                  <button onClick={() => setCampaignPushItem(detailItem)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-2xl text-sm font-bold border-2 border-indigo-400 text-indigo-600 bg-white hover:bg-indigo-50 transition-all">
                    <Megaphone size={14} /> Select to Campaign
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Cart slideout (desktop only — centered modal) ─── */}
      {showCart && mode === 'desktop' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowCart(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
              <h3 className="text-lg font-bold flex items-center gap-2"><ShoppingCart size={18} className="text-indigo-600" /> Your Cart</h3>
              <button onClick={() => setShowCart(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {cart.map(c => {
                const optP = c.opts.reduce((a, o) => a + o.opt.extraPrice, 0);
                const lineT = (c.item.basePrice + optP) * c.qty;
                return (
                  <div key={c.id} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate">{nm(c.item.nameEN, c.item.nameAR)}</p>
                      {c.opts.length > 0 && <p className="text-[10px] text-slate-400 truncate">{c.opts.map(o => nm(o.opt.nameEN, o.opt.nameAR)).join(', ')}</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => updateQty(c.id, -1)} className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-100"><Minus size={12} /></button>
                      <span className="text-sm font-bold w-7 text-center">{c.qty}</span>
                      <button onClick={() => updateQty(c.id, 1)} className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-100"><Plus size={12} /></button>
                    </div>
                    <span className="text-sm font-bold text-indigo-600 whitespace-nowrap">{loc.currencySymbol} {lineT.toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
            <div className="px-5 pb-5 pt-3 border-t border-slate-100 space-y-3 shrink-0">
              <div className="flex items-center justify-between"><span className="text-base font-bold text-slate-800">Total</span><span className="text-xl font-bold text-indigo-600">{loc.currencySymbol} {cartTotal.toFixed(2)}</span></div>
              <button className="w-full py-3.5 bg-indigo-600 text-white rounded-2xl text-sm font-bold hover:bg-indigo-700 shadow-lg">Proceed to Checkout (Demo)</button>
            </div>
          </div>
        </div>
      )}

      </div>{/* end scrollable content */}

      {/* ─── Share & Secure Modal ─── */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowShareModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <ExternalLink size={18} className="text-indigo-600" /> Share Public Menu
              </h3>
              <button onClick={() => setShowShareModal(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X size={18} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Section 1: Sharing Link */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Sharing Link</label>
                <div className="flex gap-2">
                  <input type="text" readOnly value={publicLinkUrl}
                    className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-slate-600 font-mono truncate" />
                  <button
                    onClick={() => { navigator.clipboard.writeText(publicLinkUrl); toast.success('Copied!', 'Link copied to clipboard.'); }}
                    className="px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-colors whitespace-nowrap flex items-center gap-1.5">
                    <Copy size={13} /> Copy Link
                  </button>
                </div>
                <button onClick={() => window.open(publicLinkUrl, '_blank')}
                  className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1">
                  <Globe size={11} /> Open in new tab
                </button>
              </div>

              {/* Section 2: Security Controls */}
              <div className="space-y-3 pt-3 border-t border-slate-100">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Security Controls</label>

                {/* Enable toggle */}
                <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                  <div>
                    <p className="text-sm font-bold text-slate-700">Enable Public Link</p>
                    <p className="text-[11px] text-slate-400">Allow anyone with the link to view</p>
                  </div>
                  <button onClick={() => setShareEnabled(e => !e)}
                    className={cn('relative w-11 h-6 rounded-full transition-colors',
                      shareEnabled ? 'bg-emerald-500' : 'bg-slate-300')}>
                    <span className={cn('absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform',
                      shareEnabled && 'translate-x-5')} />
                  </button>
                </div>

                {/* Expiry picker */}
                <div className="bg-slate-50 rounded-xl px-4 py-3 space-y-2">
                  <p className="text-sm font-bold text-slate-700">Automatic Expiry</p>
                  <p className="text-[11px] text-slate-400">Link will stop working after this date & time</p>
                  <input type="datetime-local" value={shareExpiry} onChange={e => setShareExpiry(e.target.value)}
                    className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white" />
                  {shareExpiry && (
                    <button onClick={() => setShareExpiry('')} className="text-[11px] text-red-500 hover:text-red-700 font-medium">
                      Clear expiry (never expires)
                    </button>
                  )}
                </div>

                {/* Save Button */}
                <button onClick={handleSaveSharing} disabled={shareSaving}
                  className="w-full py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                  {shareSaving ? 'Saving…' : 'Save Settings'}
                </button>
              </div>

              {/* Section 3: Danger Zone */}
              <div className="pt-3 border-t border-red-100 space-y-2">
                <label className="text-xs font-bold text-red-500 uppercase tracking-wider">Danger Zone</label>
                <button onClick={handleDeactivateNow} disabled={shareSaving}
                  className="w-full py-2.5 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm font-bold hover:bg-red-100 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                  🔴 Deactivate Link Now
                </button>
                <p className="text-[10px] text-slate-400 text-center">This immediately disables public access and sets expiry to now.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Campaign Target Selector Modal ═══ */}
      {campaignPushItem && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setCampaignPushItem(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 pt-5 pb-3 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center"><Megaphone size={18} className="text-white" /></div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Select to Campaign</h3>
                    <p className="text-[11px] text-slate-500">Push "{campaignPushItem.nameEN}" into a campaign</p>
                  </div>
                </div>
                <button onClick={() => setCampaignPushItem(null)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={16} className="text-slate-400" /></button>
              </div>
            </div>
            <div className="max-h-[400px] overflow-y-auto divide-y divide-slate-100">
              {eligibleCampaigns.length === 0 ? (
                <div className="px-5 py-10 text-center">
                  <Megaphone size={32} className="mx-auto text-slate-200 mb-2" />
                  <p className="text-sm font-semibold text-slate-500">No active campaigns</p>
                  <p className="text-[11px] text-slate-400 mt-1">Create a campaign in the Aggregator module first.</p>
                </div>
              ) : eligibleCampaigns.map(c => {
                const isLinked = campaignLinks[campaignPushItem.id]?.some(lk => lk.campaignId === c.id);
                const isPushing = pushingCampaignId === c.id;
                return (
                  <div key={c.id} className="px-5 py-3.5 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-800">{c.name}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold">
                            {c.discountValue}{c.discountType === 'PERCENTAGE' ? '%' : ` ${loc.currencySymbol}`} OFF
                          </span>
                          <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-bold border',
                            c.status === 'ACTIVE' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-blue-50 border-blue-200 text-blue-700'
                          )}>{c.status}</span>
                          {c.fundingType !== 'NONE' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold">{c.fundingType.replace('_', ' ')}</span>
                          )}
                        </div>
                        {c.requiredItemCount && (
                          <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                            <Package size={9} /> Goal: {c.requiredItemCount} items per brand
                          </p>
                        )}
                        <p className="text-[9px] text-slate-400 mt-0.5">
                          {new Date(c.validFrom).toLocaleDateString()} — {new Date(c.validTo).toLocaleDateString()}
                          {c.aggregators.length > 0 && ` · ${c.aggregators.join(', ')}`}
                        </p>
                      </div>
                      {isLinked ? (
                        <span className="flex items-center gap-1 px-3 py-2 rounded-xl bg-slate-100 text-slate-400 text-[10px] font-bold shrink-0">
                          <PackageCheck size={12} /> Already Sent
                        </span>
                      ) : (
                        <button onClick={() => handleCampaignPush(c)} disabled={isPushing}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-[10px] font-bold hover:from-violet-700 hover:to-indigo-700 shadow-sm transition-all shrink-0 disabled:opacity-60">
                          {isPushing ? <Loader2 size={12} className="animate-spin" /> : <Rocket size={12} />}
                          {isPushing ? 'Sending…' : 'Send to Campaign'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ═══ Post-Push Confirmation Overlay ═══ */}
      {campaignPushResult && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setCampaignPushResult(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden text-center" onClick={e => e.stopPropagation()}>
            <div className="px-6 pt-8 pb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Rocket size={28} className="text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Item Pushed!</h3>
              <p className="text-sm text-slate-500 mt-2">
                <span className="font-semibold text-slate-700">"{campaignPushResult.itemName}"</span> has been submitted to{' '}
                <span className="font-semibold text-violet-600">{campaignPushResult.campaignName}</span>
              </p>
            </div>
            <div className="px-6 pb-6 space-y-2">
              <button
                onClick={() => { setCampaignPushResult(null); nav('/apps/aggregator/submit'); }}
                className="w-full py-3 rounded-xl text-sm font-bold bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700 shadow-sm transition-all flex items-center justify-center gap-2">
                <Search size={14} /> View Submission
              </button>
              <button
                onClick={() => setCampaignPushResult(null)}
                className="w-full py-3 rounded-xl text-sm font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all flex items-center justify-center gap-2">
                <Plus size={14} /> Add Another Item
              </button>
            </div>
          </div>
        </div>
      )}

      {exportProfile && (
        <ExportModal
          isOpen={true}
          data={menuProfileToMenuData(exportProfile)}
          profile={exportProfile}
          currency={loc.currencySymbol}
          onClose={() => setExportProfile(null)}
          onExportTemplate1={() => { exportRelationalTemplate1(exportProfile, `Menu_T1_${safeExportName(exportProfile.brandName)}_${safeExportName(exportProfile.name)}`, loc.currencySymbol); toast.success('Generating Export', 'Template 1 — Detailed download will start shortly.'); setExportProfile(null); }}
          onExportTemplate2={() => { exportRelationalTemplate2(exportProfile, `Menu_T2_${safeExportName(exportProfile.brandName)}_${safeExportName(exportProfile.name)}`, loc.currencySymbol); toast.success('Generating Export', 'Template 2 — Aggregator download will start shortly.'); setExportProfile(null); }}
          onExportBoth={() => { exportRelationalBoth(exportProfile, `Menu_${safeExportName(exportProfile.brandName)}_${safeExportName(exportProfile.name)}`, loc.currencySymbol); toast.success('Generating Export', 'Both Templates download will start shortly.'); setExportProfile(null); }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// INLINE: Schedule Editor (used in Category/Item/Profile drawers)
// ═══════════════════════════════════════════════════════════════════════════════

function ScheduleEditor({ value, onChange }: { value: AvailabilitySchedule | undefined; onChange: (s: AvailabilitySchedule) => void }) {
  const sched = value || DEFAULT_SCHEDULE;
  const isAlwaysOn = sched.alwaysOn !== false;

  const toggleAlwaysOn = () => {
    if (isAlwaysOn) onChange({ alwaysOn: false, days: [...ALL_DAYS], slots: [{ start: '08:00', end: '22:00' }] });
    else onChange({ ...sched, alwaysOn: true });
  };

  const toggleDay = (d: DayOfWeek) => {
    const days = sched.days.includes(d) ? sched.days.filter(x => x !== d) : [...sched.days, d];
    onChange({ ...sched, days });
  };

  const updateSlot = (idx: number, field: 'start' | 'end', val: string) => {
    const slots = sched.slots.map((s, i) => i === idx ? { ...s, [field]: val } : s);
    onChange({ ...sched, slots });
  };

  const addSlot = () => onChange({ ...sched, slots: [...sched.slots, { start: '12:00', end: '18:00' }] });
  const removeSlot = (idx: number) => onChange({ ...sched, slots: sched.slots.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Timer size={12} className="text-indigo-500" />
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Availability Schedule</p>
        </div>
        <button onClick={toggleAlwaysOn}
          className={cn('relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
            isAlwaysOn ? 'bg-emerald-500' : 'bg-slate-300')}>
          <span className={cn('inline-block h-3 w-3 transform rounded-full bg-white transition-transform shadow',
            isAlwaysOn ? 'translate-x-5' : 'translate-x-1')} />
        </button>
      </div>
      {isAlwaysOn ? (
        <p className="text-[10px] text-emerald-600 flex items-center gap-1"><CheckCircle2 size={10} /> Always available (24/7)</p>
      ) : (
        <>
          {/* Day picker */}
          <div className="flex gap-1">
            {ALL_DAYS.map(d => (
              <button key={d} onClick={() => toggleDay(d)}
                className={cn('flex-1 py-1.5 rounded-lg text-[9px] font-bold transition-colors border',
                  sched.days.includes(d) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-500 border-slate-200 hover:border-indigo-300')}>
                {DAY_LABELS[d]}
              </button>
            ))}
          </div>
          {/* Time slots */}
          <div className="space-y-1.5">
            {sched.slots.map((slot, i) => (
              <div key={i} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2 py-1.5">
                <input type="time" value={slot.start} onChange={e => updateSlot(i, 'start', e.target.value)} className="flex-1 text-xs bg-transparent focus:outline-none" />
                <span className="text-[10px] text-slate-400">to</span>
                <input type="time" value={slot.end} onChange={e => updateSlot(i, 'end', e.target.value)} className="flex-1 text-xs bg-transparent focus:outline-none" />
                {sched.slots.length > 1 && (
                  <button onClick={() => removeSlot(i)} className="p-0.5 hover:bg-red-50 rounded"><X size={10} className="text-red-400" /></button>
                )}
              </div>
            ))}
            <button onClick={addSlot} className="text-[10px] font-bold text-indigo-600 hover:underline">+ Add Time Slot</button>
          </div>
        </>
      )}
    </div>
  );
}

/** Compact badge showing schedule status */
function ScheduleBadge({ schedule }: { schedule: AvailabilitySchedule | undefined }) {
  const available = isAvailableNow(schedule);
  if (!schedule || schedule.alwaysOn !== false) return null;
  return (
    <span className={cn('px-1.5 py-0.5 rounded text-[8px] font-bold border inline-flex items-center gap-0.5',
      available ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-600')}>
      <Timer size={8} /> {available ? 'OPEN' : 'CLOSED'}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// UPDATE REQUESTS TAB (Admin approval inbox + Manager request tracking)
// ═══════════════════════════════════════════════════════════════════════════════

const CHANGE_BADGE: Record<ChangeCategory, { label: string; bg: string; text: string }> = {
  PRICE_CHANGE:  { label: 'Price Change',  bg: 'bg-amber-100 border-amber-200',  text: 'text-amber-800' },
  TEXT_UPDATE:    { label: 'Text Update',   bg: 'bg-blue-100 border-blue-200',    text: 'text-blue-800' },
  IMAGE_UPDATE:  { label: 'Image Update',  bg: 'bg-violet-100 border-violet-200', text: 'text-violet-800' },
  VISIBILITY:    { label: 'Visibility',    bg: 'bg-slate-100 border-slate-200',   text: 'text-slate-700' },
  DELETION:      { label: 'Deletion',      bg: 'bg-red-100 border-red-200',       text: 'text-red-800' },
  NEW_ITEM:      { label: 'New Addition',  bg: 'bg-emerald-100 border-emerald-200', text: 'text-emerald-800' },
  OPTION_GROUP:  { label: 'Modifier Update', bg: 'bg-cyan-100 border-cyan-200',  text: 'text-cyan-800' },
  MIXED:         { label: 'Multiple Changes', bg: 'bg-indigo-100 border-indigo-200', text: 'text-indigo-800' },
};

function UpdateRequestsTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'menu-manage', 'profiles', 'admin');
  const toast = useToast();
  const [requests, setRequests] = useState<MenuChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>(isAdmin ? 'PENDING' : '');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReq, setSelectedReq] = useState<MenuChangeRequest | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [processing, setProcessing] = useState(false);

  // ── Department Pipeline State ──
  const [departments, setDepartments] = useState<Department[]>([]);

  const dynamicStages = useMemo(() => {
    const menuDepts = departments.filter(d => (d.linkedModules ?? []).includes('menu-manage'));
    if (menuDepts.length > 0) return menuDepts.sort((a, b) => (a.order ?? 999) - (b.order ?? 999)).map(d => d.name);
    return [] as string[];
  }, [departments]);

  useEffect(() => {
    getRepository().getDepartments().then(setDepartments).catch(() => {});
  }, []);

  // Security gate: can this user approve/forward the request at its current stage?
  // currentStage is now a dept ID (not name) — compare directly.
  const canApprove = (req: MenuChangeRequest): boolean => {
    if (!isAdmin) return false;
    const isSuperAdminOrPlatform = user?.role === 'SUPER_ADMIN' || user?.role === 'PLATFORM_OWNER' || user?.role === 'PLATFORM_SUPPORT';
    if (isSuperAdminOrPlatform) return true;
    if (dynamicStages.length === 0) return true; // no pipeline configured — allow all admins
    const stageId = req.currentStage;
    if (!stageId) return true; // no stage set — allow
    const currentDept = departments.find(d => d.id === stageId && (d.linkedModules ?? []).includes('menu-manage'));
    if (!currentDept) return true; // stage ID not in registry — allow
    return (user?.assignedDepartments ?? []).includes(currentDept.id);
  };

  const load = async () => {
    try {
      const raw = await fetchChangeRequests(filter ? { status: filter } : undefined);
      setRequests(raw as MenuChangeRequest[]);
    } catch { /* fallback empty */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [filter]);

  // ── Sync: auto-refresh when change requests are submitted/approved/rejected elsewhere ──
  useEffect(() => {
    const handler = () => { load(); };
    window.addEventListener(MENU_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(MENU_DATA_UPDATED_EVENT, handler);
  }, []);

  const handleApprove = async (req: MenuChangeRequest) => {
    setProcessing(true);
    setShowApproveConfirm(false);
    try {
      const result = await approveChangeRequest(req.id);
      if (result?.nextStage) {
        // ACTION A: Pipeline forwarded — keep drawer open, advance stepper
        const nextDept = departments.find(d => d.name === result.nextStage);
        const nextStageId = result.nextStageId ?? nextDept?.id ?? result.nextStage;
        const updated: MenuChangeRequest = { ...req, currentStage: nextStageId, status: 'UNDER_REVIEW' };
        setSelectedReq(updated);
        setRequests(prev => prev.map(r => r.id === req.id ? updated : r));
        toast.success('Approved & Forwarded', `Forwarded to ${result.nextStage} for review.`);
      } else {
        // ACTION B: Final approval — close drawer, remove from list, publish event
        setRequests(prev => prev.filter(r => r.id !== req.id));
        setSelectedReq(null);
        toast.success('Approved & Published', `"${req.menuName}" changes are now live.`);
        window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
      }
      load();
    } catch (e: any) {
      toast.error('Failed', e?.message || 'Could not approve.');
      load();
    }
    setProcessing(false);
  };

  const handleReject = async () => {
    if (!selectedReq) return;
    const reqId = selectedReq.id;
    const reqName = selectedReq.menuName;
    setProcessing(true);
    try {
      // Optimistic: instantly remove from list & close drawer
      setRequests(prev => prev.filter(r => r.id !== reqId));
      setSelectedReq(null); setShowRejectModal(false); setRejectNote('');
      toast.success('Rejected', `"${reqName}" change request rejected.`);
      window.dispatchEvent(new Event(MENU_DATA_UPDATED_EVENT));
      await rejectChangeRequest(reqId, rejectNote);
      load();
    } catch (e: any) {
      toast.error('Failed', e?.message || 'Could not reject.');
      load();
    }
    setProcessing(false);
  };

  // ── Comparison model via ComparisonService ──
  const comparison = useMemo<ComparisonModel | null>(() => {
    if (!selectedReq) return null;
    try { return buildComparison(selectedReq); } catch { return null; }
  }, [selectedReq]);

  // ── Legacy full-menu diff (non-entity requests) ──
  const isEntityLevel = !!(selectedReq?.entityId && selectedReq?.entityType);
  const diffs = useMemo(() => {
    if (!selectedReq || isEntityLevel) return null;
    try {
      const oldCats = selectedReq.previousData ? JSON.parse(selectedReq.previousData) : [];
      const newCats = JSON.parse(selectedReq.changes);
      return computeMenuDiff(oldCats, newCats);
    } catch { return null; }
  }, [selectedReq, isEntityLevel]);
  const diffCounts = diffs ? countChanges(diffs) : null;

  // ── Precompute per-request comparisons for the list badges ──
  const requestComparisons = useMemo(() => {
    const map = new Map<string, ComparisonModel>();
    for (const r of requests) {
      if (r.entityId && r.entityType) {
        try { map.set(r.id, buildComparison(r)); } catch { /* skip */ }
      }
    }
    return map;
  }, [requests]);

  const pendingCount = requests.filter(r => r.status === 'SUBMITTED' || r.status === 'UNDER_REVIEW').length;

  // ── Client-side text search across brand, item name, requester ──
  const filteredRequests = useMemo(() => {
    if (!searchQuery.trim()) return requests;
    const q = searchQuery.toLowerCase();
    return requests.filter(r => {
      const comp = requestComparisons.get(r.id);
      const entityName = comp?.entityName || r.menuName || '';
      const brandName = (r as any).brandName || '';
      return entityName.toLowerCase().includes(q)
        || brandName.toLowerCase().includes(q)
        || r.requestedByName.toLowerCase().includes(q)
        || r.menuName.toLowerCase().includes(q);
    });
  }, [requests, searchQuery, requestComparisons]);

  const STATUS_COLORS: Record<string, string> = {
    SUBMITTED: 'bg-amber-50 border-amber-200 text-amber-700',
    UNDER_REVIEW: 'bg-blue-50 border-blue-200 text-blue-700',
    APPROVED: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    REJECTED: 'bg-red-50 border-red-200 text-red-700',
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-indigo-400" /></div>;

  // ── Field highlight helper for comparison drawer ──
  const fieldBg = (f: ComparisonField) =>
    f.highlight === 'modified' ? 'bg-blue-50/60' :
    f.highlight === 'added' ? 'bg-emerald-50/60' :
    f.highlight === 'removed' ? 'bg-red-50/60' : '';

  return (
    <div className="space-y-4">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">{isAdmin ? 'Approval Inbox' : 'My Requests'}</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {isAdmin
              ? pendingCount > 0
                ? <span className="text-amber-600 font-semibold">{pendingCount} request{pendingCount !== 1 ? 's' : ''} awaiting your review</span>
                : 'All caught up — no pending requests.'
              : 'Track your change request history.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[
            { value: '', label: 'All' },
            { value: 'PENDING', label: 'Pending' },
            { value: 'APPROVED', label: 'Approved' },
            { value: 'REJECTED', label: 'Rejected' },
          ].map(f => (
            <button key={f.value} onClick={() => setFilter(f.value)}
              className={cn('px-3 py-1.5 rounded-lg text-xs font-bold transition-colors border',
                filter === f.value ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300')}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Search Bar ── */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search by brand, item name, or requester…"
          className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={14} /></button>
        )}
      </div>

      {/* ── Empty State ── */}
      {filteredRequests.length === 0 ? (
        <div className="py-16 text-center border-2 border-dashed border-slate-200 rounded-xl">
          <CheckCircle2 size={28} className="mx-auto text-emerald-300 mb-2" />
          <p className="text-sm font-semibold text-slate-500">{searchQuery ? 'No matching requests' : filter ? `No ${filter.toLowerCase()} requests` : 'No requests yet'}</p>
          <p className="text-[11px] text-slate-400 mt-0.5">{searchQuery ? 'Try a different search term.' : 'Change requests will appear here when submitted.'}</p>
        </div>
      ) : (
        /* ── Professional Inbox Table ── */
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-50/80 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-bold uppercase text-[10px] tracking-wider text-slate-500">Brand</th>
                <th className="text-left px-4 py-3 font-bold uppercase text-[10px] tracking-wider text-slate-500">Target</th>
                <th className="text-left px-4 py-3 font-bold uppercase text-[10px] tracking-wider text-slate-500">Type</th>
                <th className="text-left px-4 py-3 font-bold uppercase text-[10px] tracking-wider text-slate-500">Requester</th>
                <th className="text-left px-4 py-3 font-bold uppercase text-[10px] tracking-wider text-slate-500">Status</th>
                <th className="text-right px-4 py-3 font-bold uppercase text-[10px] tracking-wider text-slate-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredRequests.map(r => {
                const comp = requestComparisons.get(r.id);
                const badge = comp ? CHANGE_BADGE[comp.changeCategory] : null;
                const isPending = r.status === 'SUBMITTED' || r.status === 'UNDER_REVIEW';
                return (
                  <tr key={r.id} className={cn('transition-colors', isPending ? 'hover:bg-amber-50/30' : 'hover:bg-slate-50/50')}>
                    {/* BRAND */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center shrink-0 border border-indigo-200/50">
                          <span className="text-[10px] font-black text-indigo-600">{((r as any).brandName || r.menuName || '?').charAt(0).toUpperCase()}</span>
                        </div>
                        <span className="text-[11px] font-semibold text-slate-700 truncate max-w-[100px]">{(r as any).brandName || '—'}</span>
                      </div>
                    </td>
                    {/* TARGET */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                          {r.entityType === 'profile' ? <BookOpen size={14} className="text-amber-500" />
                            : r.entityType === 'category' ? <FolderOpen size={14} className="text-violet-500" />
                            : r.entityType === 'optionGroup' ? <Layers size={14} className="text-cyan-500" />
                            : <Package size={14} className="text-indigo-500" />}
                        </div>
                        <div className="min-w-0">
                          <p className="text-[12px] font-bold text-slate-800 truncate">{comp?.entityName || r.menuName || 'Untitled'}</p>
                          <p className="text-[10px] text-slate-400 truncate">{r.menuName} · <span className="capitalize">{r.entityType || 'menu'}</span></p>
                        </div>
                      </div>
                    </td>
                    {/* TYPE */}
                    <td className="px-4 py-3">
                      {r.changeType === 'CREATE_PROFILE' ? (
                        <span className="px-2 py-0.5 rounded-md text-[9px] font-bold border bg-amber-50 border-amber-200 text-amber-700">[NEW PROFILE REQUEST]</span>
                      ) : badge ? (
                        <span className={cn('px-2 py-0.5 rounded-md text-[9px] font-bold border', badge.bg, badge.text)}>{badge.label}</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-md text-[9px] font-bold border bg-slate-100 border-slate-200 text-slate-600">Full Menu</span>
                      )}
                    </td>
                    {/* REQUESTER */}
                    <td className="px-4 py-3">
                      <p className="text-[11px] font-semibold text-slate-700">{r.requestedByName}</p>
                      <p className="text-[10px] text-slate-400">{new Date(r.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    </td>
                    {/* STATUS */}
                    <td className="px-4 py-3">
                      {isPending ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[9px] font-bold border border-amber-200 bg-amber-50 text-amber-700 whitespace-nowrap">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse shrink-0" />
                          Waiting on: {r.currentStage ? (departments.find(d => d.id === r.currentStage)?.name ?? 'Review') : 'Review'}
                        </span>
                      ) : r.status === 'APPROVED' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold border border-emerald-200 bg-emerald-50 text-emerald-700">
                          ✅ APPROVED
                        </span>
                      ) : r.status === 'REJECTED' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold border border-red-200 bg-red-50 text-red-700">
                          ✕ REJECTED
                        </span>
                      ) : (
                        <span className={cn('px-2 py-0.5 rounded-md text-[9px] font-bold border', STATUS_COLORS[r.status] || 'bg-slate-100')}>
                          {r.status.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    {/* ACTION */}
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setSelectedReq(r)}
                        className={cn('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold transition-colors',
                          isAdmin && isPending
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
                        <Search size={11} /> {isAdmin && isPending ? 'Review & Compare' : 'View Details'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ Side Drawer: High-Fidelity Comparison ═══ */}
      {selectedReq && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 bg-black/30 animate-[fadeIn_0.2s_ease]" onClick={() => !processing && setSelectedReq(null)} />

          {/* Drawer */}
          <div
            className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl bg-white shadow-2xl flex flex-col border-l border-slate-200 animate-slide-in-right">

            {/* ── Drawer Header ── */}
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/50 shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center shrink-0',
                    comparison?.isDeletion ? 'bg-red-100' : comparison?.isAddition ? 'bg-emerald-100' : 'bg-indigo-100')}>
                    {comparison?.isDeletion ? <Trash2 size={18} className="text-red-600" />
                      : comparison?.isAddition ? <Plus size={18} className="text-emerald-600" />
                      : <Activity size={18} className="text-indigo-600" />}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-bold text-slate-900 truncate">
                      {(selectedReq.status === 'SUBMITTED' || selectedReq.status === 'UNDER_REVIEW') && selectedReq.currentStage
                        ? `${departments.find(d => d.id === selectedReq.currentStage)?.name ?? 'Review'} Review: ${comparison?.entityName || selectedReq.menuName}`
                        : `Reviewing: ${comparison?.entityName || selectedReq.menuName}`}
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {comparison?.entityTypeLabel || 'Menu'} · by <span className="font-semibold text-slate-700">{selectedReq.requestedByName}</span> · {new Date(selectedReq.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {comparison && (
                    <span className={cn('px-2.5 py-1 rounded-md text-[10px] font-bold border', CHANGE_BADGE[comparison.changeCategory]?.bg, CHANGE_BADGE[comparison.changeCategory]?.text)}>
                      {comparison.changeTypeLabel}
                    </span>
                  )}
                  <button onClick={() => setSelectedReq(null)} className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors"><X size={16} className="text-slate-400" /></button>
                </div>
              </div>
            </div>

            {/* ── Admin Note (if rejected previously) ── */}
            {selectedReq.adminNote && (
              <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <p className="text-[10px] font-bold text-red-600 uppercase tracking-wider mb-1">Admin Rejection Note</p>
                <p className="text-xs text-red-700">{selectedReq.adminNote}</p>
              </div>
            )}

            {/* ── Department Pipeline Timeline ── */}
            {dynamicStages.length > 0 && (
              <div className="mx-6 mt-4 px-5 py-4 bg-slate-50 border border-slate-200 rounded-xl">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Review Pipeline</p>
                <ReviewTimeline
                  compact
                  currentStage={
                    selectedReq.status === 'APPROVED'
                      ? 'DONE'
                      : (selectedReq.currentStage
                          ? (departments.find(d => d.id === selectedReq.currentStage)?.name ?? dynamicStages[0])
                          : dynamicStages[0])
                  }
                  status={
                    selectedReq.status === 'APPROVED' ? SubmissionStatus.APPROVED
                    : selectedReq.status === 'REJECTED' ? SubmissionStatus.REJECTED
                    : SubmissionStatus.IN_REVIEW
                  }
                  reviewHistory={[]}
                  stages={dynamicStages}
                />
              </div>
            )}

            {/* ── Drawer Body: Side-by-Side Comparison ── */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {isEntityLevel && comparison ? (
                <>
                  {/* ── Dual Column View ── */}
                  {comparison.isDeletion ? (
                    <div className="border-2 border-dashed border-red-300 rounded-xl p-6 text-center bg-red-50/50">
                      <Trash2 size={32} className="mx-auto text-red-400 mb-2" />
                      <p className="text-sm font-bold text-red-700">This {comparison.entityTypeLabel} is marked for deletion</p>
                      <p className="text-xs text-red-500 mt-1">Approving will permanently remove "{comparison.entityName}" from the live menu.</p>
                    </div>
                  ) : (
                    <>
                      {/* Image Comparison */}
                      {comparison.image && comparison.image.changed && (
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Photo Comparison</p>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="border border-slate-200 rounded-xl overflow-hidden">
                              <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200"><p className="text-[9px] font-bold text-slate-500 uppercase">Current Photo</p></div>
                              {comparison.image.currentUrl ? (
                                <img src={comparison.image.currentUrl} alt="Current" className="w-full h-40 object-cover" />
                              ) : (
                                <div className="w-full h-40 bg-slate-100 flex items-center justify-center"><ImageIcon size={20} className="text-slate-300" /><span className="text-[10px] text-slate-400 ml-1">No image</span></div>
                              )}
                            </div>
                            <div className="border-2 border-blue-200 rounded-xl overflow-hidden bg-blue-50/30">
                              <div className="px-3 py-1.5 bg-blue-50 border-b border-blue-200"><p className="text-[9px] font-bold text-blue-600 uppercase">New Photo</p></div>
                              {comparison.image.proposedUrl ? (
                                <img src={comparison.image.proposedUrl} alt="Proposed" className="w-full h-40 object-cover" />
                              ) : (
                                <div className="w-full h-40 bg-slate-100 flex items-center justify-center"><ImageIcon size={20} className="text-slate-300" /><span className="text-[10px] text-slate-400 ml-1">No image</span></div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Field-by-Field Comparison: Dual Column */}
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Field Comparison</p>
                        <div className="grid grid-cols-2 gap-3">
                          {/* LEFT: Current (Live) */}
                          <div className="border border-slate-200 rounded-xl overflow-hidden">
                            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                              <Globe size={12} className="text-slate-500" />
                              <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Live on Menu</p>
                            </div>
                            <div className="divide-y divide-slate-100">
                              {comparison.fields.map(f => (
                                <div key={f.key} className={cn('px-4 py-2.5', f.changed ? 'bg-red-50/30' : '')}>
                                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">{f.label}</p>
                                  <p className={cn('text-[12px] font-medium', f.changed ? 'text-slate-500 line-through decoration-red-400' : 'text-slate-700')}>
                                    {f.currentValue || <span className="text-slate-300 italic">Empty</span>}
                                  </p>
                                </div>
                              ))}
                              {comparison.fields.length === 0 && comparison.isAddition && (
                                <div className="px-4 py-6 text-center text-[11px] text-slate-400 italic">New {comparison.entityTypeLabel} — no existing data</div>
                              )}
                            </div>
                          </div>

                          {/* RIGHT: Proposed (New) */}
                          <div className="border-2 border-blue-200 rounded-xl overflow-hidden bg-blue-50/10">
                            <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-200 flex items-center gap-2">
                              <Edit2 size={12} className="text-blue-600" />
                              <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">Proposed Changes</p>
                            </div>
                            <div className="divide-y divide-blue-100/50">
                              {comparison.fields.map(f => (
                                <div key={f.key} className={cn('px-4 py-2.5', fieldBg(f))}>
                                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">{f.label}</p>
                                  <p className={cn('text-[12px] font-semibold', f.changed ? 'text-blue-700' : 'text-slate-700')}>
                                    {f.proposedValue || <span className="text-slate-300 italic">Empty</span>}
                                  </p>
                                </div>
                              ))}
                              {comparison.fields.length === 0 && comparison.isAddition && (
                                <div className="px-4 py-6 text-center text-[11px] text-emerald-500 font-semibold">New {comparison.entityTypeLabel} to be added</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Option Groups */}
                      {comparison.optionGroups.length > 0 && (
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Modifier Groups</p>
                          <div className="space-y-2">
                            {comparison.optionGroups.map(g => (
                              <div key={g.id} className={cn('border rounded-xl overflow-hidden',
                                g.status === 'added' ? 'border-emerald-200' : g.status === 'removed' ? 'border-red-200' : 'border-blue-200')}>
                                <div className={cn('px-4 py-2 flex items-center gap-2',
                                  g.status === 'added' ? 'bg-emerald-50' : g.status === 'removed' ? 'bg-red-50' : 'bg-blue-50')}>
                                  <Layers size={12} className={cn(g.status === 'added' ? 'text-emerald-600' : g.status === 'removed' ? 'text-red-600' : 'text-blue-600')} />
                                  <span className="text-[11px] font-bold text-slate-800">{g.name}</span>
                                  <span className={cn('px-1.5 py-0.5 rounded text-[8px] font-bold uppercase',
                                    g.status === 'added' ? 'bg-emerald-200 text-emerald-800' : g.status === 'removed' ? 'bg-red-200 text-red-800' : 'bg-blue-200 text-blue-800')}>
                                    {g.status}
                                  </span>
                                </div>
                                {g.fields.map(f => (
                                  <div key={f.key} className="px-4 py-1.5 border-t border-slate-100 flex items-center gap-2 text-[11px]">
                                    <span className="text-slate-500 font-medium w-28">{f.label}:</span>
                                    <span className="text-red-500 line-through text-[10px]">{f.currentValue || '-'}</span>
                                    <ChevronRight size={10} className="text-slate-300" />
                                    <span className="text-blue-600 font-semibold text-[10px]">{f.proposedValue}</span>
                                  </div>
                                ))}
                                {g.options.filter(o => o.status !== 'unchanged').map((o, i) => (
                                  <div key={i} className="px-4 py-1 border-t border-slate-100 flex items-center gap-2 text-[10px]">
                                    <span className={cn('w-1.5 h-1.5 rounded-full', o.status === 'added' ? 'bg-emerald-500' : 'bg-red-500')} />
                                    <span className={cn('font-medium', o.status === 'removed' ? 'text-red-500 line-through' : 'text-emerald-700')}>{o.name}</span>
                                    {o.price && <span className="text-slate-400 ml-auto">{o.price}</span>}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : diffs && diffs.length > 0 ? (
                /* Legacy full-menu diff */
                <div className="space-y-3">
                  {diffCounts && (
                    <div className="flex items-center gap-3">
                      {diffCounts.added > 0 && <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-bold">+{diffCounts.added} Added</span>}
                      {diffCounts.modified > 0 && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-bold">{diffCounts.modified} Modified</span>}
                      {diffCounts.removed > 0 && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold">-{diffCounts.removed} Removed</span>}
                    </div>
                  )}
                  {diffs.map(cat => (
                    <div key={cat.id} className={cn('border rounded-xl overflow-hidden',
                      cat.type === 'added' ? 'border-emerald-200' : cat.type === 'removed' ? 'border-red-200' : 'border-blue-200')}>
                      <div className={cn('px-4 py-2.5 flex items-center gap-2',
                        cat.type === 'added' ? 'bg-emerald-50' : cat.type === 'removed' ? 'bg-red-50' : 'bg-blue-50')}>
                        <FolderOpen size={14} className={cn(cat.type === 'added' ? 'text-emerald-500' : cat.type === 'removed' ? 'text-red-500' : 'text-blue-500')} />
                        <span className="text-xs font-bold text-slate-700">{cat.nameEN}</span>
                        <span className={cn('px-1.5 py-0.5 rounded text-[8px] font-bold uppercase',
                          cat.type === 'added' ? 'bg-emerald-200 text-emerald-800' : cat.type === 'removed' ? 'bg-red-200 text-red-800' : 'bg-blue-200 text-blue-800')}>
                          {cat.type}
                        </span>
                      </div>
                      {cat.fields.map(f => (
                        <div key={f.field} className="px-4 py-1.5 border-t border-slate-100 flex items-center gap-2 text-[11px]">
                          <span className="text-slate-500 font-medium w-24">{f.label}:</span>
                          <span className="text-red-500 line-through">{String(f.oldValue ?? '-')}</span>
                          <ChevronRight size={10} className="text-slate-300" />
                          <span className="text-blue-600 font-semibold">{String(f.newValue ?? '-')}</span>
                        </div>
                      ))}
                      {cat.items.map(item => (
                        <div key={item.id} className={cn('border-t px-4 py-2',
                          item.type === 'added' ? 'bg-emerald-50/50 border-emerald-100' : item.type === 'removed' ? 'bg-red-50/50 border-red-100' : 'border-slate-100')}>
                          <div className="flex items-center gap-2">
                            <Package size={11} className={cn(item.type === 'added' ? 'text-emerald-500' : item.type === 'removed' ? 'text-red-500' : 'text-blue-500')} />
                            <span className="text-[11px] font-semibold text-slate-700">{item.nameEN}</span>
                            <span className={cn('px-1 py-0.5 rounded text-[7px] font-bold uppercase',
                              item.type === 'added' ? 'bg-emerald-200 text-emerald-800' : item.type === 'removed' ? 'bg-red-200 text-red-800' : 'bg-blue-200 text-blue-800')}>
                              {item.type}
                            </span>
                          </div>
                          {item.fields.map(f => (
                            <div key={f.field} className="flex items-center gap-2 pl-5 mt-1 text-[10px]">
                              <span className="text-slate-400 w-20">{f.label}:</span>
                              <span className="text-red-400 line-through">{String(f.oldValue ?? '-')}</span>
                              <ChevronRight size={8} className="text-slate-300" />
                              <span className="text-blue-600 font-semibold">{String(f.newValue ?? '-')}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-10 text-center">
                  <FileText size={24} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-400">No comparison data available</p>
                </div>
              )}
            </div>

            {/* ── Action Footer ── */}
            {isAdmin && (selectedReq.status === 'SUBMITTED' || selectedReq.status === 'UNDER_REVIEW') && (
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/80 flex items-center justify-between shrink-0">
                {canApprove(selectedReq)
                  ? <p className="text-[10px] text-slate-400">Review the comparison above before taking action.</p>
                  : <p className="text-[10px] text-amber-600 font-semibold">⚠️ You are not assigned to the current review stage ({selectedReq.currentStage ?? '—'}).</p>
                }
                <div className="flex items-center gap-2">
                  <button onClick={() => { setRejectNote(''); setShowRejectModal(true); }} disabled={processing || !canApprove(selectedReq)}
                    className="flex items-center gap-1.5 px-4 py-2.5 bg-white border border-red-200 text-red-700 rounded-xl text-xs font-bold hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    <XCircle size={14} /> Reject with Note
                  </button>
                  <button onClick={() => setShowApproveConfirm(true)} disabled={processing || !canApprove(selectedReq)}
                    className="flex items-center gap-1.5 px-4 py-2.5 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">
                    <CheckCircle2 size={14} /> Approve & Forward
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ═══ Approve Confirmation Modal ═══ */}
      {showApproveConfirm && selectedReq && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={() => setShowApproveConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle2 size={24} className="text-emerald-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 text-center">
              {selectedReq && dynamicStages.length > 0 && selectedReq.currentStage && dynamicStages.indexOf(selectedReq.currentStage) < dynamicStages.length - 1
                ? 'Forward to Next Stage?'
                : 'Push to Live Menu?'
              }
            </h3>
            <p className="text-xs text-slate-500 text-center">
              {selectedReq && dynamicStages.length > 0 && selectedReq.currentStage && dynamicStages.indexOf(selectedReq.currentStage) < dynamicStages.length - 1
                ? `This will forward the request to the next review stage.`
                : `Are you sure you want to publish these changes to the `
              }
              {selectedReq && !(dynamicStages.length > 0 && selectedReq.currentStage && dynamicStages.indexOf(selectedReq.currentStage) < dynamicStages.length - 1) && (
                <><span className="font-bold text-emerald-700">LIVE</span> menu? This will immediately affect customer-facing data.</>
              )}
            </p>
            <div className="flex justify-center gap-2 pt-2">
              <button onClick={() => setShowApproveConfirm(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg font-medium">Cancel</button>
              <button onClick={() => handleApprove(selectedReq)} disabled={processing}
                className="flex items-center gap-1.5 px-5 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 disabled:opacity-50 shadow-sm">
                {processing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                {selectedReq && dynamicStages.length > 0 && selectedReq.currentStage && dynamicStages.indexOf(selectedReq.currentStage) < dynamicStages.length - 1
                  ? 'Yes, Forward'
                  : 'Yes, Publish Live'
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Reject Modal ═══ */}
      {showRejectModal && selectedReq && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={() => setShowRejectModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><XCircle size={18} className="text-red-500" /> Reject Request</h3>
            <p className="text-xs text-slate-500">Provide a reason for rejecting <span className="font-semibold text-slate-700">"{comparison?.entityName || selectedReq.menuName}"</span>.</p>
            <textarea value={rejectNote} onChange={e => setRejectNote(e.target.value)} placeholder="e.g. Invalid Arabic translation, price too high…"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-red-400 h-24 resize-none" autoFocus />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg font-medium">Cancel</button>
              <button onClick={handleReject} disabled={processing}
                className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-50">
                {processing ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 5. SETTINGS TAB
// ═══════════════════════════════════════════════════════════════════════════════

function SettingsTab({ user }: { user: User | null }) {
  const canEdit = hasTabVariant(user, 'menu-manage', 'settings', 'admin');
  const repo = getRepository();
  const toast = useToast();
  const [settings, setSettings] = useState<MenuSettings>(DEFAULT_MENU_SETTINGS);
  const [saving, setSaving] = useState(false);

  const applySettings = useCallback((raw?: Partial<MenuSettings> | Record<string, unknown> | null) => {
    setSettings(toMenuSettingsState(raw));
  }, []);

  // Profiles list for module connections display
  const [sharingProfiles, setSharingProfiles] = useState<MenuProfile[]>([]);

  const loadSettings = useCallback(() => {
    if (isRemoteMode()) {
      fetchMenuSettings().then(s => { applySettings(s); }).catch(() => { applySettings(DEFAULT_MENU_SETTINGS); });
    } else {
      repo.getAll<MenuSettings>('menu-settings').then(all => { applySettings(all[0] || DEFAULT_MENU_SETTINGS); }).catch(() => { applySettings(DEFAULT_MENU_SETTINGS); });
    }
  }, [applySettings, repo]);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => {
    const handleSettingsUpdated = (event: Event) => {
      const raw = getSettingsFromEvent(event);
      if (raw) {
        applySettings(raw);
        return;
      }
      loadSettings();
    };
    window.addEventListener(MENU_SETTINGS_UPDATED, handleSettingsUpdated);
    return () => window.removeEventListener(MENU_SETTINGS_UPDATED, handleSettingsUpdated);
  }, [applySettings, loadSettings]);

  // Load profiles for module connections display
  useEffect(() => {
    repo.getAll<MenuProfile>('menu-profiles').then(all => {
      const active = all.filter(p => p.status !== MenuProfileStatus.ARCHIVED);
      setSharingProfiles(active);
    });
  }, []);

  const persistSettings = async (nextSettings: MenuSettings, successMessage?: string) => {
    setSaving(true);
    try {
      if (isRemoteMode()) {
        await saveMenuSettings(nextSettings as unknown as Record<string, unknown>);
      } else {
        await repo.save('menu-settings', user?.orgId ?? 'default', { ...nextSettings, orgId: user?.orgId });
      }
      window.dispatchEvent(new CustomEvent<MenuSettingsEventDetail>(MENU_SETTINGS_UPDATED, { detail: { settings: nextSettings } }));
      if (successMessage) toast.success('Saved', successMessage);
      return true;
    } catch {
      toast.error('Save Failed', 'Could not update menu settings.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    await persistSettings(settings, 'Menu settings have been updated.');
  };

  const handleEngineToggle = async () => {
    const previous = settings;
    const nextSettings = {
      ...settings,
      allowNonAdminRecipeAccess: !settings.allowNonAdminRecipeAccess,
      allowNonAdminCostAccess: settings.allowNonAdminRecipeAccess ? settings.allowNonAdminCostAccess : false,
    };
    setSettings(nextSettings);
    const saved = await persistSettings(nextSettings);
    if (!saved) setSettings(previous);
  };

  const handleManagerVisibilityToggle = async () => {
    const previous = settings;
    const nextSettings = {
      ...settings,
      allowNonAdminRecipeAccess: true,
      allowNonAdminCostAccess: !settings.allowNonAdminCostAccess,
    };
    setSettings(nextSettings);
    const saved = await persistSettings(nextSettings);
    if (!saved) setSettings(previous);
  };

  const secLang = SECONDARY_LANGUAGES.find(l => l.code === settings.secondaryLanguage) || SECONDARY_LANGUAGES[0];

  const [settingsSubTab, setSettingsSubTab] = useState<'general' | 'pricing' | 'security' | 'sharing'>('general');

  const subTabs: { key: typeof settingsSubTab; label: string }[] = [
    { key: 'general', label: 'General' },
    { key: 'pricing', label: 'Pricing' },
    ...(canEdit ? [{ key: 'security' as const, label: 'Security' }] : []),
    ...(canEdit ? [{ key: 'sharing' as const, label: 'Sharing' }] : []),
  ];

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Header + Save button */}
      <div className="flex items-center justify-between">
        <div><h2 className="text-2xl font-bold tracking-tight">Menu Settings</h2><p className="text-sm text-slate-500 mt-1">Configure language, currency, and module preferences.</p></div>
        {canEdit && (
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700 disabled:opacity-50 shadow-sm"><Check size={14} /> {saving ? 'Saving…' : 'Save Settings'}</button>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
        {subTabs.map(t => (
          <button key={t.key} onClick={() => setSettingsSubTab(t.key)}
            className={cn('px-4 py-2 rounded-lg text-xs font-bold transition-all',
              settingsSubTab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── General ─── */}
      {settingsSubTab === 'general' && (
        <div className="space-y-6">
          {/* Language Configuration */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Language Configuration</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Primary Language</label>
                <div className="w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-500 cursor-not-allowed">English (Fixed)</div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Secondary Language</label>
                <select value={settings.secondaryLanguage} onChange={e => canEdit && setSettings(p => ({ ...p, secondaryLanguage: e.target.value }))} disabled={!canEdit}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed">
                  {SECONDARY_LANGUAGES.map(l => (
                    <option key={l.code} value={l.code}>{l.name} ({l.nativeName})</option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-400 mt-1">All "Name (AR)" labels will change to "Name ({secLang.name})"</p>
              </div>
            </div>
          </div>

          {/* Currency Configuration */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Currency Configuration</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Currency Symbol</label>
                <select value={settings.currency} onChange={e => canEdit && setSettings(p => ({ ...p, currency: e.target.value }))} disabled={!canEdit}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed">
                  {CURRENCY_OPTIONS.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-400 mt-1">Or enter a custom symbol below</p>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Custom Symbol</label>
                <input value={settings.currency} onChange={e => canEdit && setSettings(p => ({ ...p, currency: e.target.value }))} disabled={!canEdit}
                  placeholder="e.g. QAR, $, €" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed" />
              </div>
            </div>
          </div>

          {/* Other Settings */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Other Settings</p>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Ministry Permit Expiry Warning (Days)</label>
              <input type="number" min="1" max="365" value={settings.expiryWarningDays} onChange={e => setSettings(p => ({ ...p, expiryWarningDays: Number(e.target.value) }))} disabled={!canEdit} className="w-48 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm disabled:opacity-50" />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <div>
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center gap-2"><CircleDollarSign size={14} className="text-amber-500" /> Financial Visibility</p>
              <p className="text-[10px] text-slate-400 mt-1">Recipe and cost controls are shown here so they are visible immediately in Settings.</p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-xs font-semibold text-slate-700">Enable Recipe & Costing Engine</label>
                <p className="text-[10px] text-slate-400 mt-0.5">When OFF, the Cost input and profit display vanish from the item drawer for every role.</p>
              </div>
              <button onClick={handleEngineToggle}
                disabled={saving || !canEdit}
                className={cn('relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                  settings.allowNonAdminRecipeAccess ? 'bg-teal-600' : 'bg-slate-300',
                  (saving || !canEdit) && 'opacity-60 cursor-not-allowed')}>
                <span className={cn('inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow',
                  settings.allowNonAdminRecipeAccess ? 'translate-x-6' : 'translate-x-1')} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <label className="block text-xs font-semibold text-slate-700">Show Cost (Internal) in Manager Accounts</label>
                  <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold', settings.allowNonAdminRecipeAccess && settings.allowNonAdminCostAccess ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500')}>
                    {settings.allowNonAdminRecipeAccess && settings.allowNonAdminCostAccess ? 'ON' : 'OFF'}
                  </span>
                </div>
                <p className="text-[10px] text-slate-400 mt-0.5">When ON, Managers can see the Cost (Internal) field and Profit in read-only mode. Digital Preview Cost stays Admin-only.</p>
              </div>
              <button onClick={handleManagerVisibilityToggle}
                disabled={saving || !canEdit || !settings.allowNonAdminRecipeAccess}
                className={cn('relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                  settings.allowNonAdminCostAccess ? 'bg-amber-500' : 'bg-slate-300',
                  (saving || !canEdit || !settings.allowNonAdminRecipeAccess) && 'opacity-60 cursor-not-allowed')}>
                <span className={cn('inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow',
                  settings.allowNonAdminCostAccess ? 'translate-x-6' : 'translate-x-1')} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Pricing ─── */}
      {settingsSubTab === 'pricing' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
            <div>
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center gap-2"><DollarSign size={14} className="text-indigo-500" /> Global Pricing Strategy</p>
              <p className="text-[10px] text-slate-400 mt-1">Controls how product card prices are displayed for zero-price items with mandatory option groups. Selects a single option value from the pool of all required choices. A "Pinned" option always takes priority over this setting.</p>
            </div>
            <div className="space-y-2.5">
              {([
                { value: 'LOWEST' as const, label: 'Show Lowest Mandatory Option', desc: 'Finds the single lowest price among all required choices. Displays as "From QAR X".' },
                { value: 'HIGHEST' as const, label: 'Show Highest Mandatory Option', desc: 'Finds the single highest price among all required choices. Displays as "Up to QAR X".' },
              ]).map(opt => (
                <button key={opt.value} onClick={() => canEdit && setSettings(p => ({ ...p, pricingStrategy: opt.value }))}
                  disabled={!canEdit}
                  className={cn('w-full flex items-start gap-3 px-4 py-3 rounded-xl border text-left transition-all',
                    (settings.pricingStrategy || 'LOWEST') === opt.value
                      ? 'border-indigo-300 bg-indigo-50/60 ring-1 ring-indigo-200'
                      : 'border-slate-200 hover:border-slate-300 disabled:opacity-50')}>
                  <div className={cn('mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0',
                    (settings.pricingStrategy || 'LOWEST') === opt.value ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300')}>
                    {(settings.pricingStrategy || 'LOWEST') === opt.value && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-700">{opt.label}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{opt.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ─── Security ─── */}
      {settingsSubTab === 'security' && canEdit && (
        <div className="space-y-6">
          {/* Approval Policy */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <div>
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center gap-2"><Shield size={14} className="text-indigo-500" /> Approval Policy</p>
              <p className="text-[10px] text-slate-400 mt-1">When enabled, Manager-level publish actions create a Change Request instead of going live. Admins can still direct-publish.</p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-xs font-semibold text-slate-700">Manual Approval for Menu Changes</label>
                <p className="text-[10px] text-slate-400 mt-0.5">Managers must submit changes for Admin review before publishing.</p>
              </div>
              <button onClick={() => setSettings(p => ({ ...p, requireApproval: !p.requireApproval }))}
                className={cn('relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                  settings.requireApproval ? 'bg-indigo-600' : 'bg-slate-300')}>
                <span className={cn('inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow',
                  settings.requireApproval ? 'translate-x-6' : 'translate-x-1')} />
              </button>
            </div>
            {settings.requireApproval && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <p className="text-[10px] text-amber-700 leading-relaxed flex items-center gap-1">
                  <AlertTriangle size={10} /> <strong>Active:</strong> Managers will see "Submit for Approval" instead of "Publish Live". Admins & Platform staff bypass this gate.
                </p>
              </div>
            )}
          </div>

          {/* Emergency Shutdown */}
          <div className={cn('border rounded-xl p-6 space-y-4', settings.emergencyShutdown ? 'bg-red-50 border-red-300' : 'bg-white border-slate-200')}>
            <div>
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center gap-2">🚨 Temporary Menu Shutdown</p>
              <p className="text-[10px] text-slate-400 mt-1">Emergency override — immediately marks ALL menus as CLOSED, bypassing all schedules. Use for store emergencies or unexpected holidays.</p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className={cn('block text-xs font-semibold', settings.emergencyShutdown ? 'text-red-700' : 'text-slate-700')}>Global Emergency Shutdown</label>
                <p className="text-[10px] text-slate-400 mt-0.5">When active, public menu links and previews show "Menu is currently CLOSED".</p>
              </div>
              <button onClick={() => setSettings(p => ({ ...p, emergencyShutdown: !p.emergencyShutdown }))}
                className={cn('relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                  settings.emergencyShutdown ? 'bg-red-600' : 'bg-slate-300')}>
                <span className={cn('inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow',
                  settings.emergencyShutdown ? 'translate-x-6' : 'translate-x-1')} />
              </button>
            </div>
            {settings.emergencyShutdown && (
              <div className="bg-red-100 border border-red-300 rounded-lg px-3 py-2">
                <p className="text-[10px] text-red-700 leading-relaxed flex items-center gap-1">
                  <AlertTriangle size={10} /> <strong>ACTIVE:</strong> All menus are showing as CLOSED to customers. Disable this toggle and Save to restore normal operations.
                </p>
              </div>
            )}
          </div>


        </div>
      )}

      {/* ─── Sharing ─── */}
      {settingsSubTab === 'sharing' && canEdit && (
        <div className="space-y-6">
          {/* Active Module Connections */}
          {(() => {
            const ensureArr = (v: unknown): string[] => { if (Array.isArray(v)) return v; if (typeof v === 'string') { try { const p = JSON.parse(v); if (Array.isArray(p)) return p; } catch {} } return []; };
            const connected = sharingProfiles.filter(p => ensureArr(p.connectedModules).length > 0);
            if (connected.length === 0) return null;
            return (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                  <Link2 size={14} className="text-indigo-600" />
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Active Module Connections</p>
                  <span className="ml-auto text-[9px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">{connected.length} menu{connected.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="divide-y divide-slate-50">
                  {connected.map(p => (
                    <div key={p.id} className="flex items-center gap-3 px-5 py-3">
                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center"><BookOpen size={14} className="text-indigo-600" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{p.name}</p>
                        <p className="text-[10px] text-slate-400">{p.brandName}</p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        {ensureArr(p.connectedModules).map(mId => (
                          <span key={mId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-[9px] font-bold text-amber-700">
                            🔗 {getModuleName(mId)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Sharing Permissions (Global) */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <div>
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center gap-2"><Globe size={14} className="text-indigo-500" /> Sharing Permissions</p>
              <p className="text-[10px] text-slate-400 mt-1">Control who can generate and share public menu links. Admins always have access.</p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-xs font-semibold text-slate-700">Allow non-admin users to share public links</label>
                <p className="text-[10px] text-slate-400 mt-0.5">When OFF, only Administrators and Platform staff can generate public sharing links. Managers and Employees will see the button as disabled.</p>
              </div>
              <button onClick={() => setSettings(p => ({ ...p, allowPublicSharing: !p.allowPublicSharing }))}
                className={cn('relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                  settings.allowPublicSharing ? 'bg-emerald-500' : 'bg-slate-300')}>
                <span className={cn('inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow',
                  settings.allowPublicSharing ? 'translate-x-6' : 'translate-x-1')} />
              </button>
            </div>
            {!settings.allowPublicSharing && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <p className="text-[10px] text-amber-700 leading-relaxed flex items-center gap-1">
                  <AlertTriangle size={10} /> <strong>Restricted:</strong> Only Admins can share public menu links. Non-admin users will see the Share button as disabled with a tooltip.
                </p>
              </div>
            )}
            <div className="border-t border-slate-100 pt-4 flex items-center justify-between">
              <div>
                <label className="block text-xs font-semibold text-slate-700">Show Export Menu in manager accounts</label>
                <p className="text-[10px] text-slate-400 mt-0.5">When OFF, managers will not see the Export Menu button in Digital Preview. Admins keep access.</p>
              </div>
              <button onClick={() => setSettings(p => ({ ...p, allowNonAdminMenuExport: !p.allowNonAdminMenuExport }))}
                className={cn('relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                  settings.allowNonAdminMenuExport ? 'bg-emerald-500' : 'bg-slate-300')}>
                <span className={cn('inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow',
                  settings.allowNonAdminMenuExport ? 'translate-x-6' : 'translate-x-1')} />
              </button>
            </div>
            {!settings.allowNonAdminMenuExport && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                <p className="text-[10px] text-slate-600 leading-relaxed flex items-center gap-1">
                  <AlertTriangle size={10} /> <strong>Hidden for managers:</strong> The Export Menu action is removed from manager preview accounts until an Admin turns this back on.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function updateLocal(arg0: { categories: MenuCategory[]; id: string; name: string; brandId: string; brandName: string; tag: string; type: MenuProfileType; status: MenuProfileStatus; permitSyncRequired: boolean; ministryFileIds: string[]; createdBy: string; createdByName: string; createdAt: number; updatedAt: number; orgId?: string; connectedModules?: string[]; publicLinkEnabled?: boolean; publicLinkExpiry?: number; }) {
  throw new Error('Function not implemented.');
}

